import { gezeichnetesEnde } from "./ueberfaellig";
import { archivvermerkAus, type Archivvermerk } from "./archivvermerk";
import { ATTRAPPE, Fehler, aendere, ersetze, hole, holeDatei, kontextSetzen, loesche, sende } from "./klient";
import { einheitenNachtragen } from "../i18n";
import { betriebswerteAusIch } from "./betriebswerte";
import { vorabendJeTag } from "./vorabend";
import { vorschlagAus, type Zeitraumvorschlag } from "./zeitraumvorschlag";
import { annahmelueckenAus, type Annahmeluecken } from "./annahmeangaben";
import * as A from "./attrappe/daten";
/* Die Ortsaufnahmen liegen neben den übrigen Beispieldaten, aber in einer
   eigenen Datei: Sie werden von drei Modulen gelesen und von einem
   verändert, und ein Stand an zwei Stellen läuft auseinander. */
import * as AUF from "./attrappe/aufnahmen";
/* Dasselbe für die Einsätze: Die Tagesmappe liest sie hier, der Einsatzschirm
   in `daten/einsatzlage.ts` — und beide müssen dieselbe Belegung meinen, sonst
   führt eine Zeile der Mappe auf „Diesen Einsatz gibt es nicht mehr". */
import * as AEI from "./attrappe/einsaetze";
import type {
  Angebot, Anmeldung, Ansprechpartner, BelegZustand, Belegung, Benutzer,
  Einsatzort, Einstellungen, Ich, Job, JobSchritt, Kataloge, Kunde, Lage, LexVorrang,
  LexWechsel, LexWechselVorschau, LexZugang, Liste, Leistungsart, Leistungsgeraet,
  Preiskategorie, Mengensatz, Leistungszuschlag,
  Geraetegruppe, Kostenmiete, Kostenwirkung, Maschinentyp, MaschineAkte, PersonAkte, PersonQual,
  Planung, Position, Positionsart, Protokoll, Protokolle, Recht, Ressource, Rolle, Start,
  StartAusblick, StartHakt, StartKarteZeile, Tarif, Verfuegbarkeit, Vorbereitungsphase, Vorgang, VorgangZustand,
  Zuschlag, Preisauskunft, SperrGrund, Aufnahmetermin,
  BenutzerEingabe, Farbpaar, Kalenderfarben, Nummernkreis, Angebotsvorgaben,
  VorgangAmpel, Schadensfall, Tausch, TauschErgebnis, Defekt, DefektArt, DefektSchwere,
  Vorgangsnotiz, Kundennotiz, Kundenvorgang, Typakte, Zubehoerart, Zubehoerlage, Zubehoerstueck,
  Zuordnung, Warenartikel, Nachbereitungszeile,
  Mietkalender, Absagegrund, Notizart, Ortsart, Zahlungsziel,
  Masse, Gespannzeile, Hindernis, Geraetevorschlag, Aufliegervorschlag, Aufliegerstueck,
  HandyTag, HandyZeile, Geraetefoto,
  Bausteinart, Bausteinverlauf, Belegart, Belegtext, Belegtextstand, Textbaustein, Vermieter,
  Bedienung, Bedienungsart, Bedienerbindung, Zuschlagsbindung, Bedienerkandidat, Pflichtfeld, Pflichtfeldliste, Planstand, Takt,
  Passwortwechsel, Wiedervorlage, Pruefzeile, Vorbereitungsregel, Vorbereitungsebene, Pufferstand,
  Rollentafel, Rollenzeile, Abstand,
} from "./typen";
import { abStandAus } from "./abstand";
import {
  alsTag, datumFuer, ERSTE_STUNDE, HEUTE_SPALTE, SPALTEN, STUNDEN,
  wochenFenster, zweistellig,
} from "./kalender";
import { BLICKWINKEL, FOTOPROTOKOLLE, alsFotoabweichung, alsPflichtfotoliste, istBundesland, istKundentyp } from "@mrmachine/fach";
import type { Blickwinkel, Fotoabweichung, Fotoprotokoll, Kundentyp, Pflichtfotoliste } from "@mrmachine/fach";
import { alsAufgabe } from "./vorbereitung";
import { istJobArt } from "./jobarten";
import { mfaStand } from "./mfastand";
import { werktagAb } from "./verteilen";
import {
  tagImBetrieb, tagKurz as tagKurzImBetrieb, tagLang as tagLangImBetrieb, tageBis as tageBisImBetrieb, tagUndUhr, uhrImBetrieb,
  tagPlus as tagPlusImBetrieb, tageZwischen, wandImBetrieb, type Wanduhr,
} from "./betriebstag";
import type { Uebergabeinhalt } from "./vorbereitung";
import { alsAbholung, alsGespann } from "./abholung";
import { belegungsartFuerSperre as belegungsartFuer } from "./sperrart";
import { einmalJeAuftrag } from "./jeauftrag";
import { angebotszahlenAus, type Angebotszahlen } from "./angebotszahlen";
import { auslastungProzent } from "./auslastung";
import { einzelWieAufDemBeleg, vermerkeAusRechenweg, type Zeilenvermerk } from "./belegzeile";
import { inhaltAusPositionen, titelAus, type Inhaltszeile } from "./vorgangsinhalt";
import type { Erscheinungsbild, Logoart } from "../schale/erscheinungsbild";

/* ══════════════════════════════════════════════════════════════════
   Eine Funktion je Route aus docs/34-api.md.

   Jede kann zweierlei: mit `apps/api` sprechen oder dieselbe Form aus
   den Beispieldaten des Prototyps liefern. Umgeschaltet wird mit
   `VITE_ATTRAPPE=1` — sonst nichts.

   **Hier und nur hier werden die Formen angeglichen.** `apps/api`
   antwortet in der Sicht des Servers: `staff`, `asset_number`,
   `planned_from`, `check_notes`. Die Oberfläche denkt in Ressourcen,
   Kennungen und Spalten. Das ist keine andere Wahrheit, sondern eine
   andere Sicht — und übersetzt wird sie an einer Stelle.

   Wer eine Maske gegen `antwort.staff.initials` baut, trägt die
   Serversicht in die Oberfläche hinein und muss sie überall nachziehen,
   sobald der Server sie ändert. Darum steht in den Masken kein einziger
   Feldname aus der Datenbank.

   Drei Regeln, die hier durchgehalten werden:

   1. **Was sich ableiten lässt, kommt nicht aus der API.** Die
      Auslastungsquote rechnet diese Datei, nicht der Server.
   2. **Kein Satz aus der API wird angezeigt.** Was die Antwort an
      Schlüsseln liefert, wird auf i18n-Pfade abgebildet; was sie an
      Werten liefert, geht als Wert durch.
   3. **Beträge, die es nicht gibt, bleiben `null`.** Der Betrieb hat
      keine gewachsene Preisliste; eine `0` sähe aus wie ein Nullpreis.
   ══════════════════════════════════════════════════════════════════ */

/** Eine Antwort, die nicht sofort da ist — sonst blitzt der Ladezustand
 *  nie auf, und man baut ihn versehentlich falsch. */
const gleich = <T>(wert: T, ms = 120): Promise<T> =>
  new Promise((fertig) => setTimeout(() => fertig(wert), ms));

/* Die Attrappe hält ihren Stand für die Dauer der Sitzung. Ein
   verschobener Balken soll verschoben bleiben, solange der Reiter offen
   ist — sonst lässt sich das Brett nicht wirklich bedienen. */
let belegungen: Belegung[] = A.BELEGUNGEN.map((b) => ({ ...b }));
let angebote: Angebot[] = A.ANGEBOTE.map((a) => ({ ...a }));
let vorgaenge: Vorgang[] = A.VORGAENGE.map((v) => ({ ...v }));
let einstellungen: Einstellungen = { ...A.EINSTELLUNGEN, kalender: { ...A.EINSTELLUNGEN.kalender } };

/* ══════════════════════════════════════════════════════════════════
   Werkzeug zum Angleichen
   ══════════════════════════════════════════════════════════════════ */

/** Alles, was aus der API kommt, ist zunächst unbekannt geformt. */
type Roh = Record<string, unknown>;

/**
 * Die UUID zu einer Kennung.
 *
 * Das Brett spricht über `RL-07` — das steht auf der Maschine und in jeder
 * Zeile. Die API spricht über die Ressourcen-UUID, und schreiben lässt sich
 * nur mit ihr. Die Zuordnung entsteht beim Lesen des Bretts und lebt nur so
 * lange wie der Reiter; sie ist ein Nachschlagewerk, kein zweiter Bestand.
 */
let ressourcenSchluessel = new Map<string, string>();

function ressourcenIdMerken(ressourcen: Ressource[]): void {
  ressourcenSchluessel = new Map(
    ressourcen.filter((r) => r.res_id).map((r) => [r.id, r.res_id as string]),
  );
}

const alsListe = (wert: unknown): Roh[] => (Array.isArray(wert) ? (wert as Roh[]) : []);
const alsObjekt = (wert: unknown): Roh =>
  wert && typeof wert === "object" && !Array.isArray(wert) ? (wert as Roh) : {};

/** Eine Zeichenkette oder `""` — nie `"null"` und nie `"undefined"`. */
function txt(wert: unknown): string {
  if (wert === null || wert === undefined) return "";
  return typeof wert === "string" ? wert : String(wert);
}

/**
 * Eine Zahl oder `null`.
 *
 * `null` und nicht `0`: Die API liefert Beträge inzwischen als Zahl, aber
 * „kein Preis gerechnet" und „null Euro" sind zwei verschiedene Auskünfte, und
 * in einer Abrechnungsmaske ist der Unterschied der ganze Punkt.
 */
function zahl(wert: unknown): number | null {
  if (wert === null || wert === undefined || wert === "") return null;
  const n = typeof wert === "number" ? wert : Number(wert);
  return Number.isFinite(n) ? n : null;
}

/* Tage und Uhrzeiten aus Zeitpunkten: in deutscher Zeit, aus
   `daten/betriebstag.ts` (N8-02). Die API liefert `created_at`,
   `actual_from` … in UTC; der Tag daraus ist der Tag in Berlin, nicht der
   Anfang des ISO-Texts. Ein reiner Tag (`date`) bleibt, was er ist. */

/** Ein Tag `YYYY-MM-DD` aus einem Tag oder einem Zeitpunkt. */
const tagAusZeitpunkt = (wert: unknown): string => tagImBetrieb(txt(wert));

/** `2026-10-13` oder ein Zeitpunkt → `13.10.` — das Jahr steht im Kopf, nicht in der Zeile. */
const tagKurz = (wert: unknown): string => tagKurzImBetrieb(txt(wert));

/** Ein Zeitpunkt als `07:30` in deutscher Zeit. */
const uhrzeit = (wert: unknown): string => uhrImBetrieb(txt(wert));

/** Tag und Uhrzeit — für die Kopfzeile eines Protokolls. */
const tagUndZeit = (wert: unknown): string => tagUndUhr(txt(wert));

/** Wie viele Tage von heute bis dahin — negativ heißt überfällig. */
const tageBis = (wert: unknown): number | null => tageBisImBetrieb(txt(wert));

/* ══════════════ Wer bin ich ══════════════

   `apps/api` antwortet nach docs/34-api.md mit
   `{ staff, rollen, mandant, einstellungen }` — das ist die Sicht des
   Servers, und sie ist richtig: `staff` ist eine Tabelle, `mandant` eine
   andere. Die Oberfläche braucht eine Person mit Namen, Kürzel und Rollen. */

type IchRoh = {
  staff: { id: string; name: string; initials: string | null; staff_number: string | null };
  rollen: Rolle[];
  /** Was er am Ende darf — vom Server gerechnet (Migration 0147). */
  rechte?: Recht[];
  mandant: { id: string; name: string };
  einstellungen?: Record<string, unknown>;
  /** Nur lesen (0274): `mandant` nach Vertragsende, `pruefer` für den Prüfer. */
  nur_lesen?: "mandant" | "pruefer" | null;
};

function alsIch(roh: IchRoh): Ich {
  return {
    id: roh.staff.id,
    name: roh.staff.name,
    kurz: roh.staff.initials ?? roh.staff.name.slice(0, 2).toUpperCase(),
    mail: "",
    rollen: roh.rollen ?? [],
    /* **Die Rechte stehen neben den Rollen** (0147, 18.09.2026). Die Rolle
       ist weiterhin, was in der Oberfläche steht; wohin jemand darf,
       entscheidet diese Liste — sie kennt auch, was einem einzelnen Menschen
       dazugegeben oder entzogen wurde. Fehlt sie (ältere API), rechnet
       `schale/rechte.ts` sie aus den Rollen. */
    ...(Array.isArray(roh.rechte) ? { rechte: roh.rechte } : {}),
    mandant: roh.mandant?.name ?? "",
    nurLesen: roh.nur_lesen ?? null,
    ...betriebswerteAusIch(roh.einstellungen),
  };
}


/* ══════════════ Anmeldung ══════════════ */

/**
 * `code` geht nur mit, wenn das Konto einen zweiten Faktor verlangt. Die
 * Maske erfährt das von der API (`403 anmeldung.faktor_noetig`) und fragt
 * ihn erst dann ab — sie setzt ihn nicht voraus.
 */
export async function anmelden(
  email: string,
  passwort: string,
  code?: string,
  /** Ein eben eingerichteter Faktor (E4): Der erste Code bestätigt ihn. */
  faktorId?: string,
): Promise<Anmeldung> {
  if (ATTRAPPE) {
    const antwort: Anmeldung = { kontext: "attrappe", laeuft_ab: "", ich: A.ICH };
    kontextSetzen(antwort.kontext, antwort.laeuft_ab);
    return gleich(antwort, 260);
  }
  const roh = await sende<{ kontext: string; laeuft_ab: string; ich: IchRoh }>("/anmeldung", {
    email,
    passwort,
    ...(code ? { code } : {}),
    ...(code && faktorId ? { faktor_id: faktorId } : {}),
  });
  kontextSetzen(roh.kontext, roh.laeuft_ab);
  return { kontext: roh.kontext, laeuft_ab: roh.laeuft_ab, ich: alsIch(roh.ich) };
}

/**
 * Der Wechsel vor der Tür (Migration 0101). Die Anmeldung hat mit
 * `403 anmeldung.passwortwechsel_faellig` geantwortet; mit dem alten und dem
 * neuen Passwort geht es hier hinein — die Antwort ist dieselbe wie beim
 * Anmelden.
 */
export async function passwortWechseln(
  email: string,
  passwort: string,
  neuesPasswort: string,
): Promise<Anmeldung> {
  if (ATTRAPPE) return anmelden(email, neuesPasswort);
  const roh = await sende<{ kontext: string; laeuft_ab: string; ich: IchRoh }>("/anmeldung/passwort", {
    email,
    passwort,
    neues_passwort: neuesPasswort,
  });
  kontextSetzen(roh.kontext, roh.laeuft_ab);
  return { kontext: roh.kontext, laeuft_ab: roh.laeuft_ab, ich: alsIch(roh.ich) };
}

export async function abmelden(): Promise<void> {
  kontextSetzen(null);
  if (ATTRAPPE) return;
  await sende<void>("/abmeldung");
}

export async function ich(): Promise<Ich> {
  if (ATTRAPPE) return gleich(A.ICH);
  return alsIch(await hole<IchRoh>("/ich"));
}

/* ══════════════ Start ══════════════ */

/** Liegt die Belegung am gezeigten „heute"? (nur Attrappe) */
const heuteDa = (b: Belegung) => b.von <= HEUTE_SPALTE && b.bis >= HEUTE_SPALTE;

/**
 * Was die API als „hakt" meldet, und wohin man deswegen geht.
 *
 * Die API kennt den Befund (`belege_blockiert`), nicht den Weg dorthin —
 * Wege sind Sache des Routers, also der Oberfläche. Ein Server, der
 * `/abrechnung` in eine Antwort schreibt, weiß plötzlich, wie die Menüs
 * heißen.
 */
const HAKT: Record<string, { was: string; warum: string; geh: string }> = {
  uvv_ueberfaellig_draussen: {
    was: "start.haktUvv", warum: "start.haktUvvWarum", geh: "/ressourcen",
  },
  belege_blockiert: {
    was: "start.haktBelege", warum: "start.haktBelegeWarum", geh: "/abrechnung",
  },
  // Ein Kontowechsel wartet (Migration 0095). Freigegeben wird unter
  // Einstellungen · Lexware; ein Weg direkt auf das Blatt gibt es nicht,
  // das Blatt steht im Zustand der Maske und nicht in der Adresse.
  lexware_wechsel: {
    was: "start.haktLexwareWechsel", warum: "start.haktLexwareWechselWarum", geh: "/einstellungen/lexware",
  },
  optionen_verfallen: {
    was: "start.haktOptionenEins", warum: "start.haktOptionenWarum.bis", geh: "/angebote",
  },
  // Ein Hintergrundlauf, dessen letzter Versuch scheiterte (24.09.2026). Das
  // Laufbuch steht unter Einstellungen · Jobs.
  lauf_fehler: {
    was: "start.haktLauf", warum: "start.haktLaufWarum", geh: "/einstellungen/laeufe",
  },
  // Ein Defekt an einer Maschine, die GERADE beim Kunden steht. Der Weg
  // fuehrt ins Brett, weil dort der Tausch passiert (C2) -- nicht in eine
  // Defektliste, in der man ihn dann noch einmal suchen muesste.
  defekt_draussen: {
    was: "start.haktDefekt", warum: "start.haktDefektWarum", geh: "/kalender",
  },
  /* Der Zeitraum ist vorbei, und niemand hat zurückgenommen (22.09.2026).
     Der Weg führt in die Auftragsliste, Filter „Offen" — dort stehen die
     Aufträge, um die es geht, und dort wird gehandelt. */
  zeitraum_vorbei: {
    was: "start.haktZeitraum", warum: "start.haktZeitraumWarum", geh: "/auftraege",
  },
};

/** Die drei Fristenarten aus `GET /start` und ihr Satz in der Wiedervorlage. */
const FRIST: Record<string, string> = {
  optionsfrist: "start.fristOption",
  qualifikation: "start.fristQualifikation",
  pruefung: "start.fristPruefung",
};

export async function start(): Promise<Start> {
  if (ATTRAPPE) {
    const maschinen = A.RESSOURCEN.filter((r) => r.gruppe === "maschine");
    const belegt = maschinen.filter((r) =>
      belegungen.some((b) => b.res === r.id && heuteDa(b) && b.fest !== false)).length;
    const zurueck = vorgaenge.filter((v) => v.zustand === "zurueck");

    return gleich<Start>({
      maschinen: maschinen.length,
      belegt,
      frei: maschinen.length - belegt,
      quote: auslastungProzent(belegt, maschinen.length),
      draussen: belegungen.filter((b) => b.fest !== false && heuteDa(b)).length,
      auftraege_draussen: vorgaenge.filter((v) => v.zustand === "läuft").length,
      zu_pruefen: zurueck.length,
      angebote_offen: angebote.filter((a) => a.phase === "versendet").length,
      angebote_eilt: angebote.filter((a) => a.eilt && a.phase === "versendet").length,
      angebote_entwurf: angebote.filter((a) => a.phase === "entwurf").length,
      umsatz_netto: null,
      umsatz_vormonat: null,
      umsatz_belege: null,
      ausblick: { qualifikationen: [], termine: [], umsatz: null, raus: [] },
      zu_pruefen_zeilen: zurueck.map((v): Pruefzeile => ({
        art: "zurueck", nr: v.nr, kunde: v.kunde,
      })),
      hakt: [
        { id: "h1", stufe: "stopp", zahl: "1",
          was_key: "start.haktUvv", warum_key: "start.haktUvvWarum",
          werte: { res: "RP-05", datum: "28.04.", kunde: "Bauer GmbH" }, geh: "/ressourcen" },
      ],
      heute: [
        { id: "t1", kennung: "RL-07", wer_was: "Bauer GmbH", zeit: "07:30",
          marke_key: "start.raus", art: "raus", vorgang: "V-2026-0018" },
        { id: "t3", kennung: "RL-09", wer_was: "Meyer Bau", zeit: "16:00",
          marke_key: "start.rein", art: "rein", vorgang: "V-2026-0012" },
      ],
      /* Die Ortsaufnahmen des Prototyps stehen hier wie in der echten
         Antwort (19.09.2026): `GET /start` liefert sie unter `aufgaben`, aus
         demselben Stand, den die Begehung verändert (`attrappe/aufnahmen.ts`). */
      aufgaben: AUF.alsAufgaben(),
      wiedervorlage: [],
      lexware_abgleich_sekunden: 40,
      lexware_zustand: "verbunden" as const,
      lexware_wechsel: false,
    });
  }

  const roh = await hole<Roh>("/start");
  const k = alsObjekt(roh["kennzahlen"]);
  const umsatz = roh["umsatz"] ? alsObjekt(roh["umsatz"]) : null;

  const maschinen = zahl(k["maschinen"]) ?? 0;
  const belegt = zahl(k["belegt_heute"]) ?? 0;

  /* Karte „Heute": hinaus und zurück in **einer** Liste, nach Uhrzeit
     sortiert. Zwei Listen nebeneinander hieße, den Tag zweimal zu lesen.

     **Die Vorgangsnummer reist mit** (18.09.2026). `GET /start` liefert sie
     an jedem Ausgang und jeder Rückgabe; bis heute ist sie hier
     weggefallen, und damit war die Zeile eine Auskunft ohne Weg. Luca:
     *„Die Vorgänge sollen eigentlich am besten auch klickbar sein … Ich
     will dann direkt in die Vorgänge kommen."* */
  /* **Überfällig und verspätet** (Prüfrunde Runde 11, N11-02): `GET /start`
     liefert seit dann dieselbe Liste wie die Planung
     (`dienste/tagesbewegungen.ts`) — mit den Geräten, die seit Tagen beim
     Kunden stehen, und denen, die längst hätten hinausgehen sollen. Statt
     einer Uhrzeit von damals steht dort „seit 02.10.", die Marke sagt
     „überfällig" bzw. „verspätet", und sortiert wird nach dem Planzeitpunkt —
     so stehen sie vorn, wie in der Planung. */
  const heuteRoh: { wann: string; z: StartKarteZeile }[] = [
    ...alsListe(roh["ausgaenge"]).map((z) => {
      const leistung = txt(z["positionsart"]) === "leistung";
      const verspaetet = txt(z["verspaetet_seit"]);
      return {
        wann: txt(z["planned_from"]),
        z: {
          id: `raus-${txt(z["id"])}`,
          kennung: txt(z["ressource"]),
          wer_was: txt(z["kunde"]) || txt(z["ort"]),
          zeit: verspaetet ? tagKurz(verspaetet) : uhrzeit(z["planned_from"]),
          ...(verspaetet ? { zeit_key: "start.seitTag" } : {}),
          marke_key: verspaetet ? "start.rausVerspaetet" : leistung ? "start.leistung" : "start.raus",
          art: leistung ? "leistung" : "raus",
          ...(txt(z["vorgang"]) ? { vorgang: txt(z["vorgang"]) } : {}),
        },
      };
    }),
    ...alsListe(roh["rueckgaben"]).map((z) => {
      const ueberfaellig = txt(z["ueberfaellig_seit"]);
      return {
        wann: txt(z["planned_until"]),
        z: {
          id: `rein-${txt(z["id"])}`,
          kennung: txt(z["ressource"]),
          wer_was: txt(z["kunde"]),
          zeit: ueberfaellig ? tagKurz(ueberfaellig) : uhrzeit(z["planned_until"]),
          ...(ueberfaellig ? { zeit_key: "start.seitTag" } : {}),
          marke_key: ueberfaellig ? "start.reinUeberfaellig" : "start.rein",
          art: "rein",
          ...(txt(z["vorgang"]) ? { vorgang: txt(z["vorgang"]) } : {}),
        },
      };
    }),
  ];
  const heute: StartKarteZeile[] = heuteRoh
    .sort((a, b) => a.wann.localeCompare(b.wann))
    .map((e) => e.z);

  /* Karte „Wiedervorlage": die Fristen. Was demnächst abläuft, ist genau
     das, was wieder vorgelegt werden muss — eine zweite Liste dafür gäbe
     es in der Datenbank nicht, und eine erfundene wäre schlimmer. */
  const wiedervorlage: StartKarteZeile[] = alsListe(roh["fristen"]).map((f, i) => {
    const tage = tageBis(f["bis"]);
    return {
      id: `frist-${i}`,
      kennung: txt(f["betrifft"]),
      wer_was: txt(f["dazu"]),
      wer_was_key: FRIST[txt(f["art"])] ?? "start.fristPruefung",
      /* Die Art roh daneben: Der Weg aus einer Frist heraus hängt daran
         (`bausteine/Aufgaben.tsx`, `FRIST_WEG`), und ein i18n-Schlüssel ist
         kein Merkmal, an dem man einen Weg festmacht. */
      art: txt(f["art"]),
      /* Die Vorgangsnummer, wenn die Frist an einem Vorgang hängt — nur die
         Optionsfrist tut das. Sie schlägt den Weg aus der Art: Wer eine Frist
         sieht, will in den Vorgang, nicht ins Brett. */
      ...(txt(f["vorgang"]) ? { vorgang: txt(f["vorgang"]) } : {}),
      /* Die Qualifikation trägt ihren Namen in `dazu`, den Schlüssel nur
         noch hier — für den Zeiger (Lisas Durchgang, 08.10.2026). */
      ...(txt(f["code"]) ? { code: txt(f["code"]) } : {}),
      zeit: tage === null ? tagKurz(f["bis"]) : String(Math.abs(tage)),
      ...(tage !== null && tage < 0 ? { vorbei: true } : {}),
      /* Drei Fälle, nicht zwei: Was vorbei ist, ist **überfällig** und nicht
         „heute". HH-23 war seit dem 21.08. dran — als „heute" gelesen sähe die
         Karte harmloser aus, als die Lage ist. */
      ...(tage === null
        ? {}
        : {
            zeit_key: tage < 0
              ? "start.fristUeberfaellig"
              : tage === 0 ? "start.fristHeute" : "start.fristInTagen",
          }),
    };
  });

  const abgleich = txt(k["lexware_abgleich"]);
  const abgleichSekunden = abgleich
    ? Math.max(0, Math.round((Date.now() - new Date(abgleich).getTime()) / 1000))
    : null;

  return {
    maschinen,
    belegt,
    /* **Frei ist gezählt, nicht gerechnet** (N9-02, 05.10.2026) — dieselbe
       Zahl wie in der Planung. `maschinen − belegt` nahm die Werkstatt als
       frei mit; fehlt das Feld (ältere API), bleibt der alte Rückfall. */
    frei: zahl(k["frei_heute"]) ?? maschinen - belegt,
    // Leitsatz 4: Die Quote lässt sich ableiten, also liefert sie die API nicht.
    quote: auslastungProzent(belegt, maschinen),
    draussen: zahl(k["draussen"]) ?? 0,
    // Fehlt sie (ältere API), gilt die Gerätezahl — besser als eine Null.
    auftraege_draussen: zahl(k["auftraege_draussen"]) ?? zahl(k["draussen"]) ?? 0,
    zu_pruefen: zahl(k["warten_auf_freigabe"]) ?? 0,
    angebote_offen: zahl(k["angebote_offen"]) ?? 0,
    angebote_eilt: zahl(k["angebote_eilt"]) ?? 0,
    angebote_entwurf: zahl(k["angebote_entwurf"]) ?? 0,
    /* Bis zum 24.09.2026 stand hier fest `null` — „bis die Kalkulation aus
       04-preisfindung.md vorliegt". Die Summe freigegebener Belege braucht
       aber keine Preisliste: Sie liest, was der Lauf gerechnet hat (Prüfung
       07). `umsatz` fehlt, wenn der Anrufer keine Beträge sehen darf. */
    umsatz_netto: umsatz ? zahl(umsatz["monat"]) ?? 0 : null,
    umsatz_vormonat: umsatz ? zahl(umsatz["vormonat"]) ?? 0 : null,
    umsatz_belege: umsatz ? zahl(umsatz["belege"]) ?? 0 : null,
    ausblick: ausblickAus(alsObjekt(roh["ausblick"]), alsListe(roh["ausgaenge"])),
    /* **Vier Arten in einer Liste** (18.09.2026). Die Marke unterscheidet sie,
       die Zeilenform bleibt dieselbe wie bei den Vorgängen, die zurück sind.
       Die Kennzahl „Draußen … N zurück" zählt weiter nur diese eine Art
       (`warten_auf_freigabe`); die Summe der Karte ist die Zahl der Zeilen. */
    zu_pruefen_zeilen: alsListe(roh["zu_pruefen"]).map((z): Pruefzeile => ({
      art: ([
        "zurueck", "fahrer_fehlt", "tag_nicht_bestaetigt", "unterschrift_fehlt",
        "werkstatt_vorbei", "fotos_fehlen", "nachtanken", "stammdaten_fehlen",
      ] as const).find((a) => a === txt(z["art"])) ?? "zurueck",
      nr: txt(z["nummer"]),
      kunde: txt(z["kunde"]),
      ...(txt(z["tag"]) ? { tag: txt(z["tag"]) } : {}),
      ...(txt(z["grund"]) ? { grund: txt(z["grund"]) } : {}),
      ...(txt(z["position"]) ? { position: txt(z["position"]) } : {}),
      ...(tagAusZeitpunkt(z["seit"]) ? { seit: tagAusZeitpunkt(z["seit"]) } : {}),
      ...(txt(z["protokoll_id"]) ? { protokoll: txt(z["protokoll_id"]) } : {}),
      // Pflichtfotos, die draußen nicht gingen (0173).
      ...(txt(z["belegung_id"]) ? { belegung: txt(z["belegung_id"]) } : {}),
      ...(Array.isArray(z["blickwinkel"]) ? { blickwinkel: (z["blickwinkel"] as unknown[]).map(txt) } : {}),
      // Was dem Kunden für die Rechnung fehlt (W2) — Schlüssel, kein Satz.
      ...(Array.isArray(z["luecken"]) ? { luecken: (z["luecken"] as unknown[]).map(txt) } : {}),
    })),
    hakt: alsListe(roh["hakt"]).map((h): StartHakt => {
      const schluessel = txt(h["key"]);
      const pfade = HAKT[schluessel];
      const werte = alsObjekt(h["daten"]);
      /* **Ein Satz, der nur bei null Geräten anders lautet** (22.09.2026).
         „… davon 0 beim Kunden ohne Rücknahme" stand am Bildschirm und sagte
         das Gegenteil von dem, was gemeint war: Bei diesen Aufträgen ist gar
         nichts hinausgegangen. Die Zahl entscheidet über den Satz. */
      const warum = schluessel === "zeitraum_vorbei" && Number(werte["draussen"] ?? 0) === 0
        ? "start.haktZeitraumWarumNieRaus"
        /* Das Angebot, das heute oder morgen abläuft (P6-Optionen, 24.09.2026):
           Der Satz sagt „heute" oder „morgen" statt eines Datums. */
        : schluessel === "optionen_verfallen" && txt(werte["wann"])
          ? `start.haktOptionenWarum.${txt(werte["wann"])}`
          : pfade?.warum ?? "start.wasHakt";
      const was = schluessel === "optionen_verfallen"
        ? (Number(h["zahl"] ?? 0) === 1 ? "start.haktOptionenEins" : "start.haktOptionenMehr")
        : pfade?.was ?? "start.wasHakt";
      /* Die Zeile führt ins Angebot, nicht ins Brett — dort wird verlängert,
         nachgefasst oder abgesagt. */
      const geh = schluessel === "optionen_verfallen" && txt(werte["vorgang"])
        ? `/angebote/${encodeURIComponent(txt(werte["vorgang"]))}`
        : pfade?.geh;
      return {
        id: schluessel,
        stufe: txt(h["stufe"]) === "acht" ? "acht" : "stopp",
        zahl: String(zahl(h["zahl"]) ?? 0),
        was_key: was,
        warum_key: warum,
        // Datumswerte werden hier formatiert — die API liefert `YYYY-MM-DD`,
        // und wie ein Datum aussieht, ist eine Frage der Sprache.
        werte: Object.fromEntries(
          Object.entries(werte).map(([name, wert]) => [
            name,
            name === "datum" ? tagKurz(wert) : txt(wert),
          ]),
        ),
        ...(geh ? { geh } : {}),
      };
    }),
    heute,
    aufgaben: alsListe(roh["aufgaben"]).map(alsAufgabe),
    wiedervorlage,
    lexware_abgleich_sekunden: abgleichSekunden,
    lexware_zustand: (["ungepflegt", "verbunden", "abgelehnt", "nicht_erreichbar"] as const)
      .find((z) => z === txt(k["lexware_zustand"])) ?? "ungepflegt",
    lexware_wechsel: k["lexware_wechsel"] === true,
  };
}

/* ══════════════ Planung ══════════════ */

/**
 * **Wo eine Belegung steht — eine Ableitung für Brett und Auftrag.**
 *
 * `actual_from` leer → geplant ○, gesetzt → draußen ●, `actual_until` gesetzt
 * → zurück ✓. Bis zum 16.09.2026 las das Brett stattdessen `booking.kind`,
 * der Auftrag das Ist: V-2026-0018 stand im Brett auf „geplant" mit dem Knopf
 * „Übergeben", im Auftrag auf „zurück". Zwei Wahrheiten über eine Maschine.
 */
export function standAus(istVon: unknown, istBis: unknown): BelegZustand {
  if (txt(istBis)) return "zurueck";
  if (txt(istVon)) return "laeuft";
  return "geplant";
}

/**
 * Warum gesperrt ist — das Wort, das auf einem Sperrbalken steht.
 *
 * **Bis zum 21.09.2026 stand dort immer „Sperre".** Das Feld `grundArt` gab es
 * im Typ, aber niemand füllte es, also griff überall der Rückfall `sonstig` —
 * und Wartung, Urlaub und Werkstatt sahen im Band gleich aus.
 *
 * Der **Transportpuffer** machte es zum Problem: Er ist die einzige Sperre,
 * die kein Mensch eingetragen hat. Wer „Sperre" liest, sucht den Kollegen, der
 * sie gesetzt hat — und findet keinen. Deshalb wird er zuerst gefragt.
 */
function sperrgrundVon(b: Roh): SperrGrund | null {
  if (txt(b["puffer_fuer"])) return "transportpuffer";
  switch (txt(b["art"])) {
    case "wartung": return "wartung";
    case "urlaub": return "urlaub";
    case "schulung": return "schulung";
    // Der Termin einer Ortsaufnahme (0180) — ein eigenes Wort, ein eigener Weg.
    case "termin": return "termin";
    // `transport` ohne Puffer ist eine von Hand eingetragene Transportzeit,
    // `krank` und `sperre` haben kein eigenes Wort — beides bleibt „Sperre".
    default: return null;
  }
}

/** Die Geschäftsart eines Balkens aus dem, was die Belegung ist. */
function geschaeftVon(b: Roh, positionsart: string): Belegung["geschaeft"] {
  // `booking.kind` kennt drei Arten, die keine Vermietung sind (Migration 0005).
  if (["sperre", "wartung", "transport"].includes(txt(b["art"]))) return "sperre";
  // Eine Belegung ohne Vorgang ist keine Vermietung, sondern eine Sperre:
  // Wartung, Urlaub, Werkstatt. Der Grund steht in `notiz`.
  if (!txt(b["position_id"])) return "sperre";
  return positionsart === "leistung" ? "leistung" : "miete";
}

/** Die Dreierreihe der Startseite (08.10.2026) — fehlt sie (ältere API), bleibt sie leer. */
function ausblickAus(a: Roh, ausgaenge: Roh[]): StartAusblick {
  const arten = ["uvv", "pruefung", "wartung", "werkstatt"] as const;
  return {
    qualifikationen: alsListe(a["qualifikationen"]).map((q) => ({
      person: txt(q["person"]), wort: txt(q["wort"]), bis: txt(q["bis"]), tage: zahl(q["tage"]) ?? 0,
    })),
    termine: alsListe(a["termine"]).map((x) => ({
      art: arten.find((w) => w === txt(x["art"])) ?? "pruefung",
      kennung: txt(x["kennung"]), typ: txt(x["typ"]) || null, am: txt(x["am"]),
      tage: zahl(x["tage"]) ?? 0, notiz: txt(x["notiz"]) || null, draussen: x["draussen"] === true,
    })),
    umsatz: Array.isArray(a["umsatz"])
      ? alsListe(a["umsatz"]).map((m) => ({ monat: txt(m["monat"]), netto: zahl(m["netto"]) ?? 0 }))
      : null,
    raus: ausgaenge.map((z) => {
      const art = txt(z["uebergabeart"]);
      return {
        id: txt(z["id"]), kennung: txt(z["ressource"]), mensch: txt(z["art"]) === "person",
        kunde: txt(z["kunde"]), ort: txt(z["ort"]), vorgang: txt(z["vorgang"]) || null,
        uhr: uhrzeit(z["planned_from"]), erledigt: z["protokoll_da"] === true,
        verspaetetSeit: txt(z["verspaetet_seit"]) || null,
        uebergabeart: art === "liefern" || art === "abholen" ? art : null,
      };
    }),
  };
}

/**
 * Ein Zeitpunkt der API auf eine Spalte des gezeigten Fensters.
 *
 * Das Brett rechnet in Spalten, die Datenbank in Zeitpunkten. Umgerechnet wird
 * hier und nicht im Brett: Sonst müsste jede Maske wissen, welcher Montag
 * gerade oben steht.
 */
function spalteVon(zeitpunkt: unknown, fensterVon: Date): number {
  // Der Tag des Zeitpunkts in deutscher Zeit (N8-02); das Fenster ist ein reiner Tag.
  const tag = tagImBetrieb(txt(zeitpunkt));
  return tag ? tageZwischen(alsTag(fensterVon), tag) : 0;
}

/**
 * Der Arbeitstag, mit dem diese App jede Belegung anlegt: 07:00 bis 18:00.
 *
 * Die Zahlen standen dreimal im Code und heissen jetzt einmal so. Sie sind
 * **keine Vorgabe des Betriebs**, sondern die Uhrzeit, auf die das Tagesraster
 * eine Spalte legt — das Stundenraster daneben zeigt den echten Zeitpunkt.
 */
const TAG_BEGINN = 7;
const TAG_ENDE = 18;

/**
 * Die letzte Spalte, auf der ein Balken noch liegt.
 *
 * `planned_until` ist eine OBERE GRENZE, keine Anwesenheit: In der Datenbank
 * steht `tstzrange(planned_from, planned_until)`, und das ist halboffen.
 *
 * Fuer die uebliche Belegung (bis 18:00) faellt das nicht auf. Beim
 * Geraetetausch schon: Die alte Belegung endet **am Morgen** des Tauschtages,
 * dort wo die neue beginnt. An diesem Tag stand das alte Geraet keine Stunde
 * mehr beim Kunden — wer das Ende als Anwesenheit liest, malt beide Balken auf
 * denselben Tag, und das Brett meldet einen Konflikt, den die Datenbank
 * zurecht nicht kennt.
 *
 * Die Regel: Endet eine Belegung, bevor der Arbeitstag anfaengt, gehoert
 * dieser Tag ihr nicht mehr.
 */
function vorTagesbeginn(w: Wanduhr): boolean {
  return w.stunde < TAG_BEGINN
    || (w.stunde === TAG_BEGINN && w.minute === 0 && w.sekunde === 0);
}

function letzteSpalte(zeitpunkt: unknown, fensterVon: Date): number {
  const w = wandImBetrieb(txt(zeitpunkt));
  if (!w) return 0;
  return spalteVon(zeitpunkt, fensterVon) - (vorTagesbeginn(w) ? 1 : 0);
}

/** Die Stunde eines Zeitpunkts als Spalte der Stundenansicht (ab 06:00).
 *  Die Grenzen kommen aus `kalender.ts` — dort stehen die Spalten, und eine
 *  zweite Stelle mit `6` und `11` läuft beim ersten Umbau auseinander. */
function stundeVon(zeitpunkt: unknown): number | undefined {
  const w = wandImBetrieb(txt(zeitpunkt));
  if (!w) return undefined;
  const spalte = w.stunde - ERSTE_STUNDE;
  return spalte >= 0 && spalte <= STUNDEN.length - 1 ? spalte : undefined;
}

/**
 * Die letzte Stunde, auf der ein Balken noch liegt — dieselbe Regel wie
 * `letzteSpalte`, eine Ebene feiner.
 *
 * `planned_until` ist eine obere Grenze. Eine Belegung bis 18:00 belegt die
 * Stunde 18:00 nicht; ihre letzte ist 17:00. Bis zum 16.09.2026 stand hier
 * `stundeVon`, und das gab für 18:00 `undefined` — das Raster fiel auf die
 * letzte Spalte zurück (richtig), das Blatt daneben aber auf `STUNDEN[0]`
 * und zeigte **„Bis 06:00"** (docs/12-offene-fragen.md, 39a). Eine falsche
 * Auskunft ist schlimmer als eine fehlende.
 */
function letzteStunde(zeitpunkt: unknown): number | undefined {
  const w = wandImBetrieb(txt(zeitpunkt));
  if (!w) return undefined;
  const volleStunde = w.minute === 0 && w.sekunde === 0;
  const spalte = w.stunde - ERSTE_STUNDE - (volleStunde ? 1 : 0);
  if (spalte < 0) return undefined;
  return Math.min(spalte, STUNDEN.length - 1);
}

/** Die Uhrzeit eines Zeitpunkts als `HH:MM` — was im Blatt stehen soll.
 *  Das Raster rechnet in Spalten, die Auskunft nennt den Zeitpunkt. */
function uhrzeitVon(zeitpunkt: unknown): string | undefined {
  const w = wandImBetrieb(txt(zeitpunkt));
  return w ? `${zweistellig(w.stunde)}:${zweistellig(w.minute)}` : undefined;
}

/**
 * Anfang und Ende als Tag im Klartext (`YYYY-MM-DD`) — die Auskunft, nicht
 * die Zeichnung.
 *
 * **Der Balken wird geschnitten, die Wahrheit nicht.** Wer eine Belegung nur
 * als Spaltennummer weiterreicht, reicht den Schnitt mit: RP-05 ist vom
 * 01.08. bis zum 31.12.2026 vermietet, und das Blatt daneben sagte
 * „Von 14.09. · Bis 18.09." — die Raender des gezeigten Fensters
 * (docs/12-offene-fragen.md, 42). Wer das liest, ruft den Kunden an und sagt
 * ihm etwas Falsches.
 *
 * Gebildet wird beides **vor** dem Schneiden. Es ist derselbe Weg wie bei
 * `vonUhr`/`bisUhr` (39a): Die Spalte bleibt fuer das Zeichnen, der Zeitpunkt
 * steht daneben im Klartext.
 */
export function ersterTag(zeitpunkt: unknown): string | undefined {
  return tagImBetrieb(txt(zeitpunkt)) || undefined;
}

/** Der letzte Tag, an dem die Belegung wirklich steht — dieselbe Regel wie
 *  `letzteSpalte`, nur in Kalendertagen statt in Spalten. Eine Belegung, die
 *  am Montagmorgen endet, gehoert dem Sonntag davor; das Brett zeigt den
 *  Sonntag nicht, die Auskunft nennt ihn. */
export function letzterTag(zeitpunkt: unknown): string | undefined {
  const w = wandImBetrieb(txt(zeitpunkt));
  if (!w) return undefined;
  const tag = tagImBetrieb(txt(zeitpunkt));
  return vorTagesbeginn(w) ? tagPlusImBetrieb(tag, -1) : tag;
}

/** Kalendertage zwischen zwei `JJJJ-MM-TT` — nur für die Attrappe oben. */
function tageZwischenAttrappe(von: string, bis: string): number {
  return Math.round((new Date(`${bis}T12:00:00Z`).getTime() - new Date(`${von}T12:00:00Z`).getTime()) / 86_400_000);
}

export async function planung(von?: string, bis?: string): Promise<Planung> {
  if (ATTRAPPE) {
    /* **Die Spalten der Attrappe sind Arbeitstage ab dem Fenster** (25.09.2026).
       Die Beispielwoche ist für ein Fenster ab Montag gebaut — Spalte 0 bis 4.
       Beginnt das Fenster an einem anderen Tag (der Sprung aus „Verteilen"
       setzt es auf heute), lagen zwei Spalten davon im Wochenende und die
       Balken verschwanden. Umgerechnet in Kalendertage ab `von` liegen sie
       wieder auf fünf Arbeitstagen — und die Aufgaben der Attrappe
       (`attrappe/faelle.ts`, `VERTEILSTAND`) auf denselben. Ab Montag ändert
       sich nichts. */
    /* Eine Spalte vor dem Fenster zählt Werktage rückwärts — die Attrappe
       braucht das für den Balken, der vor dem Fenster anfängt (25.09.2026). */
    const werktag = (spalte: number): string => {
      if (!von) return "";
      if (spalte >= 0) return werktagAb(von, spalte);
      let tag = werktagAb(von, 0);
      for (let rest = -spalte; rest > 0;) {
        tag = alsTag(new Date(new Date(`${tag}T12:00:00`).getTime() - 86_400_000));
        const w = new Date(`${tag}T12:00:00`).getDay();
        if (w !== 0 && w !== 6) rest -= 1;
      }
      return tag;
    };
    const kalender = (spalte: number) => (von ? tageZwischenAttrappe(von, werktag(spalte)) : spalte);
    /* **Der echte Tag steht dabei** (25.09.2026): Ohne `vonTag`/`bisTag` weiß
       das Brett nicht, dass ein Balken über das Fenster hinausgeht — und
       zeigt an der Schnittkante keinen Pfeil zu seinem Ende. */
    return gleich<Planung>({
      ressourcen: A.RESSOURCEN,
      belegungen: belegungen.map((b) => ({
        ...b, von: kalender(b.von), bis: kalender(b.bis),
        ...(von ? { vonTag: werktag(b.von), bisTag: werktag(b.bis) } : {}),
      })),
    });
  }

  /* Ohne Angabe: die laufende Woche. Das Brett zeigt fünf Spalten, also
     fragt es fünf Tage — ein Fenster über vier Wochen zu laden und vier
     Fünftel wegzuwerfen wäre dieselbe Antwort, nur größer. */
  const fenster = von && bis ? { von, bis } : wochenFenster(0);
  const frage = new URLSearchParams({ von: fenster.von, bis: fenster.bis });
  const roh = await hole<Roh>(`/planung?${frage.toString()}`);

  const fensterVon = new Date(`${fenster.von}T00:00:00`);
  /* Wie viele Spalten das Fenster hat — **gerechnet, nicht angenommen**.
     Hier stand `SPALTEN.length`, also fest fünf. Für das Brett stimmt das,
     weil es Montag bis Freitag zeigt; für jedes andere Fenster war es falsch,
     und zwar unauffällig: Eine Belegung am siebten Tag wurde auf die fünfte
     Spalte geklemmt und stand damit an einem Tag, an dem sie nicht ist.
     Die Schnellerfassung fragt seit dem 15.09.2026 ihren eigenen Zeitraum ab
     und braucht deshalb die wirkliche Länge. */
  const fensterSpalten = Math.max(
    1,
    Math.round((new Date(`${fenster.bis}T00:00:00`).getTime() - fensterVon.getTime()) / 86_400_000) + 1,
  );
  const ressourcen: Ressource[] = [];
  const balken: Belegung[] = [];

  alsListe(roh["zeilen"]).forEach((z) => {
    const istPerson = txt(z["art"]) === "person";
    const kennung = txt(z["asset_number"]) || txt(z["label"]);
    const gruppe = txt(z["gruppe"]);

    ressourcen.push({
      id: kennung,
      res_id: txt(z["id"]),
      gruppe: istPerson ? "personal" : "maschine",
      // Menschen haben keinen Maschinentyp. Den Namen ein zweites Mal
      // darunterzuschreiben ist keine Auskunft, sondern eine Dublette.
      typ: istPerson ? "" : txt(z["typ"]),
      // Der Name des Typs steht auf dem Balken, die Kennung braucht die
      // Preisfrage: `GET /preis` rechnet ueber `machine_type_id`, nicht
      // ueber "Manitou MT 625 H".
      ...(istPerson || !txt(z["machine_type_id"]) ? {} : { typ_id: txt(z["machine_type_id"]) }),
      kuerzel: kuerzelVon(kennung),
      // Personal hat keine Gerätegruppe. `kat` trägt die Trennung zwischen
      // Gerät und Mensch schon mit — ein zweiter Filter daneben wäre derselbe
      // Filter mit anderem Namen (siehe Ressourcen.tsx).
      kat: istPerson ? PERSONALGRUPPE : gruppe,
      ...(istPerson ? {} : { status: txt(z["maschinenstatus"]) }),
      // Die Qualifikationen als Suchwörter (P-02) — nur Menschen tragen welche.
      ...(istPerson && Array.isArray(z["qualifikationen"])
        ? { qualWorte: (z["qualifikationen"] as unknown[]).map(String) } : {}),
      /* Das Anmietfenster reist mit der Zeile (Migration 0080). Brett,
         Schnellerfassung und Tauschblatt leiten „frei" selbst aus den Balken
         ab — und ein Fremdgerät, das erst nächsten Monat kommt, hat in dieser
         Woche gar keinen Balken. Wer nur Balken liest, hält es für die ganze
         Woche frei. */
      ...(istPerson ? {} : {
        fremd: z["is_external"] === true,
        fensterAb: txt(z["available_from"]) || null,
        fensterBis: txt(z["available_until"]) || null,
      }),
      /* Gezählter Bestand (0194): eine Zeile für alle Stücke, im Brett als
         „belegt · frei" je Tag statt als Balken. */
      ...(zahl(z["bestand"]) ? { bestand: zahl(z["bestand"]) as number } : {}),
    });

    alsListe(z["belegungen"]).forEach((b) => {
      /* **Überfällig wird bis jetzt gezeichnet** (Prüfrunde Runde 10, N10-04):
         Ein Gerät, das nach dem Planende noch draußen ist, hatte im Brett
         keinen Balken — die Spalte „Heute" zählte es als draußen. Gezeichnet
         wird bis jetzt; Planende (`bisZeit`, `bisUhr`) bleibt die Auskunft. */
      const ueberfaellig = b["ueberfaellig"] === true;
      const endeGezeichnet = gezeichnetesEnde(txt(b["bis"]), ueberfaellig);
      const vonSp = spalteVon(b["von"], fensterVon);
      const bisSp = letzteSpalte(endeGezeichnet, fensterVon);
      // Ein Balken, der das Fenster nur streift, wird an seinem Rand
      // abgeschnitten — sonst zeichnete das Brett außerhalb seiner Spalten.
      const links = Math.max(0, Math.min(vonSp, fensterSpalten - 1));
      const rechts = Math.max(links, Math.min(bisSp, fensterSpalten - 1));
      const art = txt(b["art"]);
      const positionsart = txt(b["positionsart"]);
      const optionBis = txt(b["option_bis"]);
      const vonStd = stundeVon(b["von"]);
      const bisStd = letzteStunde(endeGezeichnet);
      const vonUhr = uhrzeitVon(b["von"]);
      const bisUhr = uhrzeitVon(b["bis"]);
      const vonTag = ersterTag(b["von"]);
      // Der Tag bleibt das Planende — die Auskunft im Blatt, nicht die Zeichnung (N10-04).
      const bisTag = letzterTag(b["bis"]);
      const seit = ueberfaellig ? bisTag : undefined;

      balken.push({
        id: txt(b["id"]),
        res: kennung,
        ...(zahl(b["menge"]) ? { menge: zahl(b["menge"]) as number } : {}),
        von: links,
        bis: rechts,
        kunde: txt(b["kunde"]) || txt(b["notiz"]),
        geschaeft: geschaeftVon(b, positionsart),
        ...(txt(b["positionstyp"]) ? { verkauft: txt(b["positionstyp"]) } : {}),
        ...(sperrgrundVon(b) ? { grundArt: sperrgrundVon(b) as SperrGrund } : {}),
        // `option` blockt weich — das ist ihr Zweck (Migration 0005).
        fest: art !== "option",
        zustand: standAus(b["ist_von"], b["ist_bis"]),
        ...(txt(b["vorgang"]) ? { vorgang: txt(b["vorgang"]) } : {}),
        ...(txt(b["ort"]) ? { ort: txt(b["ort"]) } : {}),
        ...(optionBis ? { zusatz: tagKurz(optionBis), optionBis } : {}),
        // Auftrag im Abschluss statt Angebot (N7-05).
        ...(b["im_abschluss"] === true ? { imAbschluss: true } : {}),
        ...(b["abschluss_laeuft"] === true ? { abschlussLaeuft: true } : {}),
        ...(txt(b["grund"]) ? { grund: txt(b["grund"]) } : {}),
        ...(vonStd === undefined ? {} : { vonStd }),
        ...(bisStd === undefined ? {} : { bisStd }),
        // Die Zeitpunkte im Klartext — für das Blatt, das eine Uhrzeit nennen
        // soll und keine Spaltennummer.
        ...(vonUhr === undefined ? {} : { vonUhr }),
        ...(bisUhr === undefined ? {} : { bisUhr }),
        // Und dieselbe Trennung eine Ebene darüber: die echten Tage, gebildet
        // bevor `links`/`rechts` auf das Fenster schneiden.
        ...(vonTag === undefined ? {} : { vonTag }),
        ...(bisTag === undefined ? {} : { bisTag }),
        /* **Und die Zeitpunkte ganz roh** (21.09.2026). Alles darüber ist für
           die Zeichnung aufbereitet und dabei verkürzt: die Spalte auf das
           Fenster, `bisTag` auf den Tag davor, wenn das Ende am frühen Morgen
           liegt, die Stunde auf 06–17 Uhr. Eine Überschneidung so zu rechnen,
           wie die Datenbank sie rechnet (`tstzrange`, halboffen), geht nur
           mit dem Rohwert — und seit es mehrere Einsätze am selben Tag gibt,
           entscheidet das darüber, ob das Brett einen Konflikt meldet, den
           der Riegel gar nicht kennt. */
        ...(txt(b["von"]) ? { vonZeit: txt(b["von"]) } : {}),
        ...(txt(b["bis"]) ? { bisZeit: txt(b["bis"]) } : {}),
        /* Die Position, an der der Balken hängt. Der Stammfahrervorschlag
           braucht sie: Was ein Einsatz an Qualifikation verlangt, hängt an
           ihr und nicht nur am Gerät (12-offene-fragen.md, 41b). */
        ...(txt(b["position_id"]) ? { positionId: txt(b["position_id"]) } : {}),
        /* Mit Fahrer verkauft und keiner eingeplant (18.09.2026) — das Blatt
           sagt es, wo disponiert wird. */
        ...(b["fahrer_fehlt"] === true ? { fahrerFehlt: true } : {}),
        /* Überfällig seit dem Planende — das Brett zeichnet bis heute (N10-04). */
        ...(seit ? { ueberfaelligSeit: seit } : {}),
        /* Der Termin einer Ortsaufnahme (0180): Der Klick führt zur Aufnahme. */
        ...(txt(b["aufnahme_id"]) ? { aufnahmeId: txt(b["aufnahme_id"]) } : {}),
      });
    });
  });

  /* Das Brett spricht über Kennungen (`RL-07`), die API über UUIDs. Wer
     schreibt, braucht die UUID — und die steht nur hier, im selben Zug, in
     dem die Zeilen gelesen werden. */
  ressourcenIdMerken(ressourcen);

  return { ressourcen, belegungen: balken };
}

/** Der Wortstamm einer Kennung: `RL-07` → `RL`, `M. Lenger` → `ML`. */
function kuerzelVon(kennung: string): string {
  const strich = /^([A-Za-zÄÖÜäöü]+)-/.exec(kennung);
  if (strich?.[1]) return strich[1].toUpperCase();
  return kennung
    .split(/[\s.]+/)
    .filter(Boolean)
    .map((teil) => teil.charAt(0).toUpperCase())
    .join("")
    .slice(0, 2);
}

/** Die Kategorie, unter der Menschen im Brett stehen. Kein sichtbarer Text:
 *  Sie wird über `ressourcen.gruppePersonal` beschriftet (siehe Legende). */
export const PERSONALGRUPPE = "personal";

export function belegungAnlegen(neu: Omit<Belegung, "id">): Promise<Belegung> {
  if (ATTRAPPE) {
    const angelegt: Belegung = { ...neu, id: `n${Date.now().toString(36)}` };
    belegungen = [...belegungen, angelegt];
    return gleich(angelegt, 60);
  }
  const von = datumFuer(neu.von);
  const bis = datumFuer(neu.bis);
  bis.setHours(TAG_ENDE, 0, 0, 0);
  von.setHours(TAG_BEGINN, 0, 0, 0);
  return sende<Belegung>("/belegungen", {
    ressource_id: ressourcenSchluessel.get(neu.res) ?? neu.res,
    // Der Grund wird die Art (P-15/P-16) — Wartung, Urlaub, Schulung, sonst Sperre.
    art: neu.geschaeft === "sperre" ? belegungsartFuer(neu.grundArt) : neu.fest === false ? "option" : "fest",
    von: von.toISOString(),
    bis: bis.toISOString(),
    ...(neu.kunde ? { notiz: neu.kunde } : {}),
  });
}

/** Der Grund ist Pflicht — ohne ihn weiß drei Monate später niemand mehr,
 *  warum eine Woche verschoben wurde (34-api.md, PATCH /belegungen/:id). */
export function belegungAendern(
  id: string,
  aenderung: Partial<Belegung> & { grund?: string },
): Promise<Belegung> {
  if (ATTRAPPE) {
    belegungen = belegungen.map((b) => (b.id === id ? { ...b, ...aenderung } : b));
    const neu = belegungen.find((b) => b.id === id);
    if (!neu) throw new Error(`Belegung ${id} gibt es nicht.`);
    return gleich(neu, 60);
  }

  const koerper: Record<string, unknown> = { grund: aenderung.grund ?? "" };
  if (aenderung.von !== undefined) {
    const d = datumFuer(aenderung.von);
    d.setHours(TAG_BEGINN, 0, 0, 0);
    koerper["von"] = d.toISOString();
  }
  if (aenderung.bis !== undefined) {
    const d = datumFuer(aenderung.bis);
    d.setHours(TAG_ENDE, 0, 0, 0);
    koerper["bis"] = d.toISOString();
  }
  if (aenderung.res !== undefined) {
    koerper["ressource_id"] = ressourcenSchluessel.get(aenderung.res) ?? aenderung.res;
  }
  if (aenderung.fest !== undefined) koerper["art"] = aenderung.fest ? "fest" : "option";
  /* Der Zustand (○ ● ✓) wird hier **nicht** geschrieben. Er entsteht am
     Protokoll (`protokollAnlegen`), und `PATCH /belegungen` weist
     `art = 'laeuft'` ab (Regel 3). */
  return aendere<Belegung>(`/belegungen/${id}`, koerper);
}

export function belegungLoeschen(id: string, grund: string): Promise<void> {
  if (ATTRAPPE) {
    belegungen = belegungen.filter((b) => b.id !== id);
    return gleich(undefined, 60);
  }
  return loesche<void>(`/belegungen/${id}`, { grund });
}

/** Einen gelöschten Balken vollständig zurücklegen — „Änderung zurücknehmen"
 *  soll wirklich zurücknehmen. */
export function belegungZurueck(bel: Belegung): Promise<void> {
  if (ATTRAPPE) {
    belegungen = [...belegungen, bel];
    return gleich(undefined, 40);
  }
  return belegungAnlegen(bel).then(() => undefined);
}

/**
 * `POST /belegungen/:id/tauschen` — der Gerätetausch (docs/20-prozesse.md, C2).
 *
 * Eine Position, zwei Balken. Der Kunde sieht eine Zeile, die Disposition
 * zwei. Die Antwort trägt eine **Ampel**, wenn der Ersatz ein anderer
 * Maschinentyp ist — dann rechnet die App nicht um, sondern legt vor.
 */
export async function belegungTauschen(tausch: Tausch): Promise<TauschErgebnis> {
  if (ATTRAPPE) {
    return gleich<TauschErgebnis>({ ampel: null, sperreBis: null, defektId: null }, 80);
  }
  const roh = await sende<Roh>(`/belegungen/${tausch.belegungId}/tauschen`, {
    ressource_id: ressourcenSchluessel.get(tausch.ziel) ?? tausch.ziel,
    ab: tausch.ab,
    grund: tausch.grund,
    alte_sperren: tausch.alteSperren,
    ...(tausch.qualifikationBestaetigt ? { qualifikation_bestaetigt: true } : {}),
    ...(tausch.defekt
      ? {
          defekt: {
            art: tausch.defekt.art,
            schwere: tausch.defekt.schwere,
            beschreibung: tausch.defekt.beschreibung,
          },
        }
      : {}),
  });

  const ampel = alsObjekt(roh["ampel"]);
  const daten = alsObjekt(ampel["daten"]);
  const werte: Record<string, string> = {};
  Object.keys(daten).forEach((k) => { werte[k] = txt(daten[k]); });

  return {
    ampel: txt(ampel["key"]) ? { key: txt(ampel["key"]), werte } : null,
    sperreBis: txt(alsObjekt(roh["sperre"])["planned_until"]) || null,
    defektId: txt(alsObjekt(roh["defekt"])["id"]) || null,
  };
}

/* ══════════════ Defekte ══════════════ */

export async function defekte(nurOffene = true): Promise<Liste<Defekt>> {
  if (ATTRAPPE) return gleich({ zeilen: [], gesamt: 0 });
  const roh = await hole<Roh>(`/defekte${nurOffene ? "?offen=1" : ""}`);
  const zeilen = alsListe(roh["zeilen"]).map((d): Defekt => ({
    id: txt(d["id"]),
    ressource: txt(d["asset_number"]),
    typ: txt(d["typ"]),
    art: txt(d["kind"]) as Defekt["art"],
    schwere: txt(d["severity"]) as Defekt["schwere"],
    text: txt(d["description"]),
    gemeldetAm: tagUndZeit(d["reported_at"]),
    gemeldetVon: txt(d["gemeldet_von"]),
    verschulden: (txt(d["fault"]) || "unklar") as Defekt["verschulden"],
    draussen: d["draussen"] === true,
    ...(txt(d["vorgang"]) ? { vorgang: txt(d["vorgang"]) } : {}),
    ...(txt(d["kunde"]) ? { kunde: txt(d["kunde"]) } : {}),
    erledigtAm: txt(d["resolved_at"]) ? tagUndZeit(d["resolved_at"]) : null,
  }));
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length };
}

/**
 * `POST /protokolle/:id/schaden` — die zwei Knöpfe (C6).
 *
 * **Der Betrag kommt von einem Menschen.** Ohne ihn antwortet die API mit
 * `422 eingabe.betrag`, und das ist richtig so: Was eine Instandsetzung
 * kostet, weiß die Werkstatt und nicht diese Maske.
 */
export async function schadenEntscheiden(eingabe: {
  protokollId: string;
  entscheidung: "kunde" | "betrieb";
  art: DefektArt;
  schwere: DefektSchwere;
  bezeichnung?: string;
  betrag?: number;
}): Promise<void> {
  if (ATTRAPPE) return gleich(undefined, 80);
  await sende<Roh>(`/protokolle/${eingabe.protokollId}/schaden`, {
    entscheidung: eingabe.entscheidung,
    art: eingabe.art,
    schwere: eingabe.schwere,
    ...(eingabe.bezeichnung ? { bezeichnung: eingabe.bezeichnung } : {}),
    ...(eingabe.betrag === undefined ? {} : { betrag: eingabe.betrag }),
  });
}

export async function verfuegbarkeit(
  typ: string, von: string, bis: string,
  /** Wie viele Stück — nur beim gezählten Bestand eine Frage (0194). */
  menge = 1,
): Promise<Verfuegbarkeit> {
  if (ATTRAPPE) {
    const geraete = A.RESSOURCEN.filter((r) => r.typ === typ);
    const frei = geraete.filter((g) =>
      !belegungen.some((b) => b.res === g.id && b.fest !== false));
    const lage: Lage = frei.length === geraete.length ? "geht" : frei.length ? "teils" : "nein";
    return gleich<Verfuegbarkeit>({
      lage,
      text_key: lage === "geht" ? "lage.frei" : lage === "teils" ? "lage.teilweise" : "lage.belegt",
      tage_vergeben: SPALTEN.length - frei.length,
      punkt: lage === "geht" ? "gut" : lage === "teils" ? "acht" : "stopp",
      rand: frei.slice(0, 2).map((g) => g.id).join(" · ") || "—",
      vorschlaege: frei.map((g) => ({ maschine_id: g.id, kennung: g.id })),
    });
  }

  const frage = new URLSearchParams({ typ, von, bis, ...(menge > 1 ? { menge: String(menge) } : {}) });
  const roh = await hole<Roh>(`/verfuegbarkeit?${frage.toString()}`);
  return {
    ...(roh["pool"] === true ? {
      pool: true,
      bestand: zahl(roh["bestand"]) ?? 0,
      frei: zahl(roh["frei"]) ?? 0,
      menge: zahl(roh["menge"]) ?? menge,
    } : {}),
    lage: (txt(roh["lage"]) || "nein") as Lage,
    text_key: txt(roh["text_key"]),
    tage_vergeben: zahl(roh["tage_vergeben"]) ?? 0,
    punkt: (txt(roh["punkt"]) || "stopp") as Verfuegbarkeit["punkt"],
    // `rand` ist eine ISO-Spanne, kein Satz (34-api.md). Beschriftet wird sie
    // hier, nicht drüben.
    rand: txt(roh["rand"])
      .split("/")
      .map((teil) => tagKurz(teil))
      .filter(Boolean)
      .join(" – ") || "—",
    vorschlaege: alsListe(roh["vorschlaege"]).map((v) => ({
      maschine_id: txt(v["maschine_id"]),
      kennung: txt(v["kennung"]),
    })),
    /* Was ein anderes Angebot hält (18.09.2026). Es steht nicht unter den
       Vorschlägen — die Maske zeigt es daneben und fragt, bevor sie es nimmt. */
    reserviert: alsListe(roh["reserviert"]).map((r) => ({
      maschine_id: txt(r["maschine_id"]),
      kennung: txt(r["kennung"]),
      vorgang: txt(r["vorgang"]),
      kunde: txt(r["kunde"]),
      frist: txt(r["frist"]),
    })),
  };
}

/* ══════════════ Vorgänge ══════════════ */

/**
 * Die Zustände der Datenbank und die der Oberfläche.
 *
 * Ein Unterschied steht darin, und er ist Absicht: `abrechenbar` heißt in der
 * Maske `bereit`. Der Zustandsweg in 34-api.md kennt `abrechenbar`; die
 * Oberfläche spricht vom Vorgang, der bereit ist. Übersetzt wird hier.
 */
const ZUSTAND: Record<string, VorgangZustand> = {
  entwurf: "entwurf",
  angebot: "angebot",
  angenommen: "angenommen",
  abgelehnt: "abgelehnt",
  laeuft: "läuft",
  zurueck: "zurueck",
  abrechenbar: "bereit",
  abgerechnet: "abgerechnet",
  storniert: "storniert",
};

/**
 * Die vier Stände der Vorbereitung, wie die Schnittstelle sie schreibt
 * (`dienste/aufgaben.ts`, `VORBEREITUNGSSTAND_O`). Was nicht darin steht — eine
 * ältere Fassung der API, die das Feld nicht kennt —, ist `keine`: Dann zeigt
 * die Kette den Schritt als nicht erreicht und behauptet nichts.
 */
const VORBEREITUNG: readonly string[] = ["keine", "offen", "laeuft", "fertig"];

/** Die Tönung der Marke. Sie folgt dem Zustand und wird nicht geliefert. */
const TOENUNG: Partial<Record<VorgangZustand, string>> = {
  "läuft": "gut",
  zurueck: "acht",
  bereit: "gut",
};

/** Wann und von wem storniert oder abgelehnt wurde (Befund 11) — oder nichts. */
function endeAus(z: Roh): { ende?: { am: string; von: string | null } } {
  const e = alsObjekt(z["endvermerk"]);
  const am = tagUndZeit(e["am"]);
  return am ? { ende: { am, von: txt(e["von"]) || null } } : {};
}

/** Ohne Rechnung abgeschlossen (0280): wann, von wem, warum — oder nichts. */
function ohneRechnungAus(z: Roh): { ohneRechnung?: { am: string; von: string | null; grund: string } } {
  const o = alsObjekt(z["ohne_rechnung"]);
  const am = tagUndZeit(o["am"]);
  return am ? { ohneRechnung: { am, von: txt(o["von"]) || null, grund: txt(o["grund"]) } } : {};
}

function alsVorgang(z: Roh): Vorgang {
  const zustand = ZUSTAND[txt(z["status"])] ?? "entwurf";
  return {
    nr: txt(z["number"]),
    kunde: txt(z["kunde"]),
    // Seit dem 16.09.2026 traegt auch die LISTE die Kundennummer. Wer nach
    // dem Kunden filtern will, filtert an ihr und nicht an seinem Namen.
    ...(txt(z["kundennummer"]) ? { kundeNr: txt(z["kundennummer"]) } : {}),
    /* Ohne Betreff, was darin steckt (05.10.2026): Der Betreff ist nur noch,
       was ein Mensch eingetragen hat (vorgangsinhalt.ts). */
    was: titelAus(txt(z["subject"]), txt(z["inhalt"])),
    // Die Kommission des Kunden — die Auftragssuche findet sie (V-31).
    ...(txt(z["customer_reference"]) ? { bestellnummer: txt(z["customer_reference"]) } : {}),
    ...(txt(z["note_customer"]) ? { notiz_kunde: txt(z["note_customer"]) } : {}),
    ...(txt(z["note_internal"]) ? { notiz_intern: txt(z["note_internal"]) } : {}),
    ort: txt(z["ort"]),
    von: tagKurz(z["date"]),
    bis: tagKurz(z["valid_until"]) || tagKurz(z["date"]),
    // Was vermietet wurde und wann es wirklich draussen war — beides nur in
    // der Liste, beides nur, wenn es etwas zu sagen gibt.
    ...(txt(z["geraete"]) ? { geraete: txt(z["geraete"]) } : {}),
    ...(txt(z["kennungen"]) ? { kennungen: txt(z["kennungen"]) } : {}),
    ...(tagKurz(z["einsatz_von"]) ? { einsatzVon: tagKurz(z["einsatz_von"]) } : {}),
    ...(tagKurz(z["einsatz_bis"]) ? { einsatzBis: tagKurz(z["einsatz_bis"]) } : {}),
    ...(txt(z["einsatz_von"]) ? { einsatzVonIso: tagAusZeitpunkt(z["einsatz_von"]) } : {}),
    // Nur wenn es zutrifft: Ein `false` an jeder Zeile wäre eine Aussage über
    // nichts (22.09.2026).
    ...(z["ueberfaellig"] === true ? { ueberfaellig: true } : {}),
    ...(z["teilabgerechnet"] === true ? { teilabgerechnet: true } : {}),
    zustand,
    vorbereitung: VORBEREITUNG.includes(txt(z["vorbereitung"]))
      ? (txt(z["vorbereitung"]) as Vorbereitungsphase)
      : "keine",
    art: TOENUNG[zustand] ?? "",
    // `billing_mode` ist die Grundlage der Rechnung: einmalig oder monatlich.
    grundlage: txt(z["billing_mode"]),
    ...(txt(z["endgrund"]) || txt(z["cancel_reason"])
      ? { grund: txt(z["endgrund"]) || txt(z["cancel_reason"]) } : {}),
    ...endeAus(z),
    ...ohneRechnungAus(z),
    /* **Das Netto der Liste ist das Netto des Angebots** (Befund 24,
       24.09.2026): Positionen und Zuschläge, gerechnet in der API aus
       derselben Erwartung wie am einzelnen Vorgang. */
    netto: zahl(z["netto"]),
    ...(z["netto_offen"] === true ? { nettoOffen: true } : {}),
    ...(txt(z["note_customer"]) ? { notiz_kunde: txt(z["note_customer"]) } : {}),
    ...(txt(z["note_internal"]) ? { notiz_intern: txt(z["note_internal"]) } : {}),
    // Die Liste liefert keine Ampeln und keine Schaeden -- die haengen am
    // einzelnen Vorgang. Leer statt undefined, damit die Maske nicht prueft.
    ampeln: [],
    schaeden: [],
  };
}

/** `GET /vorgaenge/:id` liefert beides mit; die Liste nicht. */
function alsAmpeln(roh: Roh): VorgangAmpel[] {
  return alsListe(roh["ampeln"]).map((a) => {
    const daten = alsObjekt(a["daten"]);
    const werte: Record<string, string> = {};
    Object.keys(daten).forEach((k) => { werte[k] = txt(daten[k]); });
    return {
      key: txt(a["key"]),
      stufe: (txt(a["stufe"]) === "stopp" ? "stopp" : "acht") as VorgangAmpel["stufe"],
      werte,
    };
  });
}

function alsSchaeden(roh: Roh): Schadensfall[] {
  return alsListe(roh["schaeden"]).map((s) => {
    const verschulden = txt(s["verschulden"]);
    return {
      protokollId: txt(s["id"]),
      text: txt(s["damages"]),
      ressource: txt(s["ressource"]),
      verschulden: verschulden === "kunde" || verschulden === "betrieb" ? verschulden : null,
      ...(txt(s["berechnet_als"]) ? { berechnetAls: txt(s["berechnet_als"]) } : {}),
      berechnetNetto: zahl(s["berechnet_netto"]),
    };
  });
}

export async function vorgaengeListe(
  art?: "angebot" | "auftrag",
  kunde?: string,
  /** Nummer, Kunde oder Betreff, auch Teile — für die globale Suche im Kopf. */
  suche?: string,
): Promise<Liste<Vorgang>> {
  if (ATTRAPPE) {
    const wort = (suche ?? "").trim().toLowerCase();
    const zeilen = vorgaenge
      .filter((v) => !wort || `${v.nr} ${v.kunde} ${v.was}`.toLowerCase().includes(wort))
      .map((v) => ({ ...v }));
    return gleich({ zeilen, gesamt: zeilen.length });
  }
  const frage = new URLSearchParams();
  if (art) frage.set("art", art);
  if (kunde) frage.set("kunde", kunde);
  if (suche) frage.set("suche", suche);
  const anhang = frage.toString();
  const roh = await hole<Roh>(`/vorgaenge${anhang ? `?${anhang}` : ""}`);
  const zeilen = alsListe(roh["zeilen"]).map(alsVorgang);
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length };
}

/* ══════════════ „Den gleichen Kran wie beim letzten Mal" ══════════════

   Die Vorlage aus einem früheren Vorgang. Sie ist **kein Vorgang** — was
   daraus wird, entsteht in der Schnellerfassung wie jeder andere Auftrag,
   nur vorausgefüllt. Wer abbricht, hinterlässt nichts.

   Jede Zeile trägt zwei Preise: `einzelpreis` ist der Satz von **heute**
   aus dem Stamm, `einzelpreisDamals` der eingefrorene von damals (Regel 4).
   Gerechnet wird mit dem ersten; der zweite steht daneben, damit auffällt,
   wenn sich etwas geändert hat. Der Innendienst sagt dem Kunden sonst den
   Preis von vorletztem Jahr. */

/** Eine Zeile der Vorlage — dieselben Felder wie ein Entwurf, plus die Herkunft. */
export type Wiederholungszeile = {
  art: "miete" | "leistung" | "verkauf" | "zuschlag" | "text";
  bezeichnung: string;
  menge: number;
  /** i18n-Schlüssel (`einheit.tag`) oder ein Wort aus dem Stamm (`Stk`). */
  einheit: string;
  /** Der Satz von heute. `null` heißt: im Stamm steht keiner — etwa bei
   *  einem Zuschlag nach Prozent, der kein Betrag ist. */
  einzelpreis: number | null;
  /** Der eingefrorene Satz von damals. Nie zum Rechnen. */
  einzelpreisDamals: number | null;
  maschinentypId?: string;
  /** Die Ausstattung von damals — „mit Anschläger". */
  varianteId?: string;
  typ?: string;
  /**
   * Wurde **diese** Zeile mit Bediener verkauft (Migration 0096)?
   *
   * Der Kopf sagt nur, ob überhaupt jemand gefahren ist. Solange die Vorlage
   * in einen Entwurf mit einem Gerät lief, reichte das; eine Sammlung trägt
   * mehrere Mieten, und dann gehört die Angabe an die Zeile, zu der sie
   * gehört.
   */
  bedient?: boolean;
  /** Die Maschine, die es damals war — ein Wunsch, keine Zusage (Regel 2). */
  maschine?: string;
  leistungsartId?: string;
  wareId?: string;
  zuschlagCode?: string;
  /** Der Katalogeintrag ist stillgelegt: Die Zeile lässt sich so nicht wiederholen. */
  stillgelegt?: boolean;
  /** Der Katalogeintrag steht heute aktiv im Stamm — bei einem Zuschlag: seine Regel. */
  imStamm?: boolean;
  /** Die Zeile des Bedieners. Sie geht nicht in die Liste — der Bediener wird
   *  gewählt, und die Maske legt seine Zeile selbst an (Regel 8). */
  istBediener?: boolean;
};

export type Wiederholung = {
  nr: string;
  /** `24.08.` — wann der alte Vorgang geschrieben wurde. */
  datum: string;
  netto: number | null;
  kunde: string;
  ort: string;
  bedient: boolean;
  /** Die Kennung der Person, die bedient hat. */
  bediener: string;
  /** Die Zubehörwahl von damals, nach Zuordnung — so, wie `ZubehoerWahl` sie liest. */
  zubehoer: Record<string, string>;
  zeilen: Wiederholungszeile[];
};

export async function wiederholung(nr: string): Promise<Wiederholung> {
  const roh = await hole<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/wiederholung`);
  const zubehoer: Record<string, string> = {};
  alsListe(roh["zubehoer"]).forEach((z) => {
    zubehoer[txt(z["zuordnung_id"])] = txt(z["ressource_id"]);
  });
  return {
    nr: txt(roh["nr"]),
    datum: tagKurz(roh["datum"]),
    netto: zahl(roh["netto"]),
    kunde: txt(roh["kunde"]),
    ort: txt(roh["ort"]),
    bedient: roh["bedient"] === true,
    bediener: txt(roh["bediener"]),
    zubehoer,
    zeilen: alsListe(roh["positionen"]).map((p): Wiederholungszeile => {
      const art = txt(p["art"]);
      return {
        art: (["miete", "leistung", "verkauf", "zuschlag", "text"].includes(art)
          ? art : "leistung") as Wiederholungszeile["art"],
        bezeichnung: txt(p["bezeichnung"]),
        menge: zahl(p["menge"]) ?? 1,
        einheit: txt(p["einheit"]),
        einzelpreis: zahl(p["einzelpreis"]),
        einzelpreisDamals: zahl(p["einzelpreis_damals"]),
        ...(txt(p["maschinentyp_id"]) ? { maschinentypId: txt(p["maschinentyp_id"]) } : {}),
        ...(txt(p["variante_id"]) ? { varianteId: txt(p["variante_id"]) } : {}),
        ...(txt(p["typ"]) ? { typ: txt(p["typ"]) } : {}),
        ...(p["bedient"] === true ? { bedient: true } : {}),
        ...(txt(p["maschine"]) ? { maschine: txt(p["maschine"]) } : {}),
        ...(txt(p["leistungsart_id"]) ? { leistungsartId: txt(p["leistungsart_id"]) } : {}),
        ...(txt(p["ware_id"]) ? { wareId: txt(p["ware_id"]) } : {}),
        ...(txt(p["zuschlag_code"]) ? { zuschlagCode: txt(p["zuschlag_code"]) } : {}),
        ...(p["katalog_aktiv"] === false ? { stillgelegt: true } : {}),
        ...(p["katalog_aktiv"] === true ? { imStamm: true } : {}),
        ...(p["ist_bediener"] === true ? { istBediener: true } : {}),
      };
    }),
  };
}

/** Eine Maschine am Auftrag und wo sie steht — dieselbe Ableitung wie im Brett. */
export type Maschinenstand = {
  id: string;
  name: string;
  stand: BelegZustand;
  /**
   * Was die Zeile am Telefon dazu sagt (19.09.2026): die Position, an der
   * sie hängt — dort hängt auch, wer fährt —, ihr Typ, der Plan und das Ist
   * als `17.09.`. Luca: *„Wer fährt welche Maschine? Ist nicht gut gemacht."*
   * Alles aus derselben Belegungszeile, die `stand` schon liefert.
   */
  positionId?: string;
  typ?: string;
  von?: string;
  bis?: string;
  seit?: string;
  zurueckAm?: string;
};

/**
 * Eine freigegebene Rechnung am Auftrag. Die Nummer vergibt Lexware
 * (Regel 1), `pdf` ist der Pfad in der Ablage, nie ein Link nach drüben.
 */
export type Rechnung = {
  belegId: string;
  laufId: string;
  stand: "uebertragen" | "abgelehnt" | "wartet";
  nummer: string | null;
  pdf: string | null;
  /** Der Schlüssel, mit dem Lexware abgelehnt hat — `lexware.artikel_fehlt`. */
  fehler: string | null;
  periode: string;
  /** Der letzte Tag, den die Rechnung abdeckt — `21.09.` (N12-04). */
  bis: string;
  /** Der Tag der Übertragung — `05.10.`; leer, wenn noch nicht übertragen (N12-04). */
  am: string;
  netto: number | null;
};

/** Der Einzelsatz: der Vorgang mit seinen Maschinen und Rechnungen. */
/**
 * Eine Zuschlagszeile der Positionsliste: ein Zuschlag, der nach heutigem Stand
 * dazukommt.
 *
 * **Es ist eine Erwartung und keine Position.** Zuschläge werden bei der
 * Annahme als *Regeln* eingefroren (Regel 4) und erst im Abrechnungslauf
 * gerechnet — aus dem Ist des Rücknahmeprotokolls (Regel 3). Gerechnet wird
 * das hier serverseitig mit derselben Funktion wie der Lauf
 * (`dienste/zuschlagserwartung.ts`); der Klient zeigt nur.
 */
export type Erwartungszeile = {
  code: string;
  /** Aus dem Stamm — „Maschinenbruchversicherung" pflegt der Betrieb. */
  bezeichnung: string;
  /** Null, solange kein Betrag möglich ist. Dann sagt `offen`, warum. */
  netto: number | null;
  menge: number | null;
  einzelpreis: number;
  einheit_key: string;
  /** Der Satz eines Prozentzuschlags — sonst null. */
  prozent: number | null;
  /** Die Kennzahl, aus der die Menge folgt: LITER, KM, BSTD. */
  metric: string | null;
  /**
   * Warum kein Betrag dasteht — zwei verschiedene Gründe:
   *
   * * `menge_folgt` — die Regel greift, das Ist steht erst im Protokoll.
   * * `betrag_fehlt` — die Regel greift und ist im Stamm nicht beziffert.
   *   Das ist eine Lücke in den Stammdaten und keine Eigenschaft des Vorgangs.
   * * `preis_fehlt` — die Regel ist „Preis immer individuell" (0227, E1, die
   *   Anlieferung), und am Vorgang steht noch kein Preis. Er wird hier
   *   eingegeben — Pflicht vor der Annahme.
   */
  offen: null | "menge_folgt" | "betrag_fehlt" | "preis_fehlt";
  reihenfolge: number;
  /**
   * Woran die Zeile hängt (Systemprüfung B-09): die Mietposition, aus der sie
   * kommt — oder `jeAuftrag`, wenn sie einmal für den ganzen Auftrag gilt.
   * Nur zum Beschriften; gerechnet ist sie schon.
   */
  positionId?: string | null;
  jeAuftrag?: boolean;
};

export type Zuschlagserwartung = {
  /** Die Summe dessen, was heute schon beziffert ist — ein Teil, kein Ende. */
  netto: number;
  /** Mindestens eine Zeile wartet noch auf eine Zahl. */
  offen: boolean;
  zeilen: Erwartungszeile[];
};

/**
 * Ein Beleg, der zu diesem Vorgang drüben liegt — eine Zeile aus
 * `order_document` (Migration 0086).
 *
 * **Warum es Zeilen sind und nicht drei Spalten.** Lexware kennt kein Ändern
 * eines Verkaufsbelegs (31-lexware-durchtest.md, Befund 13): Jede Fassung ist
 * ein eigener Beleg mit eigener Nummer. Die Spalte am Vorgang hält genau
 * einen — wer eine zweite Fassung anlegte, verlor die erste.
 */
export type Vorgangsbeleg = {
  id: string;
  art: Papierart;
  /** Die Kennung drüben — sie holt genau dieses PDF. */
  lexId: string;
  /** `AG0020` — die Nummer, unter der der Kunde anruft. Kann fehlen. */
  nummer: string | null;
  /** Wann der Beleg bei uns vermerkt wurde. */
  wann: string;
  /**
   * Die wievielte Fassung dieser Belegart — 1, 2, 3.
   *
   * **Sie steht nicht auf dem Papier.** Drüben heißen die Fassungen AG0020 und
   * AG0021, ohne Zusammenhang; den Zusammenhang kennen nur wir, weil beide am
   * selben Vorgang hängen. Darum wird die Zahl hier abgeleitet und nie an die
   * Nummer geklebt: „AG0021 · Fassung 2" ist wahr, „AG0021-02" wäre eine
   * erfundene Belegnummer.
   */
  fassung: number;
  /** Nur am Angebot: ob dieser Beleg Wahlpositionen trug. */
  mitWahl: boolean;
  /** Wer die Fassung angelegt hat (0202) — nur bei Angeboten aus Machine Office. */
  von?: string | null;
  /** Das Papier entstand in Machine Office (0200) — dann lässt es sich von hier per Mail senden (0204). */
  eigen?: boolean;
  /** Archivstand der Fassung im Papier-Register (0265, docs/58) — „archiviert am … · gesperrt bis …". */
  archiv?: Archivvermerk;
};

function alsBelege(roh: Roh): Vorgangsbeleg[] {
  const arten: Papierart[] = ["angebot", "auftragsbestaetigung"];
  const jeArt = new Map<string, number>();
  return alsListe(roh["belege"])
    .map((b): Vorgangsbeleg | null => {
      const art = txt(b["kind"]) as Papierart;
      if (!arten.includes(art)) return null;
      const fassung = (jeArt.get(art) ?? 0) + 1;
      jeArt.set(art, fassung);
      return {
        id: txt(b["id"]),
        art,
        /* Die Kennung, mit der das PDF geholt wird: drüben die Lexware-Kennung,
           bei einem Angebot aus Machine Office (0200) die Zeile selbst. */
        lexId: txt(b["lex_id"]) || txt(b["id"]),
        nummer: txt(b["number"]) || txt(b["lex_number"]) || null,
        wann: tagKurz(b["created_at"]),
        fassung,
        mitWahl: b["has_options"] === true,
        von: txt(b["angelegt_von"]) || null,
        eigen: b["eigen"] === true,
        ...(archivvermerkAus(b["archiv"]) ? { archiv: archivvermerkAus(b["archiv"]) as Archivvermerk } : {}),
      };
    })
    .filter((b): b is Vorgangsbeleg => b !== null);
}

export type Auftrag = Omit<Vorgang, "maschinen"> & {
  maschinen?: Maschinenstand[];
  rechnungen: Rechnung[];
  /**
   * Die gestempelte Anfahrt und ob sie abgerechnet wird (G5).
   *
   * `eintraege: 0` heißt: niemand ist gefahren — dann gibt es nichts zu
   * entscheiden und die Maske fragt auch nicht. `schalter: false` heißt: Die
   * Entscheidung gibt es hier noch gar nicht, weil Migration 0133 an dieser
   * Datenbank nicht angekommen ist. Auch dann wird nicht gefragt — ein
   * Schalter, der beim Umlegen scheitert, ist schlimmer als keiner.
   */
  anfahrt: {
    eintraege: number;
    stunden: number | null;
    schalter: boolean;
    abgerechnet: boolean;
  };
  /** Was zur Miete dazukommt. Fehlt, solange es nichts zu erwarten gibt. */
  erwartung?: Zuschlagserwartung;
  /**
   * **Liefern wir, oder holt der Kunde?** (Systemprüfung B-04.) `moeglich`:
   * Eine Regel, die bei Selbstabholung entfällt, greift an diesem Vorgang.
   * `an`: Sie ist abgewählt. `schaltbar`: In diesem Zustand lässt sich das
   * noch umlegen — auch nach der Annahme, ohne neue Preisfindung.
   */
  abholung?: {
    moeglich: boolean; an: boolean; schaltbar: boolean;
    /** Die Zuschläge, die bei Selbstabholung entfallen — an ihrer Zeile sitzt der Haken (Befund 17). */
    codes: string[];
    /**
     * Der Anhänger, den die Gerätegruppe einer Miete für die Selbstabholung
     * nennt (0127) — mit dem Zeitraum der Mieten, die ihn nennen, und ob er
     * schon als Miete dasteht (Systemprüfung Befund 32). Fehlt, wenn keine
     * Gruppe einen nennt.
     */
    anhaenger?: {
      typId: string; typ: string; varianteId: string; variante: string;
      von: string; bis: string; da: boolean;
    };
  };
  /** Die Belege drüben, das Älteste zuerst. Leer, solange keiner entstand. */
  belege: Vorgangsbeleg[];
};

function alsErwartung(roh: Roh): Zuschlagserwartung | null {
  const e = alsObjekt(roh["zuschlagserwartung"]);
  const zeilen_ = alsListe(e["zeilen"]).map((z): Erwartungszeile => {
    const grund = txt(z["offen"]);
    return {
      code: txt(z["code"]),
      bezeichnung: txt(z["bezeichnung"]),
      netto: zahl(z["netto"]),
      menge: zahl(z["menge"]),
      einzelpreis: zahl(z["einzelpreis"]) ?? 0,
      einheit_key: txt(z["einheit_key"]),
      prozent: zahl(z["prozent"]),
      metric: txt(z["metric"]) || null,
      offen: grund === "menge_folgt" || grund === "betrag_fehlt" || grund === "preis_fehlt" ? grund : null,
      reihenfolge: zahl(z["reihenfolge"]) ?? 0,
      positionId: txt(z["position_id"]) || null,
      jeAuftrag: z["je_auftrag"] === true,
    };
  });
  if (!zeilen_.length) return null;
  return { netto: zahl(e["netto"]) ?? 0, offen: e["offen"] === true, zeilen: zeilen_ };
}

/** `billing_document` → der Stand drüben, in drei Wörtern. */
function belegStand(roh: Roh): Rechnung["stand"] {
  if (txt(roh["lex_invoice_id"]) || txt(roh["nummer"]) || txt(roh["status"]) === "uebertragen") {
    return "uebertragen";
  }
  return txt(roh["status"]) === "fehler" ? "abgelehnt" : "wartet";
}

/**
 * `POST /vorgaenge/:id/auftragsbestaetigung` — eine neue AB-Fassung (0268),
 * oder die erste, wenn das Papier bei der Annahme scheiterte.
 */
export async function abFassungAnlegen(nr: string): Promise<Abstand | null> {
  if (ATTRAPPE) throw new Error("Im Attrappenbetrieb gibt es keine Auftragsbestätigung.");
  const roh = await sende<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/auftragsbestaetigung`);
  return abStandAus(roh["auftragsbestaetigung"]);
}

export async function vorgang(nr: string): Promise<Auftrag> {
  if (ATTRAPPE) {
    /* ══════════════ Kein stiller Rückfall ══════════════ (22.09.2026)

       Hier stand `?? vorgaenge[0]`: Eine unbekannte Nummer bekam
       **stillschweigend den ersten Vorgang der Liste**. Im Prototyp hieß das:
       Wer ein Angebot öffnete (`AG-2026-0044`), bekam V-2026-0142
       zurück — und weil das läuft, führte `AngebotBauen` ihn folgerichtig
       in die Auftragsmaske. Luca: *„Ich kann ein Angebot nicht mehr öffnen.
       Dann lande ich komischerweise direkt in einer Auftragsmaske."*

       **Ein Angebot IST ein Vorgang**, nur in einem früheren Zustand — die
       Attrappe baut ihn deshalb aus der Angebotszeile. Was es gar nicht gibt,
       wirft, wie die API auch: Eine Attrappe, die statt „gibt es nicht" etwas
       Falsches liefert, prüft eine Anwendung, die es nicht gibt. */
    const ausAngebot = (): Vorgang | undefined => {
      const a = A.ANGEBOTE.find((x) => x.nr === nr);
      if (!a) return undefined;
      return {
        nr: a.nr,
        kunde: a.kunde,
        was: a.was,
        ort: "",
        von: a.gestellt,
        bis: a.frist,
        zustand: a.phase === "angenommen" ? "angenommen" : a.phase === "abgelehnt" ? "abgelehnt" : a.phase === "entwurf" ? "entwurf" : "angebot",
        art: "",
        grundlage: "einmalig",
        netto: a.netto ?? null,
        ampeln: [],
        schaeden: [],
      };
    };
    const v = vorgaenge.find((x) => x.nr === nr) ?? ausAngebot();
    if (!v) throw new Fehler("vorgang.unbekannt", 404);
    /* **Die Maschinen kommen aus einer eigenen Reihe** (22.09.2026). Der
       Vorgang der Attrappe trägt sie in der schmalen Form des Listentyps
       (`id`, `name`, `stand`); der Laufzettel braucht Typ, Plan und Ist.
       Fehlt ein Eintrag, bleibt die Akte ohne Laufzettel — genau wie ein
       Vorgang ohne Belegung im Betrieb. */
    const { maschinen: _ohne, ...rest } = v;
    return gleich({
      ...rest, rechnungen: [], belege: [],
      ...(A.MASCHINENSTAND[nr] ? { maschinen: A.MASCHINENSTAND[nr] } : {}),
      anfahrt: { eintraege: 0, stunden: null, schalter: false, abgerechnet: false },
    });
  }
  const roh = await hole<Roh>(`/vorgaenge/${encodeURIComponent(nr)}`);
  const positionen = alsListe(roh["positionen"]);
  const belegungenDrin = alsListe(roh["belegungen"]);

  /* Der Einzelsatz trägt Positionen und Belegungen; Kunde und Ort stehen
     dort nur als Verweis. Was die Maske zeigt, holt sie aus dem, was da
     ist: den Gegenstand aus der ersten Position, den Zeitraum aus der
     frühesten und der spätesten Belegung. */
  /* **Der Zeitraum ist der Einsatz, nie Belegdatum bis Bindefrist**
     (Systemprüfung, Befunde 12 und 18, 24.09.2026). Zuerst die Positionen,
     wie die Liste (`einsatz_von`/`einsatz_bis`), dann die Belegungen für eine
     ältere API. Bis dahin fiel die Maske ohne Belegung auf `date` und
     `valid_until` zurück: V-2026-0048 stand mit „24.09. – 08.10." da — heute
     bis Bindefrist —, obwohl die Leistung noch keinen Termin hatte. Fehlt
     beides, bleibt der Zeitraum leer und die Maske sagt „Termin offen". */
  const von = txt(roh["einsatz_von"])
    || belegungenDrin.map((b) => txt(b["planned_from"])).filter(Boolean).sort()[0];
  const bis = txt(roh["einsatz_bis"])
    || belegungenDrin.map((b) => txt(b["planned_until"])).filter(Boolean).sort().at(-1);
  const zustand = ZUSTAND[txt(roh["status"])] ?? "entwurf";

  /* **Die Papiere zum Vorgang.** Je Art die Nummer, unter der der Kunde sie
     kennt. Fehlt eine, gibt es das Papier nicht — ein Vorgang ohne Angebot
     ist der Regelfall (Regel 10). Die AB aus Machine Office (0268) trägt die
     Nummer ihrer jüngsten Fassung. Den Lieferschein gibt es seit Phase 4
     nicht mehr als eigenes Papier — das Übergabeprotokoll ist er. */
  const ab = abStandAus(roh["auftragsbestaetigung"]);
  const papiere = ([
    // Seit 0200 die Nummer aus Machine Office (AG-2026-001-02), sonst die aus Lexware.
    ["angebot", txt(roh[roh["angebot_nummer"] ? "angebot_nummer" : "lex_quotation_number"])],
    ["auftragsbestaetigung", ab?.fassungen[0]?.nummer ?? ""],
  ] as const)
    .map(([art, nr]) => ({ art, nr }))
    .filter((p): p is { art: typeof p.art; nr: string } => p.nr !== "");

  const abr = alsObjekt(roh["abrechnung"]);

  /* Nur die Vermietungen — eine Sperre oder ein Transport ist keine
     Maschine, die jemand übergibt. Und gebundenes Zubehör auch nicht: Der Korb
     geht mit dem Teleskoplader hinaus und zurück (routen/protokolle.ts,
     `zubehoerMitziehen`); einen eigenen Übergabeknopf bekäme er sonst hier. */
  const maschinen: Maschinenstand[] = belegungenDrin
    .filter((b) => !["sperre", "wartung", "transport", "option"].includes(txt(b["kind"])))
    /* Der Anhänger, der mit seinem Träger geht (W3 (B), 25.09.2026), ebenso:
       Die API nennt ihn `anhaenger`, sein Ist setzt das Protokoll des Trägers. */
    .filter((b) => !["anbau", "zubehoer", "bediener", "anhaenger"].includes(txt(b["rolle"])))
    /* Und kein Mensch: Wer fährt, bekommt kein Übergabeprotokoll — er geht mit
       der Maschine hinaus (routen/protokolle.ts, `zubehoerMitziehen`). */
    .filter((b) => txt(b["ressourcenart"]) !== "person")
    .map((b) => ({
      id: txt(b["id"]),
      name: txt(b["ressource"]) || txt(b["maschinentyp"]),
      stand: standAus(b["actual_from"], b["actual_until"]),
      positionId: txt(b["item_id"]),
      typ: txt(b["maschinentyp"]),
      von: tagKurz(b["planned_from"]),
      bis: tagKurz(b["planned_until"]),
      seit: tagKurz(b["actual_from"]),
      zurueckAm: tagKurz(b["actual_until"]),
    }));

  const rechnungen: Rechnung[] = alsListe(roh["rechnungen"]).map((r) => ({
    belegId: txt(r["beleg_id"]),
    laufId: txt(r["lauf_id"]),
    stand: belegStand(r),
    nummer: txt(r["nummer"]) || null,
    pdf: txt(r["pdf"]) || null,
    fehler: txt(r["fehler"]) || null,
    periode: [tagKurz(r["period_from"]), tagKurz(r["period_until"])].filter(Boolean).join(" – "),
    bis: tagKurz(r["period_until"]),
    am: tagKurz(r["uebertragen_am"]),
    netto: zahl(r["netto"]),
  }));

  const erwartung = alsErwartung(roh);

  const fahrt = alsObjekt(roh["anfahrt"]);
  const abholung = alsObjekt(roh["abholung"]);

  return {
    nr: txt(roh["number"]),
    rechnungen,
    belege: alsBelege(roh),
    // Teilabgerechnet (0206) — abgeleitet aus den Belegen, kein Zustand.
    ...(roh["teilabgerechnet"] === true ? { teilabgerechnet: true } : {}),
    /* **Die Kette am Auftrag wusste nichts von der Vorbereitung** (22.09.2026).
       `GET /vorgaenge/:nr` liefert das Feld seit jeher (`VORBEREITUNGSSTAND_O`
       in `routen/vorgaenge.ts`), und `alsVorgang` — der Mapper der Liste —
       liest es. Dieser hier nicht. `Auftragskette` fiel damit auf `"keine"`
       zurueck und schrieb in der Akte „an diesem Auftrag ist nichts
       vorzubereiten", zwei Zeilen ueber `Vorbereitungsstaende`, das im selben
       Blatt „RL-07 · vorbereitet" schrieb. In der Liste stand es richtig, nur
       in der Akte nicht. */
    vorbereitung: VORBEREITUNG.includes(txt(roh["vorbereitung"]))
      ? (txt(roh["vorbereitung"]) as Vorbereitungsphase)
      : "keine",
    anfahrt: {
      eintraege: zahl(fahrt["eintraege"]) ?? 0,
      stunden: zahl(fahrt["stunden"]),
      schalter: fahrt["schalter"] === true,
      abgerechnet: fahrt["abgerechnet"] === true,
    },
    ...(erwartung ? { erwartung } : {}),
    ...(abholung["moeglich"] === true
      ? {
          abholung: {
            moeglich: true, an: abholung["an"] === true, schaltbar: abholung["schaltbar"] === true,
            codes: (Array.isArray(abholung["codes"]) ? abholung["codes"] as unknown[] : [])
              .filter((c): c is string => typeof c === "string"),
            ...(txt(alsObjekt(abholung["anhaenger"])["typ_id"])
              ? {
                  anhaenger: {
                    typId: txt(alsObjekt(abholung["anhaenger"])["typ_id"]),
                    typ: txt(alsObjekt(abholung["anhaenger"])["typ"]),
                    varianteId: txt(alsObjekt(abholung["anhaenger"])["variante_id"]),
                    variante: txt(alsObjekt(abholung["anhaenger"])["variante"]),
                    von: txt(alsObjekt(abholung["anhaenger"])["von"]),
                    bis: txt(alsObjekt(abholung["anhaenger"])["bis"]),
                    da: alsObjekt(abholung["anhaenger"])["da"] === true,
                  },
                }
              : {}),
          },
        }
      : {}),
    ...(maschinen.length ? { maschinen } : {}),
    ...(papiere.length ? { papiere } : {}),
    ...(ab ? { ab } : {}),
    ...(roh["abrechnung"]
      ? {
          abrechnung: {
            beleg_id: txt(abr["beleg_id"]),
            lauf_id: txt(abr["lauf_id"]),
            netto: zahl(abr["netto"]),
            rot: zahl(abr["rot"]) ?? 0,
            gelb: zahl(abr["gelb"]) ?? 0,
            weitere: zahl(abr["weitere"]) ?? 0,
          },
        }
      : {}),
    kunde: txt(roh["kunde"]) || txt(positionen[0]?.["label"]),
    ...(txt(roh["kundennummer"]) ? { kundeNr: txt(roh["kundennummer"]) } : {}),
    /* Ohne Betreff, was darin steckt — nicht mehr nur die erste Zeile
       (05.10.2026, vorgangsinhalt.ts). */
    was: titelAus(txt(roh["subject"]), inhaltAusPositionen(positionen as Inhaltszeile[])),
    ort: txt(roh["ort"]),
    von: tagKurz(von),
    bis: tagKurz(bis),
    zustand,
    art: TOENUNG[zustand] ?? "",
    grundlage: txt(roh["billing_mode"]),
    // Dauermiete ohne festes Ende (V-25: in der Akte umstellbar).
    ...(roh["end_date_open"] === true ? { endeOffen: true } : {}),
    ...(txt(roh["endgrund"]) || txt(roh["cancel_reason"])
      ? { grund: txt(roh["endgrund"]) || txt(roh["cancel_reason"]) } : {}),
    ...endeAus(roh),
    ...(txt(roh["note_customer"]) ? { notiz_kunde: txt(roh["note_customer"]) } : {}),
    // Die Bestellnummer/Kommission des Kunden (0188, Systemprüfung 14).
    ...(txt(roh["customer_reference"]) ? { bestellnummer: txt(roh["customer_reference"]) } : {}),
    // Der Hinweis für den Hof (0284).
    ...(txt(roh["yard_note"]) ? { hofhinweis: txt(roh["yard_note"]) } : {}),
    // Das Zahlungsziel (0190, Wunsch 25.09.2026) — gilt, abweichend, am Kunden.
    ...(roh["zahlungsziel"] && typeof roh["zahlungsziel"] === "object"
      ? (() => {
          const z = roh["zahlungsziel"] as Roh;
          return {
            zahlungsziel: {
              tage: zahl(z["tage"]), eigenes: zahl(z["eigenes"]), kunde: zahl(z["kunde"]), da: z["da"] === true,
            },
          };
        })()
      : {}),
    /* Der Vortext (Migration 0059). Er heißt hier `vortext` und nicht
       `notiz_vortext`: Der Nachtext heißt in der Oberfläche seit jeher
       „Kundentext“ — ein zweites `notiz_` davor ließe die beiden wie
       Notizen aussehen, und Notizen bleiben im Haus. Diese beiden gehen
       hinaus. */
    ...(txt(roh["note_intro"]) ? { vortext: txt(roh["note_intro"]) } : {}),
    ...(txt(roh["note_internal"]) ? { notiz_intern: txt(roh["note_internal"]) } : {}),
    netto: positionen.reduce<number | null>((summe, p) => {
      const n = zahl(p["total_net"]);
      return n === null ? summe : (summe ?? 0) + n;
    }, null),
    ampeln: alsAmpeln(roh),
    schaeden: alsSchaeden(roh),
  };
}

/* ══════════════ Protokolle ══════════════ */

/**
 * Ein Protokoll mit dem, was die Korrektur braucht: seine Kennung, die
 * Belegung, die Zahlen als Zahlen und die Zahl der Korrekturen.
 */
/**
 * Ein Eintrag der Korrekturspur — `handover.corrections`, ein Element.
 *
 * **Sie ist der Grund, warum ein Protokoll korrigierbar sein darf.** Was
 * korrigiert wird, verschwindet nicht: Die API schreibt vorher, nachher, wer
 * und warum in die Spur (`PATCH /protokolle/:id`, Migration 0042), und die
 * Rechte darauf sind mit Migration 0069 festgezurrt — `update` und `delete`
 * auf `handover` sind der Anwendung entzogen. Bis zum 16.09.2026 kam von der
 * Spur nur ihre Länge im Klienten an; das Blatt konnte deshalb „korrigiert"
 * sagen und nicht, was.
 *
 * `von` ist die Kennung des Mitarbeiters, kein Name — die Abfrage verbindet
 * `corrections` nicht mit `staff`. Solange das so ist, steht der Name nicht
 * da; eine UUID hinzuschreiben wäre schlechter als nichts.
 */
export type Protokollkorrektur = {
  /** Wann korrigiert wurde, schon gesetzt. */
  wann: string;
  grund: string;
  /** Je geändertem Feld: der Schlüssel, davor und danach. */
  felder: { feld: string; vorher: string; nachher: string }[];
};

export type ProtokollZeile = Protokoll & {
  id: string;
  belegungId: string;
  ressource: string;
  art: "ausgabe" | "ruecknahme";
  km: string | null;
  korrekturen: number;
  /** Der Grund der letzten Korrektur — ein Wert, den ein Mensch schrieb. */
  letzterGrund: string | null;
  /** Die ganze Spur, das Älteste zuerst — so, wie sie geschrieben wurde. */
  spur: Protokollkorrektur[];
  /**
   * **Was hinausging, festgehalten** (0167) — Ausstattung, Anbauteile,
   * Zubehör, Bediener, Zeitraum, Ort, wie sie im Augenblick der Unterschrift
   * dastanden. `null` bei allem, was vor dem 22.09.2026 entstand: Dort wurde
   * es nicht aufgeschrieben, und nachzuschlagen wäre die falsche Antwort —
   * ein Gerätetausch danach machte daraus ein anderes Papier.
   */
  inhalt: Uebergabeinhalt | null;
  /** Der Satz, den der Kunde bestätigt hat — im Wortlaut von damals. */
  bestaetigung: string | null;
  /**
   * Rücknahme: „Vom Kunden vollgetankt?" (0172, C-04). `null` = nicht gefragt
   * — ein Protokoll von vorher oder eine Ausgabe.
   */
  vollgetankt?: boolean | null;
  /** Was der Hof danach getankt hat — das Ist für den Kraftstoffposten. */
  nachgetanktLiter?: number | null;
  nachgetanktAm?: string | null;
  /** Wer nachgetankt hat (0279) — für „erledigt von … am …". */
  nachgetanktVon?: string | null;
  /**
   * **Nachbereitung Nachtanken fällig** (0279): Rücknahme, und der Kunde hat
   * nicht vollgetankt oder die Tankstufe steht unter voll. Erledigt ist sie,
   * sobald `nachgetanktLiter` steht (auch 0).
   */
  nachbereitungNoetig?: boolean;
  /**
   * **Hat das Gerät einen Zähler?** (P8-01) — `machine_type.has_hour_meter`,
   * sonst die Kennzahl, wie in der API (`ZAEHLER_DER_BELEGUNG`). Nur dort
   * verlangt der Prüfkasten den Rücknahmestand. Fehlt das Feld, gilt: ja.
   */
  zaehlerPflicht?: boolean;
};

export type ProtokollListe = Protokolle & { liste: ProtokollZeile[] };

function alsProtokollZeile(h: Roh): ProtokollZeile {
  const korrekturen = alsListe(h["corrections"]);
  const km = zahl(h["odometer_km"]);
  return {
    ...alsProtokoll(h),
    id: txt(h["id"]),
    belegungId: txt(h["booking_id"]),
    ressource: txt(h["ressource"]),
    art: txt(h["kind"]) === "ruecknahme" ? "ruecknahme" : "ausgabe",
    km: km === null ? null : String(km),
    korrekturen: korrekturen.length,
    letzterGrund: txt(korrekturen.at(-1)?.["grund"]) || null,
    spur: korrekturen.map(alsKorrektur),
    inhalt: alsInhalt(h["content"]),
    bestaetigung: txt(h["confirmation"]) || null,
    vollgetankt: typeof h["vom_kunden_vollgetankt"] === "boolean" ? h["vom_kunden_vollgetankt"] : null,
    nachgetanktLiter: zahl(h["nachgetankt_liter"]),
    nachgetanktAm: txt(h["nachgetankt_am"]) ? tagUndZeit(h["nachgetankt_am"]) : null,
    nachgetanktVon: txt(h["nachgetankt_von"]) || null,
    nachbereitungNoetig: h["nachbereitung_noetig"] === true,
    zaehlerPflicht: h["zaehler_pflicht"] !== false,
  };
}

/** `handover.content` (0167) — oder `null`, wenn nichts festgehalten wurde. */
function alsInhalt(roh: unknown): Uebergabeinhalt | null {
  if (!roh || typeof roh !== "object" || Array.isArray(roh)) return null;
  const o = roh as Roh;
  const liste = (wert: unknown): string[] =>
    Array.isArray(wert) ? wert.map(txt).filter(Boolean) : [];
  return {
    ausstattung: txt(o["ausstattung"]) || null,
    anbau: liste(o["anbau"]),
    zubehoer: liste(o["zubehoer"]),
    bediener: liste(o["bediener"]),
    vonIso: txt(o["von"]),
    bisIso: txt(o["bis"]),
    ort: txt(o["ort"]) || null,
  };
}

/** Die Felder, die `PATCH /protokolle/:id` in die Spur schreibt. */
const KORREKTURFELD: Record<string, string> = {
  operating_hours: "uebergabe.betriebsstunden",
  fuel_percent: "uebergabe.tank",
  odometer_km: "uebergabe.kilometer",
  condition: "uebergabe.zustand",
  damages: "uebergabe.schaeden",
  signed_by: "vorgang.protUnterschrieben",
  occurred_at: "uebergabe.zeitpunkt",
  // Die Liter der Nachbereitung, geändert mit Grund (0279).
  refuel_liters: "vorgang.protNachtankenLiter",
};

function alsKorrektur(k: Roh): Protokollkorrektur {
  const vorher = alsObjekt(k["vorher"]);
  const nachher = alsObjekt(k["nachher"]);
  /* Die Schlüssel der Spur sind die der Datenbank. Welche Felder geändert
     wurden, sagt `nachher` — `vorher` trägt dieselben, nur mit dem alten
     Wert, und ein Feld, das erst gesetzt wurde, steht dort als null. */
  const felder = Object.keys(nachher).map((feld) => ({
    feld: KORREKTURFELD[feld] ?? feld,
    vorher: feld === "occurred_at" ? tagUndZeit(vorher[feld]) : txt(vorher[feld]),
    nachher: feld === "occurred_at" ? tagUndZeit(nachher[feld]) : txt(nachher[feld]),
  }));
  return { wann: tagUndZeit(k["am"]), grund: txt(k["grund"]), felder };
}

/** `spaet` aus `GET /protokolle` — vollständig oder gar nicht. */
function spaetAus(roh: unknown): Protokoll["spaet"] | null {
  if (!roh || typeof roh !== "object") return null;
  const r = roh as Record<string, unknown>;
  const geplant = zahl(r["geplant"]);
  const berechnet = zahl(r["berechnet"]);
  if (!txt(r["mietende"]) || !txt(r["berechnetBis"]) || geplant === null || berechnet === null) return null;
  return { mietende: txt(r["mietende"]), berechnetBis: txt(r["berechnetBis"]), geplant, berechnet };
}

function alsProtokoll(h: Roh): Protokoll {
  const schaden = h["damages"];
  const stunden = zahl(h["operating_hours"]);
  const tank = zahl(h["fuel_percent"]);
  return {
    ...(txt(h["nummer"]) ? { nummer: txt(h["nummer"]) } : {}),
    wann: tagUndZeit(h["occurred_at"]),
    ...(txt(h["occurred_at"]) ? { wannIso: txt(h["occurred_at"]) } : {}),
    // Abholung am Vorabend, Rückgabe am Morgen (0286): der berechnete Tag.
    ...(txt(h["berechnet"]) ? { berechnetIso: txt(h["berechnet"]) } : {}),
    // Später zurück als vereinbart (Lisas Interview): nur, wenn der Server es sagt.
    ...(spaetAus(h["spaet"]) ? { spaet: spaetAus(h["spaet"]) as NonNullable<Protokoll["spaet"]> } : {}),
    wer: txt(h["mitarbeiter"]),
    wo: txt(h["ort"]) || txt(h["ressource"]),
    ...(stunden !== null ? { stunden: String(stunden) } : {}),
    ...(tank !== null ? { tank: String(tank) } : {}),
    ...(txt(h["condition"]) ? { zustand: txt(h["condition"]) } : {}),
    // `damages` ist in der Datenbank ein Text, keine Liste. Der Abgleich
    // gegen ein Array lief deshalb immer ins Leere, und die Schadensnotiz
    // stand nie in der Maske -- obwohl sie am Protokoll hing.
    schaden: Array.isArray(schaden)
      ? (schaden.length ? schaden.map((x) => txt(alsObjekt(x)["text"] ?? x)).join(" · ") : null)
      : (txt(schaden) || null),
    unterschrift: txt(h["signed_by"]),
    unterschriftPfad: txt(h["signature_url"]),
    unterschriebenAm: tagUndZeit(h["signed_at"]),
    ...(txt(h["unterschrift_grund"]) ? { unterschriftGrund: txt(h["unterschrift_grund"]) } : {}),
    ...(txt(h["unterschrift_freigegeben_am"]) ? { unterschriftFreigegebenAm: tagUndZeit(h["unterschrift_freigegeben_am"]) } : {}),
    ...(archivvermerkAus(h["archiv"]) ? { archiv: archivvermerkAus(h["archiv"]) as Archivvermerk } : {}),
    art: txt(h["kind"]),
  };
}

/**
 * Die Betriebsstunden einer Position — je Belegung, dann summiert.
 *
 * Wortgleich mit `istBetriebsstunden()` in `@mrmachine/fach`, und das ist
 * Absicht: Die Zahl in der Maske und die Zahl auf der Rechnung muessen
 * dieselbe sein. Nach einem Geraetetausch haengen zwei Maschinen an einer
 * Position, und jede zaehlt fuer sich.
 */
function stundenJeBelegung(zeilen: Roh[]): number | null {
  const belegungen = [...new Set(zeilen.map((h) => txt(h["booking_id"])))];
  let summe = 0;
  let gezaehlt = false;

  for (const belegung of belegungen) {
    const eigene = zeilen.filter((h) => txt(h["booking_id"]) === belegung);
    const ausgabe = eigene.find((h) => txt(h["kind"]) === "ausgabe");
    const ruecknahme = eigene.find((h) => txt(h["kind"]) === "ruecknahme");
    // Eine Belegung ohne Ausgabe ist noch nicht gelaufen.
    if (!ausgabe) continue;
    const von = zahl(ausgabe["operating_hours"]);
    const bis = ruecknahme ? zahl(ruecknahme["operating_hours"]) : null;
    if (von === null || bis === null) return null;
    summe += bis - von;
    gezaehlt = true;
  }

  return gezaehlt ? summe : null;
}

/**
 * Ein Übergabe- oder Rücknahmeprotokoll anlegen.
 *
 * **Das war die Lücke.** `POST /protokolle` gibt es seit dem 14.09.2026 und
 * die API tut daran alles Richtige — sie setzt `actual_from`, stellt die
 * Belegung auf `laeuft` und den Vorgang gleich mit. Nur rief es **niemand**:
 * Im ganzen Web-Ordner stand kein einziger Aufruf. Der Knopf „Übergeben" im
 * Brett färbte den Balken um und legte kein Protokoll an — und damit fehlte
 * genau das, woraus abgerechnet wird (Regel 3). Luca, 15.09.2026: *„Ich kann
 * auch nichts übergeben."*
 */
export type Protokolleingabe = {
  belegungId: string;
  art: "ausgabe" | "ruecknahme";
  /** Gezählter Bestand (0194): wie viele Stück hinaus- oder zurückgingen. */
  stueck?: number | null;
  /** ISO-Zeitpunkt. Vorbelegt ist „jetzt" — wer Montag die Rücknahme vom
   *  Freitag einträgt, stellt ihn zurück. */
  zeitpunkt?: string;
  betriebsstunden?: number | null;
  kilometerstand?: number | null;
  tankProzent?: number | null;
  zustand?: string;
  schaeden?: string;
  unterschriebenVon?: string;
  /** Ablagepfade, wie `ablageHochladen` sie zurueckgibt — nie Dateien. */
  fotos?: string[];
  /** Der Ablagepfad der gezeichneten Unterschrift. Ohne Namen weist die
   *  API sie ab (422), und das ist richtig so. */
  unterschriftUrl?: string;
  /**
   * Ob unterschrieben werden konnte — `unterschrift_moeglich` (0122).
   *
   * **Eine Auskunft, kein Schalter.** Seit dem 18.09.2026 verlangt die API
   * beim reinen Mietgeraet ohne Unterschrift IMMER einen Grund, auch wenn
   * dieses Feld fehlt. Es bleibt, weil `false` den Unterschied zwischen „hat
   * niemand versucht" und „ging nicht" traegt.
   */
  unterschriftMoeglich?: boolean;
  /** Warum niemand unterschrieben hat. Ohne Unterschrift Pflicht. */
  unterschriftGrund?: string;
  /**
   * Rücknahme: „Vom Kunden vollgetankt?" (0172, C-04). Ungefragt bleibt das
   * Feld weg — aus Schweigen wird keine Aufgabe „Nachtanken".
   */
  vollgetankt?: boolean | null;
  /**
   * **Der Satz, unter dem der Kunde gezeichnet hat** (0167) — im Wortlaut der
   * Oberfläche, wie er ihm dastand.
   *
   * Er kommt von hier und nicht aus dem Server: Sichtbarer Text entsteht nicht
   * in der API (CLAUDE.md), und was 2026 bestätigt wurde, darf 2027 nicht
   * anders dastehen, weil jemand den Schlüssel umformuliert hat. Ohne
   * Unterschrift lässt die API ihn fallen — bestätigt hat dann niemand.
   */
  bestaetigung?: string;
  /**
   * **Pflichtfotos, die nicht gingen** (0173) — je Blickwinkel ein Grund.
   * Daraus wird eine Zeile unter „Zu prüfen" für die Disposition.
   */
  fotosFehlen?: { blickwinkel: Blickwinkel; grund: string }[];
};

/** Was nach dem Protokoll ist: `zurueck`, wenn es die letzte Rücknahme war. */
export type Protokollergebnis = { vorgangZustand: string | null };

/**
 * Der Rumpf, wie `POST /protokolle` ihn liest.
 *
 * Eigen und ausgelagert, weil er **zwei** Wege geht: gleich hinaus
 * (`protokollAnlegen`) oder in die Warteschlange am Telefon
 * (`daten/warteschlange.ts`, Migration 0113). Zwei Stellen mit derselben
 * Umbenennung wären die erste Stelle, an der ein Feld vergessen wird.
 */
export function protokollRumpf(e: Protokolleingabe): Record<string, unknown> {
  return {
    belegung_id: e.belegungId,
    art: e.art,
    ...(e.zeitpunkt ? { zeitpunkt: e.zeitpunkt } : {}),
    ...(e.betriebsstunden === null || e.betriebsstunden === undefined
      ? {} : { betriebsstunden: e.betriebsstunden }),
    ...(e.kilometerstand === null || e.kilometerstand === undefined
      ? {} : { kilometerstand: e.kilometerstand }),
    ...(e.tankProzent === null || e.tankProzent === undefined
      ? {} : { tank_prozent: e.tankProzent }),
    ...(e.zustand?.trim() ? { zustand: e.zustand.trim() } : {}),
    ...(e.schaeden?.trim() ? { schaeden: e.schaeden.trim() } : {}),
    ...(e.unterschriebenVon?.trim() ? { unterschrieben_von: e.unterschriebenVon.trim() } : {}),
    ...(e.fotos?.length ? { fotos: e.fotos } : {}),
    ...(e.unterschriftUrl ? { unterschrift_url: e.unterschriftUrl } : {}),
    ...(e.unterschriftMoeglich === undefined
      ? {} : { unterschrift_moeglich: e.unterschriftMoeglich }),
    ...(e.unterschriftGrund?.trim() ? { unterschrift_grund: e.unterschriftGrund.trim() } : {}),
    ...(e.bestaetigung?.trim() ? { bestaetigung: e.bestaetigung.trim() } : {}),
    ...(e.fotosFehlen?.length ? { fotos_fehlen: e.fotosFehlen } : {}),
    ...(e.art === "ruecknahme" && typeof e.vollgetankt === "boolean"
      ? { vom_kunden_vollgetankt: e.vollgetankt } : {}),
    ...(typeof e.stueck === "number" ? { stueck: e.stueck } : {}),
  };
}

export async function protokollAnlegen(e: Protokolleingabe): Promise<Protokollergebnis> {
  if (ATTRAPPE) return gleich({ vorgangZustand: null }, 80);
  const roh = await sende<Roh>("/protokolle", protokollRumpf(e));
  return { vorgangZustand: txt(roh["vorgang_status"]) || null };
}

/**
 * `PATCH /protokolle/:id` — der Weg zurück, mit Grund.
 *
 * Geschickt wird jedes Feld, auch das leere: Wer einen Zustandstext löscht,
 * meint „kein Text" und nicht „unverändert". Was gleich geblieben ist, erkennt
 * die API selbst und schreibt es nicht in die Spur.
 */
export async function protokollKorrigieren(
  id: string,
  e: Omit<Protokolleingabe, "belegungId" | "art"> & { grund: string },
): Promise<void> {
  if (ATTRAPPE) return gleich(undefined, 80);
  await aendere<Roh>(`/protokolle/${id}`, {
    grund: e.grund,
    ...(e.zeitpunkt ? { zeitpunkt: e.zeitpunkt } : {}),
    betriebsstunden: e.betriebsstunden ?? null,
    kilometerstand: e.kilometerstand ?? null,
    tank_prozent: e.tankProzent ?? null,
    zustand: e.zustand ?? "",
    ...(e.schaeden !== undefined ? { schaeden: e.schaeden } : {}),
    /* Der Name nur, wenn das Formular ihn zeigt. Unter einer Unterschrift
       zeigt es ihn nicht — und ein mitgeschicktes `""` las die API als
       „Name löschen" und wies jede Korrektur ab (Playbook D-23/D-31). */
    ...(e.unterschriebenVon !== undefined ? { unterschrieben_von: e.unterschriebenVon } : {}),
  });
}

/**
 * `POST /protokolle/:id/nachtanken` — die Nachbereitung: die Liter, die der
 * Hof nach der Rücknahme getankt hat (0172, C-04, 0279). Noch einmal
 * geschickt, ist es eine Korrektur und braucht `grund`.
 */
export async function nachtankenEintragen(id: string, liter: number, grund?: string): Promise<void> {
  if (ATTRAPPE) return gleich(undefined, 80);
  /* Das erste Eintragen braucht keinen Grund; ändern danach schon (0279). */
  await sende<Roh>(`/protokolle/${encodeURIComponent(id)}/nachtanken`, { liter, ...(grund ? { grund } : {}) });
}

/**
 * `POST /mengen` — die Ist-Menge einer Position, vom Prüfkasten aus
 * (06.10.2026). Ein Mensch bestätigt sie; erst dann ist sie das Ist (Regel 3).
 * Eine 0 ist erlaubt: nichts verbraucht.
 */
export async function istMengeEintragen(
  e: { positionId: string; metric: string; menge: number; grund?: string },
): Promise<{ korrektur: boolean }> {
  if (ATTRAPPE) return gleich({ korrektur: !!e.grund }, 80);
  /* **Die erste Eingabe setzt, jede weitere korrigiert mit Grund** (0280):
     Der Server entscheidet, ob es eine Korrektur ist; ohne Grund antwortet er
     dann 422 `eingabe.grund`. */
  const roh = await sende<Roh>("/mengen", {
    position_id: e.positionId,
    zaehlgroesse: e.metric,
    menge: e.menge,
    nachweis: "fertigmeldung",
    fertiggemeldet_am: new Date().toISOString(),
    ...(e.grund ? { grund: e.grund } : {}),
  });
  return { korrektur: roh["korrektur"] === true };
}

/** Eine Aufwandsposition am Einsatz (`GET /mengen?belegung=`, 0280) — ohne Preis. */
export type Aufwandsposition = {
  positionId: string;
  bezeichnung: string;
  metric: string;
  geplant: number | null;
  /** Was erfasst ist — `null`: noch nichts. */
  erfasst: number | null;
  erfasstAm: string | null;
  erfasstVon: string | null;
  korrekturen: number;
};

export async function aufwandDerBelegung(belegung: string): Promise<Aufwandsposition[]> {
  if (ATTRAPPE) return gleich([], 60);
  return alsAufwandsliste(await hole<Roh>(`/mengen?belegung=${encodeURIComponent(belegung)}`));
}

/**
 * Die Aufwandspositionen eines Auftrags (`GET /mengen?vorgang=`) — für den
 * Prüfkasten am Schreibtisch: „Menge korrigieren" an jeder erfassten Menge.
 */
export async function aufwandDesVorgangs(nr: string): Promise<Aufwandsposition[]> {
  if (ATTRAPPE) return gleich([], 60);
  return alsAufwandsliste(await hole<Roh>(`/mengen?vorgang=${encodeURIComponent(nr)}`));
}

function alsAufwandsliste(roh: Roh): Aufwandsposition[] {
  return alsListe(roh["positionen"]).map((p) => ({
    positionId: txt(p["position_id"]),
    bezeichnung: txt(p["bezeichnung"]),
    metric: txt(p["metric"]),
    geplant: zahl(p["geplant"]),
    erfasst: zahl(p["erfasst"]),
    erfasstAm: txt(p["erfasst_am"]) || null,
    erfasstVon: txt(p["erfasst_von"]) || null,
    korrekturen: zahl(p["korrekturen"]) ?? 0,
  }));
}

/** `POST /protokolle/:id/unterschrift-freigeben` — die fehlende Unterschrift freigeben (0122). */
export async function unterschriftFreigeben(id: string): Promise<void> {
  if (ATTRAPPE) return gleich(undefined, 80);
  await sende<Roh>(`/protokolle/${encodeURIComponent(id)}/unterschrift-freigeben`);
}

export async function protokolle(vorgangNr: string): Promise<ProtokollListe> {
  if (ATTRAPPE) {
    const fertig = A.PROTOKOLLE[vorgangNr];
    /* Die Liste je Belegung — der Laufzettel liest sie, nicht die beiden
       Eckprotokolle. `istStunden` bleibt leer: Es ist die Summe über alle
       Belegungen und damit die Zahl, die der Laufzettel ausdrücklich NICHT
       bildet (docs/48-auftragsakte.md, 2.4). */
    return gleich({
      ...(fertig ?? { ausgabe: null, ruecknahme: null, istStunden: null }),
      liste: A.PROTOKOLLZEILEN[vorgangNr] ?? [],
    });
  }

  const roh = await hole<Roh>(`/protokolle?vorgang=${encodeURIComponent(vorgangNr)}`);
  const zeilen = alsListe(roh["zeilen"]);
  /* Die Liste kommt absteigend nach Zeitpunkt. Fuer die beiden Blaetter ist
     die FRUEHESTE Ausgabe und die SPAETESTE Ruecknahme die richtige: So
     steht dort der Anfang und das Ende des Einsatzes, auch wenn zwischendrin
     getauscht wurde. */
  const ausgaben = zeilen.filter((h) => txt(h["kind"]) === "ausgabe");
  const ruecknahmen = zeilen.filter((h) => txt(h["kind"]) === "ruecknahme");

  return {
    ausgabe: ausgaben.length ? alsProtokoll(ausgaben[ausgaben.length - 1]!) : null,
    ruecknahme: ruecknahmen.length ? alsProtokoll(ruecknahmen[0]!) : null,
    istStunden: stundenJeBelegung(zeilen),
    // Alle, in der Reihenfolge des Einsatzes: erst die Ausgabe, dann die
    // Rücknahme. Die Seitenleiste zeigt je Protokoll eine Zeile.
    liste: [...zeilen].reverse().map(alsProtokollZeile),
  };
}

/* ══════════════ Das Handy ══════════════ */

function alsHandyZeile(z: Roh, art: "raus" | "rein"): HandyZeile {
  const vorschlag = zahl(z["stunden_vorschlag"]);
  const ausgabe = zahl(z["stunden_ausgabe"]);
  return {
    belegungId: txt(z["id"]),
    ressourceId: txt(z["resource_id"]),
    kennung: txt(z["kennung"]),
    typ: txt(z["typ"]),
    kunde: txt(z["kunde"]),
    ort: txt(z["ort"]),
    ortschaft: txt(z["ortschaft"]),
    zufahrt: txt(z["zufahrt"]),
    hinweis: txt(z["hinweis"]),
    ...(txt(z["hofhinweis"]) ? { hofhinweis: txt(z["hofhinweis"]) } : {}),
    vorgang: txt(z["vorgang"]),
    zeit: uhrzeit(z[art === "raus" ? "planned_from" : "planned_until"]),
    ueberfaellig: z["ueberfaellig"] === true,
    // Bei der Rücknahme ist der Stand der Ausgabe der bessere Bezug: Der
    // Zähler steht seither höher, nie tiefer. Wer 1284 daneben stehen hat
    // und 1452 abliest, merkt sofort, ob er sich verlesen hat.
    // **Als Wert übernehmen lässt die Maske ihn dort nicht** (17.09.2026):
    // Übernommen ergäbe er 0 h gefahren (`HandyProtokoll.tsx`).
    stundenVorschlag: (art === "rein" ? (ausgabe ?? vorschlag) : vorschlag) === null
      ? "" : String(art === "rein" ? (ausgabe ?? vorschlag) : vorschlag),
    stundenAusgabe: ausgabe === null ? "" : String(ausgabe),
    tage: zahl(z["tage"]),
    vonIso: txt(z["planned_from"]) || null,
    genommen: txt(alsObjekt(z["genommen"])["id"])
      ? { id: txt(alsObjekt(z["genommen"])["id"]), name: txt(alsObjekt(z["genommen"])["name"]) }
      : null,
    meins: z["meins"] === true,
    darfNehmen: z["darf_nehmen"] === true,
    darfZurueckgeben: z["darf_zurueckgeben"] === true,
    bediener: alsListe(z["bediener"]).map((b) => ({ id: txt(b["id"]), name: txt(b["name"]) })),
    bedienerBelegungId: txt(z["bediener_belegung_id"]) || null,
    zeitQuittiertAm: txt(z["zeit_quittiert_am"]) || null,
    zeitErfasst: z["zeit_erfasst"] === true,
    zeitLaeuft: z["zeit_laeuft"] === true,
    bisIso: txt(z["planned_until"]) || null,
    anschrift: txt(z["anschrift"]),
    telefon: txt(z["telefon"]),
    // Fehlt das Feld (eine Mappe von vor dem 17.09.2026), gilt die alte Regel:
    // Unterschrift ja.
    unterschriftNoetig: z["unterschrift_noetig"] !== false,
  };
}

/**
 * Der Tag des Fahrers — `GET /api/handy/heute`.
 *
 * Zwei Listen und sonst nichts: was hinausgeht, was zurueckkommt
 * (09-mobile.md). Liegengebliebenes steht mit drin und traegt
 * `ueberfaellig` — eine Liste, die nur den heutigen Tag zeigt, verliert die
 * Maschine von gestern, und der Fahrer erfaehrt es nie.
 */
export async function handyHeute(): Promise<HandyTag> {
  /* **Seit dem 18.09.2026 vier gefüllte Listen statt vier leeren.** Eine leere
     Tagesmappe hieß im Prototyp nicht nur „heute ist nichts" — sie ließ sich
     auch nicht antippen, und damit war der Einsatzschirm von nirgendwo aus zu
     erreichen. Die Zeilen stammen aus derselben Datei wie die Lagen. */
  if (ATTRAPPE) return gleich(AEI.handyTag());
  const roh = await hole<Roh>("/handy/heute");
  return {
    // Die Vorbereitung liest ihre Uhrzeit wie RAUS: Es geht um den Beginn.
    vorbereiten: alsListe(roh["vorbereiten"]).map((z) => alsHandyZeile(z, "raus")),
    raus: alsListe(roh["raus"]).map((z) => alsHandyZeile(z, "raus")),
    rein: alsListe(roh["rein"]).map((z) => alsHandyZeile(z, "rein")),
    // Läuft: Die Zeile hat die Form einer Rücknahme — ihr Tag ist das Ende.
    laufend: alsListe(roh["laufend"]).map((z) => alsHandyZeile(z, "rein")),
    // Die offenen Ortsaufnahmen (19.09.2026) — dieselbe Form wie unter `/start`.
    aufnahmen: alsListe(roh["aufnahmen"]).map(alsAufgabe),
    // Die Nachbereitung — Nachtanken (P8-02).
    nachbereiten: alsListe(roh["nachbereiten"]).map((n): Nachbereitungszeile => ({
      protokollId: txt(n["protokoll_id"]),
      belegungId: txt(n["belegung_id"]),
      kennung: txt(n["kennung"]),
      vorgang: txt(n["vorgang"]),
      kunde: txt(n["kunde"]),
      seit: txt(n["seit"]),
      besitzer: txt(alsObjekt(n["besitzer"])["name"]) || null,
      meins: n["meins"] === true,
      frei: n["frei"] === true,
      weg: txt(n["weg"]),
    })),
  };
}

/** Die Nachbereitung an einem Einsatz (`GET /handy/nachbereitung?belegung=`, P8-02). */
export type NachtankenAmEinsatz = {
  protokollId: string;
  kennung: string;
  noetig: boolean;
  liter: number | null;
  erledigtAm: string | null;
  erledigtVon: string | null;
};

export async function nachbereitungAmEinsatz(belegung: string): Promise<NachtankenAmEinsatz[]> {
  if (ATTRAPPE) return gleich([], 60);
  const roh = await hole<Roh>(`/handy/nachbereitung?belegung=${encodeURIComponent(belegung)}`);
  return alsListe(roh["nachtanken"]).map((n) => ({
    protokollId: txt(n["protokoll_id"]),
    kennung: txt(n["kennung"]),
    noetig: n["noetig"] === true,
    liter: zahl(n["liter"]),
    erledigtAm: tagUndZeit(n["erledigt_am"]) || null,
    erledigtVon: txt(n["erledigt_von"]) || null,
  }));
}

/**
 * Die Fotos einer Maschine — `GET /api/protokolle/fotos?ressource=`.
 *
 * **Sie liegen nicht doppelt.** Die Maschinenakte liest denselben Bestand
 * wie das Protokoll, ueber die Belegung. Jede Zeile traegt ihren
 * Zusammenhang mit: Vorgang, Kunde, Ausgabe oder Ruecknahme, Zeitpunkt.
 */
export async function geraetefotos(ressourceId: string): Promise<Geraetefoto[]> {
  if (ATTRAPPE) return gleich([]);
  const roh = await hole<Roh>(`/protokolle/fotos?ressource=${encodeURIComponent(ressourceId)}`);
  return alsListe(roh["zeilen"]).map((z) => ({
    pfad: txt(z["pfad"]),
    protokollId: txt(z["protokoll_id"]),
    art: txt(z["kind"]) === "ruecknahme" ? "ruecknahme" : "ausgabe",
    wann: tagUndZeit(z["occurred_at"]),
    vorgang: txt(z["vorgang"]),
    kunde: txt(z["kunde"]),
    ort: txt(z["ort"]),
    wer: txt(z["mitarbeiter"]),
    zustand: txt(z["condition"]),
    schaden: txt(z["damages"]),
  }));
}

/* ══════════════ Was am Vorgang geschieht ══════════════ */

/**
 * `POST /vorgaenge/:nr/ohne-rechnung` — **ohne Rechnung abschließen** (0280).
 * Nur mit Grund; der Server prüft, dass wirklich 0,00 offen ist (sonst 409
 * mit Satz). Danach liest die Maske den Vorgang neu.
 */
export async function vorgangOhneRechnung(nr: string, grund: string): Promise<void> {
  if (ATTRAPPE) {
    vorgaenge = vorgaenge.map((v) => (v.nr === nr
      ? { ...v, zustand: "abgerechnet" as const, art: "", ohneRechnung: { am: "", von: null, grund } }
      : v));
    return gleich(undefined, 120);
  }
  await sende<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/ohne-rechnung`, { grund });
}

export async function vorgangFreigeben(nr: string): Promise<Vorgang> {
  if (ATTRAPPE) {
    vorgaenge = vorgaenge.map((v) => {
      if (v.nr !== nr) return v;
      const { zutun: _weg, ...rest } = v;
      return { ...rest, zustand: "bereit" as const, art: "" };
    });
    const v = vorgaenge.find((x) => x.nr === nr);
    if (!v) throw new Error(`Vorgang ${nr} gibt es nicht.`);
    return gleich(v, 200);
  }
  return alsVorgang(await sende<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/freigeben`));
}

/**
 * `POST /vorgaenge/:nr/zurueck` — **„Leistung erledigt"** (P8-04): Ein Auftrag
 * ohne Gerät — nur Leistungen — hat keine Rücknahme, die ihn nach „Zurück"
 * bringt. Danach steht der Prüfkasten da: Menge eintragen, zur Abrechnung
 * geben. Ist ein Gerät draußen, weist die API ab (`vorgang.ruecknahme_fehlt`).
 */
export async function vorgangErledigt(nr: string): Promise<Vorgang> {
  if (ATTRAPPE) {
    vorgaenge = vorgaenge.map((v) => (v.nr === nr ? { ...v, zustand: "zurueck" as const } : v));
    const v = vorgaenge.find((x) => x.nr === nr);
    if (!v) throw new Error(`Vorgang ${nr} gibt es nicht.`);
    return gleich(v, 200);
  }
  return alsVorgang(await sende<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/zurueck`));
}

/** Eine Tauschsperre, die beim Storno stehen bleibt (F35) — Gerät, ab wann, mit Defekt. */
export type BleibendeSperre = { kennung: string; ab: string; defekt: boolean };

export async function vorgangStornieren(
  nr: string, grund: string, grundCode?: string,
): Promise<Vorgang & { sperrenBleiben?: BleibendeSperre[] }> {
  if (ATTRAPPE) {
    vorgaenge = vorgaenge.map((v) =>
      v.nr === nr ? { ...v, zustand: "storniert" as const, art: "", grund } : v);
    const v = vorgaenge.find((x) => x.nr === nr);
    if (!v) throw new Error(`Vorgang ${nr} gibt es nicht.`);
    return gleich(v, 200);
  }
  const roh = await sende<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/stornieren`, {
    grund,
    ...(grundCode ? { grund_code: grundCode } : {}),
  });
  /* **Was stehen bleibt, sagt die Antwort** (F35): eine Sperre aus einem
     Tausch, die schon begonnen hat oder an der ein Defekt hängt. */
  const bleiben = alsListe(roh["tauschsperren_bleiben"]).map((s): BleibendeSperre => ({
    kennung: txt(s["kennung"]), ab: txt(s["ab"]), defekt: s["defekt"] === true,
  }));
  return { ...alsVorgang(roh), ...(bleiben.length ? { sperrenBleiben: bleiben } : {}) };
}


/* ══════════════ Angebote ══════════════ */

const PHASE: Record<string, Angebot["phase"]> = {
  entwurf: "entwurf",
  angebot: "versendet",
  angenommen: "angenommen",
  abgelehnt: "abgelehnt",
  /* Ein angenommenes Angebot ist danach der Auftrag und läuft weiter (AG-57):
     In der Angebotsliste bleibt es „angenommen", nicht „Entwurf". */
  laeuft: "angenommen",
  zurueck: "angenommen",
  abrechenbar: "angenommen",
  abgerechnet: "angenommen",
};

function alsAngebot(z: Roh): Angebot {
  const frist = txt(z["valid_until"]);
  const tage = frist ? tageBis(frist) : null;
  return {
    nr: txt(z["number"]),
    kunde: txt(z["kunde"]),
    ...(txt(z["kundennummer"]) ? { kundeNr: txt(z["kundennummer"]) } : {}),
    /* Der Betreff, wie er gespeichert ist — ihn bearbeitet die Maske. `was`
       ist die Anzeige: ohne Betreff, was darin steckt (05.10.2026). */
    betreff: txt(z["subject"]),
    was: titelAus(txt(z["subject"]), txt(z["inhalt"])),
    gestellt: tagKurz(z["date"]),
    frist: tagKurz(frist),
    fristIso: frist || null,
    ort: txt(z["ort"]),
    phase: PHASE[txt(z["status"])] ?? "entwurf",
    tage,
    // „Eilt" ist nicht geliefert, sondern abgelesen: drei Tage oder weniger
    // bis zum Ablauf der Bindefrist (Leitsatz 4).
    ...(tage !== null && tage <= 3 && PHASE[txt(z["status"])] === "versendet" ? { eilt: true } : {}),
    ...(txt(z["rejection_reason"]) ? { grund: txt(z["rejection_reason"]) } : {}),
    // Positionen und Zuschläge — dasselbe Netto wie im Angebot (Befund 24).
    netto: zahl(z["netto"]),
    ...(z["netto_offen"] === true ? { nettoOffen: true } : {}),
    /* Was das Verwerfen hält, und was dabei mitgeht (Befund 40, 25.09.2026):
       gesperrt nur von einer durchgeführten Aufnahme oder Fotos; eine nur
       bestellte Fahrt steht in der Rückfrage. */
    ...(txt(z["aufnahme_gemessen_am"]) || (zahl(z["fotos_draussen"]) ?? 0) > 0
      ? {
        hatAufnahme: true,
        ...(txt(z["aufnahme_gemessen_am"]) ? { aufnahmeGemessenAm: txt(z["aufnahme_gemessen_am"]) } : {}),
        fotosDraussen: zahl(z["fotos_draussen"]) ?? 0,
      }
      : {}),
    ...(z["aufnahme_bestellt"] === true ? { aufnahmeBestellt: true } : {}),
    ...(() => {
      const termin = z["aufnahme_termin"] as Roh | null | undefined;
      return termin && txt(termin["von"])
        ? { aufnahmeTermin: { von: txt(termin["von"]), person: txt(termin["person"]) } }
        : {};
    })(),
    belegDrueben: z["beleg_drueben"] === true,
    // Welche Nummer gilt, entscheidet die Phase: Ein angenommenes Angebot IST
    // der Auftrag, und auf seinem Papier steht die AB-Nummer (0268).
    ...(() => {
      const nr = txt(z["ab_nummer"]) || txt(z["angebot_nummer"]) || txt(z["lex_quotation_number"]);
      return nr ? { lexNr: nr } : {};
    })(),
    // Angebotsnummer und Fassung für die Liste (AG5) — gezählt in der API.
    ...((txt(z["angebot_nummer"]) || txt(z["lex_quotation_number"]))
      ? { angebotNr: txt(z["angebot_nummer"]) || txt(z["lex_quotation_number"]) } : {}),
    fassungen: zahl(z["angebotsfassungen"]) ?? 0,
  };
}

export async function angeboteListe(): Promise<Liste<Angebot> & { zahlen: Angebotszahlen }> {
  if (ATTRAPPE) {
    // Was an einem Entwurf draußen hängt, liest der Stand der Aufnahmen (Befund 40).
    const zeilen = angebote.map((a) => ({ ...a, ...(a.phase === "entwurf" ? AUF.angebotsbezug(a.nr) : {}) }));
    return gleich({ zeilen, gesamt: zeilen.length, zahlen: angebotszahlenAus(zeilen) });
  }
  const roh = await hole<Roh>("/vorgaenge?art=angebot");
  const zeilen = alsListe(roh["zeilen"]).map(alsAngebot);
  /* Die Zählung der API (N9-03) — dieselbe wie „Angebote offen" auf der
     Startseite. Fehlt sie (ältere API), zählt die Liste selbst. */
  const z = roh["zahlen"] ? alsObjekt(roh["zahlen"]) : null;
  const zahlen: Angebotszahlen = z
    ? { wartet: zahl(z["wartet"]) ?? 0, eilt: zahl(z["eilt"]) ?? 0, entwurf: zahl(z["entwurf"]) ?? 0 }
    : angebotszahlenAus(zeilen);
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length, zahlen };
}

export async function positionen(vorgangNr: string): Promise<Position[]> {
  if (ATTRAPPE) return gleich(A.POSITIONEN.map((p) => ({ ...p })));
  // „neu" ist kein Vorgang, sondern ein leeres Blatt: Das Angebot wird erst
  // gebaut. Die API danach zu fragen hieße, einen 404 als Ladezustand zu
  // zeigen.
  if (!vorgangNr || vorgangNr === "neu") return [];

  const roh = await hole<Roh>(`/vorgaenge/${encodeURIComponent(vorgangNr)}`);
  return alsListe(roh["positionen"]).map(alsPosition);
}

/**
 * Eine Positionszeile aus der Serversicht in die der Maske.
 *
 * `id` gehört ausdrücklich dazu: Ohne sie lässt sich eine Position weder
 * ändern noch löschen — `sort_order` ist eine Anzeige und kein Schlüssel.
 * Bis zum 15.09.2026 fehlte sie hier, und genau deshalb war die Liste im
 * Angebot eine Anzeige und keine Bearbeitung.
 */
function alsPosition(p: Roh, i = 0): Position {
  const art = txt(p["kind"]);
  return {
    id: txt(p["id"]),
    nr: zahl(p["sort_order"]) ?? i + 1,
    was: txt(p["label"]),
    bei: [tagKurz(p["planned_from"]), tagKurz(p["planned_until"])].filter(Boolean).join(" – "),
    artikel: txt(p["kind"]),
    menge: String(zahl(p["quantity"]) ?? ""),
    einheit: txt(p["unit_label"]),
    einzel: zahl(p["unit_price"]),
    summe: zahl(p["total_net"]),
    art: (["miete", "leistung", "verkauf", "text", "zuschlag"].includes(art)
      ? art : "leistung") as Positionsart,
    typ_id: txt(p["machine_type_id"]) || null,
    // Die Stückzahl (0194) — `piece_count` aus der Liste, `stueck` aus Anlegen/Ändern.
    ...((zahl(p["piece_count"]) ?? zahl(p["stueck"]) ?? 1) > 1
      ? { stueck: (zahl(p["piece_count"]) ?? zahl(p["stueck"])) as number } : {}),
    von: txt(p["planned_from"]) || null,
    bis: txt(p["planned_until"]) || null,
    // Wie genau der Zeitraum gemeint ist (Migration 0154). Fehlt die Angabe
    // — eine Antwort von vor der Migration —, gilt `genau`: Das ist das
    // Verhalten von gestern und nicht eine leere Anzeige.
    zeitschaerfe: txt(p["zeitschaerfe"]) === "kw" ? "kw" as const : "genau" as const,
    // Regel 4: Was eingefroren ist, bleibt eingefroren. Die Maske sperrt
    // dann das Preisfeld, statt einen 409 abzuwarten.
    eingefroren: p["price_snapshot"] !== null && p["price_snapshot"] !== undefined,
    // Der Rabatt (Migration 0106): Prozent oder Betrag, und ob die Zeile ihn
    // tragen darf. Fehlt `rabattfaehig` (eine Antwort ohne die Spalte), gilt
    // nein — ein Feld, das dann 422 bekäme, wäre schlimmer als keins.
    rabattProzent: zahl(p["discount_percent"]) ?? 0,
    rabattBetrag: zahl(p["discount_net"]) ?? 0,
    // Der Betrag vor dem Rabatt — von der API gerechnet (B-12), hier nur gelesen.
    vorRabatt: zahl(p["netto_vor_rabatt"]) ?? null,
    rabattfaehig: p["rabattfaehig"] === true,
    optional: p["is_optional"] === true,
    alternativeZu: txt(p["alternative_to_id"]) || null,
    leistungsartId: txt(p["service_type_id"]) || null,
    wareId: txt(p["goods_article_id"]) || null,
    aufmass: p["aufmass"] === true,
    gradAngenommen: txt(p["grad_angenommen"]) || null,
    mindestmenge: zahl(p["mindestmenge"]) ?? null,
    bedient: p["with_operator"] === true,
    bedienungspflicht: alsBedienungsart(p["bedienungspflicht"]),
    bediener: (() => {
      const b = alsObjekt(p["bediener"]);
      return txt(b["id"])
        ? {
            id: txt(b["id"]),
            name: txt(b["name"]),
            kennung: txt(b["kennung"]),
            vorgemerkt: txt(b["art"]) === "option",
            unterwegs: b["unterwegs"] === true,
          }
        : null;
    })(),
    geraete: alsListe(p["geraete"]).map((g) => ({
      id: txt(g["id"]),
      kennung: txt(g["kennung"]),
      typ: txt(g["typ"]),
      rolle: txt(g["rolle"]) === "anbau" ? "anbau" as const
        : txt(g["rolle"]) === "zubehoer" ? "zubehoer" as const : "traeger" as const,
      vorgemerkt: txt(g["art"]) === "option",
      unterwegs: g["unterwegs"] === true,
      von: txt(g["von"]),
    })),
    anbauAn: txt(p["parent_item_id"]) || null,
    abholzeile: art === "zuschlag" && p["entfaellt_bei_abholung"] === true
      && (zahl(p["unit_price"]) ?? 0) === 0 && (zahl(p["total_net"]) ?? 0) === 0,
    zuschlagReihenfolge: art === "zuschlag" ? zahl(p["zuschlag_reihenfolge"]) ?? null : null,
    abgeholt: art === "miete" && p["uebergabeart"] === "abholen",
  };
}

/**
 * Wer diese Miete fahren kann — alle disponierbaren Menschen, begründet
 * (dienste/bediener.ts). Wer nicht geht, fehlt nicht, sondern sagt warum.
 */
export async function bedienerauswahl(vorgangNr: string, posId: string): Promise<Bedienerkandidat[]> {
  if (ATTRAPPE) return gleich([]);
  const roh = await hole<Roh>(
    `/vorgaenge/${encodeURIComponent(vorgangNr)}/positionen/${encodeURIComponent(posId)}/bediener`,
  );
  return alsListe(roh["zeilen"]).map(alsBedienerkandidat);
}

/** Ein Mensch aus einer Bedienerauswahl — an der Position oder am Gerät (A-06/E-02). */
function alsBedienerkandidat(k: Roh): Bedienerkandidat {
  return {
    id: txt(k["id"]),
    name: txt(k["name"]),
    kuerzel: txt(k["kuerzel"]) || null,
    frei: k["frei"] === true,
    // Vor 0230 kannte die API das Feld nicht -- dann galt: waehlbar heisst frei.
    waehlbar: k["waehlbar"] === undefined ? k["frei"] === true : k["waehlbar"] === true,
    grund: (["belegt", "qualifikation_fehlt", "qualifikation_abgelaufen"] as const)
      .find((g) => g === k["grund"]) ?? null,
    belegtBis: txt(k["belegt_bis"]) || null,
    vorgemerkt: k["vorgemerkt"] === true,
    qualifikation: txt(k["qualifikation"]) || null,
    gueltigBis: txt(k["gueltig_bis"]) || null,
    stammfahrer: k["stammfahrer"] === true,
    gewaehlt: k["gewaehlt"] === true,
  };
}

export async function angebotVersenden(nr: string): Promise<Angebot> {
  if (ATTRAPPE) {
    angebote = angebote.map((a) =>
      a.nr === nr
        ? { ...a, phase: "versendet" as const, nr: a.nr === "—" ? "AG-2026-0045" : a.nr,
            gestellt: "14.09.", frist: "28.09.", tage: 0 }
        : a);
    const a = angebote.find((x) => x.nr === (nr === "—" ? "AG-2026-0045" : nr));
    if (!a) throw new Error(`Angebot ${nr} gibt es nicht.`);
    return gleich(a, 220);
  }
  return alsAngebot(await sende<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/angebot`));
}

/**
 * Annehmen — mit der Wahl des Kunden.
 *
 * `gewaehlt` sind die optionalen und alternativen Positionen, die er nimmt.
 * Fehlt die Liste, nimmt er an, was in der Summe stand: Keine Option rutscht
 * still in einen Auftrag (23-angebotsprozess.md).
 */
/** Was dem Kunden für die Rechnung fehlt — vor der Annahme (F8, AG-40). */
export async function annahmeLuecken(nr: string): Promise<Annahmeluecken | null> {
  if (ATTRAPPE) return null;
  return annahmelueckenAus(await hole<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/annahme-luecken`));
}

/** Was zur Annahme außer der Wahl noch mitgeht (F8, F20, F23). */
export type Annahmezusatz = {
  /** Was das Annahmeblatt dem Kunden nachgetragen hat (F8) — die API füllt nur Leeres. */
  kundeAngaben?: Record<string, unknown> | null;
  /** „Nachträglich erfassen" bestätigt — der Mietbeginn liegt zurück (F20). */
  nachtraeglich?: boolean;
  /**
   * Wer fährt — je Position die UUID des Bedieners (F23). Am versendeten
   * Angebot ist die Position gesperrt; der Bediener steht nicht auf dem
   * Papier und ändert keinen Preis, also wird er hier gewählt.
   */
  bediener?: Record<string, string>;
};

export async function angebotAnnehmen(nr: string, gewaehlt: string[] = [], zusatz: Annahmezusatz = {}): Promise<Angebot> {
  if (ATTRAPPE) {
    const a = angebote.find((x) => x.nr === nr);
    if (!a) throw new Error(`Angebot ${nr} gibt es nicht.`);
    if (a.bel) {
      belegungen = belegungen.map((b) => (b.id === a.bel ? { ...b, fest: true, zusatz: "" } : b));
    }
    angebote = angebote.map((x) =>
      x.nr === nr ? { ...x, phase: "angenommen" as const, tage: null, eilt: false } : x);
    const neu = angebote.find((x) => x.nr === nr);
    if (!neu) throw new Error(`Angebot ${nr} gibt es nicht.`);
    return gleich(neu, 220);
  }
  const bediener = Object.entries(zusatz.bediener ?? {}).filter(([, b]) => b);
  const roh = await sende<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/annehmen`, {
    ...(gewaehlt.length ? { optionen_gewaehlt: gewaehlt } : {}),
    ...(zusatz.kundeAngaben ? { kunde_angaben: zusatz.kundeAngaben } : {}),
    ...(zusatz.nachtraeglich ? { nachtraeglich: true } : {}),
    ...(bediener.length ? { bediener: bediener.map(([position_id, bediener_id]) => ({ position_id, bediener_id })) } : {}),
  });
  /* **Was dem Kunden für die Rechnung fehlt** (W2, 24.09.2026) — nur, wenn
     etwas fehlt. Die Maske führt dann in seine Akte. */
  const luecken = alsObjekt(roh["kunde_luecken"]);
  const felder = Array.isArray(luecken["felder"]) ? (luecken["felder"] as unknown[]).map(txt) : [];
  return {
    ...alsAngebot(roh),
    ...(txt(luecken["nummer"]) && felder.length ? { kundeLuecken: { nr: txt(luecken["nummer"]), felder } } : {}),
  };
}

export async function angebotAblehnen(nr: string, grund: string, grundCode?: string): Promise<Angebot> {
  if (ATTRAPPE) {
    const a = angebote.find((x) => x.nr === nr);
    if (a?.bel) belegungen = belegungen.filter((b) => b.id !== a.bel);
    angebote = angebote.map((x) =>
      x.nr === nr ? { ...x, phase: "abgelehnt" as const, grund, tage: null, eilt: false } : x);
    const neu = angebote.find((x) => x.nr === nr);
    if (!neu) throw new Error(`Angebot ${nr} gibt es nicht.`);
    return gleich(neu, 220);
  }
  return alsAngebot(await sende<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/ablehnen`, {
    grund,
    ...(grundCode ? { grund_code: grundCode } : {}),
  }));
}

/**
 * Einen Entwurf verwerfen, der nie hinausging (Systemprüfung B-12). Die API
 * löscht ihn samt Positionen und Vormerkungen — und weist ab, was schon
 * einen Beleg drüben oder eine Ortsaufnahme trägt.
 */
export async function angebotVerwerfen(nr: string): Promise<void> {
  if (ATTRAPPE) {
    angebote = angebote.filter((x) => x.nr !== nr);
    AUF.begehungenDesEntwurfsVerwerfen(nr);
    return gleich(undefined, 220);
  }
  await loesche<Roh>(`/vorgaenge/${encodeURIComponent(nr)}`);
}

/* ══════════════ Ein Angebot bauen (15.09.2026) ══════════════

   Bis heute war „Angebot bauen" eine Anzeige: Die Maske hielt ihre
   Positionen im Speicher, der Knopf „Anlegen" stand still, und was
   jemand eintippte, war beim Verlassen der Seite weg. Das hier sind
   die Wege, die aus der Anzeige eine Bearbeitung machen — alle vier
   stehen seit je in 34-api.md, es rief sie nur niemand auf. */

/** Was ein Angebotsentwurf beim Anlegen braucht. Mehr nicht (Regel 11). */
export type AngebotEingabe = {
  /** Die Kundennummer aus der Liste — `10001`, nicht die UUID. */
  kunde: string;
  /** Der Einsatzort, so wie er beim Kunden heißt. Leer heißt: keiner. */
  ort?: string;
  betreff?: string;
  /** Die Bindefrist als `JJJJ-MM-TT`. Sie ist zugleich die Optionsfrist. */
  gueltig_bis?: string;
  /**
   * Vor- und Nachtext des Angebots, wenn sie beim Anlegen schon feststehen.
   *
   * Sie **ersetzen** dann den Standardbaustein (Migration 0094): Wer am
   * Telefon einen Satz diktiert bekommt, will ihn nicht hinterher über den
   * Standard schreiben müssen. Wer keinen mitschickt, bekommt den Standard
   * wie bisher.
   */
  vortext?: string;
  nachtext?: string;
  /** Die interne Notiz am Vorgang. Sie geht auf keinen Beleg. */
  notiz_intern?: string;
  /** Die Bestellnummer/Kommission des Kunden (0188). Gedruckt ab dem Angebot (Befund 35). */
  bestellnummer?: string;
  /** Der Hinweis für den Hof (0284) — geht auf kein Angebot, bleibt bei der Annahme stehen. */
  hofhinweis?: string;
  /** Ein Zahlungsziel, das vom Kunden abweicht (0190). Ohne: wie beim Kunden. */
  zahlungsziel_tage?: number;
};

/**
 * Legt den Entwurf an und gibt seine Nummer zurück.
 *
 * Der Entwurf entsteht **erst mit der ersten Position** — wer die Maske
 * öffnet und wieder verlässt, hinterlässt keinen leeren Vorgang in der
 * Liste. Angelegt wird hier nur; die Nummer vergibt die API.
 */
export async function angebotAnlegen(eingabe: AngebotEingabe): Promise<{ nr: string; id: string }> {
  if (ATTRAPPE) return gleich({ nr: "AG-2026-099", id: "attrappe" });

  const kunde = await hole<Roh>(`/kunden/${encodeURIComponent(eingabe.kunde)}`);
  const ortId = eingabe.ort
    ? txt(alsListe(kunde["orte"]).find((o) => txt(o["label"]) === eingabe.ort)?.["id"] ?? "")
    : "";

  const angelegt = await sende<Roh>("/vorgaenge", {
    kunde_id: txt(kunde["id"]),
    // Ein Angebot trägt seine Angebotsnummer, keine Vorgangsnummer (02.10.2026).
    art: "angebot",
    /* Vor- und Nachtext aus den Standardbausteinen (Migration 0094). Luca:
       *„ein Standard-Textbaustein für Angebote, der immer vorausgewählt ist."*
       Vorausgewählt, nicht festgeschrieben — das Feld bleibt offen. */
    standardtexte: true,
    ...(ortId ? { ort_id: ortId } : {}),
    ...(eingabe.betreff ? { betreff: eingabe.betreff } : {}),
    ...(eingabe.gueltig_bis ? { gueltig_bis: eingabe.gueltig_bis } : {}),
    /* Ein mitgeschickter Satz schlägt den Standard: `POST /vorgaenge` belegt
       nur vor, was **nicht** im Rumpf steht (`standardtexte`). */
    ...(eingabe.vortext ? { vortext: eingabe.vortext } : {}),
    ...(eingabe.nachtext ? { nachtext: eingabe.nachtext } : {}),
    ...(eingabe.notiz_intern ? { notiz_intern: eingabe.notiz_intern } : {}),
    ...(eingabe.bestellnummer ? { bestellnummer: eingabe.bestellnummer } : {}),
    ...(eingabe.hofhinweis ? { hofhinweis: eingabe.hofhinweis } : {}),
    ...(eingabe.zahlungsziel_tage !== undefined ? { zahlungsziel_tage: eingabe.zahlungsziel_tage } : {}),
  });
  return { nr: txt(angelegt["number"]), id: txt(angelegt["id"]) };
}

/** Kopfdaten eines bestehenden Entwurfs ändern — Betreff, Ort, Bindefrist. */
export async function angebotKopfAendern(
  nr: string,
  teil: { betreff?: string; ort_id?: string | null; gueltig_bis?: string | null },
): Promise<void> {
  if (ATTRAPPE) return gleich(undefined);
  await aendere<Roh>(`/vorgaenge/${encodeURIComponent(nr)}`, teil);
}

/**
 * Was beim Anlegen vorbelegt wird — heute genau die Bindefrist.
 *
 * `null` heißt: Der Betrieb hat nichts gepflegt. Die Maske sagt das dann
 * auch so; eine erfundene Frist sähe aus wie eine gepflegte.
 */
export async function angebotVorgaben(): Promise<Angebotsvorgaben> {
  if (ATTRAPPE) return gleich({ frist_tage: null, frist_bis: null });
  const roh = await hole<Roh>("/vorgaenge/vorgaben");
  return {
    frist_tage: zahl(roh["angebotsfrist_tage"]),
    frist_bis: txt(roh["angebotsfrist_bis"]) || null,
  };
}

/** Was eine neue Position mitbringt. Der Preis darf fehlen — dann rechnet ihn
 *  die API aus dem Tarif (Regel 4 gilt erst bei der Annahme). */
export type PositionEingabe = {
  art: "miete" | "leistung" | "verkauf" | "zuschlag" | "text";
  /** Aus dem Katalog darf sie fehlen — dann kommt sie aus dem Stammsatz. */
  bezeichnung?: string;
  maschinentyp_id?: string;
  /** Die Ausstattung des Typs — „mit Anschläger". Leer heißt Grundpreis (16.09.2026). */
  variante_id?: string;
  /** Die Leistungsart: Bezeichnung, Einheit, Satz und Verrechnungsart kommen von dort. */
  leistungsart_id?: string;
  /** Der Warenartikel: Bezeichnung, Einheit, Preis und Steuersatz kommen von dort. */
  ware_id?: string;
  /**
   * Der Code aus dem Zuschlagsstamm — nur an `art: "zuschlag"` (18.09.2026).
   *
   * Eine Zuschlagszeile von Hand schlägt drüben die Regel desselben Codes
   * („ein zugesagter Wert vor einem gerechneten", `packages/fach`). Bis
   * hierher kannte nur die Schnellerfassung diesen Weg; das Angebot am
   * Telefon übernimmt seit heute die Sammlung und braucht ihn damit auch.
   */
  zuschlag_code?: string;
  von?: string;
  bis?: string;
  /** `kw` = der Tag nennt seine Woche (Migration 0154). Vorgabe: `genau`. */
  zeitschaerfe?: "genau" | "kw";
  menge?: number;
  einheit?: string;
  einzelpreis?: number;
  /** Die Zubehörstücke, die an die Miete gebunden werden — Ressourcen-UUIDs.
   *  Je Stück entsteht eine eigene Belegung (Regel 8). */
  zubehoer?: string[];
  /**
   * Die Rolle im Angebot, **schon beim Anlegen**.
   *
   * `POST …/positionen` nimmt beides seit jeher an und prüft es durch dieselbe
   * Regel wie das Ändern (`rolleSetzen` in routen/vorgaenge.ts). Nur diese
   * Funktion reichte es bis zum 16.09.2026 nicht durch — sie zählt die Felder
   * einzeln auf, und diese beiden fehlten in der Liste. Die Maske schickte
   * „optional", und was ankam, war eine gewöhnliche Position.
   */
  optional?: boolean;
  alternative_zu?: string;
  /** Wie viele Stück — nur an der Miete eines gezählten Bestands über 1 (0194). */
  stueck?: number;
};

export async function positionAnlegen(vorgangNr: string, eingabe: PositionEingabe): Promise<Position> {
  if (ATTRAPPE) return gleich({ nr: 1, was: eingabe.bezeichnung ?? "", bei: "", artikel: eingabe.art, menge: "1", einheit: "" });
  const roh = await sende<Roh>(`/vorgaenge/${encodeURIComponent(vorgangNr)}/positionen`, {
    art: eingabe.art,
    ...(eingabe.bezeichnung ? { bezeichnung: eingabe.bezeichnung } : {}),
    ...(eingabe.maschinentyp_id ? { maschinentyp_id: eingabe.maschinentyp_id } : {}),
    ...(eingabe.variante_id ? { variante_id: eingabe.variante_id } : {}),
    ...(eingabe.leistungsart_id ? { leistungsart_id: eingabe.leistungsart_id } : {}),
    ...(eingabe.ware_id ? { ware_id: eingabe.ware_id } : {}),
    ...(eingabe.zuschlag_code ? { zuschlag_code: eingabe.zuschlag_code } : {}),
    ...(eingabe.von ? { von: eingabe.von } : {}),
    ...(eingabe.bis ? { bis: eingabe.bis } : {}),
    ...(eingabe.zeitschaerfe ? { zeitschaerfe: eingabe.zeitschaerfe } : {}),
    ...(eingabe.menge !== undefined ? { menge: eingabe.menge } : {}),
    ...(eingabe.einheit ? { einheit: eingabe.einheit } : {}),
    ...(eingabe.einzelpreis !== undefined ? { einzelpreis: eingabe.einzelpreis } : {}),
    ...(eingabe.zubehoer?.length ? { zubehoer: eingabe.zubehoer.map((ressource_id) => ({ ressource_id })) } : {}),
    ...(eingabe.optional ? { optional: true } : {}),
    ...(eingabe.alternative_zu ? { alternative_zu: eingabe.alternative_zu } : {}),
    ...(eingabe.stueck && eingabe.stueck > 1 ? { stueck: eingabe.stueck } : {}),
  });
  return alsPosition(roh);
}

/**
 * Eine Position ändern.
 *
 * `reihenfolge` ist mit dabei, weil Umsortieren nichts anderes ist: Zwei
 * Zeilen tauschen ihre `sort_order`. Eine eigene Route dafür wäre eine
 * zweite Wahrheit über dieselbe Spalte.
 */
export async function positionAendern(
  vorgangNr: string,
  posId: string,
  teil: {
    bezeichnung?: string; menge?: number; einzelpreis?: number | null;
    von?: string | null; bis?: string | null; einheit?: string; reihenfolge?: number;
    /**
     * Wie genau der Zeitraum gemeint ist (Migration 0154). `kw` heißt: Der
     * genannte Tag nennt seine **Woche**; Montag und Sonntag rechnet die API
     * daraus. `genau` verengt eine grob geplante Zeile wieder auf den Tag —
     * das tut die Ortsaufnahme, sobald ein Termin feststeht.
     */
    zeitschaerfe?: "genau" | "kw";
    /** Der Rabatt — Prozent oder Betrag (Migration 0106). Wer die eine Art setzt, nimmt die andere zurück. */
    rabatt_prozent?: number; rabatt_betrag?: number;
    /** Nur im Entwurf: die Rolle im Angebot. */
    optional?: boolean; alternative_zu?: string | null;
    /** Mit Bediener verkauft — nur an einer Miete (Migration 0096). */
    bedient?: boolean;
    /** Wer fährt — `null` löst ihn. Gebucht an derselben Position (dienste/bediener.ts). */
    bediener_id?: string | null;
    /**
     * Das Zubehör dieser Miete — **die Liste ist der Stand** (18.09.2026).
     * Was fehlt, wird gelöst; was dazukommt, gebucht. Ohne das Feld bleibt es,
     * wie es ist (dienste/belegung.ts, `zubehoerSetzen`).
     */
    zubehoer?: { ressource_id: string }[];
    /**
     * Welches Stück die Belegung bekommt (18.09.2026, Durchgang B5). Die
     * Position hängt weiter am Typ (Regel 2) — das hier ist die Disposition.
     */
    maschine_id?: string;
    /** Ein reserviertes Stück trotzdem nehmen — bestätigt (siehe `belegung.reserviert`). */
    reservierung_uebergehen?: boolean;
    /** Den Bediener trotz abgelaufener Qualifikation einplanen -- bestaetigt (0230, Regel "warnen"). */
    qualifikation_bestaetigt?: boolean;
    /** Wie viele Stück — nur am gezählten Bestand (0194). Die Belegung zieht mit. */
    stueck?: number;
    /**
     * Die neue Dauer an einer angenommenen Miete bestätigen (P4-01): Der Satz
     * bleibt eingefroren, Miete und Zuschläge folgen den neuen Miettagen.
     */
    dauer_uebernehmen?: true;
    /**
     * „Übernehmen" nimmt die Zubehörzeilen mit eigenem Preis mit (05.10.2026,
     * F3-Linie) — je Zeile, was ihr Vorschlag sagt.
     */
    anbauteile?: { id: string; menge: number; einzelpreis?: number; dauer_uebernehmen?: true }[];
  },
): Promise<Position & { vorschlag: Zeitraumvorschlag | null }> {
  if (ATTRAPPE) {
    return { ...await gleich({ nr: 1, was: teil.bezeichnung ?? "", bei: "", artikel: "miete", menge: "1", einheit: "" }), vorschlag: null };
  }
  const roh = await aendere<Roh>(
    `/vorgaenge/${encodeURIComponent(vorgangNr)}/positionen/${encodeURIComponent(posId)}`,
    teil,
  );
  /* Ein neuer Zeitraum bringt einen Betragsvorschlag mit (F3) — die Maske fragt. */
  return { ...alsPosition(roh), vorschlag: vorschlagAus(roh) };
}

export async function positionLoeschen(vorgangNr: string, posId: string): Promise<void> {
  if (ATTRAPPE) return gleich(undefined);
  await loesche<void>(
    `/vorgaenge/${encodeURIComponent(vorgangNr)}/positionen/${encodeURIComponent(posId)}`,
  );
}

/**
 * Ein versendetes Angebot wieder aufmachen — die **neue Fassung**.
 *
 * Nicht „bearbeiten": Das versendete Angebot liegt beim Kunden und hat
 * drüben eine Nummer. Es still zu ändern erzeugt zwei Wahrheiten. Der
 * Vorgang geht darum zurück in den Entwurf, und was danach hinausgeht,
 * ist eine neue Fassung (23-angebotsprozess.md).
 */
export async function angebotNeueFassung(nr: string): Promise<Angebot> {
  if (ATTRAPPE) {
    angebote = angebote.map((x) => (x.nr === nr ? { ...x, phase: "entwurf" as const } : x));
    const neu = angebote.find((x) => x.nr === nr);
    if (!neu) throw new Error(`Angebot ${nr} gibt es nicht.`);
    return gleich(neu, 200);
  }
  return alsAngebot(await sende<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/neue-fassung`));
}

/**
 * Eine begonnene Fassung verwerfen — der Vorgang geht zurück auf den Stand
 * des letzten versendeten Angebots (Befund 26, Migration 0182). `ohneGeraet`
 * zählt die Vormerkungen, deren alter Platz im Brett nicht mehr frei war.
 */
export async function fassungVerwerfen(nr: string): Promise<{ angebot: Angebot; ohneGeraet: number }> {
  if (ATTRAPPE) {
    angebote = angebote.map((x) => (x.nr === nr ? { ...x, phase: "versendet" as const } : x));
    const zurueck = angebote.find((x) => x.nr === nr);
    if (!zurueck) throw new Error(`Angebot ${nr} gibt es nicht.`);
    return gleich({ angebot: zurueck, ohneGeraet: 0 }, 200);
  }
  const roh = await sende<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/fassung-verwerfen`);
  return { angebot: alsAngebot(roh), ohneGeraet: zahl(roh["ohne_geraet"]) ?? 0 };
}

/**
 * Das Angebots-PDF — **es kommt von drüben** (Regel 1).
 *
 * Lexware erzeugt es beim Finalisieren aus der Druckvorlage des Kontos.
 * Ein eigenes Angebotspapier gibt es nicht: Es hinge an Firmenstammdaten,
 * Mietbedingungen und dem Vektorlogo, und die fehlen (32-was-noch-fehlt.md,
 * A2).
 *
 * Der Browser bekommt keinen Link, sondern einen Blob: Das Kontext-Token
 * liegt im Speicher und geht nur als Kopf mit.
 */
/** Die Papiere, die es zu einem Vorgang gibt — Angebot und AB (0268). Die
 *  Rechnung nicht — die liegt in der Ablage und geht über die Abrechnung. Den
 *  Lieferschein trägt seit Phase 4 das Übergabeprotokoll. */
export type Papierart = "angebot" | "auftragsbestaetigung";

export async function belegPdf(
  nr: string, art: Papierart,
  /** Eine bestimmte Fassung. Ohne Angabe die jüngste — der Regelfall. */
  belegId?: string,
): Promise<{ blob: Blob; name: string }> {
  if (ATTRAPPE) throw new Error("Im Attrappenbetrieb gibt es kein PDF.");
  const anhang = belegId ? `?beleg=${encodeURIComponent(belegId)}` : "";
  const datei = await holeDatei(`/vorgaenge/${encodeURIComponent(nr)}/${art}/pdf${anhang}`);
  return { blob: datei.blob, name: datei.name || `${nr}.pdf` };
}

/**
 * **Das Protokoll als PDF auf dem Briefpapier** (0198, docs/55 E3) — vom
 * Server gesetzt, mit Nummer, Zählerstand, Fotos und Unterschrift.
 */
export async function protokollPdf(id: string): Promise<{ blob: Blob; name: string }> {
  if (ATTRAPPE) throw new Error("Im Attrappenbetrieb gibt es kein PDF.");
  const datei = await holeDatei(`/protokolle/${encodeURIComponent(id)}/pdf`);
  return { blob: datei.blob, name: datei.name || "Protokoll.pdf" };
}

/**
 * **Die Vorschau des Angebots** (0200) — live gesetzt, nichts angelegt. Vor dem
 * ersten Anlegen steht AG-999999999 darauf, danach die Nummer dieser Fassung.
 */
export async function angebotVorschau(nr: string): Promise<{ blob: Blob; name: string }> {
  if (ATTRAPPE) throw new Error("Im Attrappenbetrieb gibt es kein PDF.");
  const datei = await holeDatei(`/vorgaenge/${encodeURIComponent(nr)}/angebot/vorschau`);
  return { blob: datei.blob, name: datei.name || `Angebot_Vorschau_${nr}.pdf` };
}

/** Was sich zwischen zwei Angebotsfassungen geändert hat (0202) — die Worte setzt die Oberfläche. */
export type Fassungsaenderung = {
  art: string;
  was: string;
  vorher: string | null;
  nachher: string | null;
};

export type Fassungseintrag = {
  id: string;
  nummer: string | null;
  revision: number | null;
  am: string;
  von: string | null;
  netto: number | null;
  aenderungen: Fassungsaenderung[];
};

/** **Wer hat was überarbeitet** — je Fassung Person, Zeit und Änderungen zur vorigen. */
export async function angebotsfassungenLesen(nr: string): Promise<Fassungseintrag[]> {
  if (ATTRAPPE) return [];
  const roh = await hole<{ fassungen: Fassungseintrag[] }>(`/vorgaenge/${encodeURIComponent(nr)}/angebot/fassungen`);
  return roh.fassungen;
}

/** Der alte Name, damit das Angebot nicht an zwei Stellen umgebaut werden muss. */
export function angebotPdf(nr: string): Promise<{ blob: Blob; name: string }> {
  return belegPdf(nr, "angebot");
}

/* ══════════════ Kunden ══════════════ */

function anschriftZeile(wert: unknown): string {
  const a = alsObjekt(wert);
  return [txt(a["plz"]), txt(a["ort"])].filter(Boolean).join(" ");
}

function alsOrt(o: Roh): Einsatzort {
  const a = alsObjekt(o["address"]);
  const km = zahl(o["distance_km"]);
  return {
    // Die Nummer, nicht die UUID: `O-0001` steht auf dem Lieferschein und
    // ist das, was jemand vorliest. Eine UUID in der Maske ist ein
    // durchgereichter Datenbankschlüssel und sonst nichts — sie fährt als
    // `uuid` mit, weil Ändern und Löschen sie brauchen, und wird nie gezeigt.
    id: txt(o["number"]),
    uuid: txt(o["id"]),
    name: txt(o["label"]),
    wo: anschriftZeile(o["address"]),
    km: km === null ? "" : String(km),
    strasse: txt(a["strasse"]),
    plz: txt(a["plz"]),
    ort: txt(a["ort"]),
    ...(txt(o["hint"]) ? { notiz: txt(o["hint"]) } : {}),
    ...(txt(o["kind"]) ? { art: txt(o["kind"]) as Ortsart } : {}),
    ...(txt(o["access"]) ? { zufahrt: txt(o["access"]) } : {}),
    standard: o["is_default"] === true,
    aktiv: o["active"] !== false,
    ...(zahl(o["vorgaenge"]) !== null ? { vorgaenge: zahl(o["vorgaenge"]) as number } : {}),
  };
}

/** `2025-03-14T…` → `03/2025`. Für „Kunde seit" zählt der Monat, nicht der Tag. */
function monatJahr(wert: unknown): string {
  const treffer = /^(\d{4})-(\d{2})/.exec(txt(wert));
  return treffer ? `${treffer[2]}/${treffer[1]}` : "";
}

function alsKunde(z: Roh): Kunde {
  const anschrift = alsObjekt(z["address"]);
  const zahlungsziel = zahl(z["payment_terms_days"]);
  const orte = alsListe(z["orte"]);
  const leute = alsListe(z["leute"]);
  const erster = leute.find((l) => l["is_primary"]) ?? leute[0];
  const rabatt = zahl(z["discount_percent"]);

  return {
    nr: txt(z["number"]),
    name: txt(z["company"]),
    ort: txt(anschrift["ort"]),
    /* **Vermieter schlägt Interessent.** Wer uns etwas vermietet, ist in der
       Liste ein Vermieter — auch wenn er (noch) nichts gemietet hat und damit
       technisch ein Interessent ist. „Interessent" hiesse dort: Wir hoffen auf
       ein Geschaeft. Bei ihm ist es umgekehrt. */
    art: z["is_vendor"] ? "vermieter" : z["is_prospect"] ? "interessent" : "kunde",
    lieferantennummer: txt(z["vendor_number"]) || null,
    // Privat oder gewerblich (0187). Leer = nicht gewählt, dann gilt der Name.
    typ: istKundentyp(z["customer_kind"]) ? z["customer_kind"] : null,
    ...(erster ? { ansprechpartner: txt(erster["name"]) } : {}),
    ...(zahlungsziel !== null ? { zahlungsziel: String(zahlungsziel) } : {}),
    // Die Sperre — nicht offene Posten (K-31). Ein Forderungsstand kommt aus
    // Lexware und wird hier nicht gelesen (Regel 1); die Spalte heißt darum
    // „Sperre" und nicht „Offen".
    offen: z["blocked"] ? "stopp" : "gut",
    orte: orte.map(alsOrt),

    /* Ab hier, was nur der Einzelsatz trägt. Die Liste liefert es nicht, und
       ein leeres Feld ist dort kein Fehler, sondern die Antwort auf eine
       Frage, die niemand gestellt hat. */
    anschrift: {
      ...(txt(anschrift["zusatz"]) ? { zusatz: txt(anschrift["zusatz"]) } : {}),
      ...(txt(anschrift["strasse"]) ? { strasse: txt(anschrift["strasse"]) } : {}),
      ...(txt(anschrift["plz"]) ? { plz: txt(anschrift["plz"]) } : {}),
      ...(txt(anschrift["ort"]) ? { ort: txt(anschrift["ort"]) } : {}),
      ...(txt(anschrift["land"]) ? { land: txt(anschrift["land"]) } : {}),
    },
    ...(txt(z["vat_id"]) ? { ustId: txt(z["vat_id"]) } : {}),
    ...(txt(z["tax_number"]) ? { steuernummer: txt(z["tax_number"]) } : {}),
    ...(txt(z["phone"]) ? { telefon: txt(z["phone"]) } : {}),
    ...(txt(z["invoice_email"]) ? { rechnungEmail: txt(z["invoice_email"]) } : {}),
    ...(txt(z["preisliste"]) ? { preisliste: txt(z["preisliste"]) } : {}),
    ...(txt(z["price_list_id"]) ? { preislisteId: txt(z["price_list_id"]) } : {}),
    ...(rabatt !== null ? { rabatt } : {}),
    kreditlimit: zahl(z["credit_limit"]),
    gesperrt: z["blocked"] === true,
    ...(txt(z["block_reason"]) ? { sperrgrund: txt(z["block_reason"]) } : {}),
    deaktiviert: z["active"] === false,
    // Der Tag bleibt ISO: Er wird verglichen und sortiert, nicht gelesen
    // (die Akte fürs Auto stellt die letzten Kunden nach oben).
    ...(txt(z["zuletzt"]) ? { zuletztIso: tagAusZeitpunkt(z["zuletzt"]) } : {}),
    drueben: Boolean(txt(z["lex_contact_id"])),
    ...(txt(z["lex_synced_at"]) ? { abgeglichen: tagUndZeit(z["lex_synced_at"]) } : {}),
    ...(txt(z["created_at"]) ? { seit: monatJahr(z["created_at"]) } : {}),
    leute: leute.map(alsPerson),
    vorgaenge: alsListe(z["vorgaenge"]).map((v): Kundenvorgang => {
      const zustand = ZUSTAND[txt(v["status"])] ?? "entwurf";
      return {
        nr: txt(v["number"]),
        zustand,
        art: TOENUNG[zustand] ?? "",
        was: txt(v["subject"]),
        wann: tagKurz(v["datum"]),
      };
    }),
  };
}

/**
 * Die Kundenliste. **Ohne `nur` stehen nur aktive darin** (Migration 0100) —
 * sie ist die Quelle jeder Kundenwahl. `nur: "deaktiviert"` zeigt die anderen;
 * `alle` beide, für den Dublettenabgleich beim Anlegen.
 */
export async function kunden(
  suche?: string, nur?: "interessent" | "kunde" | "vermieter" | "deaktiviert", alle?: boolean,
): Promise<Liste<Kunde>> {
  if (ATTRAPPE) {
    const wort = (suche ?? "").trim().toLowerCase();
    const zeilen = A.KUNDENSTAMM
      .filter((k) => !nur || nur === "deaktiviert" || k.art === nur)
      .filter((k) => !wort || (k.name + " " + k.ort + " " + k.nr).toLowerCase().includes(wort));
    return gleich({ zeilen, gesamt: zeilen.length });
  }
  const frage = new URLSearchParams();
  if (suche) frage.set("suche", suche);
  if (nur) frage.set("nur", nur);
  if (alle) frage.set("alle", "1");
  const s = frage.toString();
  const roh = await hole<Roh>(`/kunden${s ? `?${s}` : ""}`);
  const zeilen = alsListe(roh["zeilen"]).map(alsKunde);
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length };
}

export async function kunde(nr: string): Promise<Kunde> {
  if (ATTRAPPE) {
    const k = A.KUNDENSTAMM.find((x) => x.nr === nr) ?? A.KUNDENSTAMM[5];
    if (!k) throw new Error("Kein Kunde vorhanden.");
    return gleich(k);
  }
  return alsKunde(await hole<Roh>(`/kunden/${encodeURIComponent(nr)}`));
}

/**
 * Was beim Anlegen mitgeht.
 *
 * Bis zum 16.09.2026 gingen drei Felder hinüber — Firma, Ort, Art. Das
 * Formular fragte zwölf. USt-IdNr., Straße, PLZ, Rechnungs-E-Mail,
 * Zahlungsziel, Telefon und Ansprechpartner wurden getippt und verworfen, und
 * in der Akte stand danach überall ein Strich. Wer ein Feld fragt, speichert
 * es auch — sonst gehört es nicht ins Formular.
 */
export type KundeEingabe = {
  firma: string;
  interessent: boolean;
  /** Die Rolle Vermieter/Lieferant (Migration 0079) — sie steht NEBEN der
   *  Kundenrolle, nicht statt ihr, und zieht eine eigene Nummer (0087). */
  vermieter?: boolean;
  /** Privat oder gewerblich (0187). Das Formular verlangt die Wahl beim Kunden und Interessenten. */
  kundentyp?: Kundentyp;
  anschrift: { zusatz?: string; strasse?: string; plz?: string; ort?: string; land?: string };
  ust_id?: string;
  steuernummer?: string;
  telefon?: string;
  rechnung_email?: string;
  zahlungsziel_tage?: number;
  ansprechpartner?: string;
  ansprechpartner_telefon?: string;
  ansprechpartner_email?: string;
  /** Der Schlüssel dieses Formulars (0218, K-01): zweimal geschickt, einmal angelegt. */
  erfassung_id?: string;
};

export async function kundeAnlegen(neu: KundeEingabe): Promise<Kunde> {
  if (ATTRAPPE) {
    return gleich<Kunde>({
      nr: "10056",
      name: neu.firma,
      ort: neu.anschrift.ort ?? "",
      art: neu.interessent ? "interessent" : "kunde",
      orte: [],
    }, 320);
  }
  return alsKunde(await sende<Roh>("/kunden", neu));
}

/**
 * Was sich an einem Kunden ändern lässt — und was dabei ins Protokoll geht.
 *
 * Die Feldnamen sind die der API, nicht die der Maske: Ein `PATCH` ist keine
 * Ersetzung, es geht nur mit, was dasteht. Jedes gesetzte Feld schreibt eine
 * Zeile in den Verlauf (Migration 0065) — einmal je Aufruf, nicht je Feld.
 */
export type KundenFelder = {
  firma?: string;
  /** Privat oder gewerblich; `null` = nicht gewählt, dann gilt der Name (0187). */
  kundentyp?: Kundentyp | null;
  anschrift?: { zusatz?: string; strasse?: string; plz?: string; ort?: string; land?: string };
  ust_id?: string | null;
  steuernummer?: string | null;
  telefon?: string | null;
  rechnung_email?: string | null;
  zahlungsziel_tage?: number;
  preisliste_id?: string | null;
  rabatt_prozent?: number;
  kreditlimit?: number | null;
  gesperrt?: boolean;
  sperrgrund?: string | null;
  /** Aus einem Interessenten einen Kunden machen: `false` setzen. */
  interessent?: boolean;
  /** Deaktivieren (`false`) oder wieder aktivieren (`true`) — Migration 0100. */
  aktiv?: boolean;
};

/**
 * Gibt die Nummer **danach** zurück: Wird aus dem Interessenten ein Kunde,
 * wechselt sie (100001 → 10020), und die Akte muss ihr folgen — unter der
 * alten Nummer gibt es ihn nicht mehr (Playbook K-14).
 */
export async function kundeAendern(nr: string, felder: KundenFelder): Promise<{ nr: string }> {
  if (ATTRAPPE) return gleich({ nr }, 140);
  const roh = await aendere<Roh>(`/kunden/${encodeURIComponent(nr)}`, felder);
  return { nr: txt(roh?.["number"]) || nr };
}

/* ── Einsatzorte ──
   Anlegen, ändern, stilllegen, Standard setzen. Gelöscht wird nur, was
   niemand benutzt — der Riegel steht in der Datenbank, und die API macht
   daraus `einsatzort.benutzt`. */

export type OrtEingabe = {
  bezeichnung: string;
  anschrift: { strasse?: string; plz?: string; ort?: string };
  art?: string;
  zufahrt?: string | null;
  hinweis?: string | null;
  entfernung_km?: number | null;
  standard?: boolean;
};

export async function ortAnlegen(kunde: string, neu: OrtEingabe): Promise<Einsatzort> {
  if (ATTRAPPE) return gleich<Einsatzort>({ id: "O-9999", name: neu.bezeichnung, wo: "", km: "" }, 200);
  return alsOrt(await sende<Roh>(`/kunden/${encodeURIComponent(kunde)}/orte`, neu));
}

export async function ortAendern(
  kunde: string, uuid: string, felder: Partial<OrtEingabe> & { aktiv?: boolean },
): Promise<Einsatzort> {
  if (ATTRAPPE) return gleich<Einsatzort>({ id: "O-9999", name: "", wo: "", km: "" }, 160);
  return alsOrt(await aendere<Roh>(
    `/kunden/${encodeURIComponent(kunde)}/orte/${encodeURIComponent(uuid)}`, felder,
  ));
}

export async function ortLoeschen(kunde: string, uuid: string): Promise<void> {
  if (ATTRAPPE) return gleich(undefined, 160);
  await loesche<void>(`/kunden/${encodeURIComponent(kunde)}/orte/${encodeURIComponent(uuid)}`);
}

/**
 * Diesen einen Kontakt in Lexware Office anlegen.
 *
 * **Nicht der Sammellauf.** Der steht unter Einstellungen · Läufe und schiebt
 * jeden Kunden, der drüben fehlt; hier geht es um den, vor dem man gerade
 * steht. Ein Knopf, der mehr tut als sein Text, ist in diesem Projekt schon
 * dreimal als Fehler zurückgekommen.
 *
 * **Die Nummer kann sich dabei ändern.** Drüben wird beim Anlegen eine
 * vergeben, und seit Migration 0064 ist das die Nummer — sie steht im Weg
 * (`/kunden/10006`). Darum gibt die Antwort sie zurück, und die Maske geht
 * hin, wenn sie eine andere ist.
 */
export async function kundeNachLexware(nr: string): Promise<{ nr: string; drueben: boolean }> {
  if (ATTRAPPE) return gleich({ nr, drueben: true }, 400);
  const antwort = await sende<Roh>(`/kunden/${encodeURIComponent(nr)}/lexware`, {});
  return { nr: txt(antwort["nr"]) || nr, drueben: antwort["drueben"] === true };
}

/* ── Die Ansprechpartner am Kunden ──
   Angelegt werden konnten sie seit dem ersten Tag, geändert nie — wer eine
   Durchwahl falsch aufgenommen hatte, legte die Person ein zweites Mal an.
   Seit dem 16.09.2026 gibt es beides. */

export type PersonAmKunden = {
  name: string;
  funktion?: string | null;
  telefon?: string | null;
  mobil?: string | null;
  email?: string | null;
  haupt?: boolean;
};

/** Die Serversicht auf einen Ansprechpartner in die der Oberfläche. */
function alsPerson(l: Roh): Ansprechpartner {
  return {
    id: txt(l["id"]),
    name: txt(l["name"]),
    ...(txt(l["position"]) ? { funktion: txt(l["position"]) } : {}),
    ...(txt(l["phone"]) ? { telefon: txt(l["phone"]) } : {}),
    ...(txt(l["mobile"]) ? { mobil: txt(l["mobile"]) } : {}),
    ...(txt(l["email"]) ? { email: txt(l["email"]) } : {}),
    haupt: l["is_primary"] === true,
  };
}

export async function kundenpersonAnlegen(kunde: string, neu: PersonAmKunden): Promise<Ansprechpartner> {
  if (ATTRAPPE) return gleich<Ansprechpartner>({ id: "P-9999", name: neu.name, haupt: false }, 200);
  return alsPerson(await sende<Roh>(`/kunden/${encodeURIComponent(kunde)}/leute`, neu));
}

export async function kundenpersonAendern(
  kunde: string, id: string, felder: Partial<PersonAmKunden>,
): Promise<Ansprechpartner> {
  if (ATTRAPPE) return gleich<Ansprechpartner>({ id, name: felder.name ?? "", haupt: false }, 160);
  return alsPerson(await aendere<Roh>(
    `/kunden/${encodeURIComponent(kunde)}/leute/${encodeURIComponent(id)}`, felder,
  ));
}

/* ── Der Verlauf am Kunden ──
   Ein Strang, zwei Herkünfte: Notizen von Menschen und Änderungen der
   Anwendung. Geändert und gelöscht wird nichts. */

export async function kundennotizen(nr: string): Promise<Kundennotiz[]> {
  /* **Seit dem 18.09.2026 mit Zeilen** (`attrappe/daten.ts`,
     `KUNDENNOTIZEN`). Ohne sie stand in der Akte des Prototyps weder
     „Letzter Kontakt heute" noch der Abschnitt „Zuletzt besprochen" — also
     genau das, was an diesem Abend dazugekommen ist. Wer keinen Verlauf hat,
     bekommt weiter eine leere Liste: Auch der Fall gehört gesehen. */
  if (ATTRAPPE) return gleich(A.KUNDENNOTIZEN[nr] ?? []);
  const roh = await hole<Roh>(`/kunden/${encodeURIComponent(nr)}/notizen`);
  return alsListe(roh["zeilen"]).map((z): Kundennotiz => {
    const spur = alsListe(z["changes"]).map((s) => ({
      feld: txt(s["feld"]),
      vorher: txt(s["vorher"]) || null,
      nachher: txt(s["nachher"]) || null,
    }));
    return {
      id: txt(z["id"]),
      wann: tagUndZeit(z["written_at"]),
      art: txt(z["kind"]) || "vermerk",
      /* Das Wort zur Art: Schlüssel, wenn die Art mitgeliefert ist — sonst das
         Wort, das die gepflegte Zeile selbst trägt (Migration 0146). */
      ...(txt(z["kind_key"]) ? { art_schluessel: txt(z["kind_key"]) } : {}),
      ...(txt(z["kind_label"]) ? { art_wort: txt(z["kind_label"]) } : {}),
      ...(txt(z["body"]) ? { text: txt(z["body"]) } : {}),
      ...(txt(z["verfasser"]) ? { verfasser: txt(z["verfasser"]) } : {}),
      ...(txt(z["kuerzel"]) ? { kuerzel: txt(z["kuerzel"]) } : {}),
      ...(spur.length ? { spur } : {}),
    };
  });
}

/**
 * Eine Notiz schreiben — und im selben Griff die Wiedervorlage setzen.
 *
 * `wiedervorlageTage` ist der ganze Unterschied zwischen einer Notiz und
 * einer Absprache, an die sich jemand erinnert. Zwei getrennte Knöpfe hießen
 * im Auto: Einer davon wird nicht gedrückt (06-crm.md).
 */
export async function kundennotizAnlegen(
  nr: string, text_: string, art?: string, wiedervorlageTage?: number,
): Promise<void> {
  if (ATTRAPPE) return gleich(undefined, 120);
  await sende<Roh>(`/kunden/${encodeURIComponent(nr)}/notizen`, {
    text: text_,
    ...(art ? { art } : {}),
    ...(wiedervorlageTage === undefined ? {} : { wiedervorlage_tage: wiedervorlageTage }),
  });
}

/* ── Die Wiedervorlage (Migration 0111) ──
   Ein Datum am Kunden, das abgehakt wird. Sie gehört einem: Die Liste zeigt,
   was dem Angemeldeten gehört — dasselbe Prinzip wie die Tagesliste am
   Telefon (09-mobile.md). */

/** `2026-09-19` → `19.09.2026`. Hier steht das Jahr mit: Eine Wiedervorlage
 *  kann im Januar liegen, und „19.09." wäre dann eine halbe Auskunft. */
function tagLang(wert: unknown): string {
  return tagLangImBetrieb(txt(wert));
}

export async function wiedervorlagen(vorlaufTage = 14): Promise<Wiedervorlage[]> {
  if (ATTRAPPE) return gleich([]);
  const roh = await hole<Roh>(`/wiedervorlagen?tage=${vorlaufTage}`);
  return alsListe(roh["zeilen"]).map((z): Wiedervorlage => ({
    id: txt(z["id"]),
    wann: tagLang(z["due_on"]),
    wannIso: tagAusZeitpunkt(z["due_on"]),
    anlass: txt(z["kind"]) === "nachfassen" ? "nachfassen" : "notiz",
    faellig: z["faellig"] === true,
    inTagen: zahl(z["in_tagen"]) ?? 0,
    kundeNr: txt(z["kundennummer"]),
    kunde: txt(z["kunde"]),
    ortschaft: txt(z["ortschaft"]),
    telefon: txt(z["telefon"]),
    vorgang: txt(z["vorgang"]),
    notiz: txt(z["notiz"]),
  }));
}

/**
 * Eine Wiedervorlage setzen — `tage` ab heute.
 *
 * Gibt es zu Kunde und Anlass schon eine offene, wandert **sie** auf den
 * neuen Tag (Migration 0111). Ein zweiter Druck auf denselben Knopf ist kein
 * zweiter Vorsatz.
 */
export async function wiedervorlageSetzen(eingabe: {
  kunde: string;
  tage: number;
  anlass?: "notiz" | "nachfassen";
  /** Die Vorgangsnummer, wenn ein Angebot nachgefasst wird. */
  vorgang?: string;
}): Promise<void> {
  if (ATTRAPPE) return gleich(undefined, 120);
  await sende<Roh>("/wiedervorlagen", {
    kunde: eingabe.kunde,
    tage: eingabe.tage,
    ...(eingabe.anlass ? { anlass: eingabe.anlass } : {}),
    ...(eingabe.vorgang ? { vorgang: eingabe.vorgang } : {}),
  });
}

export async function wiedervorlageErledigt(id: string): Promise<void> {
  if (ATTRAPPE) return gleich(undefined, 120);
  await sende<Roh>(`/wiedervorlagen/${encodeURIComponent(id)}/erledigt`);
}

/* ══════════════ Stammdaten ══════════════ */

/**
 * Maschinen als Ressourcen.
 *
 * Drei Spalten der Maske hingen bisher an nichts: Standort, Status und
 * Prüfung. Alle drei liefert `GET /stamm/maschinen` — `ort`, `status` und
 * `next_safety_check`. Ein Strich, wo ein Wert vorliegt, ist keine
 * Zurückhaltung, sondern eine falsche Auskunft.
 */
export async function stammMaschinen(): Promise<Liste<Ressource>> {
  if (ATTRAPPE) {
    const zeilen = A.RESSOURCEN.filter((r) => r.gruppe === "maschine");
    return gleich({ zeilen, gesamt: zeilen.length });
  }
  const roh = await hole<Roh>("/stamm/maschinen");
  const zeilen = alsListe(roh["zeilen"]).map((m): Ressource => {
    const kennung = txt(m["asset_number"]);
    // Die frühere der beiden Fristen zählt: Wer die spätere anzeigt, meldet
    // eine Maschine als in Ordnung, die es seit Wochen nicht mehr ist.
    const fristen = [txt(m["next_safety_check"]), txt(m["next_inspection"])].filter(Boolean).sort();
    return {
      id: kennung,
      res_id: txt(m["id"]),
      gruppe: "maschine",
      typ: txt(m["typ"]),
      // Der Typ als Schlüssel: Daran hängt, ob die Maschine Zubehör ist.
      ...(txt(m["machine_type_id"]) ? { typ_id: txt(m["machine_type_id"]) } : {}),
      kuerzel: kuerzelVon(kennung),
      kat: txt(m["gruppe"]),
      ort: txt(m["ort"]),
      status: txt(m["status"]),
      pruefung: fristen[0] ?? null,
      // Das Fremdgerät steht in derselben Liste — es ist ein Gerät, kein
      // Sonderfall. Es trägt nur eine Marke und sein Fenster (Migration 0080).
      fremd: m["is_external"] === true,
      fensterAb: txt(m["available_from"]) || null,
      fensterBis: txt(m["available_until"]) || null,
    };
  });
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length };
}

/**
 * Die Vermieter — dieselben Geschäftspartner, andere Rolle.
 *
 * `GET /stamm/vermieter` liest `customer` mit `is_vendor` (Migration 0079).
 * Es gibt keine Vermietertabelle und keine Vermieterakte: Wer mehr wissen
 * will, öffnet die Kundenakte, weil es derselbe Stammsatz ist.
 */
export async function stammVermieter(): Promise<Liste<Vermieter>> {
  if (ATTRAPPE) return gleich({ zeilen: [], gesamt: 0 });
  const roh = await hole<Roh>("/stamm/vermieter");
  const zeilen = alsListe(roh["zeilen"]).map((v): Vermieter => ({
    id: txt(v["id"]),
    number: txt(v["number"]),
    company: txt(v["company"]),
    ort: txt(v["ort"]) || null,
    ist_kunde: v["ist_kunde"] === true,
    geraete: zahl(v["geraete"]) ?? 0,
  }));
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length };
}

/**
 * Einen Vermieter anlegen, ohne die Geräteakte zu verlassen.
 *
 * Gefragt wird nach der Firma und höchstens dem Ort — alles andere gehört in
 * die Kundenakte, wenn es gebraucht wird. Es entsteht ein gewöhnlicher
 * Geschäftspartner mit der Rolle Vermieter, kein zweiter Stammsatz.
 */
export async function vermieterAnlegen(
  felder: { firma: string; ort?: string | null },
): Promise<Vermieter> {
  const v = await sende<Roh>("/stamm/vermieter", felder);
  return {
    id: txt(v["id"]),
    number: txt(v["number"]),
    company: txt(v["company"]),
    ort: txt(v["ort"]) || null,
    ist_kunde: v["ist_kunde"] === true,
    geraete: zahl(v["geraete"]) ?? 0,
  };
}

export async function stammPersonal(): Promise<Liste<Ressource>> {
  if (ATTRAPPE) {
    const zeilen = A.RESSOURCEN.filter((r) => r.gruppe === "personal");
    return gleich({ zeilen, gesamt: zeilen.length });
  }
  const roh = await hole<Roh>("/stamm/personal");
  const zeilen = alsListe(roh["zeilen"]).map((p): Ressource => {
    const quals = alsListe(p["qualifikationen"]);
    const name = txt(p["name"]);
    // Die früheste ablaufende Qualifikation ist die, die als Nächstes Arbeit
    // macht — dieselbe Regel wie bei der Maschinenprüfung.
    const fristen = quals.map((q) => txt(q["gueltig_bis"])).filter(Boolean).sort();
    return {
      id: name,
      res_id: txt(p["id"]),
      gruppe: "personal",
      // Die Tätigkeit steht nicht in der Datenbank — was jemand darf, sagen
      // seine Qualifikationen. Die erste davon ist die Tätigkeit, unter der
      // er im Preisblatt steht.
      typ: txt(quals[0]?.["label"]),
      kuerzel: txt(p["initials"]) || kuerzelVon(name),
      kat: PERSONALGRUPPE,
      qual: quals.map((q) => txt(q["qualifikation"])).filter(Boolean),
      status: p["active"] ? "verfuegbar" : "",
      pruefung: fristen[0] ?? null,
    };
  });
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length };
}

/**
 * Maschinentypen **mit Tarifen**.
 *
 * Regel 2 als Antwortform: Die drei Preiszeilen eines Krans sind Varianten
 * desselben Typs. Und Regel 2 als Preisblatt: Eine Zeile je Typ, nicht je
 * Maschine — verkauft wird der Typ, disponiert das Individuum.
 */
export async function stammMaschinentypen(): Promise<Liste<Maschinentyp>> {
  if (ATTRAPPE) return gleich({ zeilen: [], gesamt: 0 });
  const roh = await hole<Roh>("/stamm/maschinentypen");
  const zeilen = alsListe(roh["zeilen"]).map((t): Maschinentyp => {
    const w = (t["wirksam"] ?? {}) as Roh;
    const ebene = zahl(w["ebene"]);
    return {
      id: txt(t["id"]),
      label: txt(t["label"]),
      gruppe: txt(t["gruppe"]),
      gruppe_id: txt(t["category_id"]),
      uebergruppe_id: txt(t["uebergruppe_id"]) || null,
      uebergruppe: txt(t["uebergruppe"]) || null,
      modell: txt(t["price_model"]),
      bestand: zahl(t["bestand"]) ?? 0,
      // Gezählter Bestand (0194): dann fragt die Vermietung nach der Stückzahl.
      ...(t["pool"] === true ? { pool: true } : {}),
      frei_pro_tag: zahl(t["free_hours_per_day"]),
      bedienung: alsBedienung(t["bedienung"]),
      // Die Ausstattung bringt mit, was sie bedeutet: wie viele Menschen
      // mitmüssen, welche Qualifikation sie tragen, und was davon Zubehör
      // ist. „mit Anschläger" allein sagt nichts davon.
      varianten: alsListe(t["varianten"]).map((v) => ({
        id: txt(v["id"]), code: txt(v["code"]), label: txt(v["label"]),
        mit_personal: zahl(v["requires_staff"]) ?? 0,
        qualifikation: txt(v["qualification"]) || null,
        qualifikation_label: txt(v["qualifikation_label"]) || null,
        zubehoer: Array.isArray(v["zubehoer"]) ? (v["zubehoer"] as unknown[]).map((z) => String(z)) : [],
        standard: v["is_default"] === true,
        // Wie sie rechnet (0102) und ob auf ihren Preis nichts aufschlägt
        // (0144) — beides Aussagen über den Preis dieser Ausstattung.
        modell: txt(v["price_model"]) || null,
        zuschlagsfrei: v["surcharge_free"] === true,
      })),
      mietkalender_code: txt(t["calendar_code"]) || null,
      /* Der Zähler (0151): was gilt, und daneben, ob es jemand gesagt hat.
         `zaehler_eigen` ist ausdrücklich dreiwertig — `null` heißt „nicht
         gesagt", und das ist etwas anderes als „nein". */
      zaehler: t["zaehler"] === true,
      zaehlerEigen: typeof t["zaehler_eigen"] === "boolean" ? t["zaehler_eigen"] : null,
      aktiv: t["active"] !== false,
      tarife: alsListe(t["tarife"]).map(alsTarif),
      // Womit gerechnet wird — dieselbe Kette wie `GET /preis`. Die Maske
      // zeigt das, und nicht, was sie selbst aus `tarife` ableiten würde.
      wirksam: {
        herkunft: {
          ebene: ebene === 0 || ebene === 1 || ebene === 2 ? ebene : null,
          gruppe_id: txt(w["gruppe_id"]) || null,
          label: txt(w["label"]) || null,
        },
        modell: txt(w["modell"]) || txt(t["price_model"]),
        tarife: alsListe(w["tarife"]).map(alsTarif),
      },
    };
  });
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length };
}

/** Einen Typ in eine andere Gruppe hängen — der Weg in eine Untergruppe. */
export async function maschinentypAendern(
  id: string,
  felder: {
    bezeichnung?: string; gruppe_id?: string; preismodell?: string;
    mietkalender?: string | null; aktiv?: boolean;
    /** Maß und Gewicht des Typs (Migration 0071) — der Wert für alle seine Stücke. */
    gewicht_kg?: number | null; laenge_mm?: number | null;
    breite_mm?: number | null; hoehe_mm?: number | null;
    /** Die Abweichung von der Gruppe. `null` heißt „erbt" (Migration 0083). */
    bedienung?: Bedienungsart | null;
    /** Dieselbe Abweichung für die Selbstabholung (Migration 0127). */
    abholbar?: boolean | null;
    /** Und für die Vorbereitungsregel (Migration 0138). `null` heißt „erbt". */
    vorbereitung_vorlauf_tage?: number | null;
    vorbereitung_uhrzeit?: string | null;
    /** Der Transportpuffer des Typs in Stunden (E16). `null` heißt „erbt". */
    puffer_vor_stunden?: number | null;
    puffer_nach_stunden?: number | null;
    /**
     * **Hat der Typ einen abzulesenden Zähler** (Migration 0151)? `null` heißt
     * „nicht gesagt" — dann entscheidet die Kennzahl BSTD wie bisher.
     */
    zaehler?: boolean | null;
  },
): Promise<void> {
  await aendere<unknown>(`/stamm/maschinentypen/${encodeURIComponent(id)}`, felder);
}

/**
 * **Einen Typ zählen statt einzeln führen** (Migration 0194, Lisas
 * Pool-Wunsch vom 25.09.2026). Eine Zahl stellt um oder ändert die
 * Stückzahl, `null` stellt zurück auf Einzelstücke. Was im Weg steht — ein
 * Einzelstück mit kommender Belegung, eine Stückzahl unter der Belegung —,
 * sagt die Antwort als Fehler mit Namen und Zahl.
 */
export async function maschinentypBestandSetzen(
  id: string,
  bestand: number | null,
): Promise<{ bestand: number | null; ausgemustert: string[] }> {
  if (ATTRAPPE) return gleich({ bestand, ausgemustert: [] });
  const roh = await ersetze<Roh>(`/stamm/maschinentypen/${encodeURIComponent(id)}/bestand`, { bestand });
  return {
    bestand: zahl(roh["bestand"]),
    ausgemustert: Array.isArray(roh["ausgemustert"]) ? (roh["ausgemustert"] as unknown[]).map(String) : [],
  };
}

/**
 * Einen Maschinentyp anlegen — **ohne Preis**.
 *
 * Bis zum 16.09.2026 gab es dafür keine Route. Eine neu angelegte
 * Untergruppe blieb damit für immer leer, und ein Gruppenpreis hing an
 * nichts.
 */
export async function maschinentypAnlegen(
  felder: { gruppe_id: string; bezeichnung: string; preismodell?: string },
): Promise<{ id: string }> {
  const roh = await sende<Roh>("/stamm/maschinentypen", felder);
  return { id: txt(roh["id"]) };
}

/** Löschen geht nur, solange der Typ nichts getragen hat — sonst abschalten. */
export async function maschinentypLoeschen(id: string): Promise<void> {
  await loesche<void>(`/stamm/maschinentypen/${encodeURIComponent(id)}`);
}

/**
 * **Ein Maschinentyp, ein Formular — und zwei Türen dahinter.**
 *
 * Luca, 16.09.2026: *„Bei den ‚Weiteres · Gerätegruppen und Zählgrößen' … ich
 * würde gerne zwischen Gerätegruppen und Zählgrößen Maschinentypen haben,
 * sodass man die Maschinentypen dann da sauber anlegen kann. … weil wir in
 * einem Maschinentyp auch vielleicht abweichende UVV-Fristen und so weiter
 * hinterlegen können."*
 *
 * Was an einem Typ hängt, wohnt in zwei Routen, und das mit gutem Grund:
 * `/stamm/maschinentypen` ist der Stamm (Name, Gruppe, Maß, Bedienung),
 * `/zubehoer/typen` ist das, was ihn zum Anbauteil macht und seine
 * Prüfintervalle trägt. **Eine dritte Route zu bauen, die beides noch einmal
 * kann, wäre ein zweiter Stand derselben Sache** — dann entscheidet die
 * Reihenfolge der Umbauten, welche Fassung gewinnt. Also bleiben es zwei
 * Türen, und dieses Wissen steht genau hier statt in jeder Maske.
 *
 * **Angelegt wird zuerst der Stamm.** Ein Typ ohne Gruppe kann nicht
 * entstehen; alles Weitere ist ein `PATCH` darauf und darf auch scheitern,
 * ohne dass etwas Halbes zurückbleibt.
 */
export type MaschinentypPflegeEingabe = {
  bezeichnung: string;
  gruppe_id: string;
  /** Prüfintervalle in Monaten — `null` heißt: von Hand eingetragen. */
  uvv_monate: number | null;
  hu_monate: number | null;
  /** `null` = ein gewöhnlicher Maschinentyp, kein Anbauteil. */
  art: Zubehoerart | null;
  gewicht_kg: number | null;
  laenge_mm: number | null;
  breite_mm: number | null;
  hoehe_mm: number | null;
  /** Die Abweichung von der Gerätegruppe. `null` heißt „erbt". */
  bedienung: Bedienungsart | null;
  /** Dieselbe Abweichung für die Selbstabholung (Migration 0127). */
  abholbar: boolean | null;
  /** Und dieselbe für die Vorbereitungsregel (Migration 0138). `null` = erbt. */
  vorbereitung_vorlauf_tage: number | null;
  vorbereitung_uhrzeit: string | null;
  /** Der Transportpuffer in Stunden (E16). `null` = erbt, 0 = keiner. */
  puffer_vor_stunden?: number | null;
  puffer_nach_stunden?: number | null;
  /** Nur beim Ändern: abschalten statt löschen. */
  aktiv?: boolean;
};

export async function maschinentypSpeichern(
  id: string | null,
  felder: MaschinentypPflegeEingabe,
  /** Wird gerufen, sobald ein neuer Typ steht — bevor der `PATCH` scheitern
   *  kann. Wer sich die Kennung merkt, ändert beim nächsten Versuch diesen
   *  Typ, statt einen zweiten anzulegen. */
  angelegt?: (id: string) => void,
): Promise<{ id: string }> {
  const { bezeichnung, gruppe_id, uvv_monate, hu_monate, art, aktiv, ...mass } = felder;
  const kennung = id ?? (await maschinentypAnlegen({ gruppe_id, bezeichnung })).id;
  if (id === null) angelegt?.(kennung);
  await maschinentypAendern(kennung, {
    bezeichnung,
    gruppe_id,
    ...mass,
    ...(aktiv === undefined ? {} : { aktiv }),
  });
  await zubehoerTypAendern(kennung, { art, uvv_monate, hu_monate });
  return { id: kennung };
}

/** Eigene Sätze am Typ anlegen — als Kopie dessen, was er heute erbt. */
export async function eigeneTarifeAnlegen(typId: string): Promise<void> {
  await sende<unknown>(`/stamm/maschinentypen/${encodeURIComponent(typId)}/eigene-tarife`, {});
}

/** Die eigenen Sätze löschen — danach erbt der Typ wieder. */
export async function eigeneTarifeLoeschen(typId: string): Promise<void> {
  await loesche<void>(`/stamm/maschinentypen/${encodeURIComponent(typId)}/eigene-tarife`);
}

export async function stammZuschlaege(): Promise<Liste<Zuschlag>> {
  if (ATTRAPPE) return gleich({ zeilen: [], gesamt: 0 });
  const roh = await hole<Roh>("/stamm/zuschlaege");
  /* Dieselbe Abbildung wie nach dem Speichern (`alsZuschlag`). Hier stand
     bis zum 16.09.2026 eine zweite Abschrift — und eine neue Spalte kommt
     sonst nur an einer der beiden Stellen an. */
  const zeilen = alsListe(roh["zeilen"]).map(alsZuschlag);
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length };
}

/** Die kleinen Listen, die sonst je Maske ein eigener Aufruf wären. */
export async function stammKataloge(): Promise<Kataloge> {
  if (ATTRAPPE) {
    return gleich<Kataloge>({
      /* Die Zaehlgroessen tragen die Einheit, mit der das Blatt „Aufmass
         erfassen" seine Zeilen beschriftet (21.09.2026) — ohne sie stuende
         dort „Aufmass in QM" statt „in m²". Drei reichen fuer den
         Durchgang; es sind die, die LEISTUNGEN_ATTRAPPE benutzt. */
      zaehlgroessen: [
        { code: "QM", label: "Quadratmeter", einheit: "m²" },
        { code: "HA", label: "Hektar", einheit: "ha" },
        { code: "STD", label: "Stunden", einheit: "Std." },
      ],
      qualifikationen: [], standorte: [],
      preislisten: [], leistungsarten: [], personaltarife: [],
      mietkalender: [], absagegruende: [], notizarten: [],
      /* **Die Zahlungsziele stehen als einzige darin** (18.09.2026). Sie
         sind der eine Katalog, den die Kundenakte auf ihrem ersten Blatt
         braucht: Ohne ihn liest das Feld `zahlungsziel` einen Wert, den es
         in der Liste nicht gibt, und schreibt daneben „— nicht in der
         Liste". Die übrigen Kataloge bleiben leer, bis eine Maske sie
         ebenso sichtbar vermisst.

         **Tage, kein Betrag** — hier wird nichts erfunden, was Geld ist. */
      zahlungsziele: A.ZAHLUNGSZIELE,
    });
  }
  const roh = await hole<Roh>("/stamm/kataloge");
  const zaehlgroessen = alsListe(roh["zaehlgroessen"]).map((z) => ({
    code: txt(z["code"]), label: txt(z["label"]), einheit: txt(z["unit"]),
  }));
  // Selbst angelegte Zählgrößen haben keinen Satz in de.json (AG-05).
  einheitenNachtragen(zaehlgroessen);
  return {
    zaehlgroessen,
    // Seit Migration 0050 und 0051. Beide standen vorher mit einem Strich da.
    mietkalender: alsListe(roh["mietkalender"]).map((k): Mietkalender => ({
      code: txt(k["code"]), label: txt(k["label"]),
      kalendertage: zahl(k["calendar_days"]) ?? 5,
      erster_tag_zaehlt: k["count_first_day"] !== false,
      letzter_tag_zaehlt: k["count_last_day"] !== false,
      halber_tag_vor: txt(k["last_day_half_before"]) || null,
      vorgabe: k["is_default"] === true,
      aktiv: k["active"] !== false,
      gruppen: zahl(k["gruppen"]) ?? 0,
      typen: zahl(k["typen"]) ?? 0,
    })),
    absagegruende: alsListe(roh["absagegruende"]).map((g): Absagegrund => ({
      code: txt(g["code"]), label: txt(g["label"]),
      art: txt(g["kind"]) === "storno" ? "storno" : "absage",
      sortierung: zahl(g["sort_order"]) ?? 100,
      aktiv: g["active"] !== false,
      satzPflicht: g["requires_note"] === true,
    })),
    qualifikationen: alsListe(roh["qualifikationen"]).map((q) => ({
      code: txt(q["code"]), label: txt(q["label"]), befristet: !!q["is_temporary"],
    })),
    standorte: alsListe(roh["standorte"]).map((s) => ({
      id: txt(s["id"]), label: txt(s["label"]), wo: anschriftZeile(s["address"]),
    })),
    preislisten: alsListe(roh["preislisten"]).map((p) => ({
      id: txt(p["id"]), label: txt(p["label"]),
      ab: txt(p["valid_from"]), bis: txt(p["valid_to"]) || null,
      vorgabe: p["is_default"] === true,
    })),
    leistungsarten: alsListe(roh["leistungsarten"]).map(alsLeistungsart),
    personaltarife: alsListe(roh["personaltarife"]).map((l) => ({
      id: txt(l["id"]), code: txt(l["code"]), label: txt(l["label"]),
      qualifikation: txt(l["qualification"]),
      qualifikation_label: txt(l["qualifikation"]),
      stundensatz: zahl(l["hourly_rate"]),
      mindest_stunden: zahl(l["min_hours"]),
      platzhalter: l["is_placeholder"] === true,
      rabattfaehig: l["discountable"] === true,
    })),
    zahlungsziele: alsListe(roh["zahlungsziele"]).map((z): Zahlungsziel => ({
      id: txt(z["id"]), label: txt(z["label"]), tage: zahl(z["days"]) ?? 0,
      standard: z["is_default"] === true, sortierung: zahl(z["sort_order"]) ?? 100,
      aktiv: z["active"] !== false, kunden: zahl(z["kunden"]) ?? 0,
    })),
    /* Die Notizarten (Migration 0146). `label_key` heißt hier `schluessel`:
       Steht er, kommt das Wort aus der Oberfläche; steht er nicht, trägt die
       Zeile ihres selbst. In der Maske steht kein Feldname der Datenbank. */
    notizarten: alsListe(roh["notizarten"]).map((a): Notizart => ({
      id: txt(a["id"]), code: txt(a["code"]), label: txt(a["label"]),
      schluessel: txt(a["label_key"]) || null,
      vorgabe: a["is_default"] === true, sortierung: zahl(a["sort_order"]) ?? 100,
      aktiv: a["active"] !== false, notizen: zahl(a["notizen"]) ?? 0,
    })),
  };
}

/* ══════════════ Abrechnung ══════════════ */

/**
 * Eine Prüfung am Beleg — **alle**, nicht die erste.
 *
 * Die Stufe heißt, wie die API sie speichert: `rot` · `gelb`. Bis zum
 * 16.09.2026 übersetzte diese Datei in `stopp` · `acht` · `gut`, und am
 * Vorgang zählte die API `acht` — eine Stufe, die es in der Datenbank nie
 * gab. Die Farbe wählt die Maske; das Wort bleibt eins.
 */
export type Pruefung = {
  stufe: "rot" | "gelb";
  /** i18n-Schlüssel unterhalb von `abrechnung.pruefung`. */
  key: string;
  /** Woran sie hängt — „Bomag Rüttler 35/60D · RP-31". Leer: am Beleg. */
  bezug: string | null;
  werte: Record<string, string>;
};

export type Belegzeile = {
  art: string;
  label: string;
  menge: number | null;
  einheit: string;
  /** Der Einzelpreis **wie auf der Rechnung** — nach Rabatt und Schichtfaktor (N5-01, `daten/belegzeile.ts`). */
  satz: number | null;
  netto: number | null;
  /** „inkl. 10,00 € Nachlass" und Verwandte — als Angabe, Wörter macht die Maske. */
  vermerke: Zeilenvermerk[];
};

export type Belegstand = "vorschlag" | "uebertragen" | "abgelehnt" | "wartet";

export type Beleg = {
  id: string;
  vorgang: string;
  vorgangZustand: string;
  kunde: string;
  kundeNr: string;
  periode: string;
  /** Teilrechnung (0206): trägt nur die Geräte, die schon zurück sind. */
  teil?: boolean;
  netto: number | null;
  stand: Belegstand;
  nummer: string | null;
  pdf: string | null;
  fehler: string | null;
  pruefungen: Pruefung[];
  zeilen: Belegzeile[];
};

export type Laufkopf = {
  id: string;
  status: "vorschlag" | "freigegeben";
  /** Der Tag, an dem gerechnet wurde, oder der gewählte Zeitraum. */
  von: string;
  bis: string;
  freigegebenAm: string;
  belege: number;
  netto: number | null;
  rot: number;
  uebertragen: number;
  abgelehnt: number;
};

export type Laufdetail = Laufkopf & { zeilen: Beleg[] };

function alsPruefungen(roh: Roh): Pruefung[] {
  const bezug = alsObjekt(roh["bezug"]);
  return alsListe(roh["check_notes"]).map((a): Pruefung => {
    const daten = alsObjekt(a["daten"]);
    const werte: Record<string, string> = {};
    Object.keys(daten).forEach((k) => { werte[k] = txt(daten[k]); });
    const position = alsObjekt(bezug[txt(a["item_id"])]);
    const teile = [txt(position["bezeichnung"]), txt(position["maschinen"])].filter(Boolean);
    return {
      stufe: a["blockiert"] === true || txt(a["stufe"]) === "rot" ? "rot" : "gelb",
      key: txt(a["key"]).replace(/^abrechnung\./, ""),
      bezug: teile.length ? teile.join(" · ") : null,
      werte,
    };
  })
    // Rot zuerst: Das ist die Zeile, an der die Freigabe hängt.
    .sort((a, b) => (a.stufe === b.stufe ? 0 : a.stufe === "rot" ? -1 : 1));
}

function alsBeleg(b: Roh, imVorschlag: boolean): Beleg {
  const drueben = txt(b["lex_invoice_id"]) !== "" || txt(b["status"]) === "uebertragen";
  const stand: Belegstand = imVorschlag ? "vorschlag"
    : drueben ? "uebertragen"
      : txt(b["status"]) === "fehler" ? "abgelehnt" : "wartet";
  return {
    id: txt(b["id"]),
    vorgang: txt(b["vorgang"]),
    vorgangZustand: txt(b["vorgang_status"]),
    kunde: txt(b["kunde"]),
    kundeNr: txt(b["kundennummer"]),
    periode: [tagKurz(b["period_from"]), tagKurz(b["period_until"])].filter(Boolean).join(" – "),
    ...(b["teil"] === true ? { teil: true } : {}),
    netto: zahl(b["total_net"]),
    stand,
    nummer: txt(b["lex_number"]) || null,
    pdf: txt(b["lex_pdf_url"]) || null,
    fehler: drueben ? null : txt(b["error_text"]) || null,
    pruefungen: alsPruefungen(b),
    zeilen: alsListe(b["zeilen"]).map((z) => {
      const menge = zahl(z["quantity"]);
      const satz = zahl(z["unit_price"]);
      const netto = zahl(z["total_net"]);
      return {
        art: txt(z["kind"]),
        label: txt(z["label"]),
        menge,
        einheit: txt(z["unit_label"]),
        /* N5-01: „1 Einsatz × 260,00 · 250,00" — der Satz vor dem Nachlass
           neben der Summe danach. Wie auf der Rechnung: verrechnet, mit Vermerk. */
        satz: menge !== null && satz !== null ? einzelWieAufDemBeleg(menge, satz, netto) : satz,
        netto,
        vermerke: vermerkeAusRechenweg(z["calculation"]),
      };
    }),
  };
}

function alsLaufkopf(r: Roh): Laufkopf {
  return {
    id: txt(r["id"]),
    status: txt(r["status"]) === "vorschlag" ? "vorschlag" : "freigegeben",
    von: txt(r["period_from"]),
    bis: txt(r["period_until"]),
    freigegebenAm: tagUndZeit(r["released_at"]),
    belege: zahl(r["belege"]) ?? 0,
    netto: zahl(r["netto"]),
    rot: zahl(r["rot"]) ?? 0,
    uebertragen: zahl(r["uebertragen"]) ?? 0,
    abgelehnt: zahl(r["abgelehnt"]) ?? 0,
  };
}

/**
 * Die Läufe in einem Zustand.
 *
 * **Die Maske fragt nach offenen Vorschlägen, nicht nach dem jüngsten Lauf.**
 * Bis zum 16.09.2026 stand hier `zeilen[0]` — ein längst freigegebener Lauf
 * erschien als „Vorschlag" mit einem Freigabeknopf, der `409` lieferte. Ein
 * Vorschlag ohne Belege ist keiner und steht nicht in der Liste.
 */
export async function abrechnungsLaeufe(status: Laufkopf["status"]): Promise<Laufkopf[]> {
  if (ATTRAPPE) return gleich([]);
  const roh = await hole<Roh>(`/abrechnung/laeufe?status=${status}`);
  return alsListe(roh["zeilen"]).map(alsLaufkopf).filter((l) => l.belege > 0);
}

export async function abrechnungsLauf(id: string): Promise<Laufdetail> {
  const roh = await hole<Roh>(`/abrechnung/laeufe/${id}`);
  const kopf = alsLaufkopf(roh);
  const zeilen = alsListe(roh["belege"]).map((b) => alsBeleg(b, kopf.status === "vorschlag"));
  return {
    ...kopf,
    zeilen,
    belege: zeilen.length,
    netto: zeilen.reduce<number | null>((s, z) => (z.netto === null ? s : (s ?? 0) + z.netto), null),
    rot: zeilen.filter((z) => z.pruefungen.some((p) => p.stufe === "rot")).length,
  };
}

/** Ein freigegebener Beleg und der Lauf, mit dem er hinausging. */
export type FreigegebenerBeleg = Beleg & { laufId: string; freigegebenAm: string; bezahlt: boolean };

export async function freigegebeneBelege(): Promise<FreigegebenerBeleg[]> {
  if (ATTRAPPE) return gleich([]);
  const roh = await hole<Roh>("/abrechnung/belege");
  return alsListe(roh["zeilen"]).map((b) => ({
    ...alsBeleg(b, false),
    laufId: txt(b["run_id"]),
    freigegebenAm: tagUndZeit(b["released_at"]),
    bezahlt: txt(b["paid_at"]) !== "",
  }));
}

/** Der Tageslauf, von Hand — derselbe, der um 05:00 läuft. */
export async function abrechnungStarten(): Promise<{ laufId: string | null; belege: number }> {
  if (ATTRAPPE) return gleich({ laufId: null, belege: 0 }, 300);
  const roh = await sende<Roh>("/abrechnung/laeufe", {});
  return { laufId: txt(roh["abrechnungslauf_id"]) || null, belege: zahl(roh["belege"]) ?? 0 };
}

export function abrechnungVerwerfen(id: string): Promise<void> {
  if (ATTRAPPE) return gleich(undefined, 200);
  return sende<void>(`/abrechnung/laeufe/${id}/verwerfen`);
}

/**
 * Was drüben aus den Belegen wurde — gezählt von der API, nicht vermutet.
 *
 * Bis zum 16.09.2026 meldete die Maske „Freigegeben und übertragen", auch
 * wenn Lexware einen Beleg abgelehnt hatte: Die Übertragung wirft dabei nicht,
 * sie vermerkt `fehler` am Beleg und läuft weiter.
 */
export type Uebertragung = {
  gesamt: number;
  uebertragen: number;
  abgelehnt: number;
  wartet: number;
  nummern: string[];
  abgelehnte: { vorgang: string; fehler: string | null }[];
};

export type Freigabe = {
  uebertragung: Uebertragung | null;
  /** Ein Schlüssel, wenn Lexware gar nicht erreichbar war. */
  hinweis: string | null;
};

function alsFreigabe(roh: Roh): Freigabe {
  const u = alsObjekt(roh["uebertragung"]);
  const belege = alsListe(u["belege"]);
  return {
    uebertragung: roh["uebertragung"]
      ? {
          gesamt: zahl(u["gesamt"]) ?? 0,
          uebertragen: zahl(u["uebertragen"]) ?? 0,
          abgelehnt: zahl(u["abgelehnt"]) ?? 0,
          wartet: zahl(u["wartet"]) ?? 0,
          nummern: alsListe(u["nummern"]).map((n) => txt(n)).filter(Boolean),
          abgelehnte: belege
            .filter((b) => txt(b["stand"]) === "abgelehnt")
            .map((b) => ({ vorgang: txt(b["vorgang"]), fehler: txt(b["fehler"]) || null })),
        }
      : null,
    hinweis: txt(roh["beleg_hinweis"]) || null,
  };
}

export async function abrechnungFreigeben(id: string): Promise<Freigabe> {
  if (ATTRAPPE) return gleich({ uebertragung: null, hinweis: null }, 200);
  return alsFreigabe(await sende<Roh>(`/abrechnung/laeufe/${id}/freigeben`));
}

/* Die Rückmeldung zu einer Freigabe und was danach stehen bleibt — in
   `daten/freigabeergebnis.ts`, damit es sich ohne Browser prüfen lässt (N5-04). */
export { freigabeErgebnis, freigabeRuf, laufAngekommenRuf, laufWartetNoch } from "./freigabeergebnis";

/** Die Ablehnungsgründe, für die es einen kurzen Satz gibt. */
const ABLEHNUNG = new Set([
  "artikel_fehlt", "kein_kontakt", "ungewiss", "nicht_erreichbar", "beleg_ohne_position",
  "beleg_unbrauchbar", "nicht_annehmbar", "kein_zugang", "zu_viele_anfragen",
]);

/** `lexware.artikel_fehlt` → `abrechnung.abgelehnt.artikel_fehlt`. */
export function ablehnungPfad(fehler: string | null): string {
  const kern = (fehler ?? "").replace(/^lexware\./, "");
  return `abrechnung.abgelehnt.${ABLEHNUNG.has(kern) ? kern : "sonst"}`;
}

/**
 * **Der ganze Satz zum Grund** (L-23, Durchgang 03.10.2026). Neben dem kurzen
 * Wort („abgelehnt: Beleg nicht übersetzbar") stand im Korb immer derselbe
 * Satz „Lexware Office hat den Beleg nicht angenommen" — auch dann, wenn er
 * nie drüben war oder die Verbindung abriss und er drüben liegen kann. Hier
 * nur Gründe, deren Satz ohne Werte auskommt: Der Beleg trägt den Schlüssel,
 * nicht die Bezeichnung der Position.
 */
const ABLEHNUNG_SATZ: Record<string, string> = {
  beleg_unbrauchbar: "fehler.lexwareBelegUnbrauchbar",
  beleg_ohne_position: "fehler.lexwareBelegOhnePosition",
  kein_kontakt: "fehler.lexwareKeinKontakt",
  text_zu_lang: "fehler.lexwareTextZuLang",
  ungewiss: "fehler.lexwareUngewiss",
  kein_zugang: "fehler.lexwareKeinZugang",
  zu_viele_anfragen: "fehler.lexwareZuVieleAnfragen",
  nicht_erreichbar: "fehler.lexwareNichtErreichbar",
};

/** `lexware.beleg_unbrauchbar` → `fehler.lexwareBelegUnbrauchbar`; sonst der allgemeine Satz. */
export function ablehnungSatzPfad(fehler: string | null): string {
  const kern = (fehler ?? "").replace(/^lexware\./, "");
  return ABLEHNUNG_SATZ[kern] ?? "abrechnung.abgelehntSatz";
}

/** Einen abgelehnten Beleg noch einmal schicken — nachdem die Ursache weg ist. */
export async function belegUebertragen(id: string): Promise<Freigabe> {
  if (ATTRAPPE) return gleich({ uebertragung: null, hinweis: null }, 200);
  return alsFreigabe(await sende<Roh>(`/abrechnung/belege/${id}/uebertragen`));
}

/* ══════════════ Jobs ══════════════ */

// Welche Läufe der Verlauf zeigt, steht seit dem 03.10.2026 in
// `daten/jobarten.ts` — vollständig über den Typ erzwungen (M-30).

/** `fertig` · `teilfehler` · `fehler` → die drei Ausgänge der Maske. */
const AUSGANG: Record<string, Job["ausgang"]> = {
  fertig: "gut", teilfehler: "teils", fehler: "stopp", laeuft: "teils",
};

const STUFE: Record<string, JobSchritt["stufe"]> = {
  info: "gut", warnung: "acht", fehler: "stopp",
};

export async function jobs(art?: string): Promise<Liste<Job>> {
  if (ATTRAPPE) {
    const zeilen = art ? A.JOBS.filter((j) => j.art === art) : A.JOBS;
    return gleich({ zeilen, gesamt: zeilen.length });
  }

  const roh = await hole<Roh>(`/jobs${art ? `?art=${art}` : ""}`);
  const laeufe = alsListe(roh["zeilen"]).filter((j) => istJobArt(txt(j["kind"])));

  /* Die Schritte hängen am Einzelsatz. Sie erst beim Aufklappen zu holen wäre
     ein zweiter Zustand in der Maske; die Historie zeigt fünfzig Läufe, und
     fünfzig Aufrufe wären das schlechtere Geschäft als einer je sichtbarem
     Lauf. Darum: die fünfzehn jüngsten mit Schritten, der Rest ohne. */
  const mitSchritten = await Promise.all(
    laeufe.slice(0, 15).map(async (j) => {
      try {
        return await hole<Roh>(`/jobs/${txt(j["id"])}`);
      } catch {
        return j;
      }
    }),
  );
  const alle = [...mitSchritten, ...laeufe.slice(15)];

  const zeilen = alle.map((j): Job => {
    const begonnen = txt(j["started_at"]);
    const beendet = txt(j["ended_at"]);
    const dauer = begonnen && beendet
      ? Math.max(0, Math.round((new Date(beendet).getTime() - new Date(begonnen).getTime()) / 1000))
      : 0;
    return {
      id: txt(j["id"]),
      art: txt(j["kind"]) as Job["art"],
      begonnen: tagUndZeit(begonnen),
      dauer_sekunden: dauer,
      ausgang: AUSGANG[txt(j["status"])] ?? "teils",
      schritte: alsListe(j["schritte"]).map((s): JobSchritt => ({
        wann: uhrzeit(s["occurred_at"]),
        stufe: STUFE[txt(s["level"])] ?? "gut",
        was_key: txt(s["message"]),
        ...(txt(s["subject"]) ? { dazu: txt(s["subject"]) } : {}),
      })),
    };
  });

  return { zeilen, gesamt: zeilen.length };
}

export function jobStarten(art: string): Promise<{ id: string }> {
  if (ATTRAPPE) return gleich({ id: `j-${Date.now().toString(36)}` }, 300);
  return sende<{ id: string }>(`/jobs/${art}/starten`);
}

/** Ein Takt aus der Antwort — genau eine Form gesetzt, die anderen `null`. */
function alsTakt(z: Roh): Takt {
  return {
    alleMinuten: zahl(z["alleMinuten"]),
    taeglichUm: txt(z["taeglichUm"]) || null,
    monatlichAm: zahl(z["monatlichAm"]),
    um: txt(z["um"]) || null,
  };
}

function alsPlan(roh: Roh): Planstand[] {
  return alsListe(roh["zeilen"]).map((z): Planstand => {
    const vorgabe = z["vorgabe"];
    const letzter = alsObjekt(z["letzter"]);
    return {
      art: txt(z["art"]) as Planstand["art"],
      ...alsTakt(z),
      aktiv: z["aktiv"] === true,
      eigen: z["eigen"] === true,
      vorgabe: vorgabe && typeof vorgabe === "object" ? alsTakt(vorgabe as Roh) : null,
      naechster: txt(z["naechster"]) || null,
      letzter: txt(letzter["begonnen"])
        ? {
            begonnen: txt(letzter["begonnen"]),
            beendet: txt(letzter["beendet"]) || null,
            status: txt(letzter["status"]),
          }
        : null,
      geaendertVon: txt(z["geaendert_von"]) || null,
      geaendertAm: txt(z["geaendert_am"]) || null,
    };
  });
}

/**
 * Der Zeitplan, so wie er gilt — je Lauf der wirksame Takt, die Vorgabe
 * dahinter, der nächste und der letzte Lauf.
 *
 * Luca hat die Auskunftsseite mit der Auflage bestellt: *„Keine Zahl auf dieser
 * Seite, die nicht aus dem System kommt."* Seit dem 16.09.2026 kommt der Takt
 * nicht mehr nur aus `@mrmachine/plan`, sondern zuerst aus `job_schedule` —
 * derselben Tabelle, die der Zeitgeber jede Minute liest.
 *
 * **Die Attrappe liefert hier nichts**, und das ist richtig: Ein erfundener
 * Takt wäre genau die Zahl, vor der die Auflage warnt.
 */
export async function jobPlan(): Promise<Planstand[]> {
  if (ATTRAPPE) return gleich([]);
  return alsPlan(await hole<Roh>("/jobs/plan"));
}

/** Den Takt eines Laufs setzen — oder nur anhalten und wieder anstoßen. */
export async function jobPlanSetzen(
  art: string,
  teil: Partial<{ alleMinuten: number; taeglichUm: string; monatlichAm: number; um: string; aktiv: boolean }>,
): Promise<Planstand[]> {
  if (ATTRAPPE) return gleich([], 300);
  return alsPlan(await ersetze<Roh>(`/jobs/plan/${encodeURIComponent(art)}`, teil));
}

/** Zurück auf die Vorgabe: Die eigene Zeile wird gelöscht, nicht mit der Vorgabe gefüllt. */
export async function jobPlanZurueck(art: string): Promise<Planstand[]> {
  if (ATTRAPPE) return gleich([], 300);
  return alsPlan(await loesche<Roh>(`/jobs/plan/${encodeURIComponent(art)}`));
}

/* ══════════════ Ablage ══════════════ */

/**
 * Eine zeitlich begrenzte URL auf eine abgelegte Datei.
 *
 * Der Behaelter ist nicht oeffentlich, und das soll er bleiben: Auf einem
 * Uebergabeprotokoll steht eine Unterschrift. Die URL laeuft ab, eine
 * dauerhafte waere eine dauerhafte Freigabe.
 */
/* ══════════════ Das Bild, das es im Prototyp nicht gibt ══════════════

   Im Attrappenbetrieb gibt es keinen Behälter und keine signierte URL. Bis
   zum 18.09.2026 kam hier eine leere Zeichenkette zurück — und `Fotos`
   wartet auf eine URL, die nie kommt: drei graue Kacheln, die ewig laden.
   Wer den Prototyp durchklickt, sah an jedem Protokoll und an jeder
   Begehung dasselbe Bild eines Fehlers.

   **Ein Platzhalter sagt, was er ist.** Schraffiert und mit dem Wort
   „BEISPIEL" darin — dieselbe Form, mit der die Oberfläche kennzeichnet,
   was nicht ihr gehört (28-designkonzept.md, „Bedeutungsfarben"). Er wird
   hier gebaut und nicht geladen: ein `data:`-Bild kostet keine Anfrage und
   kann im Funkloch nicht fehlen. */
const PLATZHALTERBILD = "data:image/svg+xml;utf8," + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 120" width="160" height="120">`
  + `<defs><pattern id="s" width="8" height="8" patternUnits="userSpaceOnUse"`
  + ` patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="8"`
  + ` stroke="#C9C9C0" stroke-width="3"/></pattern></defs>`
  + `<rect width="160" height="120" fill="#EDEDE7"/>`
  + `<rect width="160" height="120" fill="url(#s)"/>`
  + `<rect x="26" y="48" width="108" height="24" rx="4" fill="#FFFFFF"/>`
  + `<text x="80" y="65" text-anchor="middle" font-family="sans-serif"`
  + ` font-size="12" letter-spacing="1.5" fill="#6B6B69">BEISPIEL</text></svg>`,
);

export async function ablageUrl(pfad: string): Promise<string> {
  if (ATTRAPPE) return gleich(PLATZHALTERBILD);
  const roh = await hole<Roh>(`/ablage/url?pfad=${encodeURIComponent(pfad)}`);
  return txt(roh["url"]);
}

/** Wofuer eine Datei da ist. Der Wert bestimmt den Ordner in der Ablage. */
export type Ablagezweck =
  | "protokoll" | "unterschrift" | "foto" | "beleg" | "import"
  /* Die allgemeine Dokumentenablage unter „Betrieb" (0158). Dieselbe Liste
     wie `ZWECKE` in `apps/api/src/routen/ablage.ts` — sie bestimmt dort den
     Ordner, und ein Wert, den der Server nicht kennt, ist ein 422. */
  | "dokument";

/**
 * Eine Datei ablegen — und den Pfad zurueckbekommen.
 *
 * **Base64 und nicht `FormData`, und das ist Absicht.** Der Browser hat genau
 * eine Tuer zur API: `ruf()` in `klient.ts`. Sie haengt das Kontext-Token an,
 * holt es bei `401 kontext.abgelaufen` nach und wiederholt genau einmal. Ein
 * `fetch` mit `FormData` daneben muesste das alles noch einmal koennen — und
 * wuerde es das eine Mal nicht, wenn ein Fahrer nach zwei Stunden auf der
 * Baustelle sein erstes Foto schickt.
 *
 * Das Drittel, das Base64 kostet, wird vorher hereingeholt: `verkleinern()`
 * bringt ein Kamerabild auf 1600 px, bevor es hier ankommt.
 */
export async function ablageHochladen(datei: Blob, zweck: Ablagezweck): Promise<string> {
  if (ATTRAPPE) return gleich(`attrappe/${zweck}/${Date.now()}.jpg`);
  const roh = await sende<Roh>("/ablage", {
    zweck,
    typ: datei.type,
    daten: await alsBasis64(datei),
  });
  return txt(roh["pfad"]);
}

/** Ein Blob als Base64 — ohne das `data:`-Praefix, das der Leser mitgibt. */
function alsBasis64(datei: Blob): Promise<string> {
  return new Promise((fertig, scheitern) => {
    const leser = new FileReader();
    leser.onerror = () => scheitern(new Error("Die Datei liess sich nicht lesen."));
    leser.onload = () => {
      const wert = typeof leser.result === "string" ? leser.result : "";
      fertig(wert.includes(",") ? wert.slice(wert.indexOf(",") + 1) : wert);
    };
    leser.readAsDataURL(datei);
  });
}

/**
 * Ein Kamerabild auf Kantenlaenge bringen, bevor es hochgeladen wird.
 *
 * *„Ein Protokoll mit zehn unkomprimierten Bildern über Baustellen-Mobilfunk
 * lädt nie fertig hoch"* (09-mobile.md). Ein Telefon liefert zwoelf Megapixel
 * und vier Megabyte; gebraucht werden 1600 px und ein paar hundert Kilobyte,
 * denn angesehen wird das Bild auf einem Schirm.
 *
 * Geht das Verkleinern schief — ein Format, das der Browser nicht zeichnen
 * kann —, geht das Original hinaus. Ein grosses Foto ist besser als keins.
 */
export async function verkleinern(datei: File, kante = 1600): Promise<Blob> {
  try {
    const bild = await bildLaden(datei);
    const gross = Math.max(bild.width, bild.height);
    if (!gross) return datei;
    const teil = Math.min(1, kante / gross);
    const breite = Math.round(bild.width * teil);
    const hoehe = Math.round(bild.height * teil);
    const tafel = document.createElement("canvas");
    tafel.width = breite;
    tafel.height = hoehe;
    const stift = tafel.getContext("2d");
    if (!stift) return datei;
    stift.drawImage(bild, 0, 0, breite, hoehe);
    const klein = await new Promise<Blob | null>((fertig) =>
      tafel.toBlob(fertig, "image/jpeg", 0.82));
    return klein && klein.size < datei.size ? klein : datei;
  } catch {
    return datei;
  }
}

function bildLaden(datei: Blob): Promise<HTMLImageElement> {
  return new Promise((fertig, scheitern) => {
    const url = URL.createObjectURL(datei);
    const bild = new Image();
    bild.onload = () => { URL.revokeObjectURL(url); fertig(bild); };
    bild.onerror = () => { URL.revokeObjectURL(url); scheitern(new Error("kein Bild")); };
    bild.src = url;
  });
}

/* ══════════════ Preisfindung ══════════════ */

/**
 * Der Preis mit seinem Rechenweg.
 *
 * Die Antwort traegt nicht nur eine Zahl, sondern die Schritte dahinter --
 * ein Disponent, der am Telefon einen Preis nennt, muss sagen koennen, wie
 * er zustande kommt. Die Schritte sind i18n-Schluessel mit Zahlen, kein
 * fertiger Satz: `{ key: "preis.staffel", ab_tag: 3, satz: 115 }`.
 */
export async function preis(frage: {
  typ: string;
  von: string;
  /** Das Enddatum. Fehlt es, sagt `miettage`, wie lang gemietet wird. */
  bis?: string | null;
  /**
   * So viele **Miettage** ab `von` — das Ende rechnet die API.
   *
   * Luca, 17.09.2026: *„Thema Preisauskunft ist noch nicht perfekt, denn der
   * berücksichtigt die Tage, also Wochenenden, nicht."* Die Maske rechnete
   * `bis = von + tage − 1` und bekam bei Fünftagekalender zwei Tage wieder
   * abgezogen. Welcher Kalender gilt, hängt an Typ, Gruppe, Übergruppe und
   * Betrieb — das weiß nur die andere Seite.
   */
  miettage?: number | null;
  variante?: string | null;
  kunde?: string | null;
  menge?: number;
  /** Mit Bediener — entscheidet über Zuschläge „nur mit/ohne Bediener". */
  bedient?: boolean;
}): Promise<Preisauskunft> {
  if (ATTRAPPE) {
    return gleich({
      netto: 0, einzelpreis: 0, menge: 1, rabatt_prozent: 0,
      einheit_key: "preis.einheitTag",
      modell: "tagesstaffel", miettage: 0, schritte: [], zuschlaege: [],
    });
  }
  const teile = new URLSearchParams({ typ: frage.typ, von: frage.von });
  // Eins von beiden, nie beides: Zwei Angaben wären zwei Fragen.
  if (frage.bis) teile.set("bis", frage.bis);
  else teile.set("miettage", String(frage.miettage ?? 1));
  if (frage.variante) teile.set("variante", frage.variante);
  if (frage.kunde) teile.set("kunde", frage.kunde);
  if (frage.menge !== undefined) teile.set("menge", String(frage.menge));
  if (frage.bedient) teile.set("bedient", "1");

  const roh = await hole<Roh>(`/preis?${teile.toString()}`);
  // `zahl()` gibt null zurueck, wo nichts steht -- hier ist eine fehlende
  // Zahl aber keine Auskunft, sondern ein Fehler der API. 0 ist der ehrliche
  // Wert: die Maske zeigt dann 0,00 und nicht einen Strich, hinter dem man
  // eine Zahl vermutet.
  return {
    netto: zahl(roh["netto"]) ?? 0,
    einzelpreis: zahl(roh["einzelpreis"]) ?? 0,
    menge: zahl(roh["menge"]) ?? 1,
    // Kein Rabatt ist 0 und nicht „unbekannt" — `netto` ist damit gerechnet.
    rabatt_prozent: zahl(roh["rabatt_prozent"]) ?? 0,
    einheit_key: txt(roh["einheit_key"]),
    modell: txt(roh["modell"]),
    miettage: zahl(roh["miettage"]) ?? 0,
    // Ein Schritt ohne `key` laesst sich nicht beschriften -- der faellt
    // weg, statt als leere Zeile unter dem Preis zu stehen.
    schritte: alsListe(roh["schritte"])
      .filter((x) => typeof x["key"] === "string")
      .map((x) => ({ ...x, key: x["key"] as string })),
    /* **Mit Rechenweg, nicht nur mit Summe.** Menge, Satz, Einheit und
       Schritte kommen aus der Preisfindung mit; sie hier wegzuwerfen hieß,
       dass die Auskunft nur noch die Summe aller Zuschläge nennen konnte
       (16.09.2026). Ein Zuschlag ist eine Position und wird wie eine
       gelesen. */
    zuschlaege: alsListe(roh["zuschlaege"]).map((z) => ({
      code: txt(z["code"]),
      bezeichnung: txt(z["label"] ?? z["bezeichnung"]),
      netto: zahl(z["netto"]) ?? 0,
      vorgewaehlt: z["vorgewaehlt"] === true || z["preselected"] === true,
      menge: zahl(z["menge"]) ?? 1,
      einzelpreis: zahl(z["einzelpreis"]) ?? 0,
      einheit_key: txt(z["einheit_key"]),
      schritte: alsListe(z["schritte"])
        .filter((x) => typeof x["key"] === "string")
        .map((x) => ({ ...x, key: x["key"] as string })),
      /* Zwei Aussagen ueber die Zeile, die keine Betraege sind: ob der Satz
         erfunden ist (0032/0036) und ob er bei Selbstabholung entfaellt
         (0127). Fehlen sie — in jeder aelteren Antwort —, gilt „nein". */
      ...(z["platzhalter"] === true ? { platzhalter: true } : {}),
      ...(z["faellt_bei_abholung"] === true ? { faelltBeiAbholung: true } : {}),
      // Preis immer individuell (0227, E1) — der Preis fehlt, bis ihn jemand nennt.
      ...(z["preis_individuell"] === true ? { preisIndividuell: true } : {}),
      // Einmal je Auftrag (AG-03/V-20) — die Preisfindung nennt den Umfang.
      ...(z["umfang"] === "auftrag" ? { jeAuftrag: true } : {}),
    })),
    /* **Auf diesen Preis schlägt nichts auf** (0144). Ohne diese Aussage
       wäre die leere Zuschlagsliste nicht von „für dieses Gerät gibt es
       keine Regeln" zu unterscheiden. Fehlt sie — in jeder älteren
       Antwort —, gilt „nein". */
    ...(roh["zuschlagsfrei"] === true ? { zuschlagsfrei: true } : {}),
    bedient: roh["bedient"] === true,
    ...(() => {
      const pflicht = alsBedienungsart(roh["bedienungspflicht"]);
      return pflicht ? { bedienungspflicht: pflicht } : {};
    })(),
    /* Das Enddatum und der Weg von den Kalendertagen zu den Miettagen
       (Durchgang 16.09.2026, PR7). Fehlen sie, sagt die Auskunft eben nur die
       Zahl — wie vorher. */
    ...(() => {
      const z = alsObjekt(roh["zeitraum"]);
      const bisTag = txt(z["bis"]);
      return bisTag
        ? { zeitraum: { von: txt(z["von"]), bis: bisTag, kalendertage: zahl(z["kalendertage"]) ?? 0 } }
        : {};
    })(),
    miettageSchritte: alsListe(roh["miettage_schritte"])
      .filter((x) => typeof x["key"] === "string")
      .map((x) => ({ ...x, key: x["key"] as string })),
    abholung: alsAbholung(roh["abholung"]),
    gespann: alsGespann(roh["gespann"]),
  };
}


/* ══════════════ Einstellungen und Benutzer ══════════════ */

function alsEinstellungen(roh: Roh): Einstellungen {
  const anschrift = alsObjekt(roh["address"]);
  const tage = zahl(roh["calendar_days"]) ?? 5;
  return {
    firma: txt(roh["company_name"]),
    rechtsform: txt(roh["company_legal_form"]),
    strasse: txt(anschrift["strasse"]),
    plz: txt(anschrift["plz"]),
    ort: txt(anschrift["ort"]),
    land: txt(anschrift["land"]),
    ust_id: txt(roh["vat_id"]),
    steuernummer: txt(roh["tax_number"]),
    telefon: txt(roh["phone"]),
    email: txt(roh["email"]),
    web: txt(roh["web"]),
    wetter_ort: txt(roh["weather_location"]),
    // Zur Auswahl steht, was gesetzt ist. Eine Liste erfundener Orte wäre eine
    // Einstellung, die nichts einstellt.
    wetter_orte: [txt(roh["weather_location"]), txt(anschrift["ort"])]
      .filter(Boolean)
      .filter((o, i, alle) => alle.indexOf(o) === i),
    /* **Ein Feld, und es wirkt.** Vier weitere wurden hier bis zum 21.09.2026
       erfunden — zwei Nullen, das Land als „Bundesland", eine weitere Null —
       und von keiner Maske gelesen. Der Transportpuffer hat jetzt eigene
       Spalten je Stufe (0158/0163) und ein eigenes Modul. */
    kalender: {
      vorgabe_kalender: tage === 7 ? "7-Tage" : tage === 6 ? "6-Tage" : "5-Tage",
    },
    // Die Spalte hat eine Vorgabe (Migration 0103); fehlt sie in der Antwort,
    // steht die Datenbank vor 0103, und dort plante die Auskunft auch mit 8.
    stunden_je_tag: zahl(roh["planned_hours_per_day"]) ?? 8,
    /* Die Übergabe beim Einsatz mit Fahrer (Migration 0126). Fehlt sie in der
       Antwort, steht die Datenbank vor 0126 — und dort gab es die Übergabe
       immer; deshalb `true` als Rückfall und `false` als Vorgabe der Spalte. */
    uebergabe_mit_fahrer: roh["operator_handover"] === undefined
      ? true : roh["operator_handover"] === true,
    /* Bediener ohne gueltige Qualifikation (0230). Fehlt die Spalte, steht die
       Datenbank davor -- und dort wurde gesperrt. */
    qualifikation: roh["qualification_mode"] === undefined
      ? "sperren" : roh["qualification_mode"] === "sperren" ? "sperren" : "warnen",
    /* Wem das Nachtanken zufällt (0280). Ohne Wert gilt die Vorgabe — wie in
       der API (`nachtankenBesitzer`): wer die Rücknahme gemacht hat. */
    nachtanken_zuteilung: roh["refuel_assignment"] === "offen" ? "offen" : "ruecknahme",
    /* Ab wann die Vorbereitung erscheint (Migration 0128). Fehlt es, gilt die
       feste Regel davor: Vortag, 06:00. */
    // Leer heißt aus — und die Mindestauftragsgröße wirkt ohnehin nicht (0138, G8).
    mindestauftrag: zahl(roh["min_order_value"]),
    /* Abholung am Vorabend, Rückgabe am Morgen (0286). Leer heißt aus — auch
       vor 0286, dann gab es die Regel nicht. */
    vorabend_ab: txt(roh["early_pickup_from"]).slice(0, 5) || null,
    morgen_bis: txt(roh["late_return_until"]).slice(0, 5) || null,
    // Je Wochentag (0288) — vor 0288 keine Abweichung.
    vorabend_je_tag: vorabendJeTag(roh["early_pickup_by_weekday"]),
    /* Das Bundesland für den Feiertagshinweis (0287), wie gespeichert.
       Leer — oder vor 0287 — heißt Vorgabe. */
    feiertagsland: istBundesland(roh["holiday_region"]) ? roh["holiday_region"] : null,
    vorbereitung_vorlauf_tage: typeof roh["preparation_lead_days"] === "number" ? roh["preparation_lead_days"] : 1,
    vorbereitung_uhrzeit: typeof roh["preparation_release_time"] === "string" && roh["preparation_release_time"]
      ? roh["preparation_release_time"] : "06:00",
    /* (Hier stand bis zum 21.09.2026 `faktorregel` aus `service_factor_mode`.
       Die Gattung „Preistreiber" ist abgebaut, Migration 0164.) */
    /* Der Zusatztext je Preisart (Migration 0141). Was nicht dasteht, ist
       kein Zusatz — eine leere Zeichenkette wäre eine Leerzeile unter der
       Position. */
    positionstexte: (() => {
      const roh_ = alsObjekt(roh["position_notes"]);
      const ergebnis: Record<string, string> = {};
      for (const [art, satz] of Object.entries(roh_)) {
        if (typeof satz === "string" && satz.trim()) ergebnis[art] = satz;
      }
      return ergebnis;
    })(),
    /* Die Pflichtfotos je Protokollart (0173). Fehlt die Spalte, steht die
       Datenbank vor 0173 — dann zeigt das Blatt Lucas Standard. */
    pflichtfotos: alsPflichtfotoliste(roh["required_photos"]),
  };
}

export async function einstellungenLesen(): Promise<Einstellungen> {
  if (ATTRAPPE) return gleich({ ...einstellungen, kalender: { ...einstellungen.kalender } });
  return alsEinstellungen(await hole<Roh>("/einstellungen"));
}

/**
 * **Die Vorbereitungsregel und wer von ihr abweicht** (`GET
 * /einstellungen/vorbereitung`, Migration 0138).
 *
 * Ohne die Liste wäre das Blatt eine Behauptung: „Vortag 06:00", während drei
 * Gerätegruppen längst etwas anderes tragen. Gezeigt wird, was **gesetzt** ist
 * — genau das, was sich hier wieder wegnehmen ließe.
 */
export type Vorbereitungsabweichung = {
  art: "gruppe" | "typ" | "maschine";
  id: string;
  label: string;
  /** `null` heißt: diese Stufe sagt nur das andere von beiden. */
  vorlauf_tage: number | null;
  uhrzeit: string | null;
  /** Worunter sie hängt — die Übergruppe, die Gruppe, der Typ. */
  ueber: string | null;
};

export type Vorbereitungsstand = {
  betrieb: { vorlauf_tage: number; uhrzeit: string };
  abweichungen: Vorbereitungsabweichung[];
};

export async function vorbereitungsstand(): Promise<Vorbereitungsstand> {
  if (ATTRAPPE) return gleich({ betrieb: { vorlauf_tage: 1, uhrzeit: "06:00" }, abweichungen: [] });
  const roh = await hole<Roh>("/einstellungen/vorbereitung");
  const b = alsObjekt(roh["betrieb"]);
  return {
    betrieb: { vorlauf_tage: zahl(b["vorlauf_tage"]) ?? 1, uhrzeit: txt(b["uhrzeit"]) || "06:00" },
    abweichungen: alsListe(roh["abweichungen"]).map((z): Vorbereitungsabweichung => ({
      art: txt(z["art"]) === "gruppe" ? "gruppe" : txt(z["art"]) === "typ" ? "typ" : "maschine",
      id: txt(z["id"]),
      label: txt(z["label"]),
      vorlauf_tage: zahl(z["vorlauf_tage"]),
      uhrzeit: txt(z["uhrzeit"]) || null,
      ueber: txt(z["ueber"]) || null,
    })),
  };
}

export async function einstellungenAendern(teil: Partial<Einstellungen>): Promise<Einstellungen> {
  if (ATTRAPPE) {
    einstellungen = {
      ...einstellungen, ...teil,
      kalender: { ...einstellungen.kalender, ...teil.kalender },
    };
    return gleich({ ...einstellungen, kalender: { ...einstellungen.kalender } }, 200);
  }
  const koerper: Record<string, unknown> = {};
  if (teil.wetter_ort !== undefined) koerper["wetterort"] = teil.wetter_ort;
  /* **Jedes Feld, das die Maske zeigt, geht auch hinaus.** Bis zum 16.09.2026
     standen hier nur USt-IdNr. und Wetterort — Firma, Straße und Ort ließen
     sich tippen und waren nach dem Neuladen wieder die alten. */
  if (teil.firma !== undefined) koerper["firma"] = teil.firma;
  if (teil.rechtsform !== undefined) koerper["rechtsform"] = teil.rechtsform;
  if (teil.ust_id !== undefined) koerper["ust_id"] = teil.ust_id;
  if (teil.steuernummer !== undefined) koerper["steuernummer"] = teil.steuernummer;
  if (teil.telefon !== undefined) koerper["telefon"] = teil.telefon;
  if (teil.email !== undefined) koerper["email"] = teil.email;
  if (teil.web !== undefined) koerper["web"] = teil.web;
  /* Die Anschrift ist EINE Spalte (`address`, jsonb) und wird als Ganzes
     geschrieben. Wer nur die Straße ändert, schickt deshalb PLZ, Ort und Land
     mit — `teil` trägt sie, weil die Maske immer den ganzen Stand schickt. */
  if (teil.strasse !== undefined || teil.plz !== undefined || teil.ort !== undefined) {
    koerper["anschrift"] = {
      strasse: teil.strasse ?? "",
      plz: teil.plz ?? "",
      ort: teil.ort ?? "",
      ...(teil.land ? { land: teil.land } : {}),
    };
  }
  if (teil.kalender?.vorgabe_kalender) {
    koerper["kalendertage"] = Number(teil.kalender.vorgabe_kalender.charAt(0));
  }
  if (teil.pflichtfelder) koerper["pflichtfelder"] = teil.pflichtfelder;
  if (teil.stunden_je_tag !== undefined) koerper["stunden_je_tag"] = teil.stunden_je_tag;
  if (teil.uebergabe_mit_fahrer !== undefined) {
    koerper["uebergabe_mit_fahrer"] = teil.uebergabe_mit_fahrer;
  }
  if (teil.qualifikation !== undefined) koerper["qualifikation"] = teil.qualifikation;
  if (teil.nachtanken_zuteilung !== undefined) koerper["nachtanken_zuteilung"] = teil.nachtanken_zuteilung;
  if (teil.vorbereitung_vorlauf_tage !== undefined) {
    koerper["vorbereitung_vorlauf_tage"] = teil.vorbereitung_vorlauf_tage;
  }
  if (teil.vorbereitung_uhrzeit !== undefined) koerper["vorbereitung_uhrzeit"] = teil.vorbereitung_uhrzeit;
  // Leer heißt aus (0286) — `null` geht ausdrücklich mit hinaus.
  if (teil.vorabend_ab !== undefined) koerper["vorabend_ab"] = teil.vorabend_ab;
  if (teil.morgen_bis !== undefined) koerper["morgen_bis"] = teil.morgen_bis;
  if (teil.vorabend_je_tag !== undefined) koerper["vorabend_je_tag"] = teil.vorabend_je_tag;
  // Leer heißt Vorgabe (0287) — `null` geht ausdrücklich mit hinaus.
  if (teil.feiertagsland !== undefined) koerper["feiertagsland"] = teil.feiertagsland;
  // Leer heißt aus (0138, G8) — `null` geht ausdrücklich mit hinaus.
  if (teil.mindestauftrag !== undefined) koerper["mindestauftrag"] = teil.mindestauftrag;
  if (teil.positionstexte !== undefined) koerper["positionstexte"] = teil.positionstexte;
  if (teil.pflichtfotos !== undefined) koerper["pflichtfotos"] = teil.pflichtfotos;
  return alsEinstellungen(await aendere<Roh>("/einstellungen", koerper));
}

/* ══════════════ Pflichtfotos, die draußen nicht gingen (0173) ══════════════

   Der Blick der Disposition unter „Zu prüfen": welche Pflichtfotos an einer
   Belegung fehlen, mit dem Grund, den der Fahrer gesagt hat — und der Knopf,
   der sagt, dass jemand hingesehen hat. */

export type Fotoausweg = {
  id: string;
  kontext: Fotoprotokoll;
  blickwinkel: Blickwinkel;
  grund: string;
  am: string;
  wer: string;
  gesehenAm: string | null;
  gesehenVon: string;
};

export type Fotoauswege = { fehlen: Fotoausweg[]; verlangt: Pflichtfotoliste };

function alsFotoauswege(roh: Roh): Fotoauswege {
  return {
    fehlen: alsListe(roh["fehlen"]).map((z): Fotoausweg => ({
      id: txt(z["id"]),
      kontext: (FOTOPROTOKOLLE as readonly string[]).includes(txt(z["kontext"]))
        ? txt(z["kontext"]) as Fotoprotokoll : "ausgabe",
      blickwinkel: (BLICKWINKEL as readonly string[]).includes(txt(z["blickwinkel"]))
        ? txt(z["blickwinkel"]) as Blickwinkel : "vorne",
      grund: txt(z["grund"]),
      am: txt(z["am"]),
      wer: txt(z["wer"]),
      gesehenAm: txt(z["gesehen_am"]) || null,
      gesehenVon: txt(z["gesehen_von"]),
    })),
    verlangt: alsPflichtfotoliste(roh["verlangt"]),
  };
}

export async function fotoauswege(belegung: string): Promise<Fotoauswege> {
  if (ATTRAPPE) return gleich({ fehlen: [], verlangt: alsPflichtfotoliste(null) });
  return alsFotoauswege(await hole<Roh>(`/pflichtfotos/fehlen?belegung=${encodeURIComponent(belegung)}`));
}

export async function fotoauswegeGesehen(belegung: string): Promise<Fotoauswege> {
  if (ATTRAPPE) return gleich({ fehlen: [], verlangt: alsPflichtfotoliste(null) });
  const roh = await sende<Roh>(`/pflichtfotos/fehlen/${encodeURIComponent(belegung)}/gesehen`, {});
  return { ...alsFotoauswege(roh), verlangt: alsPflichtfotoliste(null) };
}

/** Die Felder, die sich zur Pflicht machen lassen — in der Reihenfolge des Formulars. */
export const PFLICHTFELDER: Pflichtfeld[] = [
  "telefon", "ust_id", "steuernummer",
  "zusatz", "strasse", "plz", "ort",
  "rechnung_email", "zahlungsziel_tage",
  "ansprechpartner", "ansprechpartner_telefon", "ansprechpartner_email",
];

/** Was bisher fest im Formular galt — der Stand, wenn der Server nichts sagt. */
export const PFLICHT_VORGABE: Pflichtfeldliste = {
  kunde_privat: ["strasse", "plz", "ort"],
  kunde_gewerblich: ["strasse", "plz", "ort"],
  interessent: [],
  vermieter: ["strasse", "plz", "ort"],
};

/**
 * Was beim Anlegen ausgefüllt sein muss, je Rolle — und beim Kunden je Typ
 * (Migration 0098, seit 0187 mit `kunde_privat` und `kunde_gewerblich`).
 *
 * Über `/kunden/pflichtfelder` und nicht über `/einstellungen`: Die Einstellungen
 * darf nur die Verwaltung lesen, angelegt wird auch in der Disposition.
 */
export async function pflichtfelder(): Promise<Pflichtfeldliste> {
  if (ATTRAPPE) return gleich(PFLICHT_VORGABE);
  const roh = await hole<Roh>("/kunden/pflichtfelder");
  const liste = (wert: unknown): Pflichtfeld[] =>
    Array.isArray(wert) ? wert.filter((x): x is Pflichtfeld => PFLICHTFELDER.includes(x as Pflichtfeld)) : [];
  return {
    kunde_privat: liste(roh["kunde_privat"]),
    kunde_gewerblich: liste(roh["kunde_gewerblich"]),
    interessent: liste(roh["interessent"]),
    vermieter: liste(roh["vermieter"]),
  };
}

/* ══════════════ Der Zugang zu Lexware Office ══════════════

   Drei Wege, eine Form: Alle drei antworten mit demselben Stand,
   damit die Maske nach jedem Schritt nichts nachladen muss.

   **Der Schlüssel geht nur hinein, nie heraus.** Was zurückkommt,
   sind die letzten vier Zeichen — mehr braucht niemand, um zu
   erkennen, welcher hinterlegt ist. Deshalb wird hier auch nichts
   angeglichen: Die Antwort ist schon die Sicht der Oberfläche. */

function alsLexZugang(roh: Roh): LexZugang {
  const z = alsObjekt(roh["zuordnung"]);
  const paar = (name: string): { zu: number; alle: number } => {
    const p = alsObjekt(z[name]);
    return { zu: zahl(p["zu"]) ?? 0, alle: zahl(p["alle"]) ?? 0 };
  };
  return {
    zustand: (roh["zustand"] as LexZugang["zustand"]) ?? "ungepflegt",
    konto: txt(roh["konto"]) || null,
    geprueft_am: txt(roh["geprueft_am"]) || null,
    schluessel_gesetzt: roh["schluessel_gesetzt"] === true,
    schluessel_ende: txt(roh["schluessel_ende"]) || null,
    schluessel_quelle: (roh["schluessel_quelle"] as LexZugang["schluessel_quelle"]) ?? null,
    schluessel_unlesbar: roh["schluessel_unlesbar"] === true,
    hauptschluessel_da: roh["hauptschluessel_da"] === true,
    fehler: txt(roh["fehler"]) || null,
    vorrang: (roh["vorrang"] as LexVorrang) ?? "fragen",
    zuordnung: { kunden: paar("kunden"), artikel: paar("artikel"), belege: paar("belege") },
    wechsel: alsLexWechsel(roh["wechsel"]),
  };
}

function alsLexWechsel(wert: unknown): LexWechsel | null {
  if (!wert || typeof wert !== "object") return null;
  const w = alsObjekt(wert);
  return {
    seit: txt(w["seit"]),
    von_konto: txt(w["von_konto"]) || null,
    nach_konto: txt(w["nach_konto"]) || null,
    schluessel: txt(w["schluessel"]) === "geltend" ? "geltend" : "vorgemerkt",
  };
}

/** Der Stand der Attrappe: ohne API gibt es keinen Zugang, und das ist ehrlich. */
const LEX_ATTRAPPE: LexZugang = {
  zustand: "ungepflegt", konto: null, geprueft_am: null,
  schluessel_gesetzt: false, schluessel_ende: null, schluessel_quelle: null,
  schluessel_unlesbar: false, hauptschluessel_da: false, fehler: null, vorrang: "fragen",
  zuordnung: { kunden: { zu: 0, alle: 0 }, artikel: { zu: 0, alle: 0 }, belege: { zu: 0, alle: 0 } },
  wechsel: null,
};

export async function lexZugang(): Promise<LexZugang> {
  if (ATTRAPPE) return gleich(LEX_ATTRAPPE);
  return alsLexZugang(await hole<Roh>("/einstellungen/lexware"));
}

/** Schlüssel und/oder Vorrangregel setzen. Ein Schlüssel wird sofort geprüft. */
export async function lexZugangSetzen(
  teil: { schluessel?: string; vorrang?: LexVorrang },
): Promise<LexZugang> {
  if (ATTRAPPE) return gleich(LEX_ATTRAPPE, 400);
  return alsLexZugang(await ersetze<Roh>("/einstellungen/lexware", teil));
}

export async function lexZugangPruefen(): Promise<LexZugang> {
  if (ATTRAPPE) return gleich(LEX_ATTRAPPE, 400);
  return alsLexZugang(await sende<Roh>("/einstellungen/lexware/pruefen"));
}

/* Die Vorschau kommt so, wie der Server sie rechnet — die Form steht in
   `LexWechselVorschau` und in dienste/lexwechsel.ts, und eine zweite
   Übersetzung dazwischen wäre eine zweite Stelle, die auseinanderläuft. */

export async function lexWechselVorschau(): Promise<LexWechselVorschau> {
  if (ATTRAPPE) throw new Fehler("lexware.kein_wechsel", 409);
  return hole<LexWechselVorschau>("/einstellungen/lexware/wechsel");
}

/** Freigeben. `bestaetigt` ist Pflicht — ohne ihn weist der Server ab. */
export async function lexWechselFreigeben(): Promise<LexZugang> {
  if (ATTRAPPE) return gleich(LEX_ATTRAPPE, 400);
  const antwort = await sende<Roh>("/einstellungen/lexware/wechsel", { bestaetigt: true });
  return alsLexZugang(alsObjekt(antwort["zugang"]));
}

export async function lexWechselVerwerfen(): Promise<LexZugang> {
  if (ATTRAPPE) return gleich(LEX_ATTRAPPE, 400);
  return alsLexZugang(await loesche<Roh>("/einstellungen/lexware/wechsel"));
}

const STUFEN: readonly Passwortwechsel[] = ["nicht_noetig", "naechste_anmeldung", "sofort"];

function alsBenutzer(b: Roh): Benutzer {
  const konto = alsObjekt(b["konto"]);
  const gesperrt = txt(b["left_on"]) !== "";
  const stufe = txt(b["password_change"]) as Passwortwechsel;
  const passwortwechsel = STUFEN.includes(stufe) ? stufe : "nicht_noetig";
  return {
    id: txt(b["id"]),
    name: txt(b["name"]),
    rollen: (Array.isArray(b["rollen"]) ? b["rollen"] : []) as Rolle[],
    mfa: mfaStand({ user_id: b["user_id"], mfa_required: b["mfa_required"], konto: alsObjekt(b["konto"]) }),
    // Wer kein Konto hat, ist eingeladen und noch nicht drin. Das ist keine
    // Fehlmeldung, sondern der Normalfall für Fahrer ohne Zugang.
    //
    // **Gesperrt steht vorn**, seit die Verwaltung sperren kann: Ein
    // Gesperrter mit fälligem Passwortwechsel ist gesperrt, nicht fällig.
    zustand: gesperrt
      ? "gesperrt"
      : passwortwechsel !== "nicht_noetig" ? "wechsel_faellig" : b["user_id"] ? "" : "eingeladen",
    passwortwechsel,
    kuerzel: txt(b["initials"]),
    personalnummer: txt(b["staff_number"]),
    disponierbar: b["is_schedulable"] !== false,
    email: txt(konto["email"]) || null,
    mfa_pflicht: b["mfa_required"] === true,
    ausgeschieden_am: txt(b["left_on"]) || null,
    // Was am Ende gilt, und was davon von Hand gesetzt ist (0147). Gerechnet
    // hat es der Server; die Maske zeichnet nur.
    rechte: (Array.isArray(b["rechte"]) ? b["rechte"] : []) as Recht[],
    rechteEigen: alsObjekt(b["rechte_eigen"]) as Partial<Record<Recht, boolean>>,
  };
}

export async function benutzer(): Promise<Liste<Benutzer>> {
  if (ATTRAPPE) return gleich({ zeilen: A.BENUTZER, gesamt: A.BENUTZER.length });

  const roh = await hole<Roh>("/benutzer");
  const zeilen = alsListe(roh["zeilen"]).map(alsBenutzer);
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length };
}

/* ══════════════ Benutzer pflegen (15.09.2026) ══════════════

   „Ich kann gar nichts. Ich kann die User gar nicht bearbeiten. Ich
   kann auch keinen Benutzer anlegen, Passwort ändern, MFA
   deaktivieren." (Luca, 15.09.2026). Die Routen gab es zum Teil
   schon — was fehlte, war jeder Weg dorthin.

   **Das Passwort geht nur hinein.** Es entsteht in der Maske, geht
   einmal hier durch und kommt nirgends zurück: nicht in der Antwort,
   nicht im Protokoll, nicht in `auth_event.note`. */

/** Was aus der Maske kommt, in den Rumpf, den die Route erwartet. */
function benutzerRumpf(eingabe: Partial<BenutzerEingabe>): Record<string, unknown> {
  const rumpf: Record<string, unknown> = {};
  if (eingabe.name !== undefined) rumpf["name"] = eingabe.name;
  if (eingabe.email !== undefined) rumpf["email"] = eingabe.email;
  if (eingabe.rollen !== undefined) rumpf["rollen"] = eingabe.rollen;
  if (eingabe.kuerzel !== undefined) rumpf["kuerzel"] = eingabe.kuerzel;
  if (eingabe.personalnummer !== undefined) rumpf["personalnummer"] = eingabe.personalnummer;
  if (eingabe.disponierbar !== undefined) rumpf["disponierbar"] = eingabe.disponierbar;
  if (eingabe.mfa_pflicht !== undefined) rumpf["mfa_pflicht"] = eingabe.mfa_pflicht;
  if (eingabe.passwort !== undefined) rumpf["passwort"] = eingabe.passwort;
  if (eingabe.passwortwechsel !== undefined) rumpf["passwortwechsel"] = eingabe.passwortwechsel;
  // `null` ist hier ein Wert und kein „nicht gesetzt": Es entsperrt.
  if ("ausgeschieden_am" in eingabe) rumpf["ausgeschieden_am"] = eingabe.ausgeschieden_am;
  /* Die Abweichung von den Bündeln (0147): `{ start: false }` entzieht,
     `{ start: null }` nimmt die Abweichung zurück. `null` ist auch hier ein
     Wert und muss deshalb mitgehen. */
  if (eingabe.rechteEigen !== undefined) rumpf["rechte_eigen"] = eingabe.rechteEigen;
  return rumpf;
}

export async function benutzerAnlegen(eingabe: BenutzerEingabe): Promise<Roh> {
  if (ATTRAPPE) return gleich({}, 400);
  return sende<Roh>("/benutzer", benutzerRumpf(eingabe));
}

export async function benutzerAendern(
  id: string, teil: Partial<BenutzerEingabe>,
): Promise<Benutzer> {
  if (ATTRAPPE) return gleich(A.BENUTZER[0] as Benutzer, 400);
  return alsBenutzer(await aendere<Roh>(`/benutzer/${id}`, benutzerRumpf(teil)));
}

/** Die Verwaltung vergibt eines und sagt dazu, ob und wann es zu wechseln ist. */
export async function benutzerPasswort(
  id: string, passwort: string, passwortwechsel: Passwortwechsel,
): Promise<void> {
  if (ATTRAPPE) return gleich(undefined, 400);
  await sende<void>(`/benutzer/${id}/passwort`, { passwort, passwortwechsel });
}

/**
 * Mensch und Konto entfernen (16.09.2026) — nur, wenn nichts an ihm hängt.
 * Sonst `409 benutzer.in_gebrauch`, und die Daten sagen, was im Weg ist.
 */
export async function benutzerLoeschen(id: string): Promise<void> {
  if (ATTRAPPE) return gleich(undefined, 400);
  await loesche<void>(`/benutzer/${encodeURIComponent(id)}`);
}

/* ══════════════ Rollen und Rechte (Migration 0150) ══════════════

   Luca, 18.09.2026: *„Da machst du eine richtige Rechteverwaltung, wo man
   sagen kann, welche Rolle welche Module sehen kann, sodass wir das einmal
   richtig einstellen können."*

   **Jede dieser Routen antwortet mit der ganzen Tafel.** Eine Änderung an
   einem Bündel kann die Sortierung der Zeilen und die Zahl der Menschen
   daneben betreffen; die halbe Antwort zusammenzusetzen wäre der zweite
   Stand, aus dem „bei mir steht aber etwas anderes" kommt. */

function alsRollentafel(roh: Roh): Rollentafel {
  return {
    rechte: (Array.isArray(roh["rechte"]) ? roh["rechte"] : []) as Recht[],
    rollen: alsListe(roh["rollen"]).map((r): Rollenzeile => ({
      code: txt(r["code"]),
      label: txt(r["label"]),
      labelKey: txt(r["label_key"]) || null,
      sortierung: zahl(r["sortierung"]) ?? 100,
      aktiv: r["aktiv"] !== false,
      menschen: zahl(r["menschen"]) ?? 0,
    })),
    buendel: Object.fromEntries(
      Object.entries(alsObjekt(roh["buendel"])).map(([rolle, rechte]) => [
        rolle, (Array.isArray(rechte) ? rechte : []) as Recht[],
      ]),
    ),
  };
}

/** Die Tafel: welche Rollen es gibt, welche Rechte, und wo ein Haken steht. */
export async function rollentafel(): Promise<Rollentafel> {
  if (ATTRAPPE) return gleich({ rechte: [], rollen: [], buendel: {} });
  return alsRollentafel(await hole<Roh>("/benutzer/rechte"));
}

/**
 * Was eine Rolle bündelt — **für alle mit dieser Rolle**, ab dem nächsten
 * Kontext, also spätestens nach zwei Minuten.
 */
export async function rollenBuendelSetzen(rolle: string, rechte: Recht[]): Promise<Rollentafel> {
  if (ATTRAPPE) return gleich({ rechte: [], rollen: [], buendel: {} }, 400);
  return alsRollentafel(await ersetze<Roh>(`/benutzer/rechte/${encodeURIComponent(rolle)}`, { rechte }));
}

/** Eine neue Rolle. Der Code wird aus der Bezeichnung abgeleitet, nicht getippt. */
export async function rolleAnlegen(
  eingabe: { bezeichnung: string; sortierung?: number; rechte?: Recht[] },
): Promise<Rollentafel> {
  if (ATTRAPPE) return gleich({ rechte: [], rollen: [], buendel: {} }, 400);
  return alsRollentafel(await sende<Roh>("/benutzer/rollen", { ...eingabe }));
}

/** Umbenennen, umsortieren, stilllegen. Die mitgelieferten fünf nur das Letzte. */
export async function rolleAendern(
  code: string,
  teil: { bezeichnung?: string; sortierung?: number; aktiv?: boolean },
): Promise<Rollentafel> {
  if (ATTRAPPE) return gleich({ rechte: [], rollen: [], buendel: {} }, 400);
  return alsRollentafel(await aendere<Roh>(`/benutzer/rollen/${encodeURIComponent(code)}`, teil));
}

/** Nur, solange sie niemand trägt — sonst `409 rolle.in_gebrauch`. */
export async function rolleLoeschen(code: string): Promise<void> {
  if (ATTRAPPE) return gleich(undefined, 400);
  await loesche<void>(`/benutzer/rollen/${encodeURIComponent(code)}`);
}

/* ══════════════ Die Kalenderfarben ══════════════ */

function alsFarbpaar(f: Roh): Farbpaar {
  const wort = txt(f["wort"]);
  const basis = txt(f["basis"]);
  return {
    id: txt(f["id"]),
    // `exactOptionalPropertyTypes`: Ein Feld, das `undefined` trägt, ist
    // etwas anderes als ein Feld, das fehlt — also fällt es weg.
    ...(wort ? { wort } : {}),
    ...(basis ? { basis } : {}),
    fuell: txt(f["fuell"]),
    kante: txt(f["kante"]),
    text: txt(f["text"]),
    dunkel: txt(f["dunkel"]),
    kante_d: txt(f["kante_d"]),
  };
}

function alsFarben(roh: Roh): Kalenderfarben {
  const f = alsObjekt(roh["farben"]);
  return {
    farben: {
      miete: alsFarbpaar(alsObjekt(f["miete"])),
      leistung: alsFarbpaar(alsObjekt(f["leistung"])),
    },
    vorgabe: roh["vorgabe"] !== false,
    wahl: alsListe(roh["wahl"]).map(alsFarbpaar),
  };
}

export async function kalenderfarben(): Promise<Kalenderfarben> {
  /* **Im Attrappenbetrieb gar nicht erst fragen** (18.09.2026). Diese Route
     hatte als einzige keinen Zweig dafür: Der Aufruf ging gegen die echte
     API, kam mit 401 zurück — und ein 401 beendet die Sitzung (`klient.ts`).
     Wer sich mit `VITE_ATTRAPPE=1` anmeldete, stand Sekunden später wieder
     auf der Anmeldemaske, ohne dass etwas im Bild darauf hindeutete. Das hat
     beim Prüfen der Handy-Ansicht eine Stunde gekostet.

     Die Vorgabe ist dieselbe, die die Anwendung ohne gepflegte Farben
     zeichnet — `vorgabe: true` heißt „der Betrieb hat nichts gesetzt". */
  if (ATTRAPPE) {
    return alsFarben({ farben: {}, vorgabe: true, wahl: [] });
  }
  return alsFarben(await hole<Roh>("/farben"));
}

/**
 * Setzen — je Geschäftsart die ID eines geprüften Paares **oder** eine frei
 * gewählte Farbe als `#RRGGBB`. Aus der freien Farbe rechnet der Server das
 * Paar; übernommen wird der Farbton, nicht die Helligkeit.
 */
export async function kalenderfarbenSetzen(
  teil: Partial<Record<"miete" | "leistung", string>>,
): Promise<Kalenderfarben> {
  return alsFarben(await ersetze<Roh>("/farben", teil));
}

/** Auf Vorgabe zurück — die Spalte wird geleert, nicht gefüllt. */
export async function kalenderfarbenZurueck(): Promise<Kalenderfarben> {
  return alsFarben(await loesche<Roh>("/farben"));
}

/* ══════════════ Nummernkreise ══════════════ */

function alsNummernkreise(roh: Roh): Nummernkreis[] {
  return alsListe(roh["zeilen"]).map((n): Nummernkreis => ({
    art: txt(n["kind"]),
    vorsatz: txt(n["prefix"]),
    mit_jahr: n["with_year"] === true,
    stellen: zahl(n["digits"]) ?? 4,
    stand: zahl(n["current_no"]) ?? 0,
    jahr: zahl(n["year"]),
    vorschau: txt(n["vorschau"]),
  }));
}

export async function nummernkreise(): Promise<Nummernkreis[]> {
  if (ATTRAPPE) return gleich([]);
  return alsNummernkreise(await hole<Roh>("/einstellungen/nummern"));
}

export async function nummernkreisAendern(
  art: string,
  /* `grund` ist Pflicht, sobald sich wirklich etwas ändert (Migration 0157) —
     der Server weist einen leeren Grund mit `422` ab und schreibt ihn nach
     `number_range_change`. Optional steht er hier, weil ein `PATCH`, der
     nichts ändert, auch keinen braucht. */
  teil: { vorsatz?: string; mit_jahr?: boolean; stellen?: number; stand?: number; grund?: string },
): Promise<Nummernkreis[]> {
  if (ATTRAPPE) return gleich([], 400);
  return alsNummernkreise(await aendere<Roh>(`/einstellungen/nummern/${art}`, teil));
}

/* Wofür `alsTag` hier gebraucht wird: Die Schnellerfassung übergibt Tage,
   nicht Spalten — sie steht außerhalb des Bretts. */
export { alsTag };

/* ══════════════════════════════════════════════════════════════════
   Stammdaten pflegen (15.09.2026)

   Bis heute war dieser Abschnitt nur lesend. Die Listen standen, und
   keine davon ließ sich öffnen — „Ich kann die Ressourcen gar nicht
   öffnen" (Luca, 15.09.2026).

   Dieselbe Übersetzungsregel wie oben gilt weiter: **Die Serversicht
   kommt nicht in die Maske.** Der Rumpf einer Schreibanfrage ist
   deutsch (`inventarnummer`, `naechste_pruefung`), die Antwort trägt
   Spaltennamen, und übersetzt wird hier — an einer Stelle.
   ══════════════════════════════════════════════════════════════════ */

function alsMaschineAkte(m: Roh): MaschineAkte {
  return {
    id: txt(m["id"]),
    typ_id: txt(m["machine_type_id"]),
    typ: txt(m["typ"]),
    gruppe: txt(m["gruppe"]),
    nummer: txt(m["asset_number"]),
    seriennummer: txt(m["serial_number"]),
    kennzeichen: txt(m["plate"]),
    baujahr: zahl(m["year_built"]),
    betriebsstunden: zahl(m["operating_hours"]),
    standort_id: txt(m["location_id"]) || null,
    ort: txt(m["ort"]),
    status: txt(m["status"]),
    letzte_pruefung: txt(m["last_safety_check"]) || null,
    naechste_pruefung: txt(m["next_safety_check"]) || null,
    letzte_hu: txt(m["last_inspection"]) || null,
    naechste_hu: txt(m["next_inspection"]) || null,
    anschaffung_am: txt(m["acquired_on"]) || null,
    anschaffung_wert: zahl(m["acquisition_value"]),
    uvv_monate: zahl(m["safety_check_months"]),
    hu_monate: zahl(m["inspection_months"]),
    vorlauf_tage: zahl(m["vorlauf_tage"]),
    zubehoer_art: alsZubehoerart(m["accessory_mode"]),
    // Womit dieser Typ arbeitet und wovon sich dieses Stück ausnimmt (0154).
    // Fehlen die Felder — eine Antwort von vor der Migration —, bleiben die
    // Listen leer und der Abschnitt zeigt „nichts zugeordnet".
    leistungen: alsListe(m["leistungen"]).map((l) => ({
      id: txt(l["id"]), code: txt(l["code"]), label: txt(l["label"]),
      ebene: txt(l["ebene"]) === "typ" ? "typ" as const : "gruppe" as const,
      pflicht: l["pflicht"] === true,
    })),
    leistungsausnahmen: alsListe(m["leistungsausnahmen"]).map((l) => ({
      id: txt(l["id"]), code: txt(l["code"]), label: txt(l["label"]), grund: txt(l["grund"]),
    })),
    stammfahrer_id: txt(m["default_operator_id"]) || null,
    stammfahrer: txt(m["stammfahrer"]),
    stammfahrer_kuerzel: txt(m["stammfahrer_kuerzel"]),
    vorbereitung_vorlauf: zahl(m["vorbereitung_vorlauf_eigen"]),
    vorbereitung_uhrzeit: txt(m["vorbereitung_uhrzeit_eigen"]) || null,
    vorbereitung: alsVorbereitungsregel(m["vorbereitung"]),
    puffer: alsPufferstand(m["puffer"]),
    masse: {
      gewicht_kg: zahl(m["weight_kg"]),
      laenge_mm: zahl(m["length_mm"]),
      breite_mm: zahl(m["width_mm"]),
      hoehe_mm: zahl(m["height_mm"]),
    },
    typ_masse: {
      gewicht_kg: zahl(m["typ_weight_kg"]),
      laenge_mm: zahl(m["typ_length_mm"]),
      breite_mm: zahl(m["typ_width_mm"]),
      hoehe_mm: zahl(m["typ_height_mm"]),
    },
    fremdgeraet: m["is_external"] === true,
    vermieter_id: txt(m["vendor_id"]) || null,
    vermieter: txt(m["vermieter"]),
    verfuegbar_ab: txt(m["available_from"]) || null,
    verfuegbar_bis: txt(m["available_until"]) || null,
    einkaufspreis: zahl(m["purchase_price_net"]),
    vertragsnummer: txt(m["vendor_contract"]),
    notiz: txt(m["note"]),
    aktiv: m["active"] !== false,
  };
}

function alsZubehoerart(wert: unknown): Zubehoerart | null {
  return wert === "anbaupflichtig" || wert === "eigenstaendig" ? wert : null;
}

/** Maß und Gewicht aus einer Serverantwort — die Feldnamen sind schon deutsch. */
function alsMasse(q: Roh): Masse {
  return {
    gewicht_kg: zahl(q["gewicht_kg"]),
    laenge_mm: zahl(q["laenge_mm"]),
    breite_mm: zahl(q["breite_mm"]),
    hoehe_mm: zahl(q["hoehe_mm"]),
  };
}

const HINDERNISSE = [
  "belegt", "werkstatt", "ausserhalb", "nicht_disponierbar", "ausgetreten",
  "qualifikation_fehlt", "qualifikation_abgelaufen",
] as const;

function alsHindernis(wert: unknown): Hindernis | null {
  return HINDERNISSE.includes(wert as Hindernis) ? (wert as Hindernis) : null;
}

function alsPersonAkte(p: Roh): PersonAkte {
  return {
    id: txt(p["id"]),
    name: txt(p["name"]),
    kuerzel: txt(p["initials"]),
    personalnummer: txt(p["staff_number"]),
    disponierbar: p["is_schedulable"] !== false,
    aktiv: p["active"] !== false,
    eintritt: txt(p["joined_on"]) || null,
    austritt: txt(p["left_on"]) || null,
    rollen: (Array.isArray(p["rollen"]) ? p["rollen"] : []) as Rolle[],
    qualifikationen: alsListe(p["qualifikationen"]).map((q): PersonQual => ({
      code: txt(q["qualifikation"]),
      label: txt(q["label"]),
      erworben: txt(q["erworben"]) || null,
      gueltig_bis: txt(q["gueltig_bis"]) || null,
    })),
  };
}

export async function stammMaschine(id: string): Promise<MaschineAkte> {
  return alsMaschineAkte(await hole<Roh>(`/stamm/maschinen/${encodeURIComponent(id)}`));
}

/** Was eine Maschinenakte an die API schickt. Alle Felder sind freiwillig —
 *  ein PATCH ohne ein Feld lässt es in Ruhe (34-api.md). */
export type MaschineEingabe = Partial<{
  typ_id: string; inventarnummer: string; seriennummer: string | null;
  kennzeichen: string | null; baujahr: number | null; betriebsstunden: number | null;
  standort_id: string | null; status: string; letzte_pruefung: string | null;
  naechste_pruefung: string | null; letzte_hu: string | null; naechste_hu: string | null;
  anschaffung_am: string | null; anschaffung_wert: number | null;
  /** Der Stammfahrer — `null` nimmt ihn weg (Migration 0070). */
  stammfahrer_id: string | null;
  /** Maß und Gewicht **dieses Stücks**. `null` heißt: es gilt wieder der Typ. */
  gewicht_kg: number | null; laenge_mm: number | null;
  breite_mm: number | null; hoehe_mm: number | null;
  /* ── Das Fremdgerät (Migration 0080) ──────────────────────────────
     `fremdgeraet: false` räumt die vier anderen Felder mit aus — das macht
     die Route, nicht die Maske: Ein Vermieter an einem Gerät, das uns
     gehört, ist die Angabe, bei der jemand beim nächsten Rückruf falsch
     anruft. */
  fremdgeraet: boolean;
  vermieter_id: string | null;
  verfuegbar_ab: string | null; verfuegbar_bis: string | null;
  einkaufspreis: number | null; vertragsnummer: string | null;
  notiz: string | null; aktiv: boolean;
  /** Ab wann DIESES Stück vorbereitet wird. `null` heißt „erbt" (0138). */
  vorbereitung_vorlauf_tage: number | null;
  vorbereitung_uhrzeit: string | null;
  /** Der Transportpuffer dieses Stücks in Stunden (E16). `null` = erbt. */
  puffer_vor_stunden: number | null;
  puffer_nach_stunden: number | null;
  /**
   * **Welche Leistungen dieses Stück nicht macht** (Migration 0154) — die
   * Liste ist der Stand. Nur Ausnahmen: Zuwählen kann ein Individuum nicht,
   * das geht an Gerätegruppe oder Maschinentyp.
   */
  leistungsausnahmen: { leistung_id: string; grund?: string | null }[];
}>;

/**
 * Was beim Disponieren zu einem Gerät gehört: der Stammfahrer und der
 * Auflieger — **mit der Auskunft, ob es im Zeitraum geht**.
 *
 * Ein Vorschlag, keine Buchung. Wer nicht frei ist, kommt mit `frei: false`
 * und dem Grund zurück und nicht gar nicht: Ein leeres Feld sagt nicht, dass
 * es einen Stammfahrer gibt, der gerade im Urlaub ist.
 */
export async function maschinenVorschlag(
  id: string, von: string, bis: string,
): Promise<Geraetevorschlag> {
  const q = new URLSearchParams({ von, bis });
  const roh = await hole<Roh>(`/stamm/maschinen/${encodeURIComponent(id)}/vorschlag?${q}`);
  const f = roh["fahrer"] as Roh | null;
  return {
    /* **Die Frage vor der Frage nach dem Namen** (Migration 0083): Muss hier
       überhaupt jemand mit, und wenn ja, mit welcher Qualifikation? Steht sie
       nicht in der Antwort, muss die Maske sie erraten — und rät bei jedem
       Gerät gleich. */
    bedienung: alsBedienung(roh["bedienung"]),
    fahrer: f
      ? {
          id: txt(f["id"]),
          name: txt(f["name"]),
          kuerzel: txt(f["kuerzel"]),
          frei: f["frei"] === true,
          grund: alsHindernis(f["grund"]),
          // Der Tag, nicht der Zeitstempel: In der Maske steht „belegt bis
          // 17.09." und nicht „bis 17.09. 16:00" — dieselbe Kürzung wie beim
          // Zubehör, damit beide Auskünfte gleich klingen.
          belegt_bis: tagAusZeitpunkt(f["belegt_bis"]) || null,
          qualifikation: txt(f["qualifikation"]) || null,
          gueltig_bis: tagAusZeitpunkt(f["gueltig_bis"]) || null,
        }
      : null,
    /* Wer fahren kann — alle, mit dem Grund, warum einer nicht geht
       (Systemprüfung A-06/E-02). */
    bediener: alsListe(roh["bediener"]).map(alsBedienerkandidat),
    gespann: alsListe(roh["gespann"]).map((g): Aufliegervorschlag => ({
      id: txt(g["id"]),
      typ_id: txt(g["typ_id"]),
      label: txt(g["label"]),
      vorschlag: g["vorschlag"] === true,
      notiz: txt(g["notiz"]),
      masse: alsMasse((g["masse"] ?? {}) as Roh),
      stuecke: alsListe(g["stuecke"]).map((s): Aufliegerstueck => ({
        id: txt(s["id"]),
        kennung: txt(s["kennung"]),
        frei: s["frei"] === true,
        grund: alsHindernis(s["grund"]),
        belegt_bis: tagAusZeitpunkt(s["belegt_bis"]) || null,
      })),
      frei: zahl(g["frei"]) ?? 0,
    })),
    masse: alsMasse((roh["masse"] ?? {}) as Roh),
  };
}

export async function maschineAendern(id: string, felder: MaschineEingabe): Promise<MaschineAkte> {
  return alsMaschineAkte(await aendere<Roh>(`/stamm/maschinen/${encodeURIComponent(id)}`, felder));
}

export async function maschineAnlegen(felder: MaschineEingabe): Promise<MaschineAkte> {
  return alsMaschineAkte(await sende<Roh>("/stamm/maschinen", felder));
}

export async function stammPerson(id: string): Promise<PersonAkte> {
  return alsPersonAkte(await hole<Roh>(`/stamm/personal/${encodeURIComponent(id)}`));
}

export type PersonEingabe = Partial<{
  name: string; kuerzel: string | null; personalnummer: string | null;
  disponierbar: boolean; aktiv: boolean; eintritt: string | null; austritt: string | null;
  rollen: string[];
  qualifikationen: { code: string; erworben: string | null; gueltig_bis: string | null }[];
}>;

export async function personAendern(id: string, felder: PersonEingabe): Promise<PersonAkte> {
  return alsPersonAkte(await aendere<Roh>(`/stamm/personal/${encodeURIComponent(id)}`, felder));
}

export async function personAnlegen(felder: PersonEingabe): Promise<PersonAkte> {
  return alsPersonAkte(await sende<Roh>("/stamm/personal", felder));
}

/* ── Zubehör und Prüfintervalle am Typ ───────────────────────────── */

function alsTypakte(t: Roh): Typakte {
  return {
    id: txt(t["id"]),
    label: txt(t["label"]),
    gruppe: txt(t["gruppe"]),
    gruppe_id: txt(t["gruppe_id"]),
    uebergruppe: txt(t["uebergruppe"]) || null,
    uebergruppe_id: txt(t["uebergruppe_id"]) || null,
    gruppe_ordnung: zahl(t["gruppe_ordnung"]) ?? 0,
    untergruppe_ordnung: zahl(t["untergruppe_ordnung"]) ?? 0,
    aktiv: t["active"] !== false,
    art: alsZubehoerart(t["art"]),
    uvv_monate: zahl(t["uvv_monate"]),
    hu_monate: zahl(t["hu_monate"]),
    bestand: zahl(t["bestand"]) ?? 0,
    ...(t["pool"] === true ? { pool: true } : {}),
    eigener_tarif: t["eigener_tarif"] === true,
    preismodell: txt(t["preismodell"]) || null,
    bedienung_eigen: alsBedienungsart(t["bedienung_eigen"]),
    abholbar_eigen: typeof t["abholbar_eigen"] === "boolean" ? t["abholbar_eigen"] : null,
    abholung: alsAbholung(t["abholung"]),
    bedienung: alsBedienung(t["bedienung"]),
    vorbereitung_vorlauf: zahl(t["vorbereitung_vorlauf_eigen"]),
    vorbereitung_uhrzeit: txt(t["vorbereitung_uhrzeit_eigen"]) || null,
    vorbereitung: alsVorbereitungsregel(t["vorbereitung"]),
    puffer: alsPufferstand(t["puffer"]),
    varianten: alsListe(t["varianten"]).map((x) => ({ id: txt(x["id"]), label: txt(x["label"]) })),
    passend: alsListe(t["passend"]).map((z): Zuordnung => ({
      id: txt(z["id"]),
      zubehoer_typ_id: txt(z["zubehoer_typ_id"]),
      label: txt(z["label"]),
      art: alsZubehoerart(z["art"]) ?? "anbaupflichtig",
      variante_id: txt(z["variante_id"]) || null,
      variante: txt(z["variante"]) || null,
      pflicht: z["pflicht"] === true,
      bestand: zahl(z["bestand"]) ?? 0,
    })),
    traeger: alsListe(t["traeger"]).map((z) => ({
      typ_id: txt(z["typ_id"]),
      label: txt(z["label"]),
      variante: txt(z["variante"]) || null,
      pflicht: z["pflicht"] === true,
    })),
    masse: alsMasse(t),
    gespann: alsListe(t["gespann"]).map((g): Gespannzeile => ({
      id: txt(g["id"]),
      auflieger_typ_id: txt(g["auflieger_typ_id"]),
      label: txt(g["label"]),
      vorschlag: g["vorschlag"] === true,
      notiz: txt(g["notiz"]),
      masse: alsMasse(g),
      bestand: zahl(g["bestand"]) ?? 0,
    })),
    zieht_von: alsListe(t["zieht_von"]).map((z) => ({
      typ_id: txt(z["typ_id"]),
      label: txt(z["label"]),
      vorschlag: z["vorschlag"] === true,
    })),
  };
}

/** Alle Typen mit Zubehörart, Prüfintervallen und Zuordnungen. */
export async function zubehoerTypen(): Promise<Liste<Typakte>> {
  if (ATTRAPPE) return gleich({ zeilen: [], gesamt: 0 });
  const roh = await hole<Roh>("/zubehoer/typen");
  const zeilen = alsListe(roh["zeilen"]).map(alsTypakte);
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length };
}

/**
 * Ein Anbauteil anlegen.
 *
 * `traeger_typ_id` hängt es gleich an den Trägertyp — *„Eine Schaufel mache
 * ich auf den Maschinentyp Manitou"* (Luca, 16.09.2026). Beides in einem
 * Aufruf, damit kein Anbauteil ohne Träger stehen bleibt, wenn der zweite
 * scheitert. `pflicht` ist ohne Angabe vorbelegt: was nur mit einem Träger
 * hinausgeht, ist für diesen Träger meist Pflicht.
 */
export type ZubehoertypEingabe = {
  bezeichnung: string;
  gruppe_id: string;
  art: Zubehoerart;
  traeger_typ_id?: string;
  pflicht?: boolean;
};

export async function zubehoerTypAnlegen(felder: ZubehoertypEingabe): Promise<Typakte> {
  return alsTypakte(await sende<Roh>("/zubehoer/typen", felder));
}

/** Art und Intervalle. `null` leert — ein Feld, das fehlt, bleibt in Ruhe. */
export type TypEingabe = Partial<{
  art: Zubehoerart | null; uvv_monate: number | null; hu_monate: number | null;
}>;

export async function zubehoerTypAendern(id: string, felder: TypEingabe): Promise<Typakte> {
  return alsTypakte(await aendere<Roh>(`/zubehoer/typen/${encodeURIComponent(id)}`, felder));
}

export type ZuordnungEingabe = { zubehoer_typ_id: string; variante_id: string | null; pflicht: boolean };

/** Das passende Zubehör eines Trägers — als ganze Menge. */
export async function zubehoerPassendSetzen(id: string, passend: ZuordnungEingabe[]): Promise<Typakte> {
  return alsTypakte(
    await ersetze<Roh>(`/zubehoer/typen/${encodeURIComponent(id)}/passend`, { passend }),
  );
}

/**
 * Die **Trägergeräte** eines Anbauteils — dieselbe Menge wie `passend`, von der
 * anderen Seite gepflegt, **ein Träger je Aufruf** (Nachprüfung P6,
 * 05.10.2026). Bis hierher ging die ganze Liste per `PUT`, und was darin
 * fehlte, wurde gelöscht — auch, was nur in einer veralteten Maske fehlte.
 *
 * Nur die Kennung, keine Variante und keine Pflicht: Beides gehört dem
 * Träger („Kran MKG 401 **mit Arbeitskorb**"), nicht dem Anbauteil, und ist von
 * dieser Seite nicht zu beantworten. Ein neuer Träger kommt ohne beides; die
 * übrigen Zuordnungen behalten Kennung, Reihenfolge, Variante und Pflicht.
 */
export async function zubehoerTraegerDazu(id: string, traeger: string): Promise<Typakte> {
  return alsTypakte(
    await sende<Roh>(`/zubehoer/typen/${encodeURIComponent(id)}/traeger`, { traeger_typ_id: traeger }),
  );
}

/** Einen Träger von diesem Anbauteil nehmen — nur diesen. */
export async function zubehoerTraegerWeg(id: string, traeger: string): Promise<Typakte> {
  return alsTypakte(
    await loesche<Roh>(`/zubehoer/typen/${encodeURIComponent(id)}/traeger/${encodeURIComponent(traeger)}`),
  );
}

/**
 * **Was sich an den Trägern geändert hat** — gegen den Stand, mit dem die
 * Maske geöffnet wurde. Nur das geht an die API: Ein Träger, den inzwischen
 * jemand anders dazugenommen hat, steht nicht in `vorher` und nicht in
 * `jetzt` — er bleibt, statt still zu verschwinden.
 */
export function traegerAenderung(vorher: string[], jetzt: string[]): { dazu: string[]; weg: string[] } {
  return {
    dazu: jetzt.filter((t) => !vorher.includes(t)),
    weg: vorher.filter((t) => !jetzt.includes(t)),
  };
}

/**
 * Eine Zeile des Gespanns. **Nicht `ZuordnungEingabe`** — das Wort hier heißt
 * `vorschlag` und nicht `pflicht`: Eine Zugmaschine ohne Auflieger ist kein
 * Fehler, ein Träger ohne Pflichtanbauteil schon (Migration 0072).
 */
export type GespannEingabe = {
  auflieger_typ_id: string;
  vorschlag: boolean;
  notiz: string | null;
};

/** Die Auflieger einer Zugmaschine — als ganze Menge, wie `passend`. */
export async function zubehoerGespannSetzen(id: string, gespann: GespannEingabe[]): Promise<Typakte> {
  return alsTypakte(
    await ersetze<Roh>(`/zubehoer/typen/${encodeURIComponent(id)}/gespann`, { gespann }),
  );
}

/** Was an einen Typ passt und welche Stücke davon im Zeitraum frei sind. */
export async function zubehoerAuswahl(typ: string, von: string, bis: string): Promise<Zubehoerlage[]> {
  if (ATTRAPPE) return gleich([]);
  const frage = new URLSearchParams({ typ, von, bis });
  const roh = await hole<Roh>(`/zubehoer/auswahl?${frage.toString()}`);
  return alsListe(roh["zeilen"]).map((z): Zubehoerlage => ({
    zuordnung_id: txt(z["zuordnung_id"]),
    zubehoer_typ_id: txt(z["zubehoer_typ_id"]),
    label: txt(z["label"]),
    art: alsZubehoerart(z["art"]) ?? "anbaupflichtig",
    variante: txt(z["variante"]) || null,
    pflicht: z["pflicht"] === true,
    eigener_tarif: z["eigener_tarif"] === true,
    stuecke: alsListe(z["stuecke"]).map((s): Zubehoerstueck => ({
      id: txt(s["id"]),
      kennung: txt(s["kennung"]),
      frei: s["frei"] === true,
      /* **Alle vier Gründe, nicht zwei.** `ausserhalb` (0080) und
         `reserviert` (18.09.2026) fielen hier bis dahin auf `null` — das Stück
         stand dann gesperrt in der Auswahl, ohne zu sagen warum, und die
         Zubehörzeile schwieg über das, was der Server längst antwortet
         (`dienste/belegung.ts`, `zubehoerLage`). */
      grund: s["grund"] === "belegt" || s["grund"] === "werkstatt"
        || s["grund"] === "ausserhalb" || s["grund"] === "reserviert"
        ? s["grund"]
        : null,
      belegt_bis: tagAusZeitpunkt(s["belegt_bis"]) || null,
      vorgemerkt: s["vorgemerkt"] === true,
    })),
    frei: zahl(z["frei"]) ?? 0,
  }));
}

/* ── Tarife ──────────────────────────────────────────────────────── */

function alsTarif(r: Roh): Tarif {
  return {
    id: txt(r["id"]),
    einheit: txt(r["unit"]),
    ab_tag: zahl(r["from_day"]) ?? 1,
    netto: zahl(r["price_net"]),
    mindest_netto: zahl(r["min_charge_net"]),
    frei_einheiten: zahl(r["included_units"]),
    mehrstunde_netto: zahl(r["excess_hour_net"]),
    variante_id: txt(r["variant_id"]) || null,
    preisliste_id: txt(r["price_list_id"]) || null,
  };
}

/** `typ_id` ODER `gruppe_id` — ein Satz hängt am Typ oder an einer Gruppe. */
export type TarifEingabe = Partial<{
  typ_id: string; gruppe_id: string; variante_id: string | null; einheit: string; ab_tag: number;
  /** Nur beim ersten Satz einer Gruppe: ihre Preisart, gleich mit gesetzt. */
  preismodell: string;
  netto: number | null; mindest_netto: number | null; frei_einheiten: number | null;
  mehrstunde_netto: number | null;
}>;

export async function tarifAnlegen(felder: TarifEingabe): Promise<Tarif> {
  return alsTarif(await sende<Roh>("/stamm/tarife", felder));
}

export async function tarifAendern(id: string, felder: TarifEingabe): Promise<Tarif> {
  return alsTarif(await aendere<Roh>(`/stamm/tarife/${encodeURIComponent(id)}`, felder));
}

export async function tarifLoeschen(id: string): Promise<void> {
  await loesche<void>(`/stamm/tarife/${encodeURIComponent(id)}`);
}

/* ── Zuschläge ───────────────────────────────────────────────────── */

export type ZuschlagEingabe = Partial<{
  bezeichnung: string; art: string; basis: string; wert: number;
  umfang: string; mindest_netto: number | null; frei_einheiten: number | null;
  steuersatz: number; sortierung: number; wahlfrei: boolean; vorgewaehlt: boolean;
  /** Siehe `Zuschlagsbindung` — auch die Ausschlussliste (Migration 0143). */
  bindung: Zuschlagsbindung; aktiv: boolean; gruppen: string[];
  /** Die Maschinentypen derselben Liste — neben den Gruppen. */
  typen: string[];
  /** Mit oder ohne Bediener (Migration 0096). */
  bedienerbindung: Bedienerbindung;
  /** `false` bestätigt einen erfundenen Betrag als richtig. */
  platzhalter: boolean;
  /** Darf eine Position mit dieser Regel einen Rabatt tragen? (Migration 0106) */
  rabattfaehig: boolean;
  /** Preis immer individuell (Migration 0227, E1). */
  preis_individuell: boolean;
}>;

function alsZuschlag(z: Roh): Zuschlag {
  return {
    code: txt(z["code"]),
    label: txt(z["label"]),
    art: txt(z["kind"]),
    basis: txt(z["basis"]),
    wert: zahl(z["value"]),
    umfang: txt(z["scope"]),
    mindest_netto: zahl(z["min_charge_net"]),
    steuersatz: zahl(z["tax_rate"]),
    wahlfrei: !!z["is_optional"],
    vorgewaehlt: !!z["preselected"],
    /* Für welche Geräte (0003/0037/0143). Geprüft und nicht durchgereicht:
       Ein Wert, den die Maske nicht kennt, sähe dort aus wie „alle Geräte"
       und könnte beim nächsten Speichern eine Ausschlussliste in eine
       Positivliste verwandeln. */
    bindung: z["scope_mode"] === "nur_gruppen" || z["scope_mode"] === "alle_ausser"
      ? z["scope_mode"]
      : "global",
    gruppen: Array.isArray(z["gruppen"]) ? (z["gruppen"] as unknown[]).map((g) => String(g)) : [],
    aus_bei: Array.isArray(z["aus_bei"]) ? (z["aus_bei"] as unknown[]).map((g) => String(g)) : [],
    typen: Array.isArray(z["typen"]) ? (z["typen"] as unknown[]).map((g) => String(g)) : [],
    bedienerbindung: z["operator_mode"] === "nur_mit_bediener" || z["operator_mode"] === "nur_ohne_bediener"
      ? z["operator_mode"]
      : "egal",
    zaehlgroesse: txt(z["metric"]),
    aktiv: z["active"] !== false,
    platzhalter: z["is_placeholder"] === true,
    rabattfaehig: z["discountable"] === true,
    preisIndividuell: z["preis_individuell"] === true,
  };
}

export async function zuschlagAendern(code: string, felder: ZuschlagEingabe): Promise<Zuschlag> {
  return alsZuschlag(await aendere<Roh>(`/stamm/zuschlaege/${encodeURIComponent(code)}`, felder));
}

/** Löschen geht nur, solange kein Kunde und keine Position darauf zeigt. */
export async function zuschlagLoeschen(code: string): Promise<void> {
  await loesche<void>(`/stamm/zuschlaege/${encodeURIComponent(code)}`);
}

export async function zuschlagAnlegen(
  felder: ZuschlagEingabe & { code: string },
): Promise<Zuschlag> {
  return alsZuschlag(await sende<Roh>("/stamm/zuschlaege", felder));
}

/* ── Personaltarife ──────────────────────────────────────────────────
   Was eine Stunde Mensch kostet. Ein Satz darf fehlen — dann steht
   „kein Preis" da, nicht 0,00 (Migration 0037). */

export type PersonaltarifEingabe = Partial<{
  code: string;
  bezeichnung: string;
  qualifikation: string | null;
  stundensatz: number | null;
  mindest_stunden: number | null;
  platzhalter: boolean;
  /** Rabattfähig (Migration 0106). */
  rabattfaehig: boolean;
}>;

export async function personaltarifAnlegen(felder: PersonaltarifEingabe & { code: string }): Promise<void> {
  await sende<unknown>("/stamm/personaltarife", felder);
}

export async function personaltarifAendern(id: string, felder: PersonaltarifEingabe): Promise<void> {
  await aendere<unknown>(`/stamm/personaltarife/${encodeURIComponent(id)}`, felder);
}

export async function personaltarifLoeschen(id: string): Promise<void> {
  await loesche<void>(`/stamm/personaltarife/${encodeURIComponent(id)}`);
}

/* ── Textbausteine (Migration 0058) ─────────────────────────────
   Gespeicherte Freitexte mit Schnellzugriff: die Zeile in der
   Positionsliste und die Sätze darüber und darunter. Bis zum 16.09.2026
   wurde jeder davon jedes Mal neu getippt — und damit jedes Mal ein
   bisschen anders (12-offene-fragen.md, „Textbausteine gibt es noch
   nicht“). */

function alsBaustein(b: Roh): Textbaustein {
  return {
    id: txt(b["id"]),
    stichwort: txt(b["label"]),
    text: txt(b["body"]),
    art: (txt(b["scope"]) || "positionstext") as Bausteinart,
    belegart: (txt(b["document_kind"]) || null) as Belegart | null,
    aktiv: b["active"] !== false,
    benutzt: zahl(b["use_count"]) ?? 0,
    angelegtAm: txt(b["created_at"]),
    angelegtVon: txt(b["created_name"]) || null,
    angelegtKuerzel: txt(b["created_initials"]) || null,
    geaendertAm: txt(b["updated_at"]),
    geaendertVon: txt(b["updated_name"]) || null,
    geaendertKuerzel: txt(b["updated_initials"]) || null,
    standard: b["is_default"] === true,
  };
}

function alsVerlaufszeile(r: Roh): Bausteinverlauf {
  return {
    id: txt(r["id"]),
    wann: txt(r["changed_at"]),
    art: txt(r["kind"]),
    stichwort: txt(r["label"]),
    text: txt(r["body"]),
    umfang: (txt(r["scope"]) || "positionstext") as Bausteinart,
    belegart: (txt(r["document_kind"]) || null) as Belegart | null,
    aktiv: r["active"] !== false,
    standard: r["is_default"] === true,
    wer: txt(r["wer"]) || null,
    kuerzel: txt(r["kuerzel"]) || null,
  };
}

export type BausteinEingabe = Partial<{
  stichwort: string;
  text: string;
  art: Bausteinart;
  /** Die Belegart eines Vor- oder Nachtexts (0105). Fehlt sie, meint die API das Angebot. */
  beleg: Belegart;
  aktiv: boolean;
  /** Der Standard seiner Art und Belegart — nur Vor- und Nachtext. */
  standard: boolean;
}>;

/**
 * `GET /textbausteine` — die Liste.
 *
 * `art` grenzt auf eine Art ein, `beleg` auf eine Belegart (0105), `nurAktive`
 * auf die, die noch benutzt werden. Der Schnellzugriff fragt mit allen dreien —
 * am Lieferschein stehen nur die Sätze des Lieferscheins zur Wahl —, das Blatt
 * in den Einstellungen ohne: Dort muss auch das Stillgelegte zu sehen sein.
 */
export async function textbausteine(
  art?: Bausteinart,
  nurAktive = false,
  beleg?: Belegart,
): Promise<Liste<Textbaustein>> {
  if (ATTRAPPE) return gleich({ zeilen: [], gesamt: 0 });
  const frage = new URLSearchParams();
  if (art) frage.set("art", art);
  if (beleg) frage.set("beleg", beleg);
  if (nurAktive) frage.set("aktiv", "1");
  const anhang = frage.toString();
  const roh = await hole<Roh>(`/textbausteine${anhang ? `?${anhang}` : ""}`);
  const zeilen = alsListe(roh["zeilen"]).map(alsBaustein);
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length };
}

export async function textbausteinAnlegen(
  felder: BausteinEingabe & { stichwort: string; text: string },
): Promise<Textbaustein> {
  return alsBaustein(await sende<Roh>("/textbausteine", felder));
}

export async function textbausteinAendern(id: string, felder: BausteinEingabe): Promise<Textbaustein> {
  return alsBaustein(await aendere<Roh>(`/textbausteine/${encodeURIComponent(id)}`, felder));
}

/** `GET /textbausteine/:id/verlauf` — das Neueste zuerst. */
export async function textbausteinVerlauf(id: string): Promise<Bausteinverlauf[]> {
  if (ATTRAPPE) return gleich([]);
  const roh = await hole<Roh>(`/textbausteine/${encodeURIComponent(id)}/verlauf`);
  return alsListe(roh["zeilen"]).map(alsVerlaufszeile);
}

/**
 * `POST /textbausteine/:id/benutzt` — den Zähler um eins.
 *
 * **Sie wird abgeschickt und nicht abgewartet.** Ein Zähler, der das Einfügen
 * eines Satzes verzögert oder mit einem Fehlerband quittiert, wäre die
 * schlechteste Art, eine Statistik zu führen.
 */
export async function textbausteinBenutzt(id: string): Promise<void> {
  if (ATTRAPPE) return gleich(undefined, 0);
  try {
    await sende<Roh>(`/textbausteine/${encodeURIComponent(id)}/benutzt`, {});
  } catch {
    /* Der Satz steht trotzdem in der Zeile. */
  }
}

/* ── Die Texte je Belegart am Vorgang (Migration 0105) ─────────────
   Durchgang 16.09.2026, AG2: *„Danke für Ihre Anfrage – das muss ja nicht
   beim Auftrag sein, sondern: Vielen Dank für Ihren Auftrag."* Das Angebot
   trägt seine Texte am Vorgang (`vortext`, `nachtext` über `PATCH`); die drei
   anderen Belege tragen nur, was vom Standard ihrer Belegart abweicht. */

function alsBelegtext(roh: unknown): Belegtext {
  const t = (roh ?? {}) as Roh;
  const std = t["standard"] as Roh | null | undefined;
  const herkunft = txt(t["herkunft"]);
  return {
    eigen: txt(t["eigen"]) || null,
    standard: std ? { id: txt(std["id"]), stichwort: txt(std["stichwort"]), text: txt(std["text"]) } : null,
    gilt: txt(t["gilt"]) || null,
    herkunft: herkunft === "vorgang" || herkunft === "standard" ? herkunft : "leer",
  };
}

function alsBelegtextstand(z: Roh): Belegtextstand {
  return {
    art: txt(z["art"]) as Belegart,
    vortext: alsBelegtext(z["vortext"]),
    nachtext: alsBelegtext(z["nachtext"]),
    hinaus: alsListe(z["hinaus"]).map((h) => ({ nummer: txt(h["nummer"]) || null, am: txt(h["am"]) })),
  };
}

/** Ohne Server: vier Belegarten, nirgends ein Text, nirgends ein Standard. */
const LEERE_TEXTE: Belegtextstand[] = (["angebot", "auftragsbestaetigung", "lieferschein", "rechnung"] as const)
  .map((art) => ({
    art,
    vortext: { eigen: null, standard: null, gilt: null, herkunft: "leer" as const },
    nachtext: { eigen: null, standard: null, gilt: null, herkunft: "leer" as const },
    hinaus: [],
  }));

/** `GET /vorgaenge/:nr/texte` — alle vier Belegarten, aufgelöst. */
export async function belegtexte(nr: string): Promise<Belegtextstand[]> {
  if (ATTRAPPE) return gleich(LEERE_TEXTE);
  const roh = await hole<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/texte`);
  return alsListe(roh["zeilen"]).map(alsBelegtextstand);
}

/**
 * `PUT /vorgaenge/:nr/texte/:art` — abweichende Texte für Auftragsbestätigung,
 * Lieferschein oder Rechnung. Gesetzt wird, was im Rumpf steht; `null` heißt
 * „zurück auf den Standard".
 */
export async function belegtexteSchreiben(
  nr: string,
  art: Exclude<Belegart, "angebot">,
  felder: { vortext?: string | null; nachtext?: string | null },
): Promise<Belegtextstand[]> {
  if (ATTRAPPE) return gleich(LEERE_TEXTE);
  const roh = await ersetze<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/texte/${art}`, felder);
  return alsListe(roh["zeilen"]).map(alsBelegtextstand);
}

/* ── Warenartikel (Migration 0045) ───────────────────────────────────
   Der Katalog hinter der Position „Ware". Er war nicht da — `verkauf`
   stand als Positionsart seit der ersten Migration, ohne Stamm, und jede
   Warenzeile war Freitext mit einer getippten Zahl (Luca, 15.09.2026). */

function alsWare(w: Roh): Warenartikel {
  return {
    id: txt(w["id"]),
    label: txt(w["label"]),
    einheit: txt(w["unit_label"]) || "einheit.stueck",
    netto: zahl(w["price_net"]),
    steuersatz: zahl(w["tax_rate"]) ?? 19,
    aktiv: w["active"] !== false,
    drueben: Boolean(txt(w["lex_article_id"])),
    rabattfaehig: w["discountable"] === true,
    preisIndividuell: w["preis_individuell"] === true,
  };
}

export type WareEingabe = Partial<{
  bezeichnung: string;
  /** Ein Schlüssel aus `waren.einheiten` — `einheit.liter`. */
  einheit: string;
  /** `null` heißt „kein Preis gepflegt" — nicht 0,00. */
  netto: number | null;
  steuersatz: number;
  aktiv: boolean;
  /** Rabattfähig (Migration 0106). */
  rabattfaehig: boolean;
  /** Preis immer individuell (Migration 0227, E1). */
  preis_individuell: boolean;
}>;

/** Die Einheiten, die ein Warenartikel tragen kann — vom Server, nicht geraten. */
export type Warenliste = Liste<Warenartikel> & { einheiten: string[] };

export async function waren(): Promise<Warenliste> {
  if (ATTRAPPE) return gleich({ zeilen: [], gesamt: 0, einheiten: ["einheit.stueck"] });
  const roh = await hole<Roh>("/waren");
  const zeilen = alsListe(roh["zeilen"]).map(alsWare);
  const einheiten = Array.isArray(roh["einheiten"]) ? (roh["einheiten"] as unknown[]).map(txt) : [];
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length, einheiten };
}

export async function wareAnlegen(felder: WareEingabe & { bezeichnung: string; einheit: string }): Promise<Warenartikel> {
  return alsWare(await sende<Roh>("/waren", felder));
}

export async function wareAendern(id: string, felder: WareEingabe): Promise<Warenartikel> {
  return alsWare(await aendere<Roh>(`/waren/${encodeURIComponent(id)}`, felder));
}

/* ── Leistungsarten ──────────────────────────────────────────────────
   Der Katalog, aus dem eine Position „Leistung" gewählt wird. Bis zum
   15.09.2026 gab es ihn nur als Tabelle ohne Schreibweg — im Angebot
   blieb die Waschleistung damit eine Freitextzeile mit einer Zahl, die
   jemand tippt (Luca).

   **Die Spaltennamen kommen englisch aus der API** (`price_net`,
   `min_value`, `surcharge_net`) und heißen hier deutsch. Das ist die
   Grenze aus CLAUDE.md: Die Datenbankstruktur ist Technik, die
   Oberfläche spricht Deutsch. */

function alsLeistungsart(l: Roh): Leistungsart {
  return {
    id: txt(l["id"]),
    code: txt(l["code"]),
    label: txt(l["label"]),
    groesse: txt(l["metric"]),
    verrechnungsart: txt(l["billing_method"]) || "nur_menge",
    ortsaufnahme: l["needs_site_survey"] === true,
    netto: zahl(l["price_net"]),
    mindest_menge: zahl(l["min_quantity"]),
    mindest_wert: zahl(l["min_value"]),
    steuersatz: zahl(l["tax_rate"]),
    // Fehlt das Feld, ist die Zeile aktiv: Eine Leistungsart, die
    // versehentlich als abgeschaltet erschiene, wäre aus der Auswahl
    // verschwunden, ohne dass jemand sie abgeschaltet hat.
    aktiv: l["active"] !== false,
    platzhalter: l["is_placeholder"] === true,
    rabattfaehig: l["discountable"] === true,
    preisIndividuell: l["preis_individuell"] === true,
    vorgabeKategorieId: txt(l["vorgabe_kategorie_id"]) || null,
    kategorien: alsListe(l["kategorien"]).map(alsPreiskategorie),
    saetze: alsListe(l["saetze"]).map(alsMengensatz),
    zuschlaege: alsListe(l["zuschlaege"]).map(alsLeistungszuschlag),
    // Womit und von wem (Migration 0154). Fehlen die Felder — eine Antwort
    // von vor der Migration —, bleiben die Listen leer: Die Maske zeigt dann
    // „nichts zugeordnet" und nicht eine halbe Auskunft.
    geraete: alsListe(l["geraete"]).map((g): Leistungsgeraet => ({
      id: txt(g["id"]),
      ebene: txt(g["ebene"]) === "gruppe" ? "gruppe" : "typ",
      gruppeId: txt(g["gruppe_id"]) || null,
      maschinentypId: txt(g["maschinentyp_id"]) || null,
      bezeichnung: txt(g["bezeichnung"]),
      pflicht: g["pflicht"] === true,
      stuecke: zahl(g["stuecke"]) ?? 0,
    })),
    /* Welche · womit · von wem (0160). Fehlen die drei neuen Felder — eine
       Antwort von vor der Migration —, steht die Zeile als das da, was sie
       immer war: für die Leistung selbst, von jedem. */
    qualifikationen: alsListe(l["qualifikationen"]).map((q) => ({
      code: txt(q["code"]), label: txt(q["label"]) || txt(q["code"]),
      gruppeId: txt(q["gruppe_id"]) || null,
      maschinentypId: txt(q["maschinentyp_id"]) || null,
      womit: txt(q["womit"]) || null,
      vonWem: txt(q["von_wem"]) === "einer" ? ("einer" as const) : ("jeder" as const),
    })),
  };
}

/* Die Preiskategorie und ihr Satz (Migration 0142, G9). Sie stehen getrennt,
   weil sie es sind: Eine Kategorie ist ein Name, ein Satz ist eine Zahl — und
   eine Kategorie ohne Satz ist ein gueltiger Zustand, den die Maske anzeigt,
   statt ihn mit dem Stammsatz zu verdecken. */

function alsPreiskategorie(k: Roh): Preiskategorie {
  return {
    id: txt(k["id"]),
    code: txt(k["code"]),
    label: txt(k["label"]),
    sortierung: zahl(k["sort_order"]) ?? 100,
  };
}

function alsMengensatz(q: Roh): Mengensatz {
  return {
    id: txt(q["id"]),
    kategorieId: txt(q["variant_id"]),
    abMenge: zahl(q["from_quantity"]) ?? 0,
    netto: zahl(q["price_net"]) ?? 0,
  };
}

/* Der Leistungszuschlag und sein Satz je Grad (Migration 0152). Getrennt aus
   demselben Grund wie oben: Der Zuschlag ist ein Name, der Satz eine Zahl —
   und ein Zuschlag ohne Satz ist ein gueltiger Zustand, den die Maske anzeigt,
   statt ihn mit einer 0,00 zu verdecken. */

function alsLeistungszuschlag(z: Roh): Leistungszuschlag {
  return {
    id: txt(z["id"]),
    code: txt(z["code"]),
    label: txt(z["label"]),
    sortierung: zahl(z["sort_order"]) ?? 100,
    platzhalter: z["is_placeholder"] === true,
    // Fehlt das Feld, ist der Zuschlag aktiv -- dieselbe Linie wie bei der
    // Leistungsart: Was niemand abgeschaltet hat, gilt.
    aktiv: z["active"] !== false,
    saetze: alsListe(z["saetze"]).map((r) => ({
      id: txt(r["id"]),
      kategorieId: txt(r["variant_id"]),
      netto: zahl(r["surcharge_net"]) ?? 0,
    })),
  };
}

export type LeistungsartEingabe = Partial<{
  code: string;
  bezeichnung: string;
  zaehlgroesse: string;
  verrechnungsart: string;
  /** `null` ist erlaubt und heißt „noch kein Satz" — nicht 0,00. */
  netto: number | null;
  mindest_menge: number | null;
  mindest_wert: number | null;
  steuersatz: number;
  aktiv: boolean;
  /** Braucht eine Ortsaufnahme, bevor sie angeboten werden kann. */
  ortsaufnahme: boolean;
  /** `false` bestätigt einen erfundenen Satz als richtig. */
  platzhalter: boolean;
  /** Rabattfähig (Migration 0106). */
  rabattfaehig: boolean;
  /** Preis immer individuell (Migration 0227, E1). */
  preis_individuell: boolean;
  /** Der Vorgabegrad (Migration 0184). `null` nimmt ihn zurück. */
  vorgabe_kategorie_id: string | null;
  /**
   * **Womit die Leistung erbracht wird** (Migration 0154) — je Zeile
   * entweder eine Gerätegruppe oder ein Maschinentyp, nie beides. Die
   * Liste ist der Stand: Was fehlt, ist weg.
   */
  geraete: ({ gruppe_id?: string; maschinentyp_id?: string; pflicht?: boolean })[];
  /**
   * **Was der Mensch dafür können muss** (0154, geschärft in 0160) — je Zeile
   * welche, womit und von wem. Ein reiner Code bleibt erlaubt und heißt, was
   * er immer hieß: für die Leistung selbst, von jedem.
   */
  qualifikationen: (string | {
    code: string;
    /** Höchstens eines von beiden — leer heißt „für die Leistung selbst". */
    gruppe_id?: string | null;
    maschinentyp_id?: string | null;
    von_wem?: "jeder" | "einer";
  })[];
}>;

/* **Drei Leistungsarten im Prototyp — und nur eine verlangt ein Aufmaß**
   (21.09.2026). Luca: *„Wir haben zum Beispiel bei Mulchen oder bei Bedienung
   den Haken gar nicht gesetzt bei ‚braucht eine Ortsaufnahme'. Also will ich
   das auch gar nicht auswählen können."*

   Bis heute gab der Attrappenbetrieb hier eine **leere Liste** zurück. Das
   Blatt „Aufmaß erfassen" sagte dort deshalb „Keine Leistungsart verlangt ein
   Aufmaß" — richtig formuliert und trotzdem die falsche Auskunft: Es gab
   keine Daten, nicht keine Haken. Und wer den Prototyp durchklickt (der Weg
   aus `docs/29-pruefauftrag.md`), sah den Filter nie wirken.

   Genau Lucas Beispiel: **PV-Reinigung mit Haken, Mulchen und Bedienung
   ohne.** Im Blatt steht danach eine Zeile, nicht drei.

   ⚠ **Kein Betrag.** `netto: null` heißt „noch kein Satz gepflegt" und nicht
   0,00 (CLAUDE.md); was ein Quadratmeter kostet, sagt die Kalkulation aus
   04-preisfindung.md. */
const LEISTUNGEN_ATTRAPPE: Leistungsart[] = [
  {
    id: "lt-pv-reinigung", code: "PV_REIN", label: "PV-Reinigung",
    groesse: "QM", verrechnungsart: "nur_menge", ortsaufnahme: true,
    netto: null, mindest_menge: null, mindest_wert: null, steuersatz: 19,
    aktiv: true, platzhalter: false, rabattfaehig: false, vorgabeKategorieId: null,
    kategorien: [], saetze: [], zuschlaege: [],
    geraete: [], qualifikationen: [],
  },
  {
    id: "lt-mulchen", code: "MULCHEN", label: "Mulchen",
    groesse: "HA", verrechnungsart: "nur_menge", ortsaufnahme: false,
    netto: null, mindest_menge: null, mindest_wert: null, steuersatz: 19,
    aktiv: true, platzhalter: false, rabattfaehig: false, vorgabeKategorieId: null,
    kategorien: [], saetze: [], zuschlaege: [],
    geraete: [], qualifikationen: [],
  },
  {
    id: "lt-bedienung", code: "BEDIENUNG", label: "Bedienung",
    groesse: "STD", verrechnungsart: "nur_zeit", ortsaufnahme: false,
    netto: null, mindest_menge: null, mindest_wert: null, steuersatz: 19,
    aktiv: true, platzhalter: false, rabattfaehig: false, vorgabeKategorieId: null,
    kategorien: [], saetze: [], zuschlaege: [],
    geraete: [], qualifikationen: [],
  },
];

export async function leistungsarten(): Promise<Liste<Leistungsart>> {
  if (ATTRAPPE) return gleich({ zeilen: LEISTUNGEN_ATTRAPPE, gesamt: LEISTUNGEN_ATTRAPPE.length });
  const roh = await hole<Roh>("/stamm/leistungsarten");
  const zeilen = alsListe(roh["zeilen"]).map(alsLeistungsart);
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length };
}

export async function leistungsartAnlegen(
  felder: LeistungsartEingabe & { code: string },
): Promise<Leistungsart> {
  return alsLeistungsart(await sende<Roh>("/stamm/leistungsarten", felder));
}

export async function leistungsartAendern(
  id: string,
  felder: LeistungsartEingabe,
): Promise<Leistungsart> {
  return alsLeistungsart(
    await aendere<Roh>(`/stamm/leistungsarten/${encodeURIComponent(id)}`, felder),
  );
}

/* ── Preiskategorien und Sätze (Migration 0142, G9) ──────────────── */

export type KategorieEingabe = Partial<{
  code: string;
  bezeichnung: string;
  sortierung: number;
}>;

/** `kategorie` leer heißt: die Zeile gilt für die Leistungsart ohne Grade. */
export type MengensatzEingabe = Partial<{
  kategorie: string | null;
  ab_menge: number;
  netto: number;
}>;

export async function kategorieAnlegen(
  leistungId: string,
  felder: KategorieEingabe & { code: string; bezeichnung: string },
): Promise<Preiskategorie> {
  return alsPreiskategorie(
    await sende<Roh>(`/stamm/leistungsarten/${encodeURIComponent(leistungId)}/kategorien`, felder),
  );
}

export async function kategorieAendern(
  id: string,
  felder: KategorieEingabe,
): Promise<Preiskategorie> {
  return alsPreiskategorie(
    await aendere<Roh>(`/stamm/kategorien/${encodeURIComponent(id)}`, felder),
  );
}

export async function kategorieLoeschen(id: string): Promise<void> {
  await loesche<void>(`/stamm/kategorien/${encodeURIComponent(id)}`);
}

export async function mengensatzAnlegen(
  leistungId: string,
  felder: MengensatzEingabe & { ab_menge: number; netto: number },
): Promise<Mengensatz> {
  return alsMengensatz(
    await sende<Roh>(`/stamm/leistungsarten/${encodeURIComponent(leistungId)}/saetze`, felder),
  );
}

export async function mengensatzAendern(
  id: string,
  felder: MengensatzEingabe,
): Promise<Mengensatz> {
  return alsMengensatz(await aendere<Roh>(`/stamm/saetze/${encodeURIComponent(id)}`, felder));
}

export async function mengensatzLoeschen(id: string): Promise<void> {
  await loesche<void>(`/stamm/saetze/${encodeURIComponent(id)}`);
}

/* ── Leistungszuschläge und ihre Sätze (Migration 0152) ──────────── */

export type LeistungszuschlagEingabe = Partial<{
  code: string;
  bezeichnung: string;
  sortierung: number;
  aktiv: boolean;
  /** `false` bestätigt einen erfundenen Satz als richtig. */
  platzhalter: boolean;
}>;

export async function leistungszuschlagAnlegen(
  leistungId: string,
  felder: LeistungszuschlagEingabe & { code: string; bezeichnung: string },
): Promise<Leistungszuschlag> {
  return alsLeistungszuschlag(
    await sende<Roh>(`/stamm/leistungsarten/${encodeURIComponent(leistungId)}/zuschlaege`, felder),
  );
}

export async function leistungszuschlagAendern(
  id: string,
  felder: LeistungszuschlagEingabe,
): Promise<Leistungszuschlag> {
  return alsLeistungszuschlag(
    await aendere<Roh>(`/stamm/leistungszuschlaege/${encodeURIComponent(id)}`, felder),
  );
}

export async function leistungszuschlagLoeschen(id: string): Promise<void> {
  await loesche<void>(`/stamm/leistungszuschlaege/${encodeURIComponent(id)}`);
}

/**
 * Den Satz für **einen** Grad setzen — oder mit `netto: null` wegnehmen.
 *
 * Ein Weg statt dreier: Der Satz ist durch Zuschlag und Grad eindeutig
 * bestimmt, anlegen und ändern sind dieselbe Handlung, und ein leeres Feld
 * heißt „kein Satz" und nicht 0,00 (CLAUDE.md).
 *
 * `kategorie: null` heißt „gilt für jeden Grad".
 */
export async function zuschlagssatzSetzen(
  id: string,
  felder: { kategorie: string | null; netto: number | null },
): Promise<Leistungszuschlag> {
  return alsLeistungszuschlag(
    await ersetze<Roh>(`/stamm/leistungszuschlaege/${encodeURIComponent(id)}/satz`, felder),
  );
}

/* ── Kalkulation ─────────────────────────────────────────────────── */

function alsKostenmiete(k: Roh): Kostenmiete {
  return {
    id: txt(k["id"]),
    typ_id: txt(k["machine_type_id"]) || null,
    gruppe_id: txt(k["category_id"]) || null,
    typ: txt(k["typ"]),
    gruppe: txt(k["gruppe"]),
    gilt_ab: txt(k["valid_from"]),
    abschreibung: zahl(k["depreciation_day"]),
    zins: zahl(k["interest_day"]),
    wartung: zahl(k["maintenance_day"]),
    versicherung: zahl(k["insurance_day"]),
    steuer: zahl(k["tax_day"]),
    notiz: txt(k["note"]),
  };
}

/**
 * Die Kostenseite — **alle Stände**, nicht nur der aktuelle.
 *
 * Sie ist leer, und das bleibt sie, bis die Kalkulation aus
 * `04-preisfindung.md` durchgeführt ist. Eine erfundene Zahl wäre hier keine
 * Hilfe, sondern etwas, das später jemand für gerechnet hält.
 */
export async function kalkulation(): Promise<Liste<Kostenmiete> & { wirksam: Kostenwirkung[] }> {
  if (ATTRAPPE) return gleich({ zeilen: [], gesamt: 0, wirksam: [] });
  const roh = await hole<Roh>("/stamm/kalkulation");
  const zeilen = alsListe(roh["zeilen"]).map(alsKostenmiete);
  // Was heute je Typ gilt — Feld für Feld eigen oder Vorgabe der Gruppe. Die
  // Kette läuft in der API, damit hier nicht eine zweite entsteht.
  const wirksam = alsListe(roh["wirksam"]).map((w): Kostenwirkung => ({
    typ_id: txt(w["typ_id"]),
    werte: w["werte"] as Kostenwirkung["werte"],
    herkunft: w["herkunft"] as Kostenwirkung["herkunft"],
    staende: alsListe(w["staende"]).map((s) => ({
      ebene: (zahl(s["ebene"]) ?? 0) as 0 | 1 | 2,
      gruppe_id: txt(s["gruppe_id"]) || null,
      label: txt(s["label"]),
      gilt_ab: txt(s["gilt_ab"]),
    })),
  }));
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length, wirksam };
}

/** `typ_id` ODER `gruppe_id` — ein Stand am Typ oder als Vorgabe der Gruppe. */
export type KostenEingabe = {
  typ_id?: string; gruppe_id?: string; gilt_ab: string;
  abschreibung_tag: number | null; zins_tag: number | null; wartung_tag: number | null;
  versicherung_tag: number | null; steuer_tag: number | null; notiz: string | null;
};

export async function kalkulationSetzen(felder: KostenEingabe): Promise<Kostenmiete> {
  return alsKostenmiete(await sende<Roh>("/stamm/kalkulation", felder));
}

export async function kalkulationLoeschen(id: string): Promise<void> {
  await loesche<void>(`/stamm/kalkulation/stand/${encodeURIComponent(id)}`);
}

/* ── Die kleinen Kataloge ────────────────────────────────────────── */

/* ══════════════ Bedienung ══════════════
   Ob ein Gerät ohne Bediener hinausgeht (Migrationen 0083/0084). Drei
   Leser: die Gerätegruppe, der Maschinentyp und der Gerätevorschlag — also
   steht die Übersetzung einmal hier und nicht dreimal. */

/** Ein unbekannter Wert ist `null` und damit „erbt", nie ein Absturz. */
function alsBedienungsart(wert: unknown): Bedienungsart | null {
  const w = txt(wert);
  return w === "ohne_person" || w === "nur_mit_person" || w === "wahlweise" ? w : null;
}

function alsQualliste(wert: unknown): { code: string; label: string }[] {
  return alsListe(wert)
    .map((q) => ({ code: txt(q["code"]), label: txt(q["label"]) || txt(q["code"]) }))
    .filter((q) => q.code !== "");
}

/** Die Ebene, die entschieden hat — `null` heißt: niemand, also die Vorgabe. */
function alsEbene(wert: unknown): 0 | 1 | 2 | null {
  const n = zahl(wert);
  return n === 0 || n === 1 || n === 2 ? n : null;
}

/**
 * Die aufgelöste Vorbereitungsregel aus der Antwort (Migration 0138).
 *
 * Fehlt sie — eine ältere Fassung der API —, gilt, was seit 0128 galt: Vortag
 * 06:00, gesagt vom Betrieb. Ein Rückfall, der nicht rät.
 */
/** Der Transportpuffer einer Stufe aus der Antwort (E16, P-18). */
export function alsPufferstand(roh: unknown): Pufferstand {
  const p = alsObjekt(roh);
  return {
    vor_eigen: zahl(p["vor_eigen"]),
    nach_eigen: zahl(p["nach_eigen"]),
    vor: zahl(p["vor"]) ?? 0,
    nach: zahl(p["nach"]) ?? 0,
  };
}

export function alsVorbereitungsregel(roh: unknown): Vorbereitungsregel {
  const r = (typeof roh === "object" && roh !== null ? roh : {}) as Roh;
  const ebene = (wert: unknown): Vorbereitungsebene => {
    const n = zahl(wert);
    return n === 0 || n === 1 || n === 2 || n === 3 ? n : 4;
  };
  return {
    vorlauf_tage: zahl(r["vorlauf_tage"]) ?? 1,
    uhrzeit: txt(r["uhrzeit"]) || "06:00",
    vorlauf_ebene: ebene(r["vorlauf_ebene"]),
    vorlauf_traeger: txt(r["vorlauf_traeger"]) || null,
    uhrzeit_ebene: ebene(r["uhrzeit_ebene"]),
    uhrzeit_traeger: txt(r["uhrzeit_traeger"]) || null,
  };
}

export function alsBedienung(roh: unknown): Bedienung {
  const b = (typeof roh === "object" && roh !== null ? roh : {}) as Roh;
  return {
    pflicht: alsBedienungsart(b["pflicht"]) ?? "wahlweise",
    ebene: alsEbene(b["ebene"]),
    gruppe_id: txt(b["gruppe_id"]) || null,
    traeger: txt(b["traeger"]) || null,
    qualifikationen: alsQualliste(b["qualifikationen"]),
  };
}

/** Dieselbe Auskunft eine Ebene höher: was für eine Gruppe gilt. */
function alsGruppenbedienung(roh: unknown): Geraetegruppe["bedienung_wirkt"] {
  const b = alsBedienung(roh);
  return {
    pflicht: b.pflicht,
    ebene: b.ebene === 0 ? 0 : b.ebene === 1 ? 1 : null,
    traeger: b.traeger,
  };
}

function alsGeraetegruppe(g: Roh): Geraetegruppe {
  return {
    id: txt(g["id"]),
    label: txt(g["label"]),
    uebergruppe_id: txt(g["parent_id"]) || null,
    preismodell: txt(g["price_model"]) || null,
    mindesttage: zahl(g["min_days"]),
    kalendertage: zahl(g["calendar_days"]),
    uvv_vorlauf: zahl(g["lead_days_inspection"]),
    frei_stunden_tag: zahl(g["free_hours_per_day"]),
    erbt: {
      mindesttage: zahl((g["erbt"] as Roh | null)?.["mindesttage"]),
      kalendertage: zahl((g["erbt"] as Roh | null)?.["kalendertage"]),
      uvv_vorlauf: zahl((g["erbt"] as Roh | null)?.["uvv_vorlauf"]),
    },
    sortierung: zahl(g["sort_order"]) ?? 100,
    mietkalender_code: txt(g["calendar_code"]) || null,
    mietkalender: txt(g["mietkalender"]) || null,
    bedienung: alsBedienungsart(g["staffing"]),
    bedienung_wirkt: alsGruppenbedienung(g["bedienung"]),
    qualifikationen: alsQualliste(g["qualifikationen"]),
    abholbar: typeof g["abholbar_eigen"] === "boolean" ? g["abholbar_eigen"] : null,
    anhaenger_variante_id: txt(g["anhaenger_variante_id"]) || null,
    abholung: alsAbholung(g["abholung"]),
    vorbereitung_vorlauf: zahl(g["vorbereitung_vorlauf_eigen"]),
    vorbereitung_uhrzeit: txt(g["vorbereitung_uhrzeit_eigen"]) || null,
    vorbereitung: alsVorbereitungsregel(g["vorbereitung"]),
    puffer: alsPufferstand(g["puffer"]),
    // Die eigene Abweichung von den Pflichtfotos (0173) — leer heißt: erbt alles.
    fotoabweichung: alsFotoabweichung(g["fotoabweichung"]),
    tarife: alsListe(g["tarife"]).map(alsTarif),
    wirkung: alsListe(g["wirkung"]).map((w) => ({
      id: txt(w["id"]),
      label: txt(w["label"]),
      gruppe_id: txt(w["gruppe_id"]),
      status: txt(w["status"]) === "eigen" ? "eigen" : txt(w["status"]) === "untergruppe" ? "untergruppe" : "erbt",
    })),
  };
}

export async function stammGruppen(): Promise<Liste<Geraetegruppe>> {
  if (ATTRAPPE) return gleich({ zeilen: [], gesamt: 0 });
  const roh = await hole<Roh>("/stamm/gruppen");
  const zeilen = alsListe(roh["zeilen"]).map(alsGeraetegruppe);
  return { zeilen, gesamt: zahl(roh["gesamt"]) ?? zeilen.length };
}

export type GruppeEingabe = Partial<{
  bezeichnung: string; uebergruppe_id: string | null; preismodell: string | null;
  mindesttage: number | null; kalendertage: number | null;
  uvv_vorlauf: number | null;
  frei_stunden_tag: number | null; sortierung: number;
  /** Der Mietkalender als Kennung — `null` heißt: die Spalten gelten für sich. */
  mietkalender: string | null;
  /** Ob hier ein Mensch mitmuss. `null` heißt „erbt" (Migration 0083). */
  bedienung: Bedienungsart | null;
  /** Welche Qualifikation er mitbringt — als **ganze Menge** (Migration 0084). */
  qualifikationen: string[];
  /** Ob der Kunde selbst abholen kann. `null` heißt „erbt" (Migration 0127). */
  abholbar: boolean | null;
  /** Die Ausstattung des Anhängers, die dann vorgeschlagen wird. `null` = keiner. */
  anhaenger_variante_id: string | null;
  /** Ab wann die Vorbereitung hinausgeht. `null` heißt „erbt" (Migration 0138). */
  vorbereitung_vorlauf_tage: number | null;
  vorbereitung_uhrzeit: string | null;
  /** Der Transportpuffer der Gruppe in Stunden (E16) — die Spalten, die die Pufferrechnung liest. */
  puffer_vor_stunden: number | null;
  puffer_nach_stunden: number | null;
  /** Welche Pflichtfotos hier entfallen oder dazukommen — als ganze Zuordnung (0173). */
  fotoabweichung: Fotoabweichung;
  /** Der Schlüssel des Anlegeblatts (0218, S-01): zweimal geschickt, einmal angelegt. */
  erfassung_id: string;
}>;

export async function gruppeAnlegen(felder: GruppeEingabe): Promise<Geraetegruppe> {
  return alsGeraetegruppe(await sende<Roh>("/stamm/gruppen", felder));
}

export async function gruppeAendern(id: string, felder: GruppeEingabe): Promise<Geraetegruppe> {
  return alsGeraetegruppe(await aendere<Roh>(`/stamm/gruppen/${encodeURIComponent(id)}`, felder));
}

export type ZaehlgroesseEingabe = Partial<{
  code: string; bezeichnung: string; einheit: string; erfassung: string;
}>;

export async function zaehlgroesseAnlegen(felder: ZaehlgroesseEingabe): Promise<void> {
  await sende<Roh>("/stamm/zaehlgroessen", felder);
}

export async function zaehlgroesseAendern(code: string, felder: ZaehlgroesseEingabe): Promise<void> {
  await aendere<Roh>(`/stamm/zaehlgroessen/${encodeURIComponent(code)}`, felder);
}

export type QualifikationEingabe = Partial<{
  code: string; bezeichnung: string; befristet: boolean;
}>;

export async function qualifikationAnlegen(felder: QualifikationEingabe): Promise<void> {
  await sende<Roh>("/stamm/qualifikationen", felder);
}

export async function qualifikationAendern(
  code: string, felder: QualifikationEingabe,
): Promise<void> {
  await aendere<Roh>(`/stamm/qualifikationen/${encodeURIComponent(code)}`, felder);
}

/** Löschen geht nur, solange nichts damit gezählt hat — sonst nennt die API es. */
export async function zaehlgroesseLoeschen(code: string): Promise<void> {
  await loesche<void>(`/stamm/zaehlgroessen/${encodeURIComponent(code)}`);
}

/** Löschen geht nur, solange niemand sie trägt und nichts sie verlangt. */
export async function qualifikationLoeschen(code: string): Promise<void> {
  await loesche<void>(`/stamm/qualifikationen/${encodeURIComponent(code)}`);
}

/** Löschen geht nur, solange die Gruppe leer ist — sonst nennt die API, was darin steht. */
export async function gruppeLoeschen(id: string): Promise<void> {
  await loesche<void>(`/stamm/gruppen/${encodeURIComponent(id)}`);
}

/* ── Mietkalender und Absagegründe (Migrationen 0050, 0051) ─────────
   Gelesen werden beide mit `stammKataloge`; geschrieben wird hier. */

export type MietkalenderEingabe = Partial<{
  code: string; bezeichnung: string; kalendertage: number;
  erster_tag_zaehlt: boolean; letzter_tag_zaehlt: boolean;
  halber_tag_vor: string | null; sortierung: number; aktiv: boolean;
}>;

export async function mietkalenderAnlegen(felder: MietkalenderEingabe): Promise<void> {
  await sende<Roh>("/stamm/mietkalender", felder);
}

export async function mietkalenderAendern(code: string, felder: MietkalenderEingabe): Promise<void> {
  await aendere<Roh>(`/stamm/mietkalender/${encodeURIComponent(code)}`, felder);
}

/** Löschen geht nur, solange nichts darauf zeigt — sonst abschalten. */
export async function mietkalenderLoeschen(code: string): Promise<void> {
  await loesche<void>(`/stamm/mietkalender/${encodeURIComponent(code)}`);
}

export type AbsagegrundEingabe = Partial<{
  code: string; bezeichnung: string; art: "absage" | "storno"; sortierung: number; aktiv: boolean;
  /** Verlangt einen eigenen Satz (0255, Interview F25). */
  satz_pflicht: boolean;
}>;

/** Legt den Grund an und nennt seinen Code — ohne `code` leitet die API ihn ab (F16). */
export async function absagegrundAnlegen(felder: AbsagegrundEingabe): Promise<string> {
  const roh = await sende<Roh>("/stamm/absagegruende", felder);
  return txt(roh["code"]);
}

export async function absagegrundAendern(code: string, felder: AbsagegrundEingabe): Promise<void> {
  await aendere<Roh>(`/stamm/absagegruende/${encodeURIComponent(code)}`, felder);
}

/** Löschen geht nur, solange kein Vorgang darauf zeigt — sonst abschalten. */
export async function absagegrundLoeschen(code: string): Promise<void> {
  await loesche<void>(`/stamm/absagegruende/${encodeURIComponent(code)}`);
}

/* ── Zahlungsziele (Migration 0106) ─────────────────────────────────
   Die Auswahl am Kunden. Gelesen über `stammKataloge`; hier die drei
   Schreibwege. Am Kunden steht die Zahl — nichts hiervon ändert einen. */

export type ZahlungszielEingabe = Partial<{
  bezeichnung: string; tage: number; standard: boolean; sortierung: number; aktiv: boolean;
}>;

export async function zahlungszielAnlegen(felder: ZahlungszielEingabe): Promise<void> {
  await sende<Roh>("/stamm/zahlungsziele", felder);
}

export async function zahlungszielAendern(id: string, felder: ZahlungszielEingabe): Promise<void> {
  await aendere<Roh>(`/stamm/zahlungsziele/${encodeURIComponent(id)}`, felder);
}

export async function zahlungszielLoeschen(id: string): Promise<void> {
  await loesche<void>(`/stamm/zahlungsziele/${encodeURIComponent(id)}`);
}

/* ── Notizarten (Migration 0146) ────────────────────────────────────
   Die Auswahl über der Gesprächsnotiz. Gelesen über `stammKataloge`;
   hier die drei Schreibwege.

   **Kein `code` im Rumpf.** Ihn leitet die API aus der Bezeichnung ab —
   was abgeleitet werden kann, wird abgeleitet (Regel 11). Bei den
   Nachbarn ist der Code der Primärschlüssel und muss getippt werden;
   hier ist der Schlüssel `(tenant_id, id)`. */

export type NotizartEingabe = Partial<{
  bezeichnung: string; sortierung: number; aktiv: boolean;
}>;

/** Legt die Notizart an und nennt ihren Code — den Wert, den eine Notiz trägt (F16). */
export async function notizartAnlegen(felder: NotizartEingabe): Promise<string> {
  const roh = await sende<Roh>("/stamm/notizarten", felder);
  return txt(roh["code"]);
}

export async function notizartAendern(id: string, felder: NotizartEingabe): Promise<void> {
  await aendere<Roh>(`/stamm/notizarten/${encodeURIComponent(id)}`, felder);
}

/** Löschen geht nur, solange keine Notiz darauf zeigt — sonst stilllegen. */
export async function notizartLoeschen(id: string): Promise<void> {
  await loesche<void>(`/stamm/notizarten/${encodeURIComponent(id)}`);
}

/* ══════════════ Schnellvermietung ══════════════ */

export type Schnellauftrag = {
  /** Die Kundennummer aus der Liste — `10001`, nicht die UUID. */
  kunde: string;
  /** Der Einsatzort, so wie er beim Kunden heißt. Leer heißt: keiner. */
  ort?: string;
  /**
   * Die UUID des Maschinentyps. Der Preis hängt am Typ (Regel 2).
   *
   * **Leer heißt: kein Gerät im Kopf** — ein reiner Leistungsauftrag
   * (Systemprüfung Befund 45, 25.09.2026). Dann entsteht keine Mietzeile und
   * keine Belegung; `bezeichnung` wird nicht gebraucht, und alles steht in
   * `positionen`.
   */
  typ_id: string;
  /** Die Ausstattung — „mit Anschläger". Leer heißt Grundpreis (16.09.2026). */
  variante_id?: string;
  /** Die Kennung der gewünschten Maschine — leer heißt: eine freie nehmen. */
  maschine?: string;
  /** Wie viele Stück — nur bei einem gezählten Bestand über 1 (Migration 0194). */
  stueck?: number;
  /**
   * Der Mensch hat bestätigt, dass er ein Gerät nimmt, das ein anderes Angebot
   * für seine Bindefrist reserviert hat (18.09.2026). Ohne die Bestätigung
   * weist die API mit `belegung.reserviert` ab und nennt Angebot, Kunde und
   * Frist; mit ihr wird die fremde Vormerkung beim Buchen gelöst.
   */
  reservierung_uebergehen?: boolean;
  /** Die Bezeichnung der Mietzeile im Kopf — nicht mehr der Betreff (05.10.2026). */
  bezeichnung: string;
  /** Der Betreff, wenn ihn jemand eingetragen hat. Ohne: keiner (Luca, 05.10.2026). */
  betreff?: string;
  von: string;
  bis: string;
  /** Mit Bediener? */
  bedient?: boolean;
  /** Die Kennung der Person, die bedient. Leer heißt: noch niemand. */
  bediener?: string;
  /** Ein von Hand gesetzter Nettopreis für die Miete — statt des Tarifs. */
  einzelpreis?: number;
  /** Die Zubehörstücke, die mit hinausgehen — Ressourcen-UUIDs. */
  zubehoer?: string[];
  /**
   * Weitere Zeilen, die am Telefon dazukommen — Leistung, Ware, Zuschlag, Text.
   *
   * Sie gehen **vor** der Annahme hinaus, und das ist der ganze Grund, warum
   * sie hier stehen und nicht hinterher angehängt werden: Die Annahme friert
   * die Preise ein (Regel 4) und stößt die Auftragsbestätigung an. Eine Zeile,
   * die danach entsteht, fehlt auf dem Papier, das der Kunde bekommt.
   */
  positionen?: Schnellposition[];
  /**
   * Vortext und Nachtext — das, was über und unter der Positionsliste auf
   * dem Papier steht.
   *
   * Sie stehen hier, weil die Schnellerfassung der schnelle **Einstieg**
   * ist und nicht das Ende: Wer den Satz beim Telefonat schon zur Hand hat
   * („Anlieferung Montag früh, Schlüssel beim Polier"), soll ihn nicht erst
   * im Vorgang nachtragen müssen. Wer ihn nicht hat, lässt beides leer —
   * Pflicht ist keiner von beiden (Regel 11).
   *
   * **Sie gehören zur Auftragsbestätigung** (Migration 0105). Die
   * Direktvermietung hat kein Angebot (Regel 10); der erste Beleg, den sie
   * erzeugt, ist die Auftragsbestätigung bei der Annahme. Bis zum 16.09.2026
   * gingen beide Sätze in `note_intro`/`note_customer` — die Felder des
   * Angebots — und von dort auf jeden Beleg. Jetzt gehen sie als abweichender
   * Text der Auftragsbestätigung hinaus; Lieferschein und Rechnung nehmen den
   * Standard ihrer Belegart.
   */
  vortext?: string;
  nachtext?: string;
  /** Was am Telefon notiert wurde. Sie geht auf keinen Beleg. */
  notiz_intern?: string;
  /** Die Bestellnummer/Kommission des Kunden (0188) — Kopfzeile auf AB, Lieferschein, Rechnung. */
  bestellnummer?: string;
  /** Der Hinweis für den Hof (0284) — für Vorbereitung, Fahrer und Übergabeprotokoll. */
  hofhinweis?: string;
  /**
   * Der Mietbeginn liegt in der Vergangenheit, und der Mensch hat „nachträglich
   * erfassen" bestätigt (Interview F20). Ohne die Bestätigung antwortet die
   * Annahme mit `annahme.beginn_vergangen`.
   */
  nachtraeglich?: boolean;
};

/** Eine Zeile, die mit dem Schnellauftrag entsteht — die Form von `POST …/positionen`. */
export type Schnellposition = {
  art: "miete" | "leistung" | "verkauf" | "zuschlag" | "text";
  bezeichnung?: string;
  menge?: number;
  einzelpreis?: number;
  /** Aus dem Katalog — dann kommen Bezeichnung, Einheit und Satz aus dem Stamm. */
  leistungsart_id?: string;
  /** Der gewählte Grad einer Leistung (P8-05) — geht als `kategorie_id` an die Position. */
  kategorie_id?: string;
  /**
   * **Die Menge folgt aus dem Aufmaß** (V-22, Durchgang 03.10.2026). Geht
   * nicht an die API: Am angenommenen Auftrag bestellt `schnellVermieten`
   * damit die Ortsaufnahme zur Position, statt sie zu vergessen.
   */
  aufmass?: boolean;
  ware_id?: string;
  /** Der Code aus dem Zuschlagsstamm. Er schlägt die gerechnete Regel desselben
   *  Codes im Abrechnungslauf — ein zugesagter Wert vor einem gerechneten. */
  zuschlag_code?: string;
  maschinentyp_id?: string;
  /** Die Ausstattung der Mietzeile — ohne sie findet ein Kran keinen Satz. */
  variante_id?: string;
  /** Wie viele Stück — nur an der Miete eines gezählten Bestands über 1 (0194). */
  stueck?: number;
  /**
   * Geht diese Maschine mit Bediener hinaus?
   *
   * Sie steht an der Position (Migration 0096), weil Zuschläge, die nur mit
   * oder nur ohne Bediener gelten, dort nachlesen. Bis zum 17.09.2026 trug
   * nur die **erste** Zeile eines Schnellauftrags sie — und in der Merkliste
   * hat jeder Posten seine eigene Antwort: „Schlepper und Mulde mit Fahrer"
   * (Luca, 17.09.2026) heißt nicht, dass der Anhänger einen Fahrer braucht.
   */
  bedient?: boolean;
  von?: string;
  bis?: string;
  /**
   * Die Kennung der gewünschten Maschine — `RL-07`, nicht die UUID.
   *
   * Bis zum 17.09.2026 trug nur die **erste** Zeile eines Schnellauftrags
   * einen Wunsch; jede weitere bekam „die erste freie". In der Sammlung des
   * Vermietmoduls hat jeder Posten seine eigene Wahl, sichtbar und änderbar
   * (`masken/vermieten/Postenblatt.tsx`) — und ein Gerät, das die Maske
   * zeigt, muss auch das gebuchte sein.
   *
   * **Sie geht nicht an die API**: Die Position hängt am Typ (Regel 2), nur
   * die Belegung bekommt das Individuum.
   */
  maschine?: string;
  /**
   * Der Mensch hat bestätigt, dass er ein Gerät nimmt, das ein anderes Angebot
   * für seine Bindefrist reserviert hat (18.09.2026). Ohne die Bestätigung
   * weist die API mit `belegung.reserviert` ab und nennt Angebot, Kunde und
   * Frist; mit ihr wird die fremde Vormerkung beim Buchen gelöst.
   */
  reservierung_uebergehen?: boolean;
  /** Die Kennung der Person, die bedient. Leer heißt: noch niemand. */
  bediener?: string;
  /** Die Zubehörstücke, die an diese Position gebunden werden — Ressourcen-UUIDs. */
  zubehoer?: string[];
};

/**
 * Aus vier Feldern ein angenommener Auftrag.
 *
 * Fünf Aufrufe, und keiner davon ist verzichtbar:
 *
 *   1. Kunde auflösen — die Maske spricht `10001`, die API will die UUID
 *   2. Vorgang anlegen
 *   3. Position anlegen — Preis und Menge rechnet die API aus dem Tarif,
 *      die gewählte Maschine geht als `maschine_id` mit
 *   4. Belegung — legt die API an der Position selbst an (`maschineBinden`),
 *      sonst hängt der Balken im Nichts
 *   5. Was am Telefon dazukam (`positionen`) — Leistung, Ware, Zuschlag, Text
 *   6. Annehmen — **hier friert der Preis ein** (Regel 4)
 *
 * Die Maschine wird gewählt, nicht geraten: Steht in `maschine` eine Kennung,
 * gilt die. Steht dort nichts, fragt die Verfügbarkeit nach einer freien.
 * Findet sie keine, bricht es hier ab — einen Auftrag ohne Maschine anzulegen
 * hieße, das Problem auf morgen zu verschieben.
 *
 * **Mit Bediener wird disponiert, nicht nur vermerkt.** Ein Mensch ist eine
 * Ressource (Regel 8) und bekommt deshalb, was eine Ressource bekommt: eine
 * eigene Position und eine eigene Belegung. Gewählt wird er in der Maske aus
 * denen, die im Zeitraum frei sind — bis zum 15.09.2026 stand dort nur, dass
 * die Planung das schon machen werde.
 *
 * **Die Bedienzeile entsteht mit 0,00**, und das ist kein Versehen:
 * `service_type` ist leer, es gibt keinen gepflegten Satz für Bedienstunden.
 * Einen zu erfinden wäre der teuerste Handgriff in dieser Datei. Der Betrag
 * kommt vom Innendienst, wenn die Stunden feststehen — dieselbe Nachberechnung
 * wie bei jedem anderen Ist.
 *
 * **Ein Preis von Hand schlägt den Tarif.** Am Telefon wird verhandelt; was
 * zugesagt ist, muss eintragbar sein, sonst steht auf der Rechnung etwas
 * anderes als am Telefon gesagt wurde. Er geht als `einzelpreis` mit und wird
 * bei der Annahme eingefroren wie jeder andere (Regel 4).
 */
/**
 * Eine Reservierung, die beim Buchen gefallen ist — wessen Angebot es war und
 * welches Gerät. Die Maske schreibt daraus die Notiz an jenem Angebot: Wem das
 * Gerät weggenommen wurde, muss es an seinem Vorgang wiederfinden.
 */
export type AufgehobeneReservierung = {
  vorgang: string;
  kunde: string;
  kennung: string;
  von: string;
  bis: string;
};

function alsAufgehoben(roh: Roh): AufgehobeneReservierung[] {
  return alsListe(alsObjekt(roh["maschine"])["aufgehoben"]).map((a) => ({
    vorgang: txt(a["vorgang"]),
    kunde: txt(a["kunde"]),
    kennung: txt(a["kennung"]),
    von: txt(a["von"]),
    bis: txt(a["bis"]),
  })).filter((a) => a.vorgang !== "");
}

export async function schnellVermieten(
  auftrag: Schnellauftrag,
): Promise<{ nr: string; aufgehoben: AufgehobeneReservierung[] }> {
  if (ATTRAPPE) return gleich({ nr: "V-2026-0099", aufgehoben: [] }, 200);

  const kunde = await hole<Roh>(`/kunden/${encodeURIComponent(auftrag.kunde)}`);
  const kundeId = txt(kunde["id"]);
  const ortId = auftrag.ort
    ? txt(alsListe(kunde["orte"]).find((o) => txt(o["label"]) === auftrag.ort)?.["id"] ?? "")
    : "";

  /* **Erst alle Maschinen suchen, dann anlegen.** Wer mitten im Anlegen
     merkt, dass für die zweite Mietzeile nichts frei ist, hinterlässt einen
     halben Vorgang — und der steht danach in der Liste und in niemandes Kopf.
     Vergeben wird jede Maschine nur einmal: `verfuegbarkeit` weiß nichts von
     der Zeile davor, weil deren Belegung noch nicht existiert. */
  const vergeben = new Set<string>();
  async function freieMaschine(
    typ: string, von: string, bis: string, gewuenscht?: string, menge = 1,
  ): Promise<string> {
    const lage = await verfuegbarkeit(typ, von, bis, menge);
    /* **Ein gezählter Bestand ist kein Stück, das vergeben wird** (0194):
       Zwei Zeilen mit Fahrbahnplatten gehen auf denselben Pool. Gefragt wird
       nach der Stückzahl; reicht sie nicht, bricht es hier ab — vor dem
       ersten Anlegen, wie beim Einzelstück. */
    if (lage.pool) {
      const pool = lage.vorschlaege?.[0];
      if (!pool) throw new Error("schnell.bestandReichtNicht");
      return pool.maschine_id;
    }
    const genannt = gewuenscht ? (ressourcenSchluessel.get(gewuenscht) ?? "") : "";
    if (genannt && !vergeben.has(genannt)) { vergeben.add(genannt); return genannt; }
    const frei = (lage.vorschlaege ?? []).find((v) => !vergeben.has(v.maschine_id));
    if (!frei) throw new Error("schnell.keineFreieMaschine");
    vergeben.add(frei.maschine_id);
    return frei.maschine_id;
  }

  /* Ohne Typ kein Gerät im Kopf — ein reiner Leistungsauftrag (Befund 45). */
  const mitKopf = Boolean(auftrag.typ_id);
  const maschineId = mitKopf
    ? await freieMaschine(auftrag.typ_id, auftrag.von, auftrag.bis, auftrag.maschine, auftrag.stueck ?? 1)
    : "";

  /* Die weiteren Mietzeilen bekommen ihre Maschine ebenfalls vorab. Eine
     Mietposition ohne Belegung wäre eine Zeile auf der Rechnung, die im Brett
     nicht steht — und niemand lädt sie am Morgen auf. */
  const weitereMieten: { zeile: Schnellposition; maschine: string }[] = [];
  for (const zeile of auftrag.positionen ?? []) {
    if (zeile.art !== "miete" || !zeile.maschinentyp_id) continue;
    const von = zeile.von ?? auftrag.von;
    const bis = zeile.bis ?? auftrag.bis;
    weitereMieten.push({
      zeile,
      // Der Wunsch der Zeile gilt, wie bei der ersten. Ohne ihn die erste freie.
      maschine: await freieMaschine(zeile.maschinentyp_id, von, bis, zeile.maschine, zeile.stueck ?? 1),
    });
  }

  const vorgang = await sende<Roh>("/vorgaenge", {
    kunde_id: kundeId,
    // Die Direktvermietung ist sofort ein Auftrag: V-Nummer, keine Angebotsnummer (02.10.2026).
    art: "auftrag",
    ...(ortId ? { ort_id: ortId } : {}),
    /* **Der Betreff nur, wenn ihn jemand eingetragen hat** (Luca, 05.10.2026).
       Bis dahin stand hier die Bezeichnung der ersten Maschine — und auf der
       AB über vier Positionen „MOSA Notstromaggregat GE 15 PSX". */
    ...(auftrag.betreff?.trim() ? { betreff: auftrag.betreff.trim() } : {}),
    /* **Die interne Notiz kommt vom Anrufer, nicht von der Maske.** Bis zum
       17.09.2026 stand hier „mit Bediener" — ein deutscher Satz im Code für
       etwas, das seit Migration 0096 an der Position steht (`with_operator`)
       und dort auch gelesen wird. Was hier hineingehört, ist das, was
       jemand beim Telefonat notiert hat. */
    ...(auftrag.notiz_intern?.trim() ? { notiz_intern: auftrag.notiz_intern.trim() } : {}),
    // Die Bestellnummer des Kunden (0188) — am Telefon durchgegeben, auf der AB gedruckt.
    ...(auftrag.bestellnummer?.trim() ? { bestellnummer: auftrag.bestellnummer.trim() } : {}),
    // Der Hinweis für den Hof (0284) — am Vorgang, nicht mehr im Lieferschein-Vortext.
    ...(auftrag.hofhinweis?.trim() ? { hofhinweis: auftrag.hofhinweis.trim() } : {}),
  });
  const vorgangId = txt(vorgang["id"]);
  const aufgehoben: AufgehobeneReservierung[] = [];
  const zumAufmass: string[] = [];

  /* **Scheitert ein Schritt, bleibt kein halber Auftrag liegen** (V-42,
     03.10.2026). Zwei Disponenten schlossen gleichzeitig ab; der zweite
     scheiterte an der Position — und V-2026-0030 blieb als leerer Entwurf
     stehen. Bis zur Annahme ist der Vorgang ein Entwurf, den niemand gesehen
     hat; er wird verworfen (`DELETE /vorgaenge/:id`, nur ein Entwurf ohne
     Beleg drüben), und der Fehler geht wie bisher an die Maske. */
  try {

  /* Vor- und Nachtext, wenn am Telefon schon einer dastand — als Texte der
     **Auftragsbestätigung** (0105), nicht des Angebots, das es hier nicht
     gibt. Vor der Annahme, weil die Auftragsbestätigung dort entsteht und
     sie sonst nicht trüge. */
  if (auftrag.vortext?.trim() || auftrag.nachtext?.trim()) {
    await ersetze<Roh>(`/vorgaenge/${vorgangId}/texte/auftragsbestaetigung`, {
      ...(auftrag.vortext?.trim() ? { vortext: auftrag.vortext.trim() } : {}),
      ...(auftrag.nachtext?.trim() ? { nachtext: auftrag.nachtext.trim() } : {}),
    });
  }

  const bedienerId = auftrag.bediener ? (ressourcenSchluessel.get(auftrag.bediener) ?? "") : "";
  if (mitKopf) {
    const ersteZeile = await sende<Roh>(`/vorgaenge/${vorgangId}/positionen`, {
      art: "miete",
      maschinentyp_id: auftrag.typ_id,
      ...(auftrag.variante_id ? { variante_id: auftrag.variante_id } : {}),
      bezeichnung: auftrag.bezeichnung,
      von: auftrag.von,
      bis: auftrag.bis,
      /* **Die Maschine geht an der Position mit** (17.09.2026). Bis dahin folgte
         hier ein eigenes `POST /belegungen`. Seit die API jede Mietposition
         selbst ins Brett bringt (dienste/belegung.ts, `maschineBinden`), hätte
         das eine zweite Belegung daneben gelegt — und die Annahme wäre an der
         eigenen Vormerkung gescheitert. Der Balken beginnt dort wie hier um
         07:00 und endet um 18:00. */
      maschine_id: maschineId,
      // Die Stückzahl eines gezählten Bestands (0194) — sonst eins.
      ...(auftrag.stueck && auftrag.stueck > 1 ? { stueck: auftrag.stueck } : {}),
      /* **Die Bestätigung geht mit** (18.09.2026): Hält ein anderes Angebot das
         Gerät, weist die API ohne sie ab — mit ihr löst sie die fremde
         Vormerkung. Gefragt wird in der Maske, nicht hier. */
      ...(auftrag.reservierung_uebergehen ? { reservierung_uebergehen: true } : {}),
      // Ohne Angabe rechnet die API aus dem Tarif. Mit Angabe gilt sie.
      ...(auftrag.einzelpreis !== undefined ? { einzelpreis: auftrag.einzelpreis } : {}),
      /* **Mit Bediener steht an der Position** (Migration 0096), nicht mehr nur
         als Satz in der internen Notiz: Zuschläge, die nur mit oder nur ohne
         Bediener gelten, lesen es dort — auch wenn noch niemand gewählt ist. */
      ...(auftrag.bedient ? { bedient: true } : {}),
      /* **Und wer fährt, hängt an derselben Position** (dienste/bediener.ts) —
         nicht mehr als eigene Leistungszeile mit 0,00. So prüft die Buchung die
         Qualifikation gegen den Typ, und die Übergabe der Maschine nimmt ihn
         mit hinaus und zurück. */
      ...(auftrag.bedient && bedienerId ? { bediener_id: bedienerId } : {}),
      /* Das Zubehör hängt an derselben Position wie sein Träger — ein Gespann,
         eine Zeile für den Kunden, ein Balken je Stück für die Disposition.
         `fest` wie der Träger: Die Schnellerfassung bucht, sie merkt nicht vor. */
      ...(auftrag.zubehoer?.length
        ? { zubehoer: auftrag.zubehoer.map((ressource_id) => ({ ressource_id })), zubehoer_belegung: "fest" }
        : {}),
    });
    aufgehoben.push(...alsAufgehoben(ersteZeile));
  }

  /* Die Zeilen, die am Telefon dazugekommen sind — der Reihe nach, damit sie
     unten auf dem Papier in derselben Reihenfolge stehen wie hier auf dem
     Schirm. **Vor** dem Annehmen: sonst fehlen sie im Schnappschuss und auf
     der Auftragsbestätigung. */
  for (const zeile of auftrag.positionen ?? []) {
    /* `maschine`, `bediener` und `zubehoer` sprechen Kennungen — die API
       spricht UUIDs. Die Position hängt am Typ (Regel 2); die vorab gesuchte
       Maschine geht als `maschine_id` mit, und die API legt ihre Belegung an
       derselben Position an. Also wird hier übersetzt, an einer Stelle. */
    // `reservierung_uebergehen` bleibt in `rumpf` und geht damit an die API.
    const { maschine: _weg, bediener, zubehoer, aufmass, ...rumpf } = zeile;
    const fahrerId = bediener ? (ressourcenSchluessel.get(bediener) ?? "") : "";
    const mit = weitereMieten.find((x) => x.zeile === zeile);
    const weitereZeile = await sende<Roh>(`/vorgaenge/${vorgangId}/positionen`, {
      ...rumpf,
      ...(mit ? { maschine_id: mit.maschine } : {}),
      ...(fahrerId ? { bediener_id: fahrerId } : {}),
      ...(zubehoer?.length
        ? {
          zubehoer: zubehoer.map((ressource_id) => ({ ressource_id })),
          // Wie beim Träger oben: Die Schnellvermietung bucht, sie merkt nicht vor.
          zubehoer_belegung: "fest",
        }
        : {}),
    });
    aufgehoben.push(...alsAufgehoben(weitereZeile));
    if (aufmass && !zeile.menge && txt(weitereZeile["id"])) zumAufmass.push(txt(weitereZeile["id"]));
  }

  await sende<Roh>(`/vorgaenge/${vorgangId}/annehmen`, auftrag.nachtraeglich ? { nachtraeglich: true } : {});
  } catch (fehler) {
    await loesche<void>(`/vorgaenge/${vorgangId}`).catch(() => undefined);
    throw fehler;
  }
  /* **Wer misst, erfährt es** (V-22, Durchgang 03.10.2026). Eine Leistung,
     deren Menge aus dem Aufmaß folgt, ging mit Menge 0 hinaus — ohne
     Ortsaufnahme bliebe sie dort, bis jemand die Rechnung sieht. Bestellt
     wird wie aus dem Angebot (`POST /aufnahmen`); scheitert es, steht die
     Position weiter mit „Menge folgt aus dem Aufmaß" am Auftrag. */
  for (const position of zumAufmass) {
    await sende<Roh>("/aufnahmen", { position_id: position }).catch(() => undefined);
  }
  return { nr: txt(vorgang["number"]), aufgehoben };
}

/* ══════════════ Der Abschluss der Merkliste (17.09.2026) ══════════════

   Luca, 17.09.2026: *„Unten rechts in der Ecke habe ich generell zwei
   Buttons: jetzt vermieten und Angebot erstellen. Das ist auf Basis der
   gemerkten Artikel, und dann kommt die Abfrage: welcher Kunde, welche
   Baustelle. … Bei Vermieten legt man direkt den Auftrag an — da muss man
   nochmal bestätigen, der Kunde hat telefonisch bestellt. Oder man erzeugt
   das Angebot, und das geht vorausgefüllt auf."*

   **Zwei Knöpfe, ein Weg — bis zum letzten Schritt.** Kunde, Einsatzort,
   Zeitraum und Verfügbarkeit stehen bei beiden vorher fest; erst danach
   trennen sie sich: Der eine legt einen angenommenen Auftrag an, der andere
   einen Entwurf, den die Angebotsmaske vorausgefüllt aufnimmt.

   **Und beide gehen durch die bestehenden Wege.** Der Auftrag durch
   `schnellVermieten` — dieselben sechs Aufrufe, dieselbe Maschinenwahl,
   dieselbe Annahme. Eine zweite Schreibrichtung für dieselbe Sache wäre
   eine zweite Wahrheit, und die erste, die auseinanderläuft. */

/** Eine Zeile aus der Merkliste, so wie sie geschrieben wird. */
export type Merkzeile = {
  /** Die UUID des Maschinentyps. Die Position hängt am Typ (Regel 2). */
  typ_id: string;
  /** Die Ausstattung — leer heißt Grundpreis. */
  variante_id?: string;
  /** Der Name, der auf dem Beleg steht — mit Ausstattung, wenn es eine gibt. */
  bezeichnung: string;
  von: string;
  bis: string;
  bedient: boolean;
  /** Wie viele Stück. Jedes bekommt eine eigene Position und eine eigene Belegung. */
  menge: number;

  /* ── Was das Blatt am Posten dazulegt (17.09.2026) ──────────────────
     docs/38-vermietmodul.md, Schritt 2: Was die Schnellerfassung an einer
     Zeile kann, kann jetzt jeder Posten. Alles davon ist freiwillig — ein
     Posten aus der Preisauskunft trägt nichts davon. */

  /** Die Kennung der gewünschten Maschine. Leer heißt: eine freie nehmen. */
  maschine?: string;
  /**
   * Der Mensch hat bestätigt, dass er ein Gerät nimmt, das ein anderes Angebot
   * für seine Bindefrist reserviert hat (18.09.2026). Ohne die Bestätigung
   * weist die API mit `belegung.reserviert` ab und nennt Angebot, Kunde und
   * Frist; mit ihr wird die fremde Vormerkung beim Buchen gelöst.
   */
  reservierung_uebergehen?: boolean;
  /** Die Kennung der Person, die bedient. Leer heißt: noch niemand. */
  bediener?: string;
  /** Die Zubehörstücke, die mit hinausgehen — Ressourcen-UUIDs. */
  zubehoer?: string[];
  /** Ein zugesagter Nettosatz **je Einheit** statt des Tarifs. */
  einzelpreis?: number;
  /**
   * Die Zuschläge, an denen jemand etwas geändert hat.
   *
   * Nur die: Was niemand angefasst hat, rechnet der Abrechnungslauf aus der
   * Regel. Eine Zeile mit demselben Code **schlägt** sie dort — ein
   * zugesagter Wert vor einem gerechneten (`packages/fach/src/abrechnung.ts`).
   * Abgewählt heißt `einzelpreis: 0`: Die Zeile steht auf dem Beleg und sagt,
   * dass nichts berechnet wird, statt spurlos zu verschwinden.
   */
  zuschlaege?: { code: string; bezeichnung: string; menge: number; einzelpreis: number; je_auftrag?: boolean }[];
};

export type MerklisteAbschluss = {
  /** Die Kundennummer aus der Liste — `10001`, nicht die UUID. */
  kunde: string;
  /** Der Einsatzort, so wie er beim Kunden heißt. Leer heißt: keiner. */
  ort?: string;
  /**
   * Vor- und Nachtext des Belegs, der aus diesem Abschluss entsteht.
   *
   * **Welcher Beleg das ist, entscheidet der Knopf** (Migration 0105): Beim
   * Vermieten ist es die Auftragsbestätigung — die Direktvermietung hat kein
   * Angebot (Regel 10). Beim Angebot sind es die Texte des Angebots.
   */
  vortext?: string;
  nachtext?: string;
  /**
   * Der Hinweis für den Hof — **eine Angabe am Vorgang** (0284,
   * `rental_order.yard_note`).
   *
   * Er steht dort und nicht in der internen Notiz: Wer beim Aufladen etwas
   * wissen muss, liest keine Vorgangsnotiz — er sieht ihn in Vorbereitung und
   * Einsatz und auf dem Übergabeprotokoll. Bis 0284 war er der Vortext des
   * Lieferscheins und überschrieb dort den Kundentext.
   */
  hofhinweis?: string;
  /** Die interne Notiz am Vorgang. Sie geht auf keinen Beleg. */
  notiz_intern?: string;
  /** Die Bestellnummer/Kommission des Kunden (0188) — Kopfzeile ab der AB. */
  bestellnummer?: string;
  /** „Nachträglich erfassen" bestätigt — der Mietbeginn liegt zurück (F20). */
  nachtraeglich?: boolean;
  zeilen: Merkzeile[];
  /**
   * **Die Zeilen ohne Gerät** — Leistung, Ware, Zuschlag, Freitext
   * (18.09.2026).
   *
   * Sie standen bis dahin nur in der Schnellerfassung
   * (`docs/38-vermietmodul.md`, „Was bis zum Umschalten noch fehlt", 1) und
   * gehen über genau denselben Weg hinaus wie dort: als `Schnellposition`
   * am selben Vorgang, **vor** der Annahme. Danach sind die Preise
   * eingefroren (Regel 4) und die Auftragsbestätigung ist geschrieben —
   * eine Zeile, die später entsteht, fehlt auf dem Papier, das der Kunde
   * bekommt.
   *
   * Sie stehen **hinter** allen Mieten: Auf dem Beleg gehört jede
   * Zuschlagszeile zu ihrer Miete, und was dazukommt, kommt darunter.
   */
  positionen?: Merkposition[];
};

/**
 * Eine Zeile ohne Gerät, so wie die Sammlung sie trägt.
 *
 * **Kein Zeitraum, keine Maschine, keine Belegung.** Sie wird nicht
 * disponiert; sie steht auf dem Beleg. Die Kennung aus dem Katalog schlägt
 * Bezeichnung, Einheit und Satz drüben nach — ohne sie trägt die Zeile
 * alles selbst („Hydraulikschlauch, Ersatz").
 */
export type Merkposition = {
  art: "leistung" | "verkauf" | "zuschlag" | "text";
  bezeichnung: string;
  /** Eine Textzeile hat keine Menge und keinen Betrag. */
  menge: number;
  einzelpreis: number;
  leistungsart_id?: string;
  /** Der gewählte Grad einer Leistung (P8-05) — `service_variant_id`. */
  kategorie_id?: string;
  ware_id?: string;
  zuschlag_code?: string;
  /** Die Menge folgt aus dem Aufmaß (V-22) — geht nicht an die API. */
  aufmass?: boolean;
  /**
   * **Der Termin einer Leistung** (P10-02) — der in „Vermieten" gewählte Tag
   * oder Zeitraum. Er wird `planned_from`/`planned_until` der Position und
   * damit das Leistungsdatum auf AB und Rechnung. Nur an einer Leistung.
   */
  von?: string;
  bis?: string;
};

/** Der Termin einer Leistung, wie er an `POST …/positionen` geht (P10-02). */
function terminDerZeile(z: Merkposition): { von?: string; bis?: string } {
  return z.art === "leistung" && z.von ? { von: z.von, bis: z.bis || z.von } : {};
}

/**
 * Menge 2 heißt zwei Maschinen, nicht eine Zeile mit einer Zwei.
 *
 * Bei einer Mietposition ist die Menge die Zahl der **Miettage** — der Satz
 * gilt je Tag. Zwei Schlepper sind deshalb zwei Positionen mit je einer
 * eigenen Belegung; ein Gerät, das nur als Zahl in einer Zeile steht, lädt
 * am Morgen niemand auf (Regel 8).
 */
function einzelZeilen(zeilen: Merkzeile[], gezaehlt: ReadonlySet<string> = new Set()): Merkzeile[] {
  return zeilen.flatMap((z) =>
    /* **Außer beim gezählten Bestand** (Migration 0194, Lisas Pool-Wunsch):
       Zwanzig Fahrbahnplatten sind EINE Zeile mit Stückzahl 20 — eine
       Position, eine Belegung mit der Menge 20, eine Zeile im Brett. */
    gezaehlt.has(z.typ_id)
      ? [{ ...z, menge: Math.max(1, Math.round(z.menge)) }]
      : Array.from({ length: Math.max(1, Math.round(z.menge)) }, () => ({ ...z, menge: 1 })));
}

/** Welche Typen gezählt werden (0194) — gefragt, wenn der Abschluss sie braucht. */
async function gezaehlteTypen(zeilen: Merkzeile[]): Promise<Set<string>> {
  if (!zeilen.some((z) => z.menge > 1)) return new Set();
  const typen = await stammMaschinentypen().catch(() => ({ zeilen: [] as Maschinentyp[] }));
  return new Set(typen.zeilen.filter((x) => x.pool).map((x) => x.id));
}

/** Die Zeile als Position eines Schnellauftrags. */
function alsSchnellposition(z: Merkzeile): Schnellposition {
  return {
    art: "miete",
    maschinentyp_id: z.typ_id,
    ...(z.variante_id ? { variante_id: z.variante_id } : {}),
    bezeichnung: z.bezeichnung,
    von: z.von,
    bis: z.bis,
    ...(z.bedient ? { bedient: true } : {}),
    ...(z.maschine ? { maschine: z.maschine } : {}),
    // Nach `einzelZeilen` trägt nur ein gezählter Posten noch eine Menge über 1.
    ...(z.menge > 1 ? { stueck: z.menge } : {}),
    ...(z.reservierung_uebergehen ? { reservierung_uebergehen: true } : {}),
    ...(z.bediener ? { bediener: z.bediener } : {}),
    ...(z.zubehoer?.length ? { zubehoer: z.zubehoer } : {}),
    ...(z.einzelpreis === undefined ? {} : { einzelpreis: z.einzelpreis }),
  };
}

/**
 * Die Zuschlagszeilen eines Postens.
 *
 * Sie stehen **hinter** ihrer Miete auf dem Papier, weil sie zu ihr gehören —
 * darum werden sie je Posten erzeugt und nicht am Ende angehängt. Ein Posten
 * mit Menge 2 erzeugt sie zweimal, einmal je Maschine: Der Zuschlag hängt an
 * der Position, und zwei Maschinen sind zwei Positionen (Regel 8).
 */
function zuschlagszeilen(z: Merkzeile, schonJeAuftrag: Set<string> = new Set()): Schnellposition[] {
  /* **Einmal je Auftrag heißt einmal** (Durchgang 03.10.2026, V-20): Ein
     Posten mit zwei Radladern wird zwei Positionen, und jede trug bis hierher
     ihre eigene Anlieferungszeile — zweimal im Auftrag, sobald sie einen
     Betrag hat. Eine Zeile von Hand für eine Regel `scope = 'auftrag'` geht
     nur mit der ersten Miete hinaus, die sie trägt. */
  const zeilen = einmalJeAuftrag(
    z.zuschlaege ?? [], (x) => ({ code: x.code, jeAuftrag: x.je_auftrag === true }), schonJeAuftrag,
  );
  return zeilen.map((x) => ({
    art: "zuschlag" as const,
    zuschlag_code: x.code,
    bezeichnung: x.bezeichnung,
    menge: x.menge,
    einzelpreis: x.einzelpreis,
  }));
}

/**
 * Aus der Merkliste ein **angenommener Auftrag** — derselbe Weg wie die
 * Schnellerfassung, nur mit mehreren Mietzeilen.
 *
 * Die erste Zeile ist der Kopf (sie gibt den Betreff), die übrigen gehen als
 * `positionen` mit. Jede bekommt ihre Maschine und ihre Belegung, und zwar
 * **vorab**: Wer mitten im Anlegen merkt, dass für die dritte Zeile nichts
 * frei ist, hinterlässt einen halben Vorgang.
 */
export async function merklisteVermieten(
  eingabe: MerklisteAbschluss,
): Promise<{ nr: string; aufgehoben: AufgehobeneReservierung[] }> {
  const [kopf, ...weitere] = einzelZeilen(eingabe.zeilen, await gezaehlteTypen(eingabe.zeilen));
  /* **Ohne Miete: ein reiner Leistungsauftrag** (Systemprüfung Befund 45,
     25.09.2026) — die PV-Reinigung ohne Gerät. Derselbe Weg, nur ohne Kopf:
     Der Betreff kommt aus der ersten Zeile, die etwas berechnet, und alle
     Zeilen gehen als Positionen vor der Annahme hinaus. */
  if (!kopf) {
    const erste = (eingabe.positionen ?? []).find((z) => z.art === "leistung" || z.art === "verkauf");
    if (!erste) throw new Error("merkliste.leer");
    const ergebnis = await schnellVermieten({
      kunde: eingabe.kunde,
      ...(eingabe.ort ? { ort: eingabe.ort } : {}),
      typ_id: "",
      bezeichnung: erste.bezeichnung,
      von: "",
      bis: "",
      ...(eingabe.vortext ? { vortext: eingabe.vortext } : {}),
      ...(eingabe.nachtext ? { nachtext: eingabe.nachtext } : {}),
      ...(eingabe.notiz_intern ? { notiz_intern: eingabe.notiz_intern } : {}),
      ...(eingabe.bestellnummer ? { bestellnummer: eingabe.bestellnummer } : {}),
      ...(eingabe.hofhinweis ? { hofhinweis: eingabe.hofhinweis } : {}),
      ...(eingabe.nachtraeglich ? { nachtraeglich: true } : {}),
      positionen: (eingabe.positionen ?? []).map((z): Schnellposition => ({
        art: z.art,
        bezeichnung: z.bezeichnung,
        menge: z.menge,
        einzelpreis: z.einzelpreis,
        ...(z.leistungsart_id ? { leistungsart_id: z.leistungsart_id } : {}),
        ...(z.kategorie_id ? { kategorie_id: z.kategorie_id } : {}),
        ...(z.ware_id ? { ware_id: z.ware_id } : {}),
        ...(z.zuschlag_code ? { zuschlag_code: z.zuschlag_code } : {}),
        ...(z.aufmass ? { aufmass: true } : {}),
        ...terminDerZeile(z),
      })),
    });
    return ergebnis;
  }

  /** Welche Zuschläge „einmal je Auftrag" schon eine Zeile haben (V-20). */
  const jeAuftrag = new Set<string>();
  const { nr, aufgehoben } = await schnellVermieten({
    kunde: eingabe.kunde,
    ...(eingabe.ort ? { ort: eingabe.ort } : {}),
    typ_id: kopf.typ_id,
    ...(kopf.menge > 1 ? { stueck: kopf.menge } : {}),
    ...(kopf.variante_id ? { variante_id: kopf.variante_id } : {}),
    bezeichnung: kopf.bezeichnung,
    von: kopf.von,
    bis: kopf.bis,
    bedient: kopf.bedient,
    ...(kopf.maschine ? { maschine: kopf.maschine } : {}),
    ...(kopf.reservierung_uebergehen ? { reservierung_uebergehen: true } : {}),
    ...(kopf.bediener ? { bediener: kopf.bediener } : {}),
    ...(kopf.zubehoer?.length ? { zubehoer: kopf.zubehoer } : {}),
    ...(kopf.einzelpreis === undefined ? {} : { einzelpreis: kopf.einzelpreis }),
    ...(eingabe.vortext ? { vortext: eingabe.vortext } : {}),
    ...(eingabe.nachtext ? { nachtext: eingabe.nachtext } : {}),
    ...(eingabe.notiz_intern ? { notiz_intern: eingabe.notiz_intern } : {}),
    ...(eingabe.bestellnummer ? { bestellnummer: eingabe.bestellnummer } : {}),
    ...(eingabe.hofhinweis ? { hofhinweis: eingabe.hofhinweis } : {}),
    ...(eingabe.nachtraeglich ? { nachtraeglich: true } : {}),
    /* Die Zuschläge des Kopfes stehen direkt hinter ihm, dann die weiteren
       Mieten mit ihren eigenen. Auf dem Papier ist das die Reihenfolge, in
       der jemand sie vorliest. */
    positionen: [
      ...zuschlagszeilen(kopf, jeAuftrag),
      ...weitere.flatMap((z) => [alsSchnellposition(z), ...zuschlagszeilen(z, jeAuftrag)]),
      /* **Und dann, was kein Gerät ist** (18.09.2026): Leistung, Ware,
         Zuschlag, Freitext. Ganz unten, weil auf dem Beleg jede
         Zuschlagszeile hinter ihrer Miete steht und was dazukommt,
         darunter. */
      ...(eingabe.positionen ?? []).map((z): Schnellposition => ({
        art: z.art,
        bezeichnung: z.bezeichnung,
        menge: z.menge,
        einzelpreis: z.einzelpreis,
        ...(z.leistungsart_id ? { leistungsart_id: z.leistungsart_id } : {}),
        ...(z.kategorie_id ? { kategorie_id: z.kategorie_id } : {}),
        ...(z.ware_id ? { ware_id: z.ware_id } : {}),
        ...(z.zuschlag_code ? { zuschlag_code: z.zuschlag_code } : {}),
        ...(z.aufmass ? { aufmass: true } : {}),
        ...terminDerZeile(z),
      })),
    ],
  });

  /* **Der Hinweis für den Hof geht mit dem Anlegen hinaus** (0284) — als
     Angabe am Vorgang, nicht mehr hinterher als Vortext des Lieferscheins.
     Damit kann er nicht mehr still verloren gehen, und der Kundentext des
     Lieferscheins bleibt der Standard. */
  return { nr, aufgehoben };
}

/**
 * Aus der Merkliste ein **Angebotsentwurf**.
 *
 * Der Vorgang entsteht mit seinen Positionen und kein Beleg drüben. **Jede
 * Mietposition merkt dabei eine freie Maschine vor** — das macht die API
 * beim Anlegen (`maschineBinden`, seit dem 17.09.2026): eine `option` mit der
 * Angebotsfrist als Verfall, sichtbar im Brett und weich, sie sperrt nichts
 * (23-angebotsprozess.md, „Was mit der Option im Brett passiert"). Bis dahin
 * entstand hier gar keine Belegung, und das Brett blieb leer. Nach Lexware
 * geht es erst, wenn der Mensch in der Angebotsmaske „Angebot anlegen"
 * drückt (Regel 1).
 */
export async function merklisteAngebot(
  eingabe: MerklisteAbschluss,
): Promise<{ nr: string; aufgehoben: AufgehobeneReservierung[] }> {
  const zeilen = einzelZeilen(eingabe.zeilen, await gezaehlteTypen(eingabe.zeilen));
  /* **Kein Betreff aus der ersten Miete** (Luca, 05.10.2026): Bis dahin
     stand hier der Name der ersten Maschine, und auf dem Angebot über vier
     Positionen „MOSA Notstromaggregat GE 15 PSX". Den Betreff trägt ein
     Mensch ein, in der Angebotsmaske. Leer bleibt die Merkliste trotzdem
     nicht: ohne Miete, Leistung oder Ware kein Angebot. */
  const etwasDarin = zeilen.length > 0
    || (eingabe.positionen ?? []).some((z) => z.art === "leistung" || z.art === "verkauf");
  if (!etwasDarin) throw new Error("merkliste.leer");

  /* **Die Bindefrist wird vorbelegt** (Regel 11) — wie in der Angebotsmaske,
     aus `tenant_settings.default_quote_days`. Ohne sie ginge der Entwurf mit
     leerem Feld auf, und die Maske belegt es nicht nach: Sie tut das nur beim
     NEUEN Angebot, und dieses hier ist schon angelegt. */
  const vorgaben = await angebotVorgaben().catch(() => null);

  const angelegt = await angebotAnlegen({
    kunde: eingabe.kunde,
    ...(eingabe.ort ? { ort: eingabe.ort } : {}),
    ...(vorgaben?.frist_bis ? { gueltig_bis: vorgaben.frist_bis } : {}),
    ...(eingabe.vortext ? { vortext: eingabe.vortext } : {}),
    ...(eingabe.nachtext ? { nachtext: eingabe.nachtext } : {}),
    ...(eingabe.notiz_intern ? { notiz_intern: eingabe.notiz_intern } : {}),
    ...(eingabe.bestellnummer ? { bestellnummer: eingabe.bestellnummer } : {}),
    // Der Hinweis für den Hof (0284) — bleibt bei der Annahme am Auftrag stehen.
    ...(eingabe.hofhinweis ? { hofhinweis: eingabe.hofhinweis } : {}),
  });

  /* Der Reihe nach, nicht nebeneinander: Auf dem Papier stehen die Positionen
     in der Reihenfolge, in der sie angelegt wurden. */
  const jeAuftrag = new Set<string>();
  for (const z of zeilen) {
    await positionAnlegen(angelegt.nr, {
      art: "miete",
      maschinentyp_id: z.typ_id,
      ...(z.variante_id ? { variante_id: z.variante_id } : {}),
      bezeichnung: z.bezeichnung,
      von: z.von,
      bis: z.bis,
      ...(z.einzelpreis === undefined ? {} : { einzelpreis: z.einzelpreis }),
      // Ein gezählter Posten bleibt eine Zeile mit Stückzahl (0194).
      ...(z.menge > 1 ? { stueck: z.menge } : {}),
      /* **Kein Zubehör und kein Bediener.** Wer fährt und was mitgeht,
         entscheidet die Disposition, wenn der Kunde zusagt
         (17-planungsschnitt.md). Die Maschine selbst merkt die API vor. */
    });
    /* Die Zuschläge von Hand stehen auch im Angebot — sie sind der Preis,
       den der Kunde liest. Gerechnete Zuschläge stehen dort nicht als Zeile;
       sie entstehen beim Abrechnen aus der Regel. */
    // Einmal je Auftrag heißt einmal (V-20).
    for (const x of einmalJeAuftrag(z.zuschlaege ?? [], (y) => ({ code: y.code, jeAuftrag: y.je_auftrag === true }), jeAuftrag)) {
      await sende<Roh>(`/vorgaenge/${encodeURIComponent(angelegt.nr)}/positionen`, {
        art: "zuschlag",
        zuschlag_code: x.code,
        bezeichnung: x.bezeichnung,
        menge: x.menge,
        einzelpreis: x.einzelpreis,
      });
    }
  }

  /* **Die Zeilen ohne Gerät stehen auch im Angebot** (18.09.2026) — der
     Kunde liest sie, und sie sind Teil des Preises, den er zusagt. Der Reihe
     nach und ganz unten, wie beim Auftrag. */
  for (const z of eingabe.positionen ?? []) {
    await sende<Roh>(`/vorgaenge/${encodeURIComponent(angelegt.nr)}/positionen`, {
      art: z.art,
      bezeichnung: z.bezeichnung,
      menge: z.menge,
      einzelpreis: z.einzelpreis,
      ...(z.leistungsart_id ? { leistungsart_id: z.leistungsart_id } : {}),
      ...(z.kategorie_id ? { kategorie_id: z.kategorie_id } : {}),
      ...(z.ware_id ? { ware_id: z.ware_id } : {}),
      ...(z.zuschlag_code ? { zuschlag_code: z.zuschlag_code } : {}),
      ...terminDerZeile(z),
    });
  }

  /* Immer leer: Der Angebotsweg nennt keine Maschine, die API merkt selbst
     vor — und was sie vormerkt, ist nie ein reserviertes Stück. Das Feld steht
     trotzdem da, damit beide Wege dieselbe Antwort geben. */
  return { nr: angelegt.nr, aufgehoben: [] };
}

/* ══════════════ Die Texte am Vorgang ══════════════ */

export type VorgangsFelder = Partial<{
  betreff: string;
  /** Die Bestellnummer/Kommission des Kunden (0188). Leer löscht sie. */
  bestellnummer: string;
  /** Der Hinweis für den Hof (0284). Leer löscht ihn. */
  hofhinweis: string;
  /** Das Zahlungsziel dieses Vorgangs (0190). `null` heißt: wie beim Kunden. */
  zahlungsziel_tage: number | null;
  notiz_kunde: string;
  notiz_intern: string;
  /** Der Satz **über** der Positionsliste des Angebots — `introduction`. */
  vortext: string;
  /** Der Satz **darunter** — `remark`. Dieselbe Spalte wie `notiz_kunde`. */
  nachtext: string;
  gueltig_bis: string;
  ort_id: string;
  /**
   * **Wird die Anfahrtzeit dieses Vorgangs abgerechnet?** (G5, 18.09.2026)
   *
   * Keine Regel und kein Satz, sondern eine Entscheidung je Vorgang, getroffen
   * bei der Prüfung des zurückgekommenen Auftrags. Die API schreibt damit die
   * gestempelten Anfahrtzeilen mit — gelesen wird beim Abrechnen nur die Zeile.
   */
  anfahrt_abrechnen: boolean;
  /** Abrechnung einmalig oder je Monat (V-25) — bis der erste Lauf etwas abgerechnet hat. */
  abrechnung: "einmalig" | "monatlich";
  /** Dauermiete ohne festes Ende (V-25). */
  ende_offen: boolean;
}>;

/**
 * `PATCH /vorgaenge/:nr` — Betreff, Texte, Frist.
 *
 * Die API weist einen abgerechneten, stornierten oder abgelehnten Vorgang ab
 * (`409 vorgang.zustand`): Wer ihn ändert, ändert die Grundlage einer Rechnung,
 * die drüben schon eine Nummer hat. Die Maske sperrt die Felder deshalb vorher,
 * statt die Absage abzuwarten.
 */
export async function vorgangAendern(nr: string, felder: VorgangsFelder): Promise<void> {
  if (ATTRAPPE) return gleich(undefined, 120);
  await aendere<Roh>(`/vorgaenge/${encodeURIComponent(nr)}`, felder);
}

/**
 * „Kunde holt ab" am Vorgang umlegen — auch nach der Annahme (Systemprüfung
 * B-04). Die API legt die 0,00-Zeile der Anlieferung an oder nimmt sie weg;
 * gerechnet wird nichts neu.
 */
/**
 * „Kunde holt ab" umlegen. Zurück kommen die Mieten, an denen „mit Bediener"
 * dabei aufgehoben wurde (Paket 9, Angebot Runde 4) — die Maske nennt sie.
 */
export async function abholungSchalten(nr: string, an: boolean): Promise<{ ohneBediener: string[] }> {
  if (ATTRAPPE) return gleich({ ohneBediener: [] }, 120);
  const roh = await sende<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/abholung`, { an });
  const liste = Array.isArray(roh?.["ohne_bediener"]) ? roh["ohne_bediener"] as unknown[] : [];
  return { ohneBediener: liste.map((x) => String(x)) };
}

/* ══════════════ Das Notizprotokoll ══════════════ */

export async function vorgangsnotizen(nr: string): Promise<Vorgangsnotiz[]> {
  if (ATTRAPPE) return gleich([]);
  const roh = await hole<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/notizen`);
  return alsListe(roh["zeilen"]).map((z): Vorgangsnotiz => ({
    id: txt(z["id"]),
    wann: tagUndZeit(z["written_at"]),
    art: txt(z["kind"]) || "vermerk",
    text: txt(z["body"]),
    ...(txt(z["verfasser"]) ? { verfasser: txt(z["verfasser"]) } : {}),
    ...(txt(z["kuerzel"]) ? { kuerzel: txt(z["kuerzel"]) } : {}),
    ...(z["aus_feld"] ? { ausFeld: true } : {}),
  }));
}

export async function notizAnlegen(
  nr: string, text_: string, art?: string,
): Promise<void> {
  if (ATTRAPPE) return gleich(undefined, 120);
  await sende<Roh>(`/vorgaenge/${encodeURIComponent(nr)}/notizen`, {
    text: text_,
    ...(art ? { art } : {}),
  });
}

/* ══════════════ Die Ortsaufnahme ══════════════
   **Eine Aufgabe, kein Termin** (19-ortsaufnahme.md, Migration 0091). Sie
   entsteht, weil ein Angebot eine Zahl braucht, die niemand kennt — nicht,
   weil jemand auf einen Knopf drückt. Darum hat sie kein Datum: Wann der
   Vertriebler hinfährt, entscheidet er. */

export type Aufnahme = {
  id: string;
  /** `offen` · `erledigt` · `verworfen`. */
  status: string;
  kunde: string;
  /** Der Einsatzort. Darf fehlen — bei einem Neukunden gibt es ihn noch nicht. */
  ort: string | null;
  ortschaft: string | null;
  /** Wofür gemessen wird. Beides darf fehlen. */
  vorgang: string | null;
  betreff: string | null;
  position: string | null;
  /** Was gemessen wird — `QM`, `MODUL`. Die Einheit ist ihr Wort dafür. */
  metric: string;
  einheit: string;
  /** Wer sie genommen hat. Leer heißt: im Pool. */
  wer: string | null;
  /** Dessen Kennung — damit das Blatt weiß, ob sie schon meine ist. */
  werId: string | null;
  /**
   * Die Leistungsart, für die gemessen wird (0155). Trägt eine erledigte
   * Aufnahme sie und hängt an keinem Vorgang, wird aus ihr ein Entwurf
   * (Befund 19).
   */
  leistungsartId: string | null;
  /** Wann sie bestellt wurde — **kein Termin**. */
  wann: string;
  /** Die gemessene Menge, sobald jemand sie eingetragen hat. */
  menge: number | null;
  /**
   * **Gemessen** — das Aufmaß ist eingetragen (Befund 41, 25.09.2026). Eine
   * selbst angelegte Aufnahme bleibt danach `offen`, bis ein Angebot daraus
   * wird oder sie ohne Angebot abgeschlossen ist; dieser Wert unterscheidet
   * sie von einer, zu der noch niemand hingefahren ist.
   */
  gemessen: boolean;
  /** Der Tag der Messung, kurz — leer, solange nicht gemessen. */
  gemessenAm: string;
  notiz: string | null;
  /** Der feste Termin, wenn es einen gibt (Migration 0180) — sonst `null`. */
  termin: Aufnahmetermin | null;
  /** Was gemessen wird, als Wort — die Leistung (Paket aufnahmen2). */
  leistung?: string | null;
  /** Die Kundennummer — für „Angebot daraus erstellen" (Paket aufnahmen2). */
  kundeNr?: string;
  /** Zustand des Vorgangs, an dem sie hängt: führt in Angebot oder Auftrag; Lösen nur am Entwurf. */
  vorgangStatus?: string | null;
  /** Die Angebotsnummer des Vorgangs (AG-…) — die Kennung, solange es kein Auftrag ist (Paket aufnahmeakte). */
  angebotNummer?: string | null;
  /** Der Wunschtermin ohne Person (0285): Tag (`genau`) oder Woche (`kw`). */
  wunsch?: { von: string; bis: string; schaerfe: "genau" | "kw" } | null;
};

/** Der Wunsch aus den drei Spalten der API (0285) — `null`, wenn keiner steht. */
export function wunschAus(z: Roh): { von: string; bis: string; schaerfe: "genau" | "kw" } | null {
  const von = txt(z["wunsch_von"]);
  const bis = txt(z["wunsch_bis"]);
  const schaerfe = txt(z["wunsch_schaerfe"]);
  if (!von || !bis || (schaerfe !== "genau" && schaerfe !== "kw")) return null;
  return { von, bis, schaerfe };
}

/** Der Termin aus den vier Spalten der API — `null`, wenn keiner steht. */
export function terminAus(z: Roh): Aufnahmetermin | null {
  const von = txt(z["termin_von"]);
  const bis = txt(z["termin_bis"]);
  if (!von || !bis) return null;
  return { von, bis, person: txt(z["termin_person"]), personId: txt(z["termin_person_id"]) };
}

function alsAufnahme(z: Roh): Aufnahme {
  return {
    id: txt(z["id"]),
    status: txt(z["status"]) || "offen",
    kunde: txt(z["kunde"]),
    ort: txt(z["ort"]) || null,
    ortschaft: txt(z["ortschaft"]) || null,
    vorgang: txt(z["vorgang"]) || null,
    betreff: txt(z["betreff"]) || null,
    position: txt(z["position"]) || null,
    metric: txt(z["metric"]),
    einheit: txt(z["einheit"]),
    wer: txt(z["wer"]) || null,
    werId: txt(z["assigned_to"]) || null,
    leistungsartId: txt(z["leistungsart_id"]) || null,
    wann: tagKurz(z["created_at"]),
    menge: zahl(z["quantity"]),
    gemessen: txt(z["done_at"]) !== "",
    gemessenAm: txt(z["done_at"]) ? tagKurz(z["done_at"]) : "",
    notiz: txt(z["note"]) || null,
    termin: terminAus(z),
    leistung: txt(z["leistung"]) || null,
    kundeNr: txt(z["kundennummer"]),
    vorgangStatus: txt(z["vorgang_status"]) || null,
    angebotNummer: txt(z["angebot_nummer"]) || null,
    wunsch: wunschAus(z),
  };
}

export async function aufnahmen(status?: string): Promise<Liste<Aufnahme>> {
  if (ATTRAPPE) {
    /* **Derselbe Stand wie am Telefon** (18.09.2026,
       `attrappe/aufnahmen.ts`): Wer dort eine Aufnahme nimmt, sieht sie hier
       nicht weiter im Pool. Zwei gepflegte Listen für dieselbe Sache liefen
       innerhalb einer Sitzung auseinander. */
    const alle = AUF.alsAufnahmen().filter((a) => !status || a.status === status);
    return gleich({ zeilen: alle, gesamt: alle.length });
  }
  const roh = await hole<Roh>(`/aufnahmen${status ? `?status=${encodeURIComponent(status)}` : ""}`);
  const zeilen_ = alsListe(roh["zeilen"]).map(alsAufnahme);
  return { zeilen: zeilen_, gesamt: zahl(roh["gesamt"]) ?? zeilen_.length };
}

/* ══════════════ Eine Ortsaufnahme im Angebot verwenden (Paket aufnahmen2) ══════════════
   Dieselbe Verknüpfung wie „Aufmaß bestellen" — Vorgang und Position an der
   Aufnahme. Die Riegel stehen in der API (`dienste/aufnahmezuordnung.ts`). */

/** Die Ortsaufnahmen an einem Vorgang — für die Karte „Ortsaufnahme" im Angebot. */
export async function aufnahmenAmVorgang(nr: string): Promise<Aufnahme[]> {
  if (ATTRAPPE) return gleich(AUF.alsAufnahmen().filter((a) => a.vorgang === nr));
  const roh = await hole<Roh>(`/aufnahmen?vorgang=${encodeURIComponent(nr)}`);
  return alsListe(roh["zeilen"]).map(alsAufnahme);
}

/** Eine Ortsaufnahme, die sich einem Angebot zuordnen lässt. */
export type ZuordenbareAufnahme = {
  id: string;
  status: string;
  ort: string | null;
  leistung: string | null;
  einheit: string | null;
  menge: number | null;
  gemessenIso: string | null;
  angelegtIso: string;
  wer: string | null;
  terminVon: string | null;
  terminPerson: string | null;
  wunsch: { von: string; bis: string; schaerfe: "genau" | "kw" } | null;
  /** Am Einsatzort des Angebots. */
  gleicherOrt: boolean;
  /** Das Angebot hat für diese Messgröße schon eine — nicht wählbar. */
  schonDa: boolean;
};

export async function aufnahmenZuordenbar(nr: string): Promise<ZuordenbareAufnahme[]> {
  if (ATTRAPPE) return gleich([]);
  const roh = await hole<Roh>(`/aufnahmen/zuordenbar?vorgang=${encodeURIComponent(nr)}`);
  return alsListe(roh["zeilen"]).map((z) => ({
    id: txt(z["id"]),
    status: txt(z["status"]) || "offen",
    ort: txt(z["ort"]) || null,
    leistung: txt(z["leistung"]) || null,
    einheit: txt(z["einheit"]) || null,
    menge: zahl(z["quantity"]),
    gemessenIso: txt(z["done_at"]) || null,
    angelegtIso: txt(z["created_at"]),
    wer: txt(z["wer"]) || null,
    terminVon: txt(z["termin_von"]) || null,
    terminPerson: txt(z["termin_person"]) || null,
    wunsch: wunschAus(z),
    gleicherOrt: z["gleicher_ort"] === true,
    schonDa: z["schon_da"] === true,
  }));
}

/** Ein offenes Angebot des Kunden, dem sich eine Ortsaufnahme zuordnen lässt. */
export type Angebotswahl = {
  id: string; nr: string; betreff: string | null; ort: string | null;
  angelegtIso: string; gleicherOrt: boolean; leistungDa: boolean;
};

export async function angeboteFuerAufnahme(id: string): Promise<Angebotswahl[]> {
  if (ATTRAPPE) return gleich([]);
  const roh = await hole<Roh>(`/aufnahmen/${encodeURIComponent(id)}/angebote`);
  return alsListe(roh["zeilen"]).map((z) => ({
    id: txt(z["id"]),
    nr: txt(z["nummer"]),
    betreff: txt(z["betreff"]) || null,
    ort: txt(z["ort"]) || null,
    angelegtIso: txt(z["created_at"]),
    gleicherOrt: z["gleicher_ort"] === true,
    leistungDa: z["leistung_da"] === true,
  }));
}

/** Was nach dem Zuordnen gilt — für die Meldung. */
export type Aufnahmezuordnung = { vorgang: string; uebernommen: boolean; neuePosition: boolean; ohnePosition: boolean };

export async function aufnahmeZuordnen(id: string, vorgang: string): Promise<Aufnahmezuordnung> {
  if (ATTRAPPE) throw new Error("Im Attrappenbetrieb gibt es keine Zuordnung.");
  const roh = await sende<Roh>(`/aufnahmen/${encodeURIComponent(id)}/zuordnen`, { vorgang });
  const z = (roh["zuordnung"] ?? {}) as Roh;
  return {
    vorgang: txt(z["vorgang"]) || vorgang,
    uebernommen: z["uebernommen"] === true,
    neuePosition: z["neue_position"] === true,
    ohnePosition: !txt(z["position_id"]),
  };
}

export async function aufnahmeLoesen(id: string): Promise<Aufnahme> {
  if (ATTRAPPE) throw new Error("Im Attrappenbetrieb gibt es keine Zuordnung.");
  return alsAufnahme(await sende<Roh>(`/aufnahmen/${encodeURIComponent(id)}/loesen`, {}));
}

/** Bestellen — aus der Position heraus, die auf die Zahl wartet. */
export async function aufnahmeBestellen(positionId: string): Promise<Aufnahme> {
  if (ATTRAPPE) throw new Error("Im Attrappenbetrieb gibt es keine Aufnahme.");
  return alsAufnahme(await sende<Roh>("/aufnahmen", { position_id: positionId }));
}

/** Die Zeile aus dem Stand zurück — nach jedem Griff im Attrappenbetrieb. */
function aufnahmeAusStand(id: string): Aufnahme {
  const a = AUF.alsAufnahmen().find((x) => x.id === id);
  if (!a) throw new Error(`Im Attrappenbetrieb gibt es die Aufnahme ${id} nicht.`);
  return a;
}

/** Aus dem Pool nehmen — oder mit `wer: null` zurücklegen. */
export async function aufnahmeNehmen(id: string, wer?: string | null): Promise<Aufnahme> {
  if (ATTRAPPE) {
    AUF.begehungNehmen(id, wer === null ? null : undefined);
    return gleich(aufnahmeAusStand(id));
  }
  return alsAufnahme(await sende<Roh>(
    `/aufnahmen/${encodeURIComponent(id)}/nehmen`,
    wer === undefined ? {} : { wer },
  ));
}

export async function aufnahmeErgebnis(
  id: string, menge: number, notiz?: string,
): Promise<Aufnahme> {
  if (ATTRAPPE) {
    AUF.begehungErgebnis(id, { menge, ...(notiz ? { notiz } : {}) });
    return gleich(aufnahmeAusStand(id));
  }
  return alsAufnahme(await sende<Roh>(`/aufnahmen/${encodeURIComponent(id)}/ergebnis`, {
    menge, ...(notiz ? { notiz } : {}),
  }));
}

/**
 * **Einen festen Termin setzen oder verlegen** (Migration 0180). `person`
 * fehlt im Regelfall — dann fährt, wer die Aufnahme hat, sonst der Anrufer.
 * Die Zeitpunkte gehen als ISO hinaus; welche Uhr gemeint war, hat die Maske
 * beim Umrechnen entschieden (die des Geräts).
 */
export async function aufnahmeTerminSetzen(
  id: string, termin: { von: string; bis: string; person?: string },
): Promise<Aufnahme> {
  if (ATTRAPPE) {
    AUF.begehungTermin(id, termin);
    return gleich(aufnahmeAusStand(id));
  }
  return alsAufnahme(await ersetze<Roh>(`/aufnahmen/${encodeURIComponent(id)}/termin`, termin));
}

/** Der Termin entfällt — die Aufnahme bleibt offen, ohne Datum. */
export async function aufnahmeTerminEntfernen(id: string): Promise<Aufnahme> {
  if (ATTRAPPE) {
    AUF.begehungTermin(id, null);
    return gleich(aufnahmeAusStand(id));
  }
  return alsAufnahme(await loesche<Roh>(`/aufnahmen/${encodeURIComponent(id)}/termin`));
}

export async function aufnahmeVerwerfen(id: string, grund?: string): Promise<Aufnahme> {
  if (ATTRAPPE) {
    AUF.begehungVerwerfen(id, grund ?? "");
    return gleich(aufnahmeAusStand(id));
  }
  return alsAufnahme(await sende<Roh>(`/aufnahmen/${encodeURIComponent(id)}/verwerfen`,
    grund ? { grund } : {}));
}

/* ══════════════ Erscheinungsbild (Signal, Paket 6, 0283) ══════════════
   Akzentfarbe und App-Logo des Betriebs. Gelesen wird beim Start ohne
   Kontext (`schale/erscheinungsbild.ts`), geschrieben nur mit `verwalten`.
   Jede Antwort ist der ganze neue Stand — die Maske übergibt ihn an
   `erscheinungsbildSetzen`, und die ganze Oberfläche zieht nach. */

export async function erscheinungsbildAkzent(akzent: string | null): Promise<Erscheinungsbild> {
  return ersetze<Erscheinungsbild>("/erscheinungsbild", { akzent });
}

/** Ein Logo hochladen — Base64 im JSON, derselbe Weg wie die Ablage. */
export async function erscheinungsbildLogo(art: Logoart, typ: string, daten: string): Promise<Erscheinungsbild> {
  return ersetze<Erscheinungsbild>(`/erscheinungsbild/logo/${art}`, { typ, daten });
}

export async function erscheinungsbildLogoWeg(art: Logoart): Promise<Erscheinungsbild> {
  return loesche<Erscheinungsbild>(`/erscheinungsbild/logo/${art}`);
}

/** Alles auf die Vorgabe: Signalgelb, eingebaute Logos. */
export async function erscheinungsbildZurueck(): Promise<Erscheinungsbild> {
  return loesche<Erscheinungsbild>("/erscheinungsbild");
}
